import installStrictInputValidation from './strictInputValidation';
import React, { useState, useEffect, useRef, createContext, useContext, useCallback, lazy, Suspense } from 'react';
import './App.css';
import './mobile-responsive.css';
import AIAssistant from './components/AIAssistant';
import MarketplaceHeader from './components/customer/MarketplaceHeader';
import MarketplaceHome from './components/customer/MarketplaceHome';

import MarketplaceFooter from './components/customer/MarketplaceFooter';
import PortalHeader from './components/common/PortalHeader';
import PortalFooter from './components/common/PortalFooter';
import CartDrawer from './components/customer/CartDrawer';



import { WishlistProvider, useWishlist } from './components/customer/WishlistContext';
import MobileBottomNav from './components/customer/MobileBottomNav';
import SiteAnalyticsTracker from './components/analytics/SiteAnalyticsTracker';
import VendorProductCard from './components/customer/VendorProductCard';
import ProductCard from './components/customer/ProductCard';
import VendorCard from './components/customer/VendorCard';
import UiIcon from './components/UiIcon';



import {
  buildProductShareUrl,
  buildVendorShareUrl,
  buildProductShareText,
  buildVendorShareText,
} from './utils/shareShopimax';

const ProductAdvancedFields = lazy(() => import('./components/customer/ProductAdvancedFields'));
const PaymentAccountSetup = lazy(() => import('./components/PaymentAccountSetup'));
const ProductDetail = lazy(() => import('./components/customer/ProductDetail'));
const ProductDescriptionPage = lazy(() => import('./components/customer/ProductDescriptionPage'));
const FlashDealsAdminTab = lazy(() => import('./components/admin/FlashDealsAdminTab'));
const AIAdminTab = lazy(() => import('./components/admin/AIAdminTab'));
const SearchPage = lazy(() => import('./components/customer/SearchPage'));
const DealsPage = lazy(() => import('./components/customer/DealsPage'));
const Homepage2 = lazy(() => import('./components/customer/Homepage2'));
const DealsPromotionsAdminTab = lazy(() => import('./components/admin/DealsPromotionsAdminTab'));
const AdminPageExperienceTab = lazy(() => import('./components/admin/AdminPageExperienceTab'));
const AdminAnalyticsTab = lazy(() => import('./components/admin/AdminAnalyticsTab'));

const OrderConfirmationPage = lazy(() => import('./pages/OrderConfirmationPage.tsx'));
const CustomerOrdersPage = lazy(() => import('./pages/CustomerOrdersPage.tsx'));
const CustomerOrderDetailPage = lazy(() => import('./pages/CustomerOrderDetailPage.tsx'));
const VendorOrdersPage = lazy(() => import('./pages/VendorOrdersPage.tsx'));
const VendorOrderDetailPage = lazy(() => import('./pages/VendorOrderDetailPage.tsx'));

const LazyPageFallback = () => (
  <div className="min-h-[30vh] flex items-center justify-center text-sm text-gray-500" role="status">
    Loading…
  </div>
);

// ============ TOAST NOTIFICATION SYSTEM ============
const ToastContext = createContext();

export const useToast = () => {
  const context = useContext(ToastContext);
  if (!context) {
    // Fallback for components outside provider
    return {
      showToast: (message, type) => console.log(`Toast: ${type} - ${message}`),
      success: (message) => console.log(`Success: ${message}`),
      error: (message) => console.log(`Error: ${message}`),
      info: (message) => console.log(`Info: ${message}`)
    };
  }
  return context;
};

function ToastProvider({ children }) {
  const [toasts, setToasts] = useState([]);

  const removeToast = useCallback((id) => {
    setToasts(prev => prev.filter(t => t.id !== id));
  }, []);

  const showToast = useCallback((message, type = 'info') => {
    const id = Date.now();
    setToasts(prev => [...prev, { id, message, type }]);
    setTimeout(() => removeToast(id), 4000);
  }, [removeToast]);

  const success = useCallback((message) => showToast(message, 'success'), [showToast]);
  const error = useCallback((message) => showToast(message, 'error'), [showToast]);
  const info = useCallback((message) => showToast(message, 'info'), [showToast]);

  return (
    <ToastContext.Provider value={{ showToast, success, error, info }}>
      {children}
      {/* Toast Container */}
      <div className="fixed bottom-4 right-4 z-[9999] flex flex-col gap-2">
        {toasts.map(toast => (
          <div
            key={toast.id}
            className={`px-4 py-3 rounded-lg shadow-lg text-white flex items-center gap-3 min-w-[280px] max-w-md animate-slide-in ${
              toast.type === 'success' ? 'bg-green-600' :
              toast.type === 'error' ? 'bg-red-600' :
              'bg-gray-800'
            }`}
            onClick={() => removeToast(toast.id)}
          >
            <span className="text-lg">
              {toast.type === 'success' ? '' : toast.type === 'error' ? '' : 'â„¹'}
            </span>
            <span className="flex-1 text-sm">{toast.message}</span>
            <button className="text-white/70 hover:text-white"></button>
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
}

// Helper function to format currency in Naira
const formatCurrency = (amount) => {
  return `₦${amount.toLocaleString('en-NG', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

// Helper function to format currency in Naira

// Helper function to group vendors by category
const groupVendorsByCategory = (vendors, adminCategories = []) => {
  // If admin has defined categories, use those for ordering
  if (adminCategories.length > 0) {
    const grouped = {};
    const adminCategoryNames = adminCategories.map(c => c.name.toLowerCase());
    
    // First, group by admin-defined categories (in order)
    adminCategories.forEach(cat => {
      grouped[cat.name] = {
        icon: cat.icon,
        vendors: []
      };
    });
    
    // Add an "Others" category for vendors without matching categories
    grouped['Others'] = {
      icon: '',
      vendors: []
    };
    
    vendors.forEach(vendor => {
      const categories = Array.isArray(vendor.category) 
        ? vendor.category 
        : [vendor.category || 'Others'];
      
      let matched = false;
      categories.forEach(cat => {
        const catLower = (cat || '').toLowerCase();
        // Find matching admin category
        const adminCat = adminCategories.find(ac => ac.name.toLowerCase() === catLower);
        if (adminCat) {
          if (!grouped[adminCat.name].vendors.find(v => v.id === vendor.id)) {
            grouped[adminCat.name].vendors.push(vendor);
          }
          matched = true;
        }
      });
      
      // If no match, add to Others
      if (!matched) {
        if (!grouped['Others'].vendors.find(v => v.id === vendor.id)) {
          grouped['Others'].vendors.push(vendor);
        }
      }
    });
    
    // Filter out empty categories
    const result = {};
    Object.entries(grouped).forEach(([name, data]) => {
      if (data.vendors.length > 0) {
        result[name] = data;
      }
    });
    
    return result;
  }
  
  // Fallback: group by vendor's own category field
  const grouped = {};
  vendors.forEach(vendor => {
    const categories = Array.isArray(vendor.category) 
      ? vendor.category 
      : [vendor.category || 'Others'];
    
    categories.forEach(cat => {
      if (!grouped[cat]) {
        grouped[cat] = {
          icon: '',
          vendors: []
        };
      }
      if (!grouped[cat].vendors.find(v => v.id === vendor.id)) {
        grouped[cat].vendors.push(vendor);
      }
    });
  });
  return grouped;
};

// Default vendor type configurations (fallback if API fails)
const DEFAULT_VENDOR_TYPES = [
  { type: 'food', label: 'Food', icon: 'fa-utensils', description: 'Order delicious food', color: 'bg-cyan-500' },
  { type: 'products', label: 'Products', icon: 'fa-box-open', description: 'Shop products', color: 'bg-blue-500' },
  { type: 'services', label: 'Services', icon: 'fa-screwdriver-wrench', description: 'Book services', color: 'bg-green-500' },
  { type: 'property', label: 'Property', icon: 'fa-house', description: 'Find properties', color: 'bg-purple-500' },
  { type: 'events', label: 'Events', icon: 'fa-ticket', description: 'Get tickets', color: 'bg-pink-500' },
  { type: 'giftcards', label: 'Gift', icon: 'fa-gift', description: 'Buy gift cards', color: 'bg-yellow-500' }
];

// Category Carousel Component
function CategoryCarousel({ categoryName, categoryIcon, vendors, onSelectVendor, getImageUrl, onShare }) {
  const scrollRef = useRef(null);
  const [showLeftArrow, setShowLeftArrow] = useState(false);
  const [showRightArrow, setShowRightArrow] = useState(true);

  const checkScrollButtons = () => {
    if (scrollRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = scrollRef.current;
      setShowLeftArrow(scrollLeft > 0);
      setShowRightArrow(scrollLeft < scrollWidth - clientWidth - 10);
    }
  };

  useEffect(() => {
    checkScrollButtons();
    const ref = scrollRef.current;
    if (ref) {
      ref.addEventListener('scroll', checkScrollButtons);
      return () => ref.removeEventListener('scroll', checkScrollButtons);
    }
  }, [vendors]);

  const scroll = (direction) => {
    if (scrollRef.current) {
      const scrollAmount = 300;
      scrollRef.current.scrollBy({
        left: direction === 'left' ? -scrollAmount : scrollAmount,
        behavior: 'smooth'
      });
    }
  };

  return (
    <div className="mb-8">
      {/* Category Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className="h-8 w-1.5 bg-gradient-to-b from-cyan-500 to-red-500 rounded-full"></div>
          <h3 className="text-xl md:text-2xl font-bold text-gray-800 flex items-center gap-2">
            <span className="text-2xl">{categoryIcon || ''}</span>
            {categoryName}
          </h3>
        </div>
        <span className="text-sm text-gray-600 bg-gray-100 px-3 py-1 rounded-full font-medium">
          {vendors.length} {vendors.length === 1 ? 'business' : 'businesses'}
        </span>
      </div>

      {/* Carousel Container */}
      <div className="relative group">
        {/* Left Arrow */}
        {showLeftArrow && (
          <button
            onClick={() => scroll('left')}
            className="absolute left-0 top-1/2 -translate-y-1/2 z-10 bg-white/90 hover:bg-white shadow-lg rounded-full p-2 opacity-0 group-hover:opacity-100 transition-opacity duration-300"
          >
            <svg className="w-6 h-6 text-gray-800" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
        )}

        {/* Scrollable Cards Container */}
        <div
          ref={scrollRef}
          className="flex gap-3 overflow-x-auto scrollbar-hide scroll-smooth pb-2"
          style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
        >
          {vendors.map((vendor) => {
            const numericRating = Number(vendor.rating);
            const hasRating = Number.isFinite(numericRating) && numericRating > 0;
            const fullStars = hasRating ? Math.floor(numericRating) : 0;
            const hasHalfStar = hasRating && numericRating % 1 >= 0.5;
            const emptyStars = 5 - fullStars - (hasHalfStar ? 1 : 0);
            const isClosed = vendor.is_open === false;
            const vendorTypeIcon = DEFAULT_VENDOR_TYPES.find(v => v.type === (vendor.vendor_type || 'food'))?.icon || '';

            return (
              <div
                key={vendor.id}
                onClick={() => !isClosed && onSelectVendor(vendor)}
                className={`flex-shrink-0 w-[155px] sm:w-[180px] md:w-52 bg-white rounded-2xl shadow-lg overflow-hidden transition-all duration-300 hover:shadow-xl hover:scale-105 ${
                  isClosed ? 'opacity-70 cursor-not-allowed' : 'cursor-pointer'
                }`}
              >
                {/* Image Container - Takes ~70-75% of card */}
                <div className="relative h-28 sm:h-32 md:h-40">
                  <img
                    src={getImageUrl(vendor.image_url)}
                    alt={vendor.name}
                    className="w-full h-full object-cover"
                    loading="lazy"
                  />
                  {/* Vendor Type Badge */}
                  <div className="absolute top-2 right-2 text-sm bg-white rounded-full p-2 shadow-md"><i className={`fa-solid ${vendorTypeIcon}`} aria-hidden="true" /></div>
                  {/* Share Button */}
                  <button
                    onClick={(e) => onShare && onShare('vendor', vendor, e)}
                    className="absolute top-2 left-2 bg-white/90 hover:bg-white rounded-full p-1.5 shadow-md opacity-0 group-hover:opacity-100 transition-opacity"
                  >
                    <svg className="w-3.5 h-3.5 text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" />
                    </svg>
                  </button>
                  {/* Closed Overlay */}
                  {isClosed && (
                    <div className="absolute inset-0 bg-black/60 flex items-center justify-center">
                      <div className="text-center text-white">
                        <svg className="w-6 h-6 mx-auto mb-0.5" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
                        </svg>
                        <p className="font-bold text-xs">CLOSED</p>
                      </div>
                    </div>
                  )}
                </div>

                {/* Card Content - Compact ~25-30% of card */}
                <div className="p-2">
                  <h4 className="font-bold text-gray-900 text-xs md:text-sm line-clamp-1">
                    {vendor.name}
                  </h4>
                  
                  {/* Star Rating - Compact */}
                  <div className="flex items-center gap-0.5">
                    {[...Array(fullStars)].map((_, i) => (
                      <i key={`full-${i}`} className="fa-solid fa-star text-amber-500 text-[10px]" aria-hidden="true" />
                    ))}
                    {hasHalfStar && <i className="fa-solid fa-star-half-stroke text-amber-500 text-[10px]" aria-hidden="true" />}
                    {[...Array(emptyStars)].map((_, i) => (
                      <i key={`empty-${i}`} className="fa-regular fa-star text-gray-300 text-[10px]" aria-hidden="true" />
                    ))}
                    {hasRating && <span className="text-gray-500 text-[10px] ml-0.5">({numericRating.toFixed(1)})</span>}
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Right Arrow */}
        {showRightArrow && vendors.length > 3 && (
          <button
            onClick={() => scroll('right')}
            className="absolute right-0 top-1/2 -translate-y-1/2 z-10 bg-white/90 hover:bg-white shadow-lg rounded-full p-2 opacity-0 group-hover:opacity-100 transition-opacity duration-300"
          >
            <svg className="w-6 h-6 text-gray-800" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </button>
        )}
      </div>
    </div>
  );
}


// Resolve both legacy hash routes and canonical pathname routes.
// Pathname routes are canonical; hash routes remain supported for old links.
const getNavigationHash = () => {
  const hash = window.location.hash || '';
  if (hash && hash !== '#' && hash !== '#/') return hash;

  const pathname = window.location.pathname || '/';
  const search = window.location.search || '';
  if (pathname && pathname !== '/') return `#${pathname}${search}`;
  return '#/';
};

const isPublicCustomerRoute = (hash) => (
  hash === '#/deals' ||
  hash === '#/homepage2' ||
  hash === '#/search' ||
  hash === '#/checkout' ||
  hash === '#/orders' ||
  hash.startsWith('#/product/') ||
  hash.startsWith('#/vendor/') ||
  hash.startsWith('#/category/') ||
  hash.startsWith('#/order-confirmation/') ||
  hash.startsWith('#/orders/')
);

function useStrictInputValidation() {
  useEffect(() => installStrictInputValidation(), []);
}

function App() {
  useStrictInputValidation();
  const toast = useToast();
  
  // Initialize backend URL state
  const [backendUrl, setBackendUrl] = useState('');
  
  // Determine initial page based on URL hash
  const getOrderConfirmationId = () => {
    const hash = getNavigationHash();
    const match = hash.match(/^#\/order-confirmation\/([^/?#]+)/);
    return match ? decodeURIComponent(match[1]) : null;
  };

  const getInitialPage = () => {
    const hash = getNavigationHash();
    if (hash === '#/admin-login' || hash === '#/admin') return 'admin-login';
    if (hash.startsWith('#/verify-email')) return 'verify-email';
    if (hash.startsWith('#/reset-password')) return 'reset-password';
    if (hash.startsWith('#/agent-forgot-password')) return 'agent-forgot-password';
    if (hash === '#/agent-registration-success') return 'agent-registration-success';
    if (hash === '#/homepage2') return 'homepage2';
    if (hash === '#/search') return 'search';
    if (hash.startsWith('#/agent-reset-password')) return 'agent-reset-password';
    if (hash.startsWith('#/vendor-approval')) return 'vendor-approval';
    if (hash === '#/agent-register') return 'agent-register';
    if (hash === '#/agent-login') return 'agent-login';
    if (hash === '#/agent-dashboard') return 'agent-dashboard';
    if (hash.startsWith('#/product/') && hash.includes('/description')) return 'product-description';
    if (hash.startsWith('#/product/')) return 'product';
    if (hash.startsWith('#/order-confirmation/')) return 'order-confirmation';
    if (hash.startsWith('#/orders/')) return 'order-detail';
    if (hash.startsWith('#/vendor/orders/') && hash.split('/').length >= 4) return 'vendor-order-detail';
    if (hash === '#/vendor/orders') return 'vendor-orders';
    if (hash === '#/orders') return 'orders';
    if (hash.startsWith('#/vendor/')) return 'vendor';
    if (hash.startsWith('#/category/')) return 'category';
    if (hash === '#/checkout') return 'checkout';
    return 'home';
  };
  
  // Get initial tokens from URL
  const getInitialToken = (type) => {
    const hash = getNavigationHash();
    if (type === 'reset' && (hash.startsWith('#/reset-password') || hash.startsWith('#/agent-reset-password'))) {
      const params = new URLSearchParams(hash.split('?')[1] || '');
      return params.get('token') || '';
    }
    if (type === 'verify' && hash.startsWith('#/verify-email')) {
      const params = new URLSearchParams(hash.split('?')[1] || '');
      return params.get('token') || '';
    }
    return '';
  };
  
  const [currentPage, setCurrentPage] = useState(getInitialPage);
  const [currentCategoryType, setCurrentCategoryType] = useState(null); // For category pages
  const [categoryVendors, setCategoryVendors] = useState([]); // Vendors for current category page
  const [featuredVendors, setFeaturedVendors] = useState([]); // Featured vendors for category page
  const [categorySearchTerm, setCategorySearchTerm] = useState(''); // Search in category page
  const [categoryFilter, setCategoryFilter] = useState('all'); // Filter: all, open, closed
  const [vendors, setVendors] = useState([]);
  const [locations, setLocations] = useState([]);
  const [adminCategories, setAdminCategories] = useState([]); // Admin-defined categories
  const [homepageSections, setHomepageSections] = useState([]); // Admin-curated homepage sections
  const [homepageSettings, setHomepageSettings] = useState({ featured_vendor_ids: [], featured_vendor_limit: 12, featured_vendor_location_mode: 'selected_location', featured_vendor_title: 'Featured Vendors', featured_vendor_show_view_all: true });
  const [homepageFeaturedVendors, setHomepageFeaturedVendors] = useState([]);
  const [vendorTypes, setVendorTypes] = useState(DEFAULT_VENDOR_TYPES); // Dynamic vendor types from API
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [isSearchingProducts, setIsSearchingProducts] = useState(false);
  const [isProductSearchActive, setIsProductSearchActive] = useState(false);
  const [searchSuggestions, setSearchSuggestions] = useState({ products: [], vendors: [], categories: [] });
  const [searchLoading, setSearchLoading] = useState(false);
  const [vendorSearchFallbackProducts, setVendorSearchFallbackProducts] = useState([]);
  const [selectedLocation, setSelectedLocation] = useState('');
  
  // Location detection states
  const [showLocationPrompt, setShowLocationPrompt] = useState(false);
  const [userLocation, setUserLocation] = useState(null); // { name: 'Lagos', coords: {lat, lng} }
  const [locationLoading, setLocationLoading] = useState(false);
  const [locationError, setLocationError] = useState(null);
  
  // Agent System States
  const [agentAuth, setAgentAuth] = useState(() => {
    const token = localStorage.getItem('agentToken');
    const agentData = localStorage.getItem('agentData');
    return {
      isAuthenticated: !!(token && agentData),
      token: token || null,
      agent: agentData ? JSON.parse(agentData) : null
    };
  });
  const [showAgentPromo, setShowAgentPromo] = useState(false);
  const [agentPromoShown, setAgentPromoShown] = useState(() => {
    return localStorage.getItem('agentPromoShown') === 'true';
  });
  
  // Auth flow states (for password reset and email verification)
  const [resetToken, setResetToken] = useState(() => getInitialToken('reset'));
  const [registrationResult, setRegistrationResult] = useState(null);
  const [verifyToken, setVerifyToken] = useState(() => getInitialToken('verify'));
  const [selectedVendorType, setSelectedVendorType] = useState(''); // New: Filter by vendor type
  const [currentVendor, setCurrentVendor] = useState(null);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [products, setProducts] = useState([]);
  const [services, setServices] = useState([]); // New: For service vendors
  const [properties, setProperties] = useState([]); // New: For property vendors
  const [events, setEvents] = useState([]); // New: For event vendors
  const [giftcards, setGiftcards] = useState([]); // New: For gift card vendors
  const [orders, setOrders] = useState([{ items: [], id: Date.now() }]); // Array of orders, each has items array and unique id
  const [verifiedOrder, setVerifiedOrder] = useState(() => {
    try {
      const saved = sessionStorage.getItem('shopimax_verified_order');
      return saved ? JSON.parse(saved) : null;
    } catch {
      return null;
    }
  }); // Verified Paystack order for immediate confirmation display
  const [activeOrderIndex, setActiveOrderIndex] = useState(0); // Index of currently active order
  const [customerInfo, setCustomerInfo] = useState({
    full_name: '',
    email: '',
    phone: '',
    area: '',
    street: '',
    delivery_price: 0,
    referral_code: ''
  });
  const [deliveryAreas, setDeliveryAreas] = useState([]);
  const [showExitPopup, setShowExitPopup] = useState(false);
  const [pendingVendor, setPendingVendor] = useState(null);
  const [savedDraft, setSavedDraft] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showAccountDropdown, setShowAccountDropdown] = useState(false);
  
  // Share modal state
  const [shareModalData, setShareModalData] = useState(null); // { type: 'vendor'|'product', item: {...} }
  
  // Referral code state
  const [referralCode, setReferralCode] = useState('');
  const [appliedReferral, setAppliedReferral] = useState(null);
  const [referralError, setReferralError] = useState('');
  const [applyingReferral, setApplyingReferral] = useState(false);
  
  // Public marquee state (for vendor page display)
  const [publicMarquee, setPublicMarquee] = useState({
    texts: [],
    separator: '',
    speed: 30,
    is_active: true,
    color_from: '#e11d48',
    color_via: '#c026d3',
    color_to: '#6d28d9'
  });
  
  // Helper function to construct proper image URLs
  // Default fallback images for when images fail to load
  const FALLBACK_IMAGES = {
    vendor: 'https://images.unsplash.com/photo-1555396273-367ea4eb4db5?w=400&h=300&fit=crop',
    product: 'https://images.unsplash.com/photo-1546069901-ba9599a7e63c?w=400&h=300&fit=crop',
    banner: 'https://images.unsplash.com/photo-1504674900247-0877df9cc836?w=800&h=400&fit=crop',
    default: 'https://images.unsplash.com/photo-1560472354-b33ff0c44a43?w=400&h=300&fit=crop'
  };

  // Handle image error - replace with fallback
  const handleImageError = (e, type = 'default') => {
    e.target.onerror = null; // Prevent infinite loop
    e.target.src = FALLBACK_IMAGES[type] || FALLBACK_IMAGES.default;
  };

  const getImageUrl = (imageUrl) => {
    if (!imageUrl) return FALLBACK_IMAGES.default;

    const CLOUDFRONT_MEDIA_URL = 'https://dtisp995r8dst.cloudfront.net';

    // Cloudinary URLs remain supported for legacy media.
    if (imageUrl.includes('res.cloudinary.com')) {
      return imageUrl;
    }

    // New AWS/CloudFront URLs are already complete.
    if (imageUrl.startsWith(`${CLOUDFRONT_MEDIA_URL}/`)) {
      return imageUrl;
    }

    // Convert legacy uploaded-image URLs to S3 + CloudFront.
    // Examples:
    //   /api/uploads/image.jpg
    //   https://shopimax.ng/api/uploads/image.jpg
    if (imageUrl.includes('/api/uploads/')) {
      const uploadsIndex = imageUrl.indexOf('/api/uploads/');
      const legacyPath = imageUrl.substring(uploadsIndex);
      const sourceIsLocal = /localhost|127\.0\.0\.1|172\.|192\.168\.|10\./i.test(imageUrl);
      const runningLocal = /localhost|127\.0\.0\.1|172\.|192\.168\.|10\./i.test(window.location.hostname);

      // Local upload records may have been saved with localhost/LAN hostnames.
      // Always resolve those legacy paths against the currently running backend.
      if (sourceIsLocal || runningLocal) {
        return `${backendUrl}${legacyPath}`;
      }

      // Legacy production upload records can still be resolved through CloudFront.
      const filename = legacyPath.split('/').pop();
      if (filename) return `${CLOUDFRONT_MEDIA_URL}/shopimax/${filename}`;
    }

    // Preserve other complete external URLs.
    if (
      imageUrl.startsWith('http://') ||
      imageUrl.startsWith('https://')
    ) {
      return imageUrl;
    }

    // Preserve normal frontend-relative assets.
    return `${backendUrl}${imageUrl}`;
  };
  // Vendor dashboard state
  const [vendorAuth, setVendorAuth] = useState({
    isAuthenticated: false,
    vendor: null,
    token: null
  });
  const [vendorProducts, setVendorProducts] = useState([]);
  const [vendorServices, setVendorServices] = useState([]);
  const [vendorProperties, setVendorProperties] = useState([]);
  const [vendorEvents, setVendorEvents] = useState([]);
  const [vendorGiftcards, setVendorGiftcards] = useState([]);
  const [vendorOrders, setVendorOrders] = useState([]);
  const [showProductForm, setShowProductForm] = useState(false);

  // Admin dashboard state
  const [adminAuth, setAdminAuth] = useState({
    isAuthenticated: false,
    admin: null,
    token: null
  });
  const [adminRestaurants, setAdminRestaurants] = useState([]);
  const [adminStats, setAdminStats] = useState(null);
  const [adminReferralCodes, setAdminReferralCodes] = useState([]);
  
  // Banners state for customer-facing pages
  const [activeBanners, setActiveBanners] = useState({
    top_header: [],
    header_carousel: [],
    between_cards: [],
    slideshow: []
  });
  const [currentCarouselIndex, setCurrentCarouselIndex] = useState(0);
  const [currentSlideshowIndex, setCurrentSlideshowIndex] = useState(0);

  // Cart drawer + Flash deals state (marketplace redesign)
  const [showCartDrawer, setShowCartDrawer] = useState(false);
  const [cartDrawerTab, setCartDrawerTab] = useState('cart'); // 'cart' | 'wishlist'
  const [flashDeals, setFlashDeals] = useState([]);
  const [flashDealsNearestEnd, setFlashDealsNearestEnd] = useState(null);
  // Pay on Delivery: admin-configurable settings fetched once on load.
  // Defaults here only cover the brief window before the fetch resolves --
  // the backend is the actual authority on eligibility and amounts.
  const [podSettings, setPodSettings] = useState({
    pod_enabled: true,
    pod_minimum_order_value: 10000,
    pod_upfront_percentage: 20,
  });
  const [paymentMethod, setPaymentMethod] = useState('PAY_NOW');
  const whatsappDirectEnabled = Boolean(currentVendor?.whatsapp_direct_fulfillment_enabled);
  useEffect(() => {
    if (whatsappDirectEnabled) {
      setPaymentMethod('WHATSAPP_DIRECT');
    } else if (paymentMethod === 'WHATSAPP_DIRECT') {
      setPaymentMethod('PAY_NOW');
    }
  }, [whatsappDirectEnabled, paymentMethod]);
  const wishlist = useWishlist();

  // Initialize backend URL on mount
  useEffect(() => {
    // Determine backend URL
    let url = '';
    
    // If explicitly set in environment, use that
    if (process.env.REACT_APP_BACKEND_URL) {
      url = process.env.REACT_APP_BACKEND_URL;
    } else {
      // Otherwise, use the current domain (works for both preview and production)
      const hostname = window.location.hostname;
      const protocol = window.location.protocol;
      
      // If localhost, use localhost backend
      if (hostname === 'localhost' || hostname === '127.0.0.1') {
        url = ['3001', '3010'].includes(window.location.port) ? window.location.origin : 'http://localhost:8001';
      } else {
        // For LAN/local network testing, backend runs on port 8001
        if (
          hostname.startsWith('192.168.') ||
          hostname.startsWith('10.') ||
          hostname.startsWith('172.')
        ) {
          url = `${protocol}//${hostname}:8001`;
        } else {
          // For deployed environments, use same domain
          url = `${protocol}//${hostname}`;
        }
      }
    }
    
    setBackendUrl(url);
  }, []);

  useEffect(() => {
    // Only fetch data when backendUrl is ready
    if (!backendUrl) return;
    
    // Resolve both pathname and legacy hash routes before any auth redirect.
    const hash = getNavigationHash();
    const publicRoute = isPublicCustomerRoute(hash);
    if (hash === '#/admin-login' || hash === '#/admin') {
      setCurrentPage('admin-login');
      setLoading(false);
      return; // Don't fetch vendors if going to admin
    }
    
    // Check for email verification link
    if (hash.startsWith('#/verify-email')) {
      const params = new URLSearchParams(hash.split('?')[1] || '');
      const token = params.get('token');
      if (token) {
        setVerifyToken(token);
        setCurrentPage('verify-email');
        setLoading(false);
        return;
      }
    }
    
    // Check for password reset link
    if (hash.startsWith('#/reset-password')) {
      const params = new URLSearchParams(hash.split('?')[1] || '');
      const token = params.get('token');
      if (token) {
        setResetToken(token);
        setCurrentPage('reset-password');
        setLoading(false);
        return;
      }
    }
    
    // Check for stored vendor auth BEFORE fetching vendors
    const storedAuth = localStorage.getItem('vendorAuth');
    if (!publicRoute && storedAuth) {
      try {
        const auth = JSON.parse(storedAuth);
        if (auth.isAuthenticated && auth.token) {
          setVendorAuth(auth);
          setCurrentPage('vendor-dashboard'); // Restore to vendor dashboard on refresh
          setLoading(false);
          return; // Don't fetch public vendors if logged in as vendor
        }
      } catch (error) {
        localStorage.removeItem('vendorAuth');
      }
    }
    
    // Check for stored admin auth
    const storedAdminAuth = localStorage.getItem('adminAuth');
    if (!publicRoute && storedAdminAuth) {
      try {
        const auth = JSON.parse(storedAdminAuth);
        if (auth.isAuthenticated && auth.token) {
          setAdminAuth(auth);
          setCurrentPage('admin-dashboard');
          setLoading(false);
          return; // Don't fetch vendors if logged in as admin
        }
      } catch (error) {
        localStorage.removeItem('adminAuth');
      }
    }
    
    // Only fetch public data if not logged in
    fetchVendors();
    fetchLocations();
    fetchAdminCategories(); // Fetch admin-defined categories
    fetchHomepageSections(); // Fetch admin-curated homepage sections
    const homepageSettingsRequest = fetchHomepageSettings(selectedLocation);
    fetchActiveBanners(selectedLocation); // Fetch banners for homepage
    fetchVendorTypes(); // Fetch dynamic vendor types
    fetchFlashDeals(); // Fetch active flash-deal products

    // Admin-configured default landing page applies only when a visitor enters
    // the root URL. Explicit routes such as /deals, /homepage2 and product URLs
    // always remain authoritative.
    if (hash === '' || hash === '#/' || hash === '#') {
      homepageSettingsRequest
        .then(settings => {
          const landing = settings?.default_landing_page;
          if (landing === 'homepage2' || landing === 'deals' || landing === 'home') {
            setCurrentPage(landing);
          }
        })
        .catch(() => {});
    }

    // Resolve public product deep links on first load. This keeps shared product
    // URLs useful even when the visitor has no prior marketplace state.
    if (hash.startsWith('#/product/')) {
      const isDescriptionRoute = hash.includes('/description');
      const productId = hash.replace('#/product/', '').split('/')[0].split('?')[0];
      if (productId) {
        Promise.resolve().then(async () => {
          try {
            const response = await fetch(`${backendUrl}/api/products/${productId}`);
            if (!response.ok) throw new Error('Product not found');
            const data = await response.json();
            const foundProduct = data.product;
            const foundVendor = data.vendor;

            setSelectedProduct(foundProduct);
            setCurrentVendor(foundVendor || null);
            setCurrentPage(isDescriptionRoute ? 'product-description' : 'product');
            window.history.replaceState(
              { page: isDescriptionRoute ? 'product-description' : 'product', productId: foundProduct.id, vendorId: foundVendor?.id },
              '',
              `${window.location.pathname}#/product/${foundProduct.id}${isDescriptionRoute ? '/description' : ''}`
            );
          } catch (error) {
            console.error('Error resolving product deep link:', error);
            setCurrentPage('home');
          }
        });
      }
    }

    // Resolve public vendor deep links on first load -- shared vendor cards
    // link here (see utils/shareShopimax.js), but until now there was no
    // handler for this hash on a fresh page load: openVendorPage() only ran
    // for in-app navigation, so a shared vendor link opened by a new visitor
    // silently fell through to the homepage instead of the vendor's page.
    if (hash.startsWith('#/vendor/') && !hash.startsWith('#/vendor/orders')) {
      const vendorId = hash.replace('#/vendor/', '').split('?')[0];
      if (vendorId) {
        Promise.resolve().then(async () => {
          try {
            const response = await fetch(`${backendUrl}/api/vendors/${vendorId}`);
            if (!response.ok) throw new Error('Vendor not found');
            const foundVendor = await response.json();

            setCurrentVendor(foundVendor);
            setCurrentPage('vendor');
            window.history.replaceState(
              { page: 'vendor', vendorId: foundVendor.id },
              '',
              `${window.location.pathname}#/vendor/${foundVendor.id}`
            );

            const vendorType = foundVendor.vendor_type || 'food';
            await fetchVendorItems(foundVendor.id, vendorType);
            if (vendorType === 'food' || vendorType === 'products') {
              await fetchDeliveryAreas(foundVendor.id);
              await fetchDraft(foundVendor.id);
            }
          } catch (error) {
            console.error('Error resolving vendor deep link:', error);
            setCurrentPage('home');
          }
        });
      }
    }
  }, [backendUrl]);

    // Browser history management
  useEffect(() => {
    const handleNavigation = () => {
      const hash = getNavigationHash();

      window.scrollTo({ top: 0, behavior: 'instant' });

      // Special/authentication routes
      if (hash === '#/admin-login' || hash === '#/admin') {
        setCurrentPage('admin-login');
        return;
      }

      if (hash.startsWith('#/verify-email')) {
        setCurrentPage('verify-email');
        return;
      }

      if (hash.startsWith('#/reset-password')) {
        setCurrentPage('reset-password');
        return;
      }

      if (hash.startsWith('#/agent-forgot-password')) {
        setCurrentPage('agent-forgot-password');
        return;
      }

      if (hash === '#/agent-registration-success') {
        setCurrentPage('agent-registration-success');
        return;
      }

      if (hash.startsWith('#/agent-reset-password')) {
        setCurrentPage('agent-reset-password');
        return;
      }

      if (hash.startsWith('#/vendor-approval')) {
        setCurrentPage('vendor-approval');
        return;
      }

      if (hash === '#/agent-register') {
        setCurrentPage('agent-register');
        return;
      }

      if (hash === '#/agent-login') {
        setCurrentPage('agent-login');
        return;
      }

      if (hash === '#/agent-dashboard') {
        setCurrentPage('agent-dashboard');
        return;
      }

      // Public Deals & Promotions page
      if (hash === '#/deals') {
        setCurrentVendor(null);
        setSelectedProduct(null);
        setCurrentCategoryType(null);
        setCurrentPage('deals');
        return;
      }

      // Homepage2 preview page — isolated from the existing homepage.
      if (hash === '#/homepage2') {
        setCurrentVendor(null);
        setSelectedProduct(null);
        setCurrentCategoryType(null);
        setCurrentPage('homepage2');
        return;
      }

      // Public search / discovery page
      if (hash === '#/search') {
        setCurrentVendor(null);
        setSelectedProduct(null);
        setCurrentCategoryType(null);
        setCurrentPage('search');
        return;
      }

      // Public product page / full description page
      if (hash.startsWith('#/product/')) {
        const isDescriptionRoute = hash.includes('/description');
        const productId = hash.replace('#/product/', '').split('/')[0].split('?')[0];

        if (!productId) {
          setCurrentPage('home');
          return;
        }

        fetch(`${backendUrl}/api/products/${productId}`)
          .then((response) => response.ok ? response.json() : null)
          .then((data) => {
            if (data?.product) {
              setSelectedProduct(data.product);
              setCurrentVendor(data.vendor || null);
              setCurrentPage(isDescriptionRoute ? 'product-description' : 'product');
            } else {
              setCurrentPage('home');
            }
          })
          .catch((error) => {
            console.error('Error resolving product navigation:', error);
            setCurrentPage('home');
          });

        return;
      }

      // Public vendor page
      if (hash.startsWith('#/vendor/') && !hash.startsWith('#/vendor/orders')) {
        const vendorId = hash.replace('#/vendor/', '').split('?')[0];

        if (!vendorId) {
          setCurrentPage('home');
          return;
        }

        fetch(`${backendUrl}/api/vendors/${vendorId}`)
          .then((response) => response.ok ? response.json() : null)
          .then(async (foundVendor) => {
            if (!foundVendor) {
              setCurrentPage('home');
              return;
            }

            setCurrentVendor(foundVendor);
            setSelectedProduct(null);
            setCurrentPage('vendor');

            const vendorType = foundVendor.vendor_type || 'food';
            await fetchVendorItems(foundVendor.id, vendorType);

            if (vendorType === 'food' || vendorType === 'products') {
              await fetchDeliveryAreas(foundVendor.id);
              await fetchDraft(foundVendor.id);
            }
          })
          .catch((error) => {
            console.error('Error resolving vendor navigation:', error);
            setCurrentPage('home');
          });

        return;
      }

      // Public category page
      if (hash.startsWith('#/category/')) {
        const categoryType = decodeURIComponent(
          hash.replace('#/category/', '').split('?')[0]
        );

        if (!categoryType) {
          setCurrentPage('home');
          return;
        }

        setCurrentCategoryType(categoryType);
        setCurrentPage('category');
        fetchCategoryVendors(categoryType);
        return;
      }

      // Checkout
      if (hash === '#/checkout') {
        setCurrentPage('checkout');
        return;
      }

      // Order confirmation
      if (hash.startsWith('#/order-confirmation/')) {
        setCurrentPage('order-confirmation');
        return;
      }

      // Vendor order detail
      if (hash.startsWith('#/vendor/orders/')) {
        setCurrentPage('vendor-order-detail');
        return;
      }

      // Vendor orders
      if (hash === '#/vendor/orders') {
        setCurrentPage('vendor-orders');
        return;
      }

      // Customer order detail
      if (hash.startsWith('#/orders/')) {
        setCurrentPage('order-detail');
        return;
      }

      // Customer orders
      if (hash === '#/orders') {
        setCurrentPage('orders');
        return;
      }

      // Root / homepage
      if (hash === '' || hash === '#/' || hash === '#') {
        setCurrentVendor(null);
        setSelectedProduct(null);
        setCurrentCategoryType(null);
        setCurrentPage('home');
        return;
      }

      // Unknown hash: keep the app safely on home.
      setCurrentVendor(null);
      setSelectedProduct(null);
      setCurrentCategoryType(null);
      setCurrentPage('home');
    };

    window.addEventListener('popstate', handleNavigation);
    window.addEventListener('hashchange', handleNavigation);

    // Resolve the current URL immediately. Without this initial pass, a direct
    // pathname such as /deals could remain on the initial Home state until a
    // navigation event occurred.
    handleNavigation();

    return () => {
      window.removeEventListener('popstate', handleNavigation);
      window.removeEventListener('hashchange', handleNavigation);
    };
  }, [backendUrl]);

  // Helper to fetch category vendors (used by popstate handler)
  const fetchCategoryVendors = async (categoryType) => {
    if (!backendUrl) return;
    
    try {
      const params = new URLSearchParams();
      if (categoryType && categoryType !== 'all') params.set('vendor_type', categoryType);
      if (selectedLocation) params.set('location', selectedLocation);
      if (categoryType === 'all') params.set('nearby', 'true');
      const response = await fetch(`${backendUrl}/api/vendors?${params.toString()}`);
      if (response.ok) {
        const data = await response.json();
        const allVendors = data.vendors || [];
        
        const featuredResponse = await fetch(`${backendUrl}/api/homepage-sections`);
        let featuredIds = [];
        if (featuredResponse.ok) {
          const sectionsData = await featuredResponse.json();
          sectionsData.sections?.forEach(section => {
            section.vendors?.forEach(v => {
              if (v.vendor_type === categoryType) {
                featuredIds.push(v.id);
              }
            });
          });
        }
        
        const featured = allVendors.filter(v => featuredIds.includes(v.id));
        const regular = allVendors.filter(v => !featuredIds.includes(v.id));
        
        setFeaturedVendors(featured);
        setCategoryVendors(regular);
      }
    } catch (error) {
      console.error('Error fetching category vendors:', error);
    }
  };

  // Canonical navigation helper. Public customer pages use normal paths so
  // direct links, refreshes, browser Back/Forward and mobile navigation all
  // resolve through the same route representation. Legacy hash routes remain
  // readable by getNavigationHash().
  const navigateTo = (page, options = {}) => {
    const { categoryType, vendor, replace = false } = options;
    const state = { page };
    let url = '/';

    if (page === 'category' && categoryType) {
      state.categoryType = categoryType;
      url = `/category/${encodeURIComponent(categoryType)}`;
    } else if (page === 'vendor' && vendor?.id) {
      state.vendorId = vendor.id;
      url = `/vendor/${encodeURIComponent(vendor.id)}`;
    } else if (page === 'product' && options.productId) {
      state.productId = options.productId;
      url = `/product/${encodeURIComponent(options.productId)}`;
    } else if (page === 'deals') {
      url = '/deals';
    } else if (page === 'homepage2') {
      url = '/homepage2';
    } else if (page === 'search') {
      url = '/search';
    } else if (page === 'checkout') {
      url = '/checkout';
    } else if (page === 'orders') {
      url = '/orders';
    } else if (page === 'order-detail' && options.orderId) {
      state.orderId = options.orderId;
      url = `/orders/${encodeURIComponent(options.orderId)}`;
    }

    if (replace) {
      window.history.replaceState(state, '', url);
    } else {
      window.history.pushState(state, '', url);
    }

    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'instant' });
  };

  // Refetch categories when vendor type filter changes
  useEffect(() => {
    if (backendUrl) {
      fetchAdminCategories();
    }
  }, [selectedVendorType, backendUrl]);

  // Scroll to top when page changes
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [currentPage]);

  // Fetch public marquee settings when vendor page loads
  useEffect(() => {
    if (currentPage === 'vendor' && backendUrl) {
      fetchPublicMarqueeSettings();
    }
  }, [currentPage, backendUrl]);

  // POD settings are only needed in the vendor ordering flow, not for the initial homepage.
  useEffect(() => {
    if (currentPage !== 'vendor' || !backendUrl) return;
    let cancelled = false;
    fetch(`${backendUrl}/api/pod-settings`)
      .then(response => response.ok ? response.json() : null)
      .then(data => {
        if (!cancelled && data) setPodSettings(data);
      })
      .catch(() => {}); // keep the safe client-side defaults on failure
    return () => {
      cancelled = true;
    };
  }, [currentPage, backendUrl]);

  // Fetch vendor types from API
  const fetchVendorTypes = async () => {
    try {
      const response = await fetch(`${backendUrl}/api/vendor-types`);
      if (response.ok) {
        const data = await response.json();
        if (data.vendor_types && data.vendor_types.length > 0) {
          setVendorTypes(data.vendor_types);
        }
      }
    } catch (error) {
      console.error('Error fetching vendor types:', error);
      // Keep default vendor types on error
    }
  };

  // Fetch active flash-deal products (marketplace redesign)
  const fetchFlashDeals = async () => {
    try {
      const response = await fetch(`${backendUrl}/api/flash-deals`);
      if (response.ok) {
        const data = await response.json();
        setFlashDeals(data.products || []);
        setFlashDealsNearestEnd(data.nearest_end || null);
      }
    } catch (error) {
      console.error('Error fetching flash deals:', error);
    }
  };

  // Navigate to category page
  const navigateToCategoryPage = async (categoryType) => {
    setSearchTerm('');
    setSearchSuggestions({ products: [], vendors: [], categories: [] });
    setCurrentCategoryType(categoryType);
    setCurrentPage('category');
    setLoading(true);
    // Reset search and filter for new category
    setCategorySearchTerm('');
    setCategoryFilter('all');
    
    // Push to browser history for back button support
    window.history.pushState(
      { page: 'category', categoryType }, 
      '', 
      `/category/${encodeURIComponent(categoryType)}`
    );
    
    try {
      // Fetch vendors for this category
      const response = await fetch(`${backendUrl}/api/vendors?vendor_type=${categoryType}`);
      if (response.ok) {
        const data = await response.json();
        const allVendors = data.vendors || [];
        
        // Separate featured vendors (those in homepage sections) from regular vendors
        const featuredResponse = await fetch(`${backendUrl}/api/homepage-sections`);
        let featuredIds = [];
        if (featuredResponse.ok) {
          const sectionsData = await featuredResponse.json();
          sectionsData.sections?.forEach(section => {
            section.vendors?.forEach(v => {
              if (v.vendor_type === categoryType) {
                featuredIds.push(v.id);
              }
            });
          });
        }
        
        const featured = allVendors.filter(v => featuredIds.includes(v.id));
        const regular = allVendors.filter(v => !featuredIds.includes(v.id));
        
        setFeaturedVendors(featured);
        setCategoryVendors(regular);
      }
    } catch (error) {
      console.error('Error fetching category vendors:', error);
      setCategoryVendors([]);
      setFeaturedVendors([]);
    }
    
    setLoading(false);
  };

  // Vendor authentication functions
  const vendorLogin = async (email, password) => {
    try {
      const response = await fetch(`${backendUrl}/api/vendor/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email, password })
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Login failed');
      }
      
      const data = await response.json();
      if (data.pending_approval) {
        return {
          success: true,
          pendingApproval: true,
          message: data.message || 'Registration submitted for admin approval.',
          vendor_id: data.vendor_id || data.vendor?.id || null,
          vendor: data.vendor || { name: formData.name },
          email: formData.email,
        };
      }
      const authData = {
        isAuthenticated: true,
        vendor: data.vendor,
        token: data.token
      };
      setVendorAuth(authData);
      localStorage.setItem('vendorAuth', JSON.stringify(authData));
      setCurrentPage('vendor-dashboard');
      return { success: true };
    } catch (error) {
      return { success: false, error: error.message };
    }
  };

  const vendorRegister = async (formData) => {
    try {
      const response = await fetch(`${backendUrl}/api/vendor/register`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData)
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Registration failed');
      }
      
      const data = await response.json();
      if (data.pending_approval) {
        return { success: true, pendingApproval: true, message: data.message || 'Registration submitted for admin approval.' };
      }
      const authData = {
        isAuthenticated: true,
        vendor: data.vendor,
        token: data.token
      };
      setVendorAuth(authData);
      localStorage.setItem('vendorAuth', JSON.stringify(authData));
      setCurrentPage('vendor-dashboard');
      return { success: true };
    } catch (error) {
      return { success: false, error: error.message };
    }
  };

  const vendorLogout = () => {
    setVendorAuth({
      isAuthenticated: false,
      vendor: null,
      token: null
    });
    localStorage.removeItem('vendorAuth');
    navigateToHome(); // Refetch vendors when going to homepage
  };

  // Admin authentication functions
  const adminLogin = async (email, password) => {
    try {
      const response = await fetch(`${backendUrl}/api/admin/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email, password })
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Login failed');
      }
      
      const data = await response.json();
      const authData = {
        isAuthenticated: true,
        admin: { email: data.email, admin_id: data.admin_id, role: data.role || 'super_admin' },
        token: data.token
      };
      
      setAdminAuth(authData);
      localStorage.setItem('adminAuth', JSON.stringify(authData));
      setCurrentPage('admin-dashboard');
      
      return { success: true };
    } catch (error) {
      return { success: false, error: error.message };
    }
  };

  const adminLogout = () => {
    setAdminAuth({
      isAuthenticated: false,
      admin: null,
      token: null
    });
    localStorage.removeItem('adminAuth');
    navigateToHome();
  };

  // Admin functions
  const fetchAdminRestaurants = async () => {
    if (!adminAuth.token) return;
    
    try {
      const response = await fetch(`${backendUrl}/api/admin/restaurants`, {
        headers: {
          'Authorization': `Bearer ${adminAuth.token}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setAdminRestaurants(data.restaurants);
      }
    } catch (error) {
      console.error('Error fetching restaurants:', error);
    }
  };

  const toggleRestaurantStatus = async (restaurantId) => {
    if (!adminAuth.token) return;
    
    try {
      const response = await fetch(`${backendUrl}/api/admin/restaurants/${restaurantId}/toggle`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${adminAuth.token}`
        }
      });
      
      if (response.ok) {
        await fetchAdminRestaurants(); // Refresh list
        return { success: true };
      } else {
        const error = await response.json();
        return { success: false, error: error.detail };
      }
    } catch (error) {
      return { success: false, error: error.message };
    }
  };

  const fetchAdminStats = async () => {
    if (!adminAuth.token) return;
    
    try {
      const response = await fetch(`${backendUrl}/api/admin/orders/stats`, {
        headers: {
          'Authorization': `Bearer ${adminAuth.token}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setAdminStats(data);
      }
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const fetchAdminReferralCodes = async () => {
    if (!adminAuth.token) return;
    
    try {
      const response = await fetch(`${backendUrl}/api/admin/referral-codes`, {
        headers: {
          'Authorization': `Bearer ${adminAuth.token}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setAdminReferralCodes(data.referral_codes);
      }
    } catch (error) {
      console.error('Error fetching referral codes:', error);
    }
  };

  const createReferralCode = async (codeData) => {
    if (!adminAuth.token) return;
    
    try {
      const response = await fetch(`${backendUrl}/api/admin/referral-codes`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${adminAuth.token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(codeData)
      });
      
      if (response.ok) {
        await fetchAdminReferralCodes();
        return { success: true };
      } else {
        const error = await response.json();
        return { success: false, error: error.detail };
      }
    } catch (error) {
      return { success: false, error: error.message };
    }
  };

  const deleteReferralCode = async (codeId) => {
    if (!adminAuth.token) return;
    
    try {
      const response = await fetch(`${backendUrl}/api/admin/referral-codes/${codeId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${adminAuth.token}`
        }
      });
      
      if (response.ok) {
        await fetchAdminReferralCodes();
        return { success: true };
      } else {
        const error = await response.json();
        return { success: false, error: error.detail };
      }
    } catch (error) {
      return { success: false, error: error.message };
    }
  };

  const toggleReferralCodeStatus = async (codeId, currentStatus) => {
    if (!adminAuth.token) return;
    
    try {
      const response = await fetch(`${backendUrl}/api/admin/referral-codes/${codeId}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${adminAuth.token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ is_active: !currentStatus })
      });
      
      if (response.ok) {
        await fetchAdminReferralCodes();
        return { success: true };
      } else {
        const error = await response.json();
        return { success: false, error: error.detail };
      }
    } catch (error) {
      return { success: false, error: error.message };
    }
  };

  // Vendor product management
  const fetchVendorProducts = async () => {
    if (!vendorAuth.token) return;
    
    try {
      const response = await fetch(`${backendUrl}/api/vendor/products`, {
        headers: {
          'Authorization': `Bearer ${vendorAuth.token}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setVendorProducts(data.products);
      }
    } catch (error) {
      console.error('Error fetching vendor products:', error);
    }
  };

  const createProduct = async (productData) => {
    if (!vendorAuth.token) return { success: false, error: 'Not authenticated' };
    
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 30000); // 30 second timeout
      
      const response = await fetch(`${backendUrl}/api/vendor/products`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${vendorAuth.token}`
        },
        body: JSON.stringify(productData),
        signal: controller.signal
      });
      
      clearTimeout(timeoutId);
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to create product');
      }
      
      await fetchVendorProducts(); // Refresh products
      return { success: true };
    } catch (error) {
      if (error.name === 'AbortError') {
        return { success: false, error: 'Request timed out. Please check your connection and try again.' };
      }
      return { success: false, error: error.message };
    }
  };

  const updateProduct = async (productId, productData) => {
    if (!vendorAuth.token) return { success: false, error: 'Not authenticated' };
    
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 30000); // 30 second timeout
      
      const response = await fetch(`${backendUrl}/api/vendor/products/${productId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${vendorAuth.token}`
        },
        body: JSON.stringify(productData),
        signal: controller.signal
      });
      
      clearTimeout(timeoutId);
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to update product');
      }
      
      await fetchVendorProducts(); // Refresh products
      return { success: true };
    } catch (error) {
      if (error.name === 'AbortError') {
        return { success: false, error: 'Request timed out. Please check your connection and try again.' };
      }
      return { success: false, error: error.message };
    }
  };

  const deleteProduct = async (productId) => {
    if (!vendorAuth.token) return { success: false, error: 'Not authenticated' };
    
    try {
      const response = await fetch(`${backendUrl}/api/vendor/products/${productId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${vendorAuth.token}`
        }
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to delete product');
      }
      
      await fetchVendorProducts(); // Refresh products
      return { success: true };
    } catch (error) {
      return { success: false, error: error.message };
    }
  };

  // ============ SERVICES CRUD ============
  const fetchVendorServices = async () => {
    if (!vendorAuth.token) return;
    try {
      const response = await fetch(`${backendUrl}/api/vendor/services`, {
        headers: { 'Authorization': `Bearer ${vendorAuth.token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setVendorServices(data.services || []);
      }
    } catch (error) {
      console.error('Error fetching services:', error);
    }
  };

  const createService = async (data) => {
    if (!vendorAuth.token) return { success: false, error: 'Not authenticated' };
    try {
      const response = await fetch(`${backendUrl}/api/vendor/services`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${vendorAuth.token}` },
        body: JSON.stringify(data)
      });
      if (!response.ok) throw new Error('Failed to create service');
      await fetchVendorServices();
      return { success: true };
    } catch (error) {
      return { success: false, error: error.message };
    }
  };

  const updateService = async (id, data) => {
    if (!vendorAuth.token) return { success: false, error: 'Not authenticated' };
    try {
      const response = await fetch(`${backendUrl}/api/vendor/services/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${vendorAuth.token}` },
        body: JSON.stringify(data)
      });
      if (!response.ok) throw new Error('Failed to update service');
      await fetchVendorServices();
      return { success: true };
    } catch (error) {
      return { success: false, error: error.message };
    }
  };

  const deleteService = async (id) => {
    if (!vendorAuth.token) return { success: false, error: 'Not authenticated' };
    try {
      const response = await fetch(`${backendUrl}/api/vendor/services/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${vendorAuth.token}` }
      });
      if (!response.ok) throw new Error('Failed to delete service');
      await fetchVendorServices();
      return { success: true };
    } catch (error) {
      return { success: false, error: error.message };
    }
  };

  // ============ PROPERTIES CRUD ============
  const fetchVendorProperties = async () => {
    if (!vendorAuth.token) return;
    try {
      const response = await fetch(`${backendUrl}/api/vendor/properties`, {
        headers: { 'Authorization': `Bearer ${vendorAuth.token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setVendorProperties(data.properties || []);
      }
    } catch (error) {
      console.error('Error fetching properties:', error);
    }
  };

  const createProperty = async (data) => {
    if (!vendorAuth.token) return { success: false, error: 'Not authenticated' };
    try {
      const response = await fetch(`${backendUrl}/api/vendor/properties`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${vendorAuth.token}` },
        body: JSON.stringify(data)
      });
      if (!response.ok) throw new Error('Failed to create property');
      await fetchVendorProperties();
      return { success: true };
    } catch (error) {
      return { success: false, error: error.message };
    }
  };

  const updateProperty = async (id, data) => {
    if (!vendorAuth.token) return { success: false, error: 'Not authenticated' };
    try {
      const response = await fetch(`${backendUrl}/api/vendor/properties/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${vendorAuth.token}` },
        body: JSON.stringify(data)
      });
      if (!response.ok) throw new Error('Failed to update property');
      await fetchVendorProperties();
      return { success: true };
    } catch (error) {
      return { success: false, error: error.message };
    }
  };

  const deleteProperty = async (id) => {
    if (!vendorAuth.token) return { success: false, error: 'Not authenticated' };
    try {
      const response = await fetch(`${backendUrl}/api/vendor/properties/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${vendorAuth.token}` }
      });
      if (!response.ok) throw new Error('Failed to delete property');
      await fetchVendorProperties();
      return { success: true };
    } catch (error) {
      return { success: false, error: error.message };
    }
  };

  // ============ EVENTS CRUD ============
  const fetchVendorEvents = async () => {
    if (!vendorAuth.token) return;
    try {
      const response = await fetch(`${backendUrl}/api/vendor/events`, {
        headers: { 'Authorization': `Bearer ${vendorAuth.token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setVendorEvents(data.events || []);
      }
    } catch (error) {
      console.error('Error fetching events:', error);
    }
  };

  const createEvent = async (data) => {
    if (!vendorAuth.token) return { success: false, error: 'Not authenticated' };
    try {
      const response = await fetch(`${backendUrl}/api/vendor/events`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${vendorAuth.token}` },
        body: JSON.stringify(data)
      });
      if (!response.ok) throw new Error('Failed to create event');
      await fetchVendorEvents();
      return { success: true };
    } catch (error) {
      return { success: false, error: error.message };
    }
  };

  const updateEvent = async (id, data) => {
    if (!vendorAuth.token) return { success: false, error: 'Not authenticated' };
    try {
      const response = await fetch(`${backendUrl}/api/vendor/events/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${vendorAuth.token}` },
        body: JSON.stringify(data)
      });
      if (!response.ok) throw new Error('Failed to update event');
      await fetchVendorEvents();
      return { success: true };
    } catch (error) {
      return { success: false, error: error.message };
    }
  };

  const deleteEvent = async (id) => {
    if (!vendorAuth.token) return { success: false, error: 'Not authenticated' };
    try {
      const response = await fetch(`${backendUrl}/api/vendor/events/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${vendorAuth.token}` }
      });
      if (!response.ok) throw new Error('Failed to delete event');
      await fetchVendorEvents();
      return { success: true };
    } catch (error) {
      return { success: false, error: error.message };
    }
  };

  // ============ GIFTCARDS CRUD ============
  const fetchVendorGiftcards = async () => {
    if (!vendorAuth.token) return;
    try {
      const response = await fetch(`${backendUrl}/api/vendor/giftcards`, {
        headers: { 'Authorization': `Bearer ${vendorAuth.token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setVendorGiftcards(data.giftcards || []);
      }
    } catch (error) {
      console.error('Error fetching giftcards:', error);
    }
  };

  const createGiftcard = async (data) => {
    if (!vendorAuth.token) return { success: false, error: 'Not authenticated' };
    try {
      const response = await fetch(`${backendUrl}/api/vendor/giftcards`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${vendorAuth.token}` },
        body: JSON.stringify(data)
      });
      if (!response.ok) throw new Error('Failed to create giftcard');
      await fetchVendorGiftcards();
      return { success: true };
    } catch (error) {
      return { success: false, error: error.message };
    }
  };

  const updateGiftcard = async (id, data) => {
    if (!vendorAuth.token) return { success: false, error: 'Not authenticated' };
    try {
      const response = await fetch(`${backendUrl}/api/vendor/giftcards/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${vendorAuth.token}` },
        body: JSON.stringify(data)
      });
      if (!response.ok) throw new Error('Failed to update giftcard');
      await fetchVendorGiftcards();
      return { success: true };
    } catch (error) {
      return { success: false, error: error.message };
    }
  };

  const deleteGiftcard = async (id) => {
    if (!vendorAuth.token) return { success: false, error: 'Not authenticated' };
    try {
      const response = await fetch(`${backendUrl}/api/vendor/giftcards/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${vendorAuth.token}` }
      });
      if (!response.ok) throw new Error('Failed to delete giftcard');
      await fetchVendorGiftcards();
      return { success: true };
    } catch (error) {
      return { success: false, error: error.message };
    }
  };

  // Update vendor profile
  const updateVendorProfile = async (profileData) => {
    if (!vendorAuth.token) return { success: false, error: 'Not authenticated' };
    
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 30000); // 30 second timeout
      
      const response = await fetch(`${backendUrl}/api/vendor/profile`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${vendorAuth.token}`
        },
        body: JSON.stringify(profileData),
        signal: controller.signal
      });
      
      clearTimeout(timeoutId);
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to update profile');
      }
      
      const data = await response.json();
      
      // Update vendor auth with new vendor data
      const updatedAuth = {
        ...vendorAuth,
        vendor: data.vendor
      };
      setVendorAuth(updatedAuth);
      // Also update localStorage
      localStorage.setItem('vendorAuth', JSON.stringify(updatedAuth));
      
      return { success: true, vendor: data.vendor };
    } catch (error) {
      if (error.name === 'AbortError') {
        return { success: false, error: 'Request timed out. Please check your connection and try again.' };
      }
      return { success: false, error: error.message };
    }
  };

  // Fetch homepage sections for public display
  const fetchHomepageSections = async (location = selectedLocation) => {
    try {
      const query = location ? `?location=${encodeURIComponent(location)}` : '';
      const response = await fetch(`${backendUrl}/api/homepage-sections${query}`);
      const data = await response.json();
      setHomepageSections(data.sections || []);
    } catch (error) {
      console.error('Error fetching homepage sections:', error);
    }
  };

  const fetchHomepageSettings = async (location = selectedLocation) => {
    try {
      const query = location ? `?location=${encodeURIComponent(location)}` : '';
      const response = await fetch(`${backendUrl}/api/homepage-settings${query}`);
      if (!response.ok) return;
      const data = await response.json();
      const nextSettings = data.settings || {};
      setHomepageSettings(nextSettings);
      setHomepageFeaturedVendors(data.vendors || []);
      return nextSettings;
    } catch (error) {
      console.error('Error fetching homepage settings:', error);
    }
  };

  // Vendor order management
  const fetchVendorOrders = async () => {
    if (!vendorAuth.token) return;
    
    try {
      const response = await fetch(`${backendUrl}/api/vendor/orders`, {
        headers: {
          'Authorization': `Bearer ${vendorAuth.token}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setVendorOrders(data.orders);
      }
    } catch (error) {
      console.error('Error fetching vendor orders:', error);
    }
  };

  const fetchVendors = async (search = '', location = '', vendorType = '') => {
    // Do not keep the entire app behind a spinner if the vendor API is slow.
    // The request continues in the background and updates the list when it resolves.
    const loadingFallbackTimer = window.setTimeout(() => setLoading(false), 1800);
    try {
      const params = new URLSearchParams();
      if (search) params.append('search', search);
      if (location) params.append('location', location);
      if (vendorType) params.append('vendor_type', vendorType);

      const response = await fetch(`${backendUrl}/api/vendors?${params}`);
      const data = await response.json();
      setVendors(data.vendors);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching vendors:', error);
      setLoading(false);
    } finally {
      window.clearTimeout(loadingFallbackTimer);
    }
  };

  // Fetch admin-defined categories for homepage display
  const fetchAdminCategories = async () => {
    try {
      const params = new URLSearchParams();
      if (selectedVendorType) params.append('vendor_type', selectedVendorType);
      
      const response = await fetch(`${backendUrl}/api/categories?${params}`);
      const data = await response.json();
      setAdminCategories(data.categories || []);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const fetchLocations = async () => {
    try {
      const response = await fetch(`${backendUrl}/api/locations`);
      const data = await response.json();
      setLocations(data.locations || []);
    } catch (error) {
      console.error('Error fetching locations:', error);
      setLocations([]);
    }
  };

  // ============== LOCATION DETECTION SYSTEM ==============
  
  // Reverse geocode coordinates to get location name
  const reverseGeocode = async (lat, lng) => {
    try {
      // Using free Nominatim API for reverse geocoding
      const response = await fetch(
        `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}&zoom=10`
      );
      const data = await response.json();
      // Extract city/town/village name
      const locationName = data.address?.city || 
                          data.address?.town || 
                          data.address?.village || 
                          data.address?.state ||
                          data.address?.county ||
                          'Unknown Location';
      return locationName;
    } catch (error) {
      console.error('Reverse geocode error:', error);
      return null;
    }
  };

  // Get location from IP address (fallback)
  const getLocationFromIP = async () => {
    try {
      const response = await fetch('https://ipapi.co/json/');
      const data = await response.json();
      return {
        name: data.city || data.region || 'Unknown',
        coords: { lat: data.latitude, lng: data.longitude },
        country: data.country_name
      };
    } catch (error) {
      console.error('IP location error:', error);
      return null;
    }
  };

  // Main location detection function
  const detectUserLocation = async () => {
    setLocationLoading(true);
    setLocationError(null);
    
    // Check if we have stored location preference
    const storedLocation = localStorage.getItem('userSelectedLocation');
    if (storedLocation) {
      const parsed = JSON.parse(storedLocation);
      setUserLocation(parsed);
      setSelectedLocation(parsed.name);
      setLocationLoading(false);
      return;
    }

    // Try GPS first
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        async (position) => {
          const { latitude, longitude } = position.coords;
          const locationName = await reverseGeocode(latitude, longitude);
          const detectedLocation = {
            name: locationName,
            coords: { lat: latitude, lng: longitude },
            source: 'gps'
          };
          setUserLocation(detectedLocation);
          setLocationLoading(false);
          setShowLocationPrompt(true);
        },
        async (error) => {
          console.log('GPS denied or unavailable, trying IP-based location');
          // Fallback to IP-based location
          const ipLocation = await getLocationFromIP();
          if (ipLocation) {
            setUserLocation({ ...ipLocation, source: 'ip' });
            setShowLocationPrompt(true);
          } else {
            setLocationError('Could not detect location');
            setShowLocationPrompt(true);
          }
          setLocationLoading(false);
        },
        { timeout: 10000, enableHighAccuracy: false }
      );
    } else {
      // No GPS available, use IP
      const ipLocation = await getLocationFromIP();
      if (ipLocation) {
        setUserLocation({ ...ipLocation, source: 'ip' });
      }
      setLocationLoading(false);
      setShowLocationPrompt(true);
    }
  };

  // Confirm and save user's location choice
  const confirmLocation = (locationName) => {
    const locationData = {
      name: locationName,
      confirmedAt: new Date().toISOString()
    };
    localStorage.setItem('userSelectedLocation', JSON.stringify(locationData));
    setUserLocation(locationData);
    setSelectedLocation(locationName);
    setShowLocationPrompt(false);
    fetchHomepageSettings(locationName);
    fetchHomepageSections(locationName);
  };

  // Show all vendors (no location filter)
  const showAllLocations = () => {
    localStorage.setItem('userSelectedLocation', JSON.stringify({ name: 'all', confirmedAt: new Date().toISOString() }));
    setUserLocation({ name: 'all' });
    setSelectedLocation('');
    setShowLocationPrompt(false);
    fetchHomepageSettings('');
    fetchHomepageSections('');
  };

  // Change location (opens prompt again)
  const changeLocation = () => {
    localStorage.removeItem('userSelectedLocation');
    setShowLocationPrompt(true);
  };

  // Check for location on first load
  useEffect(() => {
    // Skip location prompt for auth pages and agent pages
    const skipLocationPages = ['vendor-login', 'vendor-register', 'admin-login', 'forgot-password', 'reset-password', 'verify-email', 'agent-register', 'agent-login', 'agent-forgot-password', 'agent-reset-password', 'agent-dashboard', 'vendor-approval'];
    if (skipLocationPages.includes(currentPage)) {
      setShowLocationPrompt(false);
      return;
    }
    
    const storedLocation = localStorage.getItem('userSelectedLocation');
    if (!storedLocation) {
      // First visit - detect location after a short delay
      const timer = setTimeout(() => {
        detectUserLocation();
      }, 1500); // Wait for page to load first
      return () => clearTimeout(timer);
    } else {
      // Returning user - load their preference
      const parsed = JSON.parse(storedLocation);
      setUserLocation(parsed);
      if (parsed.name !== 'all') {
        setSelectedLocation(parsed.name);
      }
    }
  }, [currentPage]);

  // Agent Promo Popup - Show after 20 seconds on first visit
  useEffect(() => {
    // Skip if already shown, or not on home page, or location prompt is showing
    if (agentPromoShown || currentPage !== 'home' || showLocationPrompt) {
      return;
    }
    
    const timer = setTimeout(() => {
      if (!showLocationPrompt && currentPage === 'home') {
        setShowAgentPromo(true);
      }
    }, 20000); // 20 seconds
    
    return () => clearTimeout(timer);
  }, [currentPage, showLocationPrompt, agentPromoShown]);

  // Close agent promo and mark as shown
  const closeAgentPromo = () => {
    setShowAgentPromo(false);
    setAgentPromoShown(true);
    localStorage.setItem('agentPromoShown', 'true');
  };

  // ============== SHARE FUNCTIONALITY ==============
  
  // Centralized share URLs keep every customer-facing share destination consistent.
  const getVendorShareUrl = (vendor) => buildVendorShareUrl(vendor?.id);
  const getProductShareUrl = (product) => buildProductShareUrl(product?.id);

  // Open share modal
  const openShareModal = (type, item, e) => {
    if (e) {
      e.stopPropagation(); // Prevent card click
    }
    setShareModalData({ type, item });
  };

  // Close share modal
  const closeShareModal = () => {
    setShareModalData(null);
  };

  // Generate a share card image using Canvas (Square format)
  //
  // Fixes three real bugs that were making these cards look broken:
  //  1. The product/vendor photo was center-cropped to a SQUARE region of the
  //     source image, then stretched into a non-square (wider-than-tall)
  //     destination box -- that mismatch is exactly what stretched every
  //     photo horizontally ("too wide"). Now the crop always matches the
  //     destination box's own aspect ratio, so nothing gets stretched.
  //  2. Text was truncated by a fixed character count regardless of actual
  //     pixel width/font, so longer names could run off the edge of the
  //     card or collide with the next line. Now it's measured and truncated
  //     to the available width, and the content area got more vertical
  //     room and bigger, higher-contrast type.
  //  3. The "logo" was actually an emoji character, not SchoolVerse's own
  //     logo. It now loads and draws the real /shopimax-logo.png asset
  //     (the same one used in the app header).
  const loadImage = (src, allowCors = true) => new Promise((resolve, reject) => {
    const image = new Image();
    if (allowCors) image.crossOrigin = 'anonymous';
    image.onload = () => resolve(image);
    image.onerror = reject;
    image.src = src;
  });

  const fitText = (ctx, text, maxWidth) => {
    if (!text) return '';
    if (ctx.measureText(text).width <= maxWidth) return text;
    let truncated = text;
    while (truncated.length > 1 && ctx.measureText(truncated + '…').width > maxWidth) {
      truncated = truncated.slice(0, -1);
    }
    return truncated + '…';
  };

  // Center-crop the source image to exactly match the destination box's
  // aspect ratio (never a plain square crop), then draw it with no
  // stretching in either dimension.
  // Draws the whole source image inside the destination box without
  // cropping anything out -- letterboxed (padded top/bottom or side/side)
  // rather than cropped-to-fill, because cropping was cutting real content
  // off tall/portrait photos (a product shot could lose its top or bottom
  // half). A subtle background fill is drawn first so the letterbox bars
  // blend with the card instead of looking like empty gaps.
  const drawContainFit = (ctx, img, destX, destY, destW, destH, bgColor = '#fde8d0') => {
    ctx.fillStyle = bgColor;
    ctx.fillRect(destX, destY, destW, destH);

    const destAspect = destW / destH;
    const srcAspect = img.width / img.height;
    let drawW, drawH;
    if (srcAspect > destAspect) {
      // image is relatively wider than the box -> fit to width
      drawW = destW;
      drawH = destW / srcAspect;
    } else {
      // image is relatively taller than the box -> fit to height
      drawH = destH;
      drawW = destH * srcAspect;
    }
    const drawX = destX + (destW - drawW) / 2;
    const drawY = destY + (destH - drawH) / 2;
    ctx.drawImage(img, drawX, drawY, drawW, drawH);
  };

  const drawShopimaxBrand = (ctx, logoImg, canvasHeight, canvasWidth = canvasHeight) => {
    const badgeSize = 34;
    const margin = 14;
    const x = canvasWidth - margin - badgeSize;
    const y = canvasHeight - margin - badgeSize;
    if (logoImg) {
      ctx.save();
      ctx.beginPath();
      ctx.arc(x + badgeSize / 2, y + badgeSize / 2, badgeSize / 2, 0, Math.PI * 2);
      ctx.closePath();
      ctx.fillStyle = '#ffffff';
      ctx.fill();
      ctx.clip();
      ctx.drawImage(logoImg, x, y, badgeSize, badgeSize);
      ctx.restore();
    }
    ctx.fillStyle = '#1f2937';
    ctx.font = 'bold 15px Arial, sans-serif';
    ctx.textAlign = 'right';
    ctx.fillText('SchoolVerse', x - 10, y + badgeSize / 2 + 5);
    ctx.textAlign = 'left';
  };

  const generateShareImage = async (item, isVendor) => {
    // Exact reference dimensions: 1557 x 1980 portrait.
    const width = 1557;
    const height = 1980;
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('Canvas is unavailable');

    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, width, height);

    const pad = 16;
    const imageX = pad;
    const imageY = pad;
    const imageW = width - pad * 2;
    const imageH = 1525;
    const radius = 78;

    const imageUrl = getImageUrl(isVendor ? (item.image || item.image_url) : (item.image_url || item.image));
    let itemImg = null;
    try { itemImg = await loadImage(imageUrl); } catch (_) {}

    ctx.save();
    ctx.beginPath();
    ctx.moveTo(imageX + radius, imageY);
    ctx.arcTo(imageX + imageW, imageY, imageX + imageW, imageY + imageH, radius);
    ctx.arcTo(imageX + imageW, imageY + imageH, imageX, imageY + imageH, radius);
    ctx.arcTo(imageX, imageY + imageH, imageX, imageY, radius);
    ctx.arcTo(imageX, imageY, imageX + imageW, imageY, radius);
    ctx.closePath();
    ctx.clip();
    if (itemImg) {
      const srcAspect = itemImg.width / itemImg.height;
      const dstAspect = imageW / imageH;
      let drawW, drawH;
      if (srcAspect > dstAspect) { drawH = imageH; drawW = drawH * srcAspect; }
      else { drawW = imageW; drawH = drawW / srcAspect; }
      ctx.drawImage(itemImg, imageX + (imageW - drawW) / 2, imageY + (imageH - drawH) / 2, drawW, drawH);
    } else {
      ctx.fillStyle = '#f3f4f6';
      ctx.fillRect(imageX, imageY, imageW, imageH);
      ctx.fillStyle = '#9ca3af';
      ctx.font = '600 38px Arial, sans-serif';
      ctx.textAlign = 'center';
      ctx.fillText(isVendor ? 'Vendor image unavailable' : 'Product image unavailable', width / 2, imageY + imageH / 2);
      ctx.textAlign = 'left';
    }
    ctx.restore();

    const left = 38;
    const right = width - 38;
    const name = String(item.name || (isVendor ? 'Vendor' : 'Product'));
    ctx.fillStyle = '#202020';
    ctx.font = '700 52px Arial, sans-serif';
    ctx.textAlign = 'left';
    ctx.fillText(fitText(ctx, name, width - 76), left, 1632);

    if (isVendor) {
      const category = item.category || item.vendor_type || '';
      if (category) {
        ctx.fillStyle = '#6f767a';
        ctx.font = '600 37px Arial, sans-serif';
        ctx.fillText(fitText(ctx, category, 520), left, 1698);
      }
      const desc = item.description || 'Vendor on SchoolVerse';
      ctx.fillStyle = '#737a7f';
      ctx.font = '500 36px Arial, sans-serif';
      ctx.fillText(fitText(ctx, desc, 760), left, 1760);
      ctx.fillStyle = '#0b8f5b';
      ctx.font = '700 42px Arial, sans-serif';
      ctx.fillText('Order now on SchoolVerse', left, 1880);
    } else {
      ctx.fillStyle = '#ee824d';
      ctx.font = '700 48px Arial, sans-serif';
      ctx.fillText(formatCurrency(item.price), left, 1705);
      const vendorName = item.vendor_name || item.vendor?.name || '';
      if (vendorName) {
        ctx.fillStyle = '#737a7f';
        ctx.font = '700 39px Arial, sans-serif';
        ctx.textAlign = 'right';
        ctx.fillText(fitText(ctx, `Sold by ${vendorName}`, 700), right, 1705);
        ctx.textAlign = 'left';
      }
      const desc = item.short_description || item.description || 'Product on SchoolVerse';
      ctx.fillStyle = '#737a7f';
      ctx.font = '500 36px Arial, sans-serif';
      ctx.fillText(fitText(ctx, desc, width - 76), left, 1775);
      ctx.fillStyle = '#0b8f5b';
      ctx.font = '700 42px Arial, sans-serif';
      ctx.fillText('Order now on SchoolVerse', left, 1880);
    }

    let logoImg = null;
    try { logoImg = await loadImage('/shopimax-logo.png', false); } catch (_) {}
    const badgeSize = 82;
    const badgeX = width - 38 - badgeSize;
    const badgeY = height - 38 - badgeSize;
    if (logoImg) {
      ctx.save();
      ctx.beginPath();
      ctx.arc(badgeX + badgeSize / 2, badgeY + badgeSize / 2, badgeSize / 2, 0, Math.PI * 2);
      ctx.clip();
      ctx.drawImage(logoImg, badgeX, badgeY, badgeSize, badgeSize);
      ctx.restore();
    } else {
      ctx.fillStyle = '#f97316';
      ctx.beginPath();
      ctx.arc(badgeX + badgeSize / 2, badgeY + badgeSize / 2, badgeSize / 2, 0, Math.PI * 2);
      ctx.fill();
      ctx.fillStyle = '#fff';
      ctx.font = '700 46px Arial, sans-serif';
      ctx.textAlign = 'center';
      ctx.fillText('S', badgeX + badgeSize / 2, badgeY + 57);
      ctx.textAlign = 'left';
    }
    ctx.fillStyle = '#202020';
    ctx.font = '700 36px Arial, sans-serif';
    ctx.textAlign = 'right';
    ctx.fillText('SchoolVerse', badgeX - 18, badgeY + 51);
    ctx.textAlign = 'left';

    return new Promise((resolve, reject) => {
      canvas.toBlob((blob) => blob ? resolve(blob) : reject(new Error('Unable to create share image')), 'image/png');
    });
  };

  // Share to different platforms
  const shareToSocial = async (platform) => {
    if (!shareModalData) return;
    
    const { type, item } = shareModalData;
    const isVendor = type === 'vendor';
    
    const shareUrl = isVendor ? getVendorShareUrl(item) : getProductShareUrl(item);
    const title = item.name;
    
    // Use truthful, entity-specific copy.
    const baseShareText = isVendor ? buildVendorShareText(item) : buildProductShareText(item);
    const shareText = `${baseShareText}\n${shareUrl}`;

    // For platforms that support image sharing
    const shareWithImage = async () => {
      try {
        toast.info('Generating share card...');
        const imageBlob = await generateShareImage(item, isVendor);
        const file = new File([imageBlob], `${title.replace(/[^a-z0-9]/gi, '_')}.png`, { type: 'image/png' });
        
        if (navigator.canShare && navigator.canShare({ files: [file] })) {
          await navigator.share({
            title: title,
            // The link is appended into `text` (not left to the separate
            // `url` field alone) because several share targets -- WhatsApp
            // included -- silently drop `url` from the Web Share API call
            // whenever `files` is also present, which meant the actual
            // link to the vendor/product could vanish entirely from an
            // image share. Putting it in `text` guarantees it's always
            // included regardless of that platform quirk.
            text: shareText,
            url: shareUrl,
            files: [file]
          });
          closeShareModal();
          return true;
        }
      } catch (err) {
        console.log('Image share failed, falling back to text share');
      }
      return false;
    };

    switch (platform) {
      case 'whatsapp':
        const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
        
        // Try sharing with image first on mobile
        if (isMobile && navigator.share) {
          const shared = await shareWithImage();
          if (shared) return;
        }
        
        // Fallback to text-only share
        if (isMobile) {
          window.location.href = `whatsapp://send?text=${encodeURIComponent(shareText)}`;
        } else {
          window.open(`https://web.whatsapp.com/send?text=${encodeURIComponent(shareText)}`, '_blank');
        }
        break;
        
      case 'facebook':
        window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`, '_blank');
        break;
        
      case 'twitter':
        const tweetText = baseShareText;
        window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(tweetText)}&url=${encodeURIComponent(shareUrl)}`, '_blank');
        break;
        
      case 'telegram':
        const telegramText = baseShareText;
        window.open(`https://t.me/share/url?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent(telegramText)}`, '_blank');
        break;
        
      case 'copy':
        try {
          await navigator.clipboard.writeText(`${baseShareText}\n${shareUrl}`);
          toast.success('Link copied to clipboard!');
        } catch (err) {
          const textArea = document.createElement('textarea');
          textArea.value = `${baseShareText}\n${shareUrl}`;
          document.body.appendChild(textArea);
          textArea.select();
          document.execCommand('copy');
          document.body.removeChild(textArea);
          toast.success('Link copied to clipboard!');
        }
        break;
        
      case 'native':
        if (navigator.share) {
          // Try with image first
          const shared = await shareWithImage();
          if (!shared) {
            // Fallback to text only
            try {
              await navigator.share({
                title: title,
                text: baseShareText,
                url: shareUrl,
              });
            } catch (err) {
              if (err.name !== 'AbortError') {
                toast.error('Sharing failed');
              }
            }
          }
        }
        break;
        
      case 'download':
        // Download the share card image
        try {
          toast.info('Generating image...');
          const imageBlob = await generateShareImage(item, isVendor);
          const url = URL.createObjectURL(imageBlob);
          const a = document.createElement('a');
          a.href = url;
          a.download = `${title.replace(/[^a-z0-9]/gi, '_')}_shopimax.png`;
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
          URL.revokeObjectURL(url);
          toast.success('Image downloaded! Share it anywhere.');
        } catch (err) {
          toast.error('Failed to generate image');
        }
        break;
        
      default:
        break;
    }
    
    closeShareModal();
  };

  // Fetch vendor-specific items based on vendor type
  const fetchVendorItems = async (vendorId, vendorType) => {
    try {
      if (vendorType === 'food' || vendorType === 'products') {
        const response = await fetch(`${backendUrl}/api/vendors/${vendorId}/products`);
        const data = await response.json();
        setProducts(data.products || []);
      } else if (vendorType === 'services') {
        const response = await fetch(`${backendUrl}/api/vendors/${vendorId}/services`);
        const data = await response.json();
        setServices(data.services || []);
      } else if (vendorType === 'property') {
        const response = await fetch(`${backendUrl}/api/vendors/${vendorId}/properties`);
        const data = await response.json();
        setProperties(data.properties || []);
      } else if (vendorType === 'events') {
        const response = await fetch(`${backendUrl}/api/vendors/${vendorId}/events`);
        const data = await response.json();
        setEvents(data.events || []);
      } else if (vendorType === 'giftcards') {
        const response = await fetch(`${backendUrl}/api/vendors/${vendorId}/giftcards`);
        const data = await response.json();
        setGiftcards(data.giftcards || []);
      }
    } catch (error) {
      console.error('Error fetching vendor items:', error);
    }
  };

  const fetchActiveBanners = async (location = selectedLocation) => {
    try {
      const query = location ? `?location=${encodeURIComponent(location)}` : '';
      const response = await fetch(`${backendUrl}/api/banners/active${query}`);
      const data = await response.json();
      // Ensure we never set undefined - use default structure if banners is missing
      setActiveBanners(data.banners || {
        top_header: [],
        header_carousel: [],
        between_cards: [],
        slideshow: []
      });
    } catch (error) {
      console.error('Error fetching active banners:', error);
      // On error, keep the default empty structure
    }
  };

  // Refresh location-targeted homepage banners whenever the selected location changes.
  useEffect(() => {
    if (backendUrl) fetchActiveBanners(selectedLocation);
  }, [backendUrl, selectedLocation]);

  // Carousel auto-rotation effect
  useEffect(() => {
    if (activeBanners?.header_carousel?.length > 1) {
      const interval = setInterval(() => {
        setCurrentCarouselIndex((prevIndex) => 
          (prevIndex + 1) % (activeBanners?.header_carousel?.length || 1)
        );
      }, 5000); // Change banner every 5 seconds
      
      return () => clearInterval(interval);
    }
  }, [activeBanners?.header_carousel]);

  // Slideshow auto-rotation effect - slides 2 banners at a time
  useEffect(() => {
    if (activeBanners?.slideshow?.length > 2) {
      const interval = setInterval(() => {
        setCurrentSlideshowIndex((prevIndex) => {
          const maxIndex = (activeBanners?.slideshow?.length || 2) - 2;
          return prevIndex >= maxIndex ? 0 : prevIndex + 1;
        });
      }, 4000); // Change every 4 seconds
      
      return () => clearInterval(interval);
    }
  }, [activeBanners?.slideshow]);


  const getSearchContext = () => {
    if (currentPage === 'vendor' && currentVendor?.id) {
      return { scope: 'vendor', vendor_id: currentVendor.id, category_type: '', label: currentVendor.name || 'this vendor' };
    }
    if (currentPage === 'category' && currentCategoryType && currentCategoryType !== 'all') {
      const label = vendorTypes.find(v => v.type === currentCategoryType)?.label || currentCategoryType;
      return { scope: 'category', vendor_id: '', category_type: currentCategoryType, label };
    }
    return { scope: 'global', vendor_id: '', category_type: '', label: 'SchoolVerse' };
  };

  const runContextSearch = async (search, { submit = false } = {}) => {
    const trimmedSearch = String(search || '').trim();
    if (!trimmedSearch) {
      setSearchSuggestions({ products: [], vendors: [], categories: [] });
      setSearchResults([]);
      setVendorSearchFallbackProducts([]);
      setIsProductSearchActive(false);
      return;
    }

    const context = getSearchContext();
    try {
      setSearchLoading(true);
      const params = new URLSearchParams({ search: trimmedSearch, scope: context.scope, limit: '10' });
      if (context.vendor_id) params.set('vendor_id', context.vendor_id);
      if (context.category_type) params.set('category_type', context.category_type);
      const response = await fetch(`${backendUrl}/api/search?${params.toString()}`);
      if (!response.ok) throw new Error(`Search failed: ${response.status}`);
      const data = await response.json();
      setSearchSuggestions({ products: data.products || [], vendors: data.vendors || [], categories: data.categories || [] });

      if (submit) {
        setSearchResults(data.products || []);
        setIsProductSearchActive(true);
        if (context.scope === 'category') {
          setCategorySearchTerm(trimmedSearch);
        } else if (context.scope === 'global' && currentPage !== 'home') {
          setCurrentPage('home');
          window.history.pushState({ page: 'home', search: trimmedSearch }, '', `${window.location.pathname}#/`);
        }
        if (context.scope === 'vendor' && (data.products || []).length === 0) {
          const globalParams = new URLSearchParams({ search: trimmedSearch, scope: 'global', limit: '10' });
          const globalResponse = await fetch(`${backendUrl}/api/search?${globalParams.toString()}`);
          const globalData = globalResponse.ok ? await globalResponse.json() : { products: [] };
          setVendorSearchFallbackProducts(globalData.products || []);
        } else {
          setVendorSearchFallbackProducts([]);
        }
      }
    } catch (error) {
      console.error('Error searching SchoolVerse:', error);
      setSearchSuggestions({ products: [], vendors: [], categories: [] });
      if (submit) setSearchResults([]);
    } finally {
      setSearchLoading(false);
    }
  };

  useEffect(() => {
    const query = String(searchTerm || '').trim();
    if (!query) {
      setSearchSuggestions({ products: [], vendors: [], categories: [] });
      return undefined;
    }
    const timer = setTimeout(() => runContextSearch(query), 250);
    return () => clearTimeout(timer);
  }, [searchTerm, currentPage, currentVendor?.id, currentCategoryType]);

  const handleSearch = () => runContextSearch(searchTerm, { submit: true });

  const handleLocationChange = (location) => {
    setSelectedLocation(location);
    fetchVendors(searchTerm, location, selectedVendorType);
    fetchHomepageSettings(location);
    fetchHomepageSections(location);
  };


  const navigateToDeals = () => {
    setCurrentPage('deals');
    setSelectedProduct(null);
    setCurrentVendor(null);
    setCurrentCategoryType(null);
    window.history.pushState({ page: 'deals' }, '', '/deals');
    window.scrollTo({ top: 0, behavior: 'instant' });
  };

  const navigateToSearch = () => {
    setSearchTerm('');
    setSearchSuggestions({ products: [], vendors: [], categories: [] });
    setSearchResults([]);
    setVendorSearchFallbackProducts([]);
    setIsProductSearchActive(false);
    setCurrentVendor(null);
    setSelectedProduct(null);
    setCurrentCategoryType(null);
    setCurrentPage('search');
    window.history.pushState({ page: 'search' }, '', '/search');
    window.scrollTo({ top: 0, behavior: 'instant' });
  };

  const openAllVendorsPage = () => {
    setCurrentCategoryType('all');
    setCategorySearchTerm('');
    setCategoryFilter('all');
    setCurrentPage('category');
    fetchCategoryVendors('all');
    window.history.pushState({ page: 'category', categoryType: 'all' }, '', '/category/all');
  };

  const navigateToHome = () => {
    setCurrentPage('home');
    setSelectedProduct(null);
    setCurrentVendor(null);
    setCurrentCategoryType(null);

    window.history.pushState(
      { page: 'home' },
      '',
      '/'
    );

    window.scrollTo({ top: 0, behavior: 'instant' });

    fetchVendors(searchTerm, selectedLocation, selectedVendorType); // Refetch vendors with current filters
  };

  const fetchVendorProductsForCustomer = async (vendorId) => {
    try {
      const response = await fetch(`${backendUrl}/api/vendors/${vendorId}/products`);
      const data = await response.json();
      setProducts(data.products);
    } catch (error) {
      console.error('Error fetching products:', error);
    }
  };

  // Fetch public marquee settings for vendor page
  const fetchPublicMarqueeSettings = async () => {
    try {
      const response = await fetch(`${backendUrl}/api/marquee-settings`);
      if (response.ok) {
        const data = await response.json();
        setPublicMarquee(data);
      }
    } catch (error) {
      console.error('Error fetching marquee settings:', error);
    }
  };

  const fetchDeliveryAreas = async (vendorId) => {
    try {
      const response = await fetch(`${backendUrl}/api/vendors/${vendorId}/delivery-areas`);
      const data = await response.json();
      setDeliveryAreas(data.delivery_areas);
    } catch (error) {
      console.error('Error fetching delivery areas:', error);
    }
  };

  const fetchDraft = async (vendorId) => {
    try {
      // Do not automatically restore a saved draft into the active cart.
      // Keep it separate so the customer can explicitly choose to restore it.
      setSavedDraft(null);

      const response = await fetch(`${backendUrl}/api/drafts/${vendorId}`);
      if (!response.ok) {
        throw new Error(`Failed to fetch draft: ${response.status}`);
      }

      const data = await response.json();

      if (data.draft && data.draft.items && data.draft.items.length > 0) {
        setSavedDraft(data.draft);
      }
    } catch (error) {
      console.error('Error fetching draft:', error);
      setSavedDraft(null);
    }
  };

  const restoreSavedDraft = async () => {
    if (!savedDraft || !savedDraft.items || savedDraft.items.length === 0) {
      return;
    }

    try {
      const response = await fetch(`${backendUrl}/api/drafts/${savedDraft.vendor_id}`, {
        method: 'DELETE',
      });

      if (!response.ok) {
        throw new Error(`Failed to remove restored draft: ${response.status}`);
      }

      // Only place the draft into the active cart after the saved draft
      // has been successfully consumed.
      setOrders([{ items: savedDraft.items, id: Date.now() }]);
      setActiveOrderIndex(0);
      setSavedDraft(null);
    } catch (error) {
      console.error('Error restoring saved draft:', error);
      toast.error('Unable to restore your saved cart right now. Please try again.');
    }
  };

  const saveDraft = async (vendorId, items) => {
    if (items.length === 0) return;
    
    try {
      const total = items.reduce((sum, item) => sum + (item.price * item.quantity), 0);
      const response = await fetch(`${backendUrl}/api/drafts`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          vendor_id: vendorId,
          vendor_name: currentVendor.name,
          items: items,
          total: total
        })
      });
      
      if (!response.ok) {
        const error = await response.json();
        console.error('Error saving draft:', error);
      }
    } catch (error) {
      console.error('Error saving draft:', error);
    }
  };

  const openVendorPage = async (vendor) => {
    setSearchTerm('');
    setSearchSuggestions({ products: [], vendors: [], categories: [] });
    setVendorSearchFallbackProducts([]);
    // Check if user has items in any order from different vendor
    const hasItemsFromDifferentVendor = orders.some(order => order.items.length > 0) && currentVendor && currentVendor.id !== vendor.id;
    
    if (hasItemsFromDifferentVendor) {
      setPendingVendor(vendor);
      setShowExitPopup(true);
      return;
    }
    
    setCurrentVendor(vendor);
    setCurrentPage('vendor');
    
    // Scroll to top when opening vendor page
    window.scrollTo({ top: 0, behavior: 'instant' });
    
    // Push to browser history for back button support
    window.history.pushState(
      { page: 'vendor', vendorId: vendor.id }, 
      '', 
      `/vendor/${encodeURIComponent(vendor.id)}`
    );
    
    // Reset delivery info when switching vendors
    setCustomerInfo(prev => ({...prev, area: '', delivery_price: 0}));
    
    // Fetch items based on vendor type
    const vendorType = vendor.vendor_type || 'food';
    await fetchVendorItems(vendor.id, vendorType);
    
    // Only fetch delivery areas and draft for food/products vendors
    if (vendorType === 'food' || vendorType === 'products') {
      await fetchDeliveryAreas(vendor.id);
      await fetchDraft(vendor.id);
    }
  };

  const openProductPage = async (product) => {
    if (!product?.id) return;

    try {
      const response = await fetch(`${backendUrl}/api/products/${product.id}`);
      if (!response.ok) throw new Error('Product not found');
      const data = await response.json();
      const resolvedProduct = data.product || product;
      const vendor = data.vendor || vendors.find((v) => v.id === resolvedProduct.vendor_id) || null;

      setSelectedProduct(resolvedProduct);
      if (vendor) setCurrentVendor(vendor);
      setCurrentPage('product');
      window.scrollTo({ top: 0, behavior: 'instant' });
      window.history.pushState(
        { page: 'product', productId: resolvedProduct.id, vendorId: resolvedProduct.vendor_id },
        '',
        `/product/${encodeURIComponent(resolvedProduct.id)}`
      );
    } catch (error) {
      console.error('Error opening product:', error);
      toast.error('Unable to open this product right now.');
    }
  };

  const openProductDescription = (product) => {
    if (!product?.id) return;
    setSelectedProduct(product);
    window.scrollTo({ top: 0, behavior: 'instant' });
    setCurrentPage('product-description');
    window.history.pushState(
      { page: 'product-description', productId: product.id, vendorId: product.vendor_id },
      '',
      `/product/${encodeURIComponent(product.id)}/description`
    );
  };

  const addProductFromDetail = (product) => {
    if (!product?.vendor_id) return;
    const vendor = vendors.find((v) => v.id === product.vendor_id) || currentVendor;
    if (!vendor) return;
    if (!currentVendor || currentVendor.id === vendor.id) {
      setCurrentVendor(vendor);
      if (!products.some((item) => item.id === product.id)) {
        setProducts((prev) => [...prev, product]);
      }
      updateQuantity(product.id, 1, product);
      try {
        const attribution = JSON.parse(sessionStorage.getItem('shopimaxDealsAttribution') || 'null');
        if (attribution && (!attribution.started_at || Date.now() - attribution.started_at < 7 * 24 * 60 * 60 * 1000)) {
          fetch(`${backendUrl}/api/deals/track`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ event_type: 'add_to_cart', campaign_id: attribution.campaign_id, product_id: product.id }) }).catch(() => {});
        }
      } catch (_) {}
      toast.success('Added to cart');
      return;
    }
    openVendorPage(vendor);
  };

  const confirmVendorSwitch = async () => {
    if (!pendingVendor) return;
    
    // Save all current orders as draft
    const ordersWithItems = orders.filter(order => order.items.length > 0);
    if (ordersWithItems.length > 0 && currentVendor) {
      // For now, save the first order with items as draft (draft system needs update for multiple orders)
      await saveDraft(currentVendor.id, ordersWithItems[0].items);
    }
    
    setOrders([{ items: [], id: Date.now() }]); // Reset to single empty order
    setActiveOrderIndex(0);
    setCurrentVendor(pendingVendor);
    setCurrentPage('vendor');
    
    // Scroll to top when opening vendor page
    window.scrollTo({ top: 0, behavior: 'instant' });
    
    // Push to browser history for back button support
    window.history.pushState(
      { page: 'vendor', vendorId: pendingVendor.id }, 
      '', 
      `${window.location.pathname}#/vendor/${pendingVendor.id}`
    );
    
    setShowExitPopup(false);
    setPendingVendor(null);
    // Reset delivery info when switching vendors
    setCustomerInfo(prev => ({...prev, area: '', delivery_price: 0}));
    
    // Fetch items based on vendor type
    const vendorType = pendingVendor.vendor_type || 'food';
    await fetchVendorItems(pendingVendor.id, vendorType);
    
    if (vendorType === 'food' || vendorType === 'products') {
      await fetchDeliveryAreas(pendingVendor.id);
      await fetchDraft(pendingVendor.id);
    }
  };

  const updateQuantity = (productId, change, productOverride = null) => {
    setOrders(prevOrders => {
      const newOrders = [...prevOrders];
      const currentOrder = newOrders[activeOrderIndex];
      
      // Ensure we're working with the latest state
      const existingItem = currentOrder.items.find(item => item.product_id === productId);
      // productOverride lets a caller that already has the product object
      // (e.g. adding straight from the product detail page) pass it
      // directly, instead of relying on the `products` list state -- that
      // state is updated via setProducts() right before this is called in
      // some flows, and since setState is async, `products` here could
      // still be the stale pre-update array, making the lookup below
      // return undefined and crash on product.name.
      const product = productOverride || products.find(p => p.id === productId);
      
      if (!existingItem && change > 0) {
        if (!product) {
          // Nothing we can add without at least a name/price -- bail out
          // instead of crashing the whole page on product.name.
          return prevOrders;
        }
        // Add new item with quantity 1 (not change, to avoid double increment)
        currentOrder.items = [...currentOrder.items, {
          product_id: productId,
          product_name: product.name,
          price: product.price,
          image_url: product.image_url || (Array.isArray(product.image_urls) ? product.image_urls[0] : undefined),
          quantity: 1
        }];
      } else if (existingItem) {
        const newQuantity = existingItem.quantity + change;
        if (newQuantity <= 0) {
          // Remove item
          currentOrder.items = currentOrder.items.filter(item => item.product_id !== productId);
        } else {
          // Update quantity
          currentOrder.items = currentOrder.items.map(item =>
            item.product_id === productId
              ? { ...item, quantity: newQuantity }
              : item
          );
        }
      }
      return newOrders;
    });
  };

  const getItemQuantity = (productId) => {
    const item = orders[activeOrderIndex]?.items.find(item => item.product_id === productId);
    return item ? item.quantity : 0;
  };

  const getCartTotal = () => {
    return orders[activeOrderIndex]?.items.reduce((total, item) => total + (item.price * item.quantity), 0) || 0;
  };

  const getGrandTotal = () => {
    // Sum all orders
    const allOrdersTotal = orders.reduce((total, order) => 
      total + order.items.reduce((sum, item) => sum + (item.price * item.quantity), 0), 0
    );
    const deliveryPrice = customerInfo.delivery_price || 0;
    
    // Calculate packaging fees: number of orders with items * packaging fee
    const ordersWithItems = orders.filter(order => order.items.length > 0).length;
    const packagingFee = (currentVendor?.packaging_fee || 0) * ordersWithItems;
    
    // Service charge
    const serviceCharge = currentVendor?.service_charge || 0;
    
    return allOrdersTotal + deliveryPrice + packagingFee + serviceCharge;
  };

  const getPackagingTotal = () => {
    const ordersWithItems = orders.filter(order => order.items.length > 0).length;
    return (currentVendor?.packaging_fee || 0) * ordersWithItems;
  };

  // The merchandise subtotal for the order actually about to be submitted.
  // submitOrder() below flattens items from every entry in `orders` into
  // one single-vendor order (see allItems there) -- `orders` here means
  // multiple draft groupings within one vendor visit, not a multi-vendor
  // cart (OrderRequest.vendor_id is a single string on the backend), so
  // this must sum across all of them the same way, not just the active one.
  const getSubmittedOrderMerchandiseSubtotal = () => {
    return orders.reduce((total, order) =>
      total + order.items.reduce((sum, item) => sum + (item.price * item.quantity), 0), 0
    );
  };

  const isPodEligible = podSettings.pod_enabled
    && getSubmittedOrderMerchandiseSubtotal() >= podSettings.pod_minimum_order_value;

  // If the cart changes (items removed) after POD was selected and it's no
  // longer eligible, fall back to Pay Now rather than silently submitting
  // an order the backend will just reject.
  useEffect(() => {
    if (paymentMethod === 'POD' && !isPodEligible) {
      setPaymentMethod('PAY_NOW');
    }

  }, [isPodEligible]);

  const getNumberOfOrders = () => {
    return orders.filter(order => order.items.length > 0).length;
  };

  const createNewOrder = () => {
    const newOrder = { items: [], id: Date.now() };
    setOrders(prevOrders => [...prevOrders, newOrder]);
    setActiveOrderIndex(orders.length); // Set to new order index
    setCurrentPage('vendor');
  };

  const editOrder = (index) => {
    setActiveOrderIndex(index);
    setCurrentPage('vendor');
  };

  const duplicateOrder = (index) => {
    const orderToDuplicate = orders[index];
    const duplicatedOrder = {
      items: [...orderToDuplicate.items], // Deep copy items
      id: Date.now()
    };
    setOrders(prevOrders => [...prevOrders, duplicatedOrder]);
  };

  const removeOrder = (index) => {
    if (orders.length === 1) {
      // If only one order, just clear its items
      setOrders([{ items: [], id: Date.now() }]);
      setActiveOrderIndex(0);
    } else {
      setOrders(prevOrders => prevOrders.filter((_, i) => i !== index));
      // Adjust active order index if needed
      if (activeOrderIndex >= orders.length - 1) {
        setActiveOrderIndex(Math.max(0, orders.length - 2));
      }
    }
  };

  const handleAreaChange = (selectedArea) => {
    // Handle Walk In (Self Pick Up) with 0 delivery price
    if (selectedArea === "Walk In (Self Pick Up)") {
      setCustomerInfo(prev => ({
        ...prev,
        area: selectedArea,
        delivery_price: 0
      }));
      return;
    }
    
    // Handle regular delivery areas
    const areaData = deliveryAreas.find(area => area.area === selectedArea);
    const deliveryPrice = areaData ? areaData.price : 0;
    
    setCustomerInfo(prev => ({
      ...prev,
      area: selectedArea,
      delivery_price: deliveryPrice
    }));
  };

  const applyReferralCode = async () => {
    if (!referralCode.trim()) return;
    
    setApplyingReferral(true);
    setReferralError('');
    
    try {
      const orderTotal = getSubmittedOrderMerchandiseSubtotal();
      
      const response = await fetch(`${backendUrl}/api/validate-referral-code`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          code: referralCode.toUpperCase(),
          vendor_id: currentVendor.id,
          order_total: orderTotal,
          delivery_fee: customerInfo.delivery_price || 0,
          product_ids: orders.flatMap(order => order.items || []).map(item => item.product_id),
          product_quantities: Object.fromEntries(
            orders.flatMap(order => order.items || []).reduce((map, item) => {
              map.set(item.product_id, (map.get(item.product_id) || 0) + (item.quantity || 0));
              return map;
            }, new Map())
          ),
          customer_key: (customerInfo.email || customerInfo.phone || '').trim().toLowerCase()
        })
      });
      
      if (response.ok) {
        const data = await response.json();
        setAppliedReferral(data);
        setCustomerInfo(prev => ({...prev, referral_code: referralCode.toUpperCase()}));
        setReferralError('');
        
        // If free delivery, update delivery price
        if (data.discount_type === 'free_delivery') {
          setCustomerInfo(prev => ({
            ...prev,
            delivery_price: 0
          }));
        }
      } else {
        const error = await response.json();
        setReferralError(error.detail || 'Invalid referral code');
        setAppliedReferral(null);
      }
    } catch (error) {
      setReferralError('Failed to validate code. Please try again.');
      setAppliedReferral(null);
    }
    
    setApplyingReferral(false);
  };

  const removeReferralCode = () => {
    setReferralCode('');
    setAppliedReferral(null);
    setReferralError('');
    setCustomerInfo(prev => ({...prev, referral_code: ''}));
    
    // Restore delivery price if it was free delivery
    if (appliedReferral && appliedReferral.discount_type === 'free_delivery' && customerInfo.area) {
      const areaData = deliveryAreas.find(area => area.area === customerInfo.area);
      if (areaData && customerInfo.area !== "Walk In (Self Pick Up)") {
        setCustomerInfo(prev => ({
          ...prev,
          delivery_price: areaData.price
        }));
      }
    }
  };

  const generateWhatsAppLink = () => {
    const ordersWithItems = orders.filter(order => order.items.length > 0);
    
    // Generate tracking code with timestamp
    const timestamp = Date.now();
    const randomCode = Math.random().toString(36).substring(2, 8).toUpperCase();
    const trackingCode = `IM-${timestamp}-${randomCode}`;
    
    // Get current date and time
    const now = new Date();
    const orderTime = `${now.toLocaleDateString('en-GB')}, ${now.toLocaleTimeString('en-GB')}`;
    
    // Calculate totals
    const foodTotal = ordersWithItems.reduce((total, order) => 
      total + order.items.reduce((sum, item) => sum + (item.price * item.quantity), 0), 0
    );
    const deliveryPrice = customerInfo.delivery_price || 0;
    const packagingFee = (currentVendor?.packaging_fee || 0) * ordersWithItems.length;
    const serviceCharge = currentVendor?.service_charge || 0;
    const referralDiscount = appliedReferral?.discount_amount || 0;
    const grandTotal = foodTotal + deliveryPrice + packagingFee + serviceCharge - referralDiscount;
    
    // Build a share message with formatting (URL removed to avoid large preview)
    let message = ` *SchoolVerse* - Your Marketplace!\n\n`;
    message += ` *TRACKING CODE: ${trackingCode}*\n`;
    message += `----------------------------\n\n`;
    message += ` *NEW ORDER FROM ${customerInfo.full_name.toUpperCase()}*\n\n`;
    
    // Order items
    ordersWithItems.forEach((order, index) => {
      message += ` *ORDER ${index + 1}:*\n`;
      order.items.forEach(item => {
        const itemTotal = item.price * item.quantity;
        message += `₦ ${item.product_name} || qty- ${item.quantity} || ₦${itemTotal.toLocaleString()}\n`;
      });
      
      // Calculate subtotal for this order
      const orderSubtotal = order.items.reduce((sum, item) => sum + (item.price * item.quantity), 0);
      message += `_Subtotal: ₦${orderSubtotal.toLocaleString()}_\n\n`;
    });
    
    // Customer details
    message += ` *CUSTOMER DETAILS:*\n`;
    message += `Name: ${customerInfo.full_name}\n`;
    message += `Phone: ${customerInfo.phone}\n`;
    message += `*Address: ${customerInfo.street}, ${customerInfo.area}*\n\n`;
    
    // Order summary
    message += ` *ORDER SUMMARY:*\n`;
    message += `*Food Total:* ₦${foodTotal.toLocaleString()}\n`;
    if (deliveryPrice > 0) {
      message += `*Delivery Fee* (${customerInfo.area}): ₦${deliveryPrice.toLocaleString()}\n`;
    }
    if (packagingFee > 0) {
      message += `*Packaging Fee* (${ordersWithItems.length} order${ordersWithItems.length > 1 ? 's' : ''}): ₦${packagingFee.toLocaleString()}\n`;
    }
    if (serviceCharge > 0) {
      message += `*Service Charge:* ₦${serviceCharge.toLocaleString()}\n`;
    }
    if (referralDiscount > 0) {
      message += `*Discount Applied:* -₦${referralDiscount.toLocaleString()}\n`;
    }
    message += `----------------------------\n`;
    message += ` *GRAND TOTAL: ₦${grandTotal.toLocaleString()}*\n\n`;
    
    // Order time and tracking
    message += ` *Order Time:* ${orderTime}\n`;
    message += ` *Track Code:* ${trackingCode}`;
    
    const encodedMessage = encodeURIComponent(message);
    
    // Clean phone number
    const cleanPhone = currentVendor.phone.replace(/[^\d+]/g, '');
    
    // For mobile devices, use different approach
    const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
    
    if (isMobile) {
      return `whatsapp://send?phone=${cleanPhone}&text=${encodedMessage}`;
    } else {
      return `https://web.whatsapp.com/send?phone=${cleanPhone}&text=${encodedMessage}`;
    }
  };

  // Display-only mirror of the backend's calculate_pod_payment(), so
  // checkout can show the upfront/on-delivery split before submitting.
  // The backend recalculates this independently and is the real authority.
  const getPodPreview = () => {
    const merchandise = getSubmittedOrderMerchandiseSubtotal();
    const deliveryFee = customerInfo.delivery_price || 0;
    const packagingFee = getPackagingTotal();
    const serviceCharge = currentVendor?.service_charge || 0;
    const otherFees = packagingFee + serviceCharge;
    const upfrontPct = podSettings.pod_upfront_percentage;

    const upfrontProductAmount = Math.round(merchandise * upfrontPct / 100);
    const amountDueOnDelivery = Math.round(merchandise - upfrontProductAmount);
    const amountPayableNow = Math.round(upfrontProductAmount + deliveryFee + otherFees);
    const grandTotal = Math.round(merchandise + deliveryFee + otherFees);

    return { merchandise, deliveryFee, otherFees, upfrontProductAmount, amountDueOnDelivery, amountPayableNow, grandTotal };
  };

  const submitOrder = async () => {
    if (!customerInfo.full_name || !customerInfo.email || !customerInfo.phone || !customerInfo.area || !customerInfo.street) {
      toast.error('Please fill in your name, email, phone and delivery address');
      return;
    }

    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(customerInfo.email)) {
      toast.error('Please enter a valid email address');
      return;
    }

    const hasItems = orders.some(order => order.items.length > 0);
    if (!hasItems) {
      toast.error('Please add items to your order');
      return;
    }

    try {
      const allItems = [];
      orders.forEach(order => {
        if (order.items && order.items.length > 0) allItems.push(...order.items);
      });

      const idempotencyKey =
        (typeof crypto !== 'undefined' && crypto.randomUUID)
          ? crypto.randomUUID()
          : `${Date.now()}-${Math.random().toString(36).slice(2)}`;

      const response = await fetch(`${backendUrl}/api/orders`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Idempotency-Key': idempotencyKey,
        },
        body: JSON.stringify({
          vendor_id: currentVendor.id,
          vendor_name: currentVendor.name,
          vendor_phone: currentVendor.phone,
          items: allItems,
          customer_info: customerInfo,
          total: 0,
          payment_method: paymentMethod,
        })
      });

      const data = await response.json();
      if (!response.ok) {
        toast.error(data.detail || 'Unable to create order.');
        return;
      }

      const orderId = data?.order?.id;
      if (!orderId) {
        toast.error('Order was created without a valid order ID.');
        return;
      }
      try {
        const attribution = JSON.parse(sessionStorage.getItem('shopimaxDealsAttribution') || 'null');
        if (attribution && (!attribution.started_at || Date.now() - attribution.started_at < 7 * 24 * 60 * 60 * 1000)) {
          fetch(`${backendUrl}/api/deals/track`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ event_type: 'purchase', campaign_id: attribution.campaign_id, product_id: attribution.product_id }) }).catch(() => {});
          sessionStorage.removeItem('shopimaxDealsAttribution');
        }
      } catch (_) {}

      // Direct WhatsApp checkout is a single-step vendor handoff.
      // The order is persisted by SchoolVerse, then the complete server-built
      // WhatsApp message is opened immediately. There is deliberately no
      // SchoolVerse confirmation/payment page in this flow.
      if (paymentMethod === 'WHATSAPP_DIRECT') {
        const directOrder = data.order;
        const whatsappUrl = directOrder?.fulfillment?.whatsapp_url;
        if (!whatsappUrl) {
          toast.error('This vendor is enabled for WhatsApp orders, but its WhatsApp number is not available.');
          return;
        }
        setOrders([{ items: [], id: Date.now() }]);
        setActiveOrderIndex(0);
        setCustomerInfo({ full_name: '', email: '', phone: '', area: '', street: '', delivery_price: 0, referral_code: '' });
        // Go straight to WhatsApp with the complete order message. Never route through order-confirmation.
        window.location.href = whatsappUrl;
        return;
      }

      const paymentResponse = await fetch(`${backendUrl}/api/payments/paystack/initialize`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ order_id: orderId, email: customerInfo.email })
      });
      const paymentData = await paymentResponse.json();
      if (!paymentResponse.ok) {
        toast.error(paymentData.detail || 'Unable to initialize payment.');
        return;
      }

      if (!window.PaystackPop) {
        const script = document.createElement('script');
        script.src = 'https://js.paystack.co/v2/inline.js';
        script.async = true;
        await new Promise((resolve, reject) => {
          script.onload = resolve;
          script.onerror = () => reject(new Error(
            'Unable to load the payment provider. Check your internet connection, or an ad-blocker/firewall may be blocking js.paystack.co, then try again.'
          ));
          document.body.appendChild(script);
        });
      }

      if (!paymentData.access_code) {
        toast.error('Payment could not be initialized (no access code returned). Please try again or contact support.');
        return;
      }

      const popup = new window.PaystackPop();
      popup.resumeTransaction(paymentData.access_code);

      // Never trust the browser success callback as proof of payment. Poll our
      // backend, which verifies the transaction and amount with Paystack.
      let verifiedOrder = null;
      const deadline = Date.now() + 5 * 60 * 1000;
      while (Date.now() < deadline) {
        await new Promise(resolve => setTimeout(resolve, 2500));
        const verifyResponse = await fetch(`${backendUrl}/api/payments/paystack/verify`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ reference: paymentData.reference })
        });
        const verifyData = await verifyResponse.json();
        console.log('PAYSTACK VERIFY RESPONSE:', verifyResponse.status, verifyData);
        if (verifyResponse.ok && verifyData?.verified && verifyData?.order?.id) {
          verifiedOrder = verifyData.order;
          break;
        }
        if (verifyResponse.status === 502 || verifyResponse.status >= 500) {
          throw new Error(verifyData.detail || 'Payment verification service is unavailable');
        }
      }

      if (!verifiedOrder) {
        toast.error('Payment was not confirmed. Your order remains unpaid.');
        return;
      }

      
      // Keep the server-verified order for the confirmation page.
      setVerifiedOrder(verifiedOrder);

      sessionStorage.setItem(
        'shopimax_verified_order',
        JSON.stringify(verifiedOrder)
      );

      setOrders([{ items: [], id: Date.now() }]);
      setActiveOrderIndex(0);
      setCustomerInfo({ full_name: '', email: '', phone: '', area: '', street: '', delivery_price: 0, referral_code: '' });
      window.location.hash = `#/order-confirmation/${encodeURIComponent(verifiedOrder.id)}`;
      setCurrentPage('order-confirmation');
    } catch (error) {
      console.error('Error submitting order:', error);
      // Show the real reason whenever we have one -- this used to always
      // show the same hardcoded generic text no matter what actually
      // failed (network unreachable, Paystack's script blocked, payment
      // verification erroring), which made this class of failure
      // impossible to diagnose from the outside. A bare "Failed to fetch"
      // (the browser's own message when a request can't reach the server
      // at all) is the one case still worth rephrasing in plain language.
      const message = error?.message && error.message !== 'Failed to fetch'
        ? error.message
        : 'Unable to reach the server. Check your internet connection and try again.';
      toast.error(message);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div 
      className={`min-h-screen`}
      style={{
        backgroundImage: 'none',
        backgroundColor: currentPage === 'home' ? '#f4f6f8' : '#E7F1F6',
      }}
    >
      <SiteAnalyticsTracker backendUrl={backendUrl} currentPage={currentPage} />
      {/* Saved Draft Restore Prompt */}
      {savedDraft &&
        savedDraft.items &&
        savedDraft.items.length > 0 &&
        currentVendor &&
        savedDraft.vendor_id === currentVendor.id &&
        orders.every(order => !order.items || order.items.length === 0) && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-[28px] p-6 mx-4 max-w-md w-full shadow-xl">
            <h3 className="text-lg font-semibold mb-3">Restore your saved cart?</h3>
            <p className="text-gray-600 mb-2">
              You have a saved cart from {savedDraft.vendor_name || 'this vendor'}.
            </p>
            <p className="text-sm text-gray-500 mb-6">
              Would you like to continue with the items you saved earlier?
            </p>
            <div className="flex space-x-3">
              <button
                type="button"
                onClick={() => setSavedDraft(null)}
                className="flex-1 px-4 py-3 bg-gray-200 text-gray-800 rounded-[20px] hover:bg-gray-300 font-medium"
              >
                Not now
              </button>
              <button
                type="button"
                onClick={restoreSavedDraft}
                className="flex-1 px-4 py-3 bg-blue-600 text-white rounded-[20px] hover:bg-blue-700 font-medium"
              >
                Restore cart
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Exit Popup */}
      {showExitPopup && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-[35px] p-6 mx-4 max-w-md">
            <h3 className="text-lg font-semibold mb-4">Switch Restaurant?</h3>
            <p className="text-gray-600 mb-6">
              Leaving this page will keep your current order as a draft until you return.
            </p>
            <div className="flex space-x-4">
              <button
                onClick={() => setShowExitPopup(false)}
                className="flex-1 px-4 py-2 bg-gray-200 text-gray-800 rounded-[35px] hover:bg-gray-300"
              >
                Cancel
              </button>
              <button
                onClick={confirmVendorSwitch}
                className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-[35px] hover:bg-blue-700"
              >
                Continue
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Location Detection Prompt - Hide on auth pages */}
      {showLocationPrompt && 
       !['vendor-login', 'vendor-register', 'admin-login', 'forgot-password', 'reset-password', 'verify-email'].includes(currentPage) &&
       !window.location.hash.startsWith('#/reset-password') &&
       !window.location.hash.startsWith('#/verify-email') &&
       !window.location.hash.startsWith('#/admin') && (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-[100] p-4">
          <div className="bg-white rounded-3xl p-6 max-w-md w-full shadow-2xl animate-slide-in relative">
            {/* Close button (X) at top right */}
            <button
              onClick={() => confirmLocation('all')}
              className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center rounded-full bg-gray-100 hover:bg-gray-200 text-gray-500 hover:text-gray-700 transition-colors"
              title="Close"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
            
            {/* Header with icon */}
            <div className="text-center mb-6">
              <div className="w-20 h-20 bg-gradient-to-br from-cyan-500 to-red-500 rounded-full mx-auto flex items-center justify-center mb-4 shadow-lg">
                <svg className="w-10 h-10 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">Welcome to SchoolVerse!</h2>
              <p className="text-gray-600">
                {locationLoading ? (
                  'Detecting your location...'
                ) : userLocation?.name && userLocation.name !== 'all' ? (
                  <>We detected you're near <span className="font-semibold text-cyan-700">{userLocation.name}</span></>
                ) : (
                  'Select your location to find nearby businesses'
                )}
              </p>
            </div>

            {locationLoading ? (
              <div className="flex justify-center py-8">
                <div className="animate-spin rounded-full h-12 w-12 border-4 border-cyan-500 border-t-transparent"></div>
              </div>
            ) : (
              <>
                {/* Detected location confirmation */}
                {userLocation?.name && userLocation.name !== 'all' && (
                  <button
                    onClick={() => confirmLocation(userLocation.name)}
                    className="w-full bg-gradient-to-r from-cyan-500 to-red-500 text-white py-4 rounded-2xl font-bold text-lg mb-3 hover:shadow-lg transition-all flex items-center justify-center gap-2"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    Yes, show me businesses in {userLocation.name}
                  </button>
                )}

                {/* Select different location */}
                <div className="mb-4">
                  <p className="text-sm text-gray-500 mb-2 text-center">Or select a different location:</p>
                  <div className="grid grid-cols-2 gap-2 max-h-48 overflow-y-auto">
                    {(locations || []).filter(loc => loc.is_active !== false).map((location) => (
                      <button
                        key={location.id}
                        onClick={() => confirmLocation(location.name)}
                        className={`p-3 rounded-xl border-2 text-sm font-medium transition-all ${
                          userLocation?.name === location.name 
                            ? 'border-cyan-500 bg-cyan-50 text-cyan-800' 
                            : 'border-gray-200 hover:border-cyan-300 hover:bg-cyan-50'
                        }`}
                      >
                         {location.name}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Show all option */}
                <button
                  onClick={showAllLocations}
                  className="w-full py-3 rounded-2xl border-2 border-gray-300 text-gray-700 font-medium hover:bg-gray-50 transition-all flex items-center justify-center gap-2"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  Show all businesses (no filter)
                </button>
              </>
            )}
          </div>
        </div>
      )}

      {/* Agent Promo Popup - Shows after 20 seconds */}
      {showAgentPromo && (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-[100] p-4">
          <div className="bg-gradient-to-br from-green-500 to-emerald-600 rounded-3xl p-6 max-w-sm w-full shadow-2xl animate-slide-in relative overflow-hidden">
            {/* Background decoration */}
            <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full -translate-y-1/2 translate-x-1/2"></div>
            <div className="absolute bottom-0 left-0 w-24 h-24 bg-white/10 rounded-full translate-y-1/2 -translate-x-1/2"></div>
            
            {/* Close button */}
            <button
              onClick={closeAgentPromo}
              className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center rounded-full bg-white/20 hover:bg-white/30 text-white transition-colors"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
            
            {/* Content */}
            <div className="relative text-center text-white">
              {/* Icon */}
              <div className="w-20 h-20 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-10 h-10 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              
              <h2 className="text-2xl font-bold mb-2">Start Earning Today! </h2>
              <p className="text-white/90 mb-6 text-sm">
                List businesses on SchoolVerse and <strong>get paid every time someone makes an order!</strong>
              </p>
              
              {/* Benefits */}
              <div className="bg-white/10 rounded-2xl p-4 mb-6 text-left">
                <div className="flex items-center gap-3 mb-2">
                  <span className="text-xl"></span>
                  <span className="text-sm">Earn commission on every order</span>
                </div>
                <div className="flex items-center gap-3 mb-2">
                  <span className="text-xl"></span>
                  <span className="text-sm">No investment required</span>
                </div>
                <div className="flex items-center gap-3">
                  <span className="text-xl"></span>
                  <span className="text-sm">Register unlimited businesses</span>
                </div>
              </div>
              
              {/* CTA Buttons */}
              <button
                onClick={() => {
                  closeAgentPromo();
                  setCurrentPage('agent-register');
                  window.location.hash = '#/agent-register';
                }}
                className="w-full bg-white text-green-600 py-3 rounded-xl font-bold hover:bg-gray-100 transition-all mb-3"
              >
                 Become an Agent
              </button>
              
              <button
                onClick={closeAgentPromo}
                className="text-white/70 hover:text-white text-sm underline"
              >
                Maybe later
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Share Modal */}
      {shareModalData && (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-end sm:items-center justify-center z-[100]" onClick={closeShareModal}>
          <div 
            className="bg-white rounded-t-3xl sm:rounded-3xl w-full sm:max-w-md p-6 animate-slide-in"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Preview Card */}
            <div className="flex items-center gap-4 mb-6 p-4 bg-gray-50 rounded-2xl">
              <img 
                src={getImageUrl(shareModalData.type === 'vendor' 
                  ? (shareModalData.item.image || shareModalData.item.image_url) 
                  : shareModalData.item.image_url
                )}
                alt={shareModalData.item.name}
                className="w-20 h-20 rounded-xl object-cover"
              />
              <div className="flex-1 min-w-0">
                <h3 className="font-bold text-gray-900 truncate">{shareModalData.item.name}</h3>
                {shareModalData.type === 'product' && (
                  <p className="text-cyan-700 font-semibold">{formatCurrency(shareModalData.item.price)}</p>
                )}
                <p className="text-gray-500 text-sm truncate">
                  {shareModalData.item.description || (shareModalData.type === 'vendor' ? 'Business on SchoolVerse' : 'Product on SchoolVerse')}
                </p>
              </div>
            </div>

            {/* Share Title */}
            <h2 className="text-xl font-bold text-gray-900 mb-4 text-center" >Share {shareModalData.type === 'vendor' ? 'vendor' : 'product'}</h2>

            {/* Share Options Grid */}
            <div className="grid grid-cols-4 gap-4 mb-6">
              {/* WhatsApp */}
              <button
                onClick={() => shareToSocial('whatsapp')}
                className="flex flex-col items-center gap-2 p-3 rounded-xl hover:bg-gray-50 transition-colors"
              >
                <div className="w-12 h-12 bg-green-500 rounded-full flex items-center justify-center">
                  <svg className="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                  </svg>
                </div>
                <span className="text-xs text-gray-600">WhatsApp</span>
              </button>

              {/* Facebook */}
              <button
                onClick={() => shareToSocial('facebook')}
                className="flex flex-col items-center gap-2 p-3 rounded-xl hover:bg-gray-50 transition-colors"
              >
                <div className="w-12 h-12 bg-blue-600 rounded-full flex items-center justify-center">
                  <svg className="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                  </svg>
                </div>
                <span className="text-xs text-gray-600">Facebook</span>
              </button>

              {/* Twitter/X */}
              <button
                onClick={() => shareToSocial('twitter')}
                className="flex flex-col items-center gap-2 p-3 rounded-xl hover:bg-gray-50 transition-colors"
              >
                <div className="w-12 h-12 bg-black rounded-full flex items-center justify-center">
                  <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
                  </svg>
                </div>
                <span className="text-xs text-gray-600">X</span>
              </button>

              {/* Telegram */}
              <button
                onClick={() => shareToSocial('telegram')}
                className="flex flex-col items-center gap-2 p-3 rounded-xl hover:bg-gray-50 transition-colors"
              >
                <div className="w-12 h-12 bg-sky-500 rounded-full flex items-center justify-center">
                  <svg className="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z"/>
                  </svg>
                </div>
                <span className="text-xs text-gray-600">Telegram</span>
              </button>
            </div>

            {/* Download Image Card */}
            <button
              onClick={() => shareToSocial('download')}
              className="w-full flex items-center justify-center gap-2 py-3 mb-3 bg-gradient-to-r from-purple-500 to-pink-500 text-white rounded-xl font-medium hover:opacity-90 transition-opacity"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
              Download Share Card
            </button>

            {/* Copy Link & Native Share */}
            <div className="flex gap-3">
              <button
                onClick={() => shareToSocial('copy')}
                className="flex-1 flex items-center justify-center gap-2 py-3 bg-gray-100 rounded-xl font-medium text-gray-700 hover:bg-gray-200 transition-colors"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                </svg>
                Copy Link
              </button>
              
              {navigator.share && (
                <button
                  onClick={() => shareToSocial('native')}
                  className="flex-1 flex items-center justify-center gap-2 py-3 bg-cyan-500 text-white rounded-xl font-medium hover:bg-cyan-700 transition-colors"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" />
                  </svg>
                  More Options
                </button>
              )}
            </div>

            {/* Close Button */}
            <button
              onClick={closeShareModal}
              className="absolute top-4 right-4 p-2 hover:bg-gray-100 rounded-full transition-colors"
            >
              <svg className="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>
      )}

      {/* Unified marketplace header: one shared customer header/search across public shopping pages. */}
      {currentPage !== 'search' && currentPage !== 'homepage2' && !['vendor-dashboard', 'admin-dashboard', 'agent-dashboard', 'vendor-login', 'vendor-register', 'admin-login', 'forgot-password', 'reset-password', 'verify-email', 'agent-login', 'agent-register', 'agent-forgot-password', 'agent-reset-password', 'vendor-registration-success', 'agent-registration-success', 'vendor-approval'].includes(currentPage) && (
        <MarketplaceHeader
          vendorTypes={vendorTypes}
          searchTerm={searchTerm}
          setSearchTerm={setSearchTerm}
          onSearchSubmit={handleSearch}
          onCategoryFilter={(t) => { if (t) navigateToCategoryPage(t); else navigateToHome(); }}
          selectedVendorType={selectedVendorType}
          cartCount={orders[activeOrderIndex]?.items?.reduce((s, i) => s + (i.quantity || 0), 0) || 0}
          wishlistCount={wishlist.count}
          userLocation={userLocation}
          onChangeLocation={changeLocation}
          onNavigateHome={navigateToHome}
          onOpenAccountDropdown={() => setShowAccountDropdown(!showAccountDropdown)}
          showAccountDropdown={showAccountDropdown}
          isVendorAuthed={vendorAuth.isAuthenticated}
          vendorName={vendorAuth.vendor?.name}
          onVendorLogout={vendorLogout}
          onOpenAgentRegister={() => setCurrentPage('vendor-register')}
          onOpenCart={() => { setCartDrawerTab('cart'); setShowCartDrawer(true); }}
          onOpenWishlist={() => { setCartDrawerTab('wishlist'); setShowCartDrawer(true); }}
          onScrollToDeals={navigateToDeals}
          onScrollToTopSelling={() => { setCurrentPage('home'); setTimeout(() => document.getElementById('featured-products-section')?.scrollIntoView({ behavior: 'smooth' }), 100); }}
          onScrollToBrands={() => { setCurrentPage('home'); setTimeout(() => document.getElementById('top-categories-section')?.scrollIntoView({ behavior: 'smooth' }), 100); }}
          onScrollToStores={() => { setCurrentPage('home'); setTimeout(() => document.getElementById('vendors-section')?.scrollIntoView({ behavior: 'smooth' }), 100); }}
          searchContext={getSearchContext().scope}
          searchContextLabel={getSearchContext().label}
          searchSuggestions={searchSuggestions}
          searchLoading={searchLoading}
          onSearchSelectProduct={(product) => { setSearchTerm(''); openProductPage(product); }}
          onSearchSelectVendor={(vendor) => { setSearchTerm(''); openVendorPage(vendor); }}
          onSearchSelectCategory={(cat) => { if (cat.type) { setSearchTerm(''); navigateToCategoryPage(cat.type); } else setSearchTerm(cat.label || ''); }}
        />
      )}
      {/* Main Content */}
      <main className={`${currentPage === 'home' || currentPage === 'homepage2' ? 'w-full pb-20 lg:pb-8' : 'max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-2 pb-24'}`}>
        {/* These five standalone pages were previously unreachable: they'd
            been accidentally nested inside the Header's true/false check
            above instead of being rendered as actual page content -- their
            JSX was computed and then discarded every time, which is why
            (for example) landing on order-confirmation after a successful
            payment showed a blank page. Moved here, alongside every other
            page this component already renders this same way. */}
        <Suspense fallback={<div className="min-h-[40vh] flex items-center justify-center text-gray-500" role="status">Loading order page...</div>}>
        {currentPage === 'vendor-order-detail' && (
          <VendorOrderDetailPage
            apiBaseUrl={backendUrl}
            orderId={window.location.hash.split('/')[3] || ''}
            onBack={() => {
              window.location.hash = '#/vendor/orders';
              setCurrentPage('vendor-orders');
            }}
          />
        )}
        {currentPage === 'vendor-orders' && (
          <VendorOrdersPage
            apiBaseUrl={backendUrl}
            vendorId={
              (typeof currentVendor !== 'undefined' && currentVendor?.id) ||
              (typeof vendorId !== 'undefined' && vendorId) ||
              ''
            }
            onContinue={() => {
              window.location.hash = '#/';
              setCurrentPage('home');
            }}
            onOpenOrder={(orderId) => {
              window.location.hash = `#/vendor/orders/${orderId}`;
              setCurrentPage('vendor-order-detail');
            }}
          />
        )}
        {currentPage === 'order-detail' && (
          <CustomerOrderDetailPage
            apiBaseUrl={backendUrl}
            orderId={decodeURIComponent(window.location.hash.replace('#/orders/', '')) || null}
            customerPhone={customerInfo?.phone || ''}
            onBackToOrders={() => {
              window.location.hash = '#/orders';
              setCurrentPage('orders');
            }}
            onContinueShopping={() => {
              window.location.hash = '#/';
              setCurrentPage('home');
            }}
          />
        )}
        {currentPage === 'orders' && (
          <CustomerOrdersPage
            apiBaseUrl={backendUrl}
            customerPhone={customerInfo?.phone || ''}
            onOpenOrder={(id) => {
              window.location.hash = `#/orders/${encodeURIComponent(id)}`;
              setCurrentPage('order-detail');
            }}
            onContinueShopping={() => {
              window.location.hash = '#/';
              setCurrentPage('home');
            }}
          />
        )}
        {currentPage === 'order-confirmation' && (
          <OrderConfirmationPage
            orderId={getOrderConfirmationId()}
            apiBaseUrl={backendUrl}
            initialOrder={verifiedOrder}
            onContinueShopping={() => {
              window.location.hash = '#/';
              setCurrentPage('home');
            }}
          />
        )}
        </Suspense>

        {currentPage === 'deals' && (
          <Suspense fallback={<LazyPageFallback />}>
            <DealsPage
              backendUrl={backendUrl}
              selectedLocation={selectedLocation}
              getImageUrl={getImageUrl}
              formatPrice={formatCurrency}
              onSelectProduct={openProductPage}
              onSelectVendor={openVendorPage}
              onHome={navigateToHome}
            />
          </Suspense>
        )}

        {currentPage === 'homepage2' && (
          <Suspense fallback={<LazyPageFallback />}>
            <Homepage2
              backendUrl={backendUrl}
              activeBanners={activeBanners}
              vendors={vendors}
              vendorTypes={vendorTypes}
              homepageSections={homepageSections}
              homepageFeaturedVendors={homepageFeaturedVendors}
              flashDeals={flashDeals}
              userLocation={userLocation}
              selectedLocation={selectedLocation}
              getImageUrl={getImageUrl}
              formatPrice={formatCurrency}
              searchTerm={searchTerm}
              setSearchTerm={setSearchTerm}
              onSearchSubmit={handleSearch}
              onSelectVendor={openVendorPage}
              onSelectProduct={openProductPage}
              onChangeLocation={changeLocation}
              onOpenCart={() => { setCartDrawerTab('cart'); setShowCartDrawer(true); }}
              onOpenAccountDropdown={() => setShowAccountDropdown(true)}
              cartCount={orders[activeOrderIndex]?.items?.reduce((sum, item) => sum + (item.quantity || 0), 0) || 0}
              onNavigateHome={navigateToHome}
              onNavigateCategory={navigateToCategoryPage}
              onSeeAllVendors={openAllVendorsPage}
              onShareProduct={(product, e) => openShareModal('product', product, e)}
              onShareVendor={(vendor, e) => openShareModal('vendor', vendor, e)}
            />
          </Suspense>
        )}

        {currentPage === 'search' && (
          <Suspense fallback={<LazyPageFallback />}>
            <SearchPage
              backendUrl={backendUrl}
              vendors={vendors}
              vendorTypes={vendorTypes}
              homepageSections={homepageSections}
              homepageFeaturedVendors={homepageFeaturedVendors}
              flashDeals={flashDeals}
              userLocation={userLocation}
              selectedLocation={selectedLocation}
              getImageUrl={getImageUrl}
              formatPrice={formatCurrency}
              onSelectVendor={openVendorPage}
              onSelectProduct={openProductPage}
              onShareVendor={(vendor, e) => openShareModal('vendor', vendor, e)}
              onShareProduct={(product, e) => openShareModal('product', product, e)}
              onSeeAllVendors={openAllVendorsPage}
              onNavigateCategory={navigateToCategoryPage}
              onHome={navigateToHome}
            />
          </Suspense>
        )}

        {currentPage === 'home' && (
          <>
            <MarketplaceHome
              vendorTypes={vendorTypes}
              activeBanners={activeBanners}
              vendors={vendors}
              homepageSections={homepageSections}
              homepageSettings={homepageSettings}
              homepageFeaturedVendors={homepageFeaturedVendors}
              userLocation={userLocation}
              flashDeals={flashDeals}
              flashDealsNearestEnd={flashDealsNearestEnd}
              getImageUrl={getImageUrl}
              navigateToCategoryPage={navigateToCategoryPage}
              onSelectVendor={(v) => openVendorPage(v)}
              onSeeMoreVendors={openAllVendorsPage}
              onSelectProduct={(product) => openProductPage(product)}
              onShareProduct={(product, e) => openShareModal('product', product, e)}
              onShareVendor={(vendor, e) => openShareModal('vendor', vendor, e)}
              currentCarouselIndex={currentCarouselIndex}
              setCurrentCarouselIndex={setCurrentCarouselIndex}
              searchTerm={searchTerm}
              searchResults={searchResults}
              searchVendors={searchSuggestions.vendors || []}
              searchCategories={searchSuggestions.categories || []}
              isSearchingProducts={isSearchingProducts}
              isProductSearchActive={isProductSearchActive}
              onSearchChange={setSearchTerm}
              onSearchSubmit={handleSearch}
              onOpenAgentRegister={() => setCurrentPage('vendor-register')}
              onOpenDeals={navigateToDeals}
            />
          </>
        )}

        {currentPage === 'product-description' && selectedProduct && (
          <Suspense fallback={<LazyPageFallback />}>
            <ProductDescriptionPage
              product={selectedProduct}
              vendor={currentVendor}
              getImageUrl={getImageUrl}
              onBack={() => openProductPage(selectedProduct)}
            />
          </Suspense>
        )}

        {currentPage === 'product' && selectedProduct && (
          <Suspense fallback={<LazyPageFallback />}>
            <ProductDetail
              product={selectedProduct}
              backendUrl={backendUrl}
              vendor={currentVendor}
              getImageUrl={getImageUrl}
              formatPrice={formatCurrency}
              onBack={navigateToHome}
              onOpenVendor={(vendor) => openVendorPage(vendor)}
              onOpenProduct={openProductPage}
              onShare={(product) => openShareModal('product', product)}
              onOpenDescription={openProductDescription}
              onAddToCart={addProductFromDetail}
            />
          </Suspense>
        )}

        {currentPage === 'vendor' && currentVendor && (
          <div>
            {/* Storefront hero: banner image + white info card overlaid */}
            <div className="relative -mx-4 sm:-mx-6 lg:-mx-8 mb-6">
              <div className="relative h-48 md:h-64 lg:h-72 overflow-hidden">
                <img 
                  src={getImageUrl(currentVendor.image_url)}
                  alt={`${currentVendor.name} storefront`}
                  className="w-full h-full object-cover"
                  loading="eager"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent" />
                {/* Breadcrumb */}
                <div className="absolute top-3 left-4 sm:left-6 lg:left-8 flex items-center gap-2 text-xs text-white/90">
                  <button
                    onClick={navigateToHome}
                    data-testid="vendor-breadcrumb-home"
                    className="hover:text-white flex items-center gap-1"
                  >
                    <i className="fa-solid fa-house text-[10px]" />
                    Home
                  </button>
                  <i className="fa-solid fa-chevron-right text-[9px] opacity-60" />
                  <span className="text-white/90 truncate max-w-[140px]">{currentVendor.name}</span>
                </div>
              </div>

              {/* Info panel ? sits just under the banner, marketplace style */}
              <div className="max-w-7xl mx-auto -mt-8 md:-mt-10 px-4 sm:px-6 lg:px-8 relative z-10">
                <div className="bg-white rounded-xl shadow-lg border border-gray-100 p-4 md:p-5 flex flex-col md:flex-row md:items-center gap-4">
                  {/* Vendor avatar */}
                  <div className="flex items-center gap-3 flex-1 min-w-0">
                    <div className="w-14 h-14 md:w-16 md:h-16 rounded-xl bg-cyan-50 border border-cyan-100 flex items-center justify-center flex-shrink-0 overflow-hidden">
                      <img
                        src={getImageUrl(currentVendor.image_url)}
                        alt={currentVendor.name}
                        className="w-full h-full object-cover"
                        onError={(e) => { e.target.style.display = 'none'; }}
                      />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <h1 className="text-lg md:text-2xl font-black text-gray-900 truncate">{currentVendor.name}</h1>
                        <button
                          type="button"
                          onClick={() => openShareModal('vendor', currentVendor)}
                          className="flex-shrink-0 inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-full bg-cyan-50 text-cyan-700 hover:bg-cyan-100 font-semibold text-xs"
                          data-testid="vendor-page-share"
                          aria-label={`Share ${currentVendor.name}`}
                        >
                          <i className="fa-solid fa-share-nodes" />
                          <span className="hidden sm:inline">Share</span>
                        </button>
                      </div>
                      {currentVendor.description && (
                        <p className="text-xs md:text-sm text-gray-500 line-clamp-1">{currentVendor.description}</p>
                      )}
                    </div>
                  </div>

                  {/* Meta chips */}
                  <div className="flex flex-wrap items-center gap-2 text-xs md:text-sm">
                    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-gray-100 rounded-full text-gray-700">
                      <i className="fa-solid fa-location-dot text-cyan-500 text-[11px]" />
                      <span className="truncate max-w-[140px]">{currentVendor.location}</span>
                    </span>
                    {currentVendor.category && (
                      <span className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-gray-100 rounded-full text-gray-700">
                        <i className="fa-solid fa-tag text-cyan-500 text-[11px]" />
                        <span className="truncate max-w-[100px]">{currentVendor.category}</span>
                      </span>
                    )}
                    {Number(currentVendor.rating) > 0 && (
                      <span className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-yellow-50 rounded-full text-yellow-700 font-semibold">
                        <i className="fa-solid fa-star text-yellow-500 text-[11px]" />
                        {Number(currentVendor.rating).toFixed(1)}/5
                      </span>
                    )}
                    <span
                      className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-bold ${
                        currentVendor.is_open === false
                          ? 'bg-red-100 text-red-700'
                          : 'bg-green-100 text-green-700'
                      }`}
                    >
                      <span className={`w-1.5 h-1.5 rounded-full ${currentVendor.is_open === false ? 'bg-red-500' : 'bg-green-500'}`} />
                      {currentVendor.is_open === false ? 'Closed' : 'Open'}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Dynamic Moving Text/Marquee */}
            {publicMarquee.is_active && publicMarquee.texts && publicMarquee.texts.length > 0 && (
              <div className="relative -mx-4 sm:-mx-6 lg:-mx-8 mb-6 overflow-hidden shadow-xl rounded-lg">
                {/* Gradient background -- colors are admin-customizable (Moving Text tab) */}
                <div
                  className="absolute inset-0"
                  style={{
                    background: `linear-gradient(to right, ${publicMarquee.color_from || '#e11d48'}, ${publicMarquee.color_via || '#c026d3'}, ${publicMarquee.color_to || '#6d28d9'})`
                  }}
                ></div>
                
                {/* Shine effect overlay */}
                <div className="absolute inset-0 bg-gradient-to-b from-white/20 to-transparent"></div>
                
                {/* Animated sparkle pattern */}
                <div className="absolute inset-0 opacity-20" style={{
                  backgroundImage: 'radial-gradient(circle at 20% 50%, white 1px, transparent 1px), radial-gradient(circle at 80% 50%, white 1px, transparent 1px)',
                  backgroundSize: '40px 40px'
                }}></div>
                
                {/* Content with dynamic speed */}
                <div className="relative py-5 marquee">
                  <div 
                    className="marquee-content"
                    style={{ animation: `marquee ${publicMarquee.speed || 30}s linear infinite` }}
                  >
                    {/* First set of texts */}
                    {publicMarquee.texts.map((item, index) => (
                      <React.Fragment key={`marquee-1-${index}`}>
                        <span className="text-white font-semibold text-lg mx-8 drop-shadow-lg bg-white/20 px-4 py-2 rounded-full backdrop-blur-sm inline-block border border-white/30 shadow-lg">
                          {item.text}
                        </span>
                        {index < publicMarquee.texts.length - 1 && (
                          <span className="text-white/80 mx-2 font-bold text-xl">{publicMarquee.separator || ''}</span>
                        )}
                      </React.Fragment>
                    ))}
                    {/* Duplicate for seamless loop */}
                    <span className="text-white/80 mx-4 font-bold text-xl">{publicMarquee.separator || ''}</span>
                    {publicMarquee.texts.map((item, index) => (
                      <React.Fragment key={`marquee-2-${index}`}>
                        <span className="text-white font-semibold text-lg mx-8 drop-shadow-lg bg-white/20 px-4 py-2 rounded-full backdrop-blur-sm inline-block border border-white/30 shadow-lg">
                          {item.text}
                        </span>
                        {index < publicMarquee.texts.length - 1 && (
                          <span className="text-white/80 mx-2 font-bold text-xl">{publicMarquee.separator || ''}</span>
                        )}
                      </React.Fragment>
                    ))}
                  </div>
                </div>
              </div>
            )}
            
            {/* Storefront products: shared purchase-oriented card */}
            <div className="mb-4 flex items-center gap-3">
              <span className="inline-block w-1.5 h-6 bg-cyan-500 rounded-full" />
              <h2 className="text-xl md:text-2xl font-bold text-gray-900">Products</h2>
              <span className="text-sm text-gray-500">({products.length})</span>
            </div>
            {(() => {
              const q = String(searchTerm || '').trim().toLowerCase();
              const localMatches = q
                ? products.filter(p => [p.name, p.description, p.category, p.sku].filter(Boolean).join(' ').toLowerCase().includes(q))
                : products;
              const showingGlobal = q && localMatches.length === 0 && vendorSearchFallbackProducts.length > 0;
              const displayProducts = showingGlobal ? vendorSearchFallbackProducts : localMatches;
              return (
                <>
                  {q && showingGlobal && (
                    <div className="mb-3 rounded-xl border border-cyan-100 bg-cyan-50 px-3 py-2 text-xs text-cyan-800">
                      No matches in {currentVendor.name}. Showing related products across SchoolVerse.
                    </div>
                  )}
                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3 md:gap-4" data-testid="vendor-products-grid">
                    {displayProducts.map((product) => (
                      showingGlobal ? (
                        <ProductCard
                          key={product.id}
                          product={product}
                          getImageUrl={getImageUrl}
                          formatPrice={formatCurrency}
                          onClick={openProductPage}
                          onShare={(p, e) => openShareModal('product', p, e)}
                        />
                      ) : (
                        <VendorProductCard
                          key={product.id}
                          product={product}
                          vendor={currentVendor}
                          getImageUrl={getImageUrl}
                          formatPrice={formatCurrency}
                          quantity={getItemQuantity(product.id)}
                          onView={openProductPage}
                          onShare={(p, e) => openShareModal('product', p, e)}
                          onAdd={(p) => updateQuantity(p.id, 1)}
                          onIncrement={(p) => updateQuantity(p.id, 1)}
                          onDecrement={(p) => updateQuantity(p.id, -1)}
                        />
                      )
                    ))}
                    {displayProducts.length === 0 && (
                      <div className="col-span-full text-center py-12 text-gray-500">
                        <i className="fa-solid fa-box-open text-4xl mb-3 opacity-40" />
                        <p className="text-sm">No products available from this vendor yet.</p>
                      </div>
                    )}
                  </div>
                </>
              );
            })()}
          </div>
        )}

        {currentPage === 'checkout' && (
          <div>
            <div className="flex justify-between items-center mb-8">
              <h2 className="text-3xl font-bold text-gray-900">Checkout - {currentVendor.name}</h2>
              <button
                onClick={createNewOrder}
                className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 font-medium"
              >
                + New Order
              </button>
            </div>
            
            {/* All Orders */}
            {orders.map((order, index) => {
              if (order.items.length === 0) return null; // Don't show empty orders
              
              return (
                <div key={order.id} className="bg-white rounded-lg shadow-md p-6 mb-6">
                  <div className="flex justify-between items-center mb-4">
                    <h3 className="text-xl font-semibold">Order {index + 1}</h3>
                    <div className="flex space-x-2">
                      <button
                        onClick={() => editOrder(index)}
                        className="px-3 py-1 bg-blue-600 text-white rounded hover:bg-blue-700 text-sm"
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => duplicateOrder(index)}
                        className="px-3 py-1 bg-purple-600 text-white rounded hover:bg-purple-700 text-sm"
                      >
                        Duplicate
                      </button>
                      <button
                        onClick={() => removeOrder(index)}
                        className="px-3 py-1 bg-red-600 text-white rounded hover:bg-red-700 text-sm"
                      >
                        Remove
                      </button>
                    </div>
                  </div>
                  {order.items.map(item => (
                    <div key={item.product_id} className="flex justify-between items-center py-2">
                      <span>{item.product_name} x{item.quantity}</span>
                      <span className="font-semibold">{formatCurrency(item.price * item.quantity)}</span>
                    </div>
                  ))}
                  <div className="border-t pt-2 mt-2">
                    <div className="flex justify-between items-center font-bold">
                      <span>Subtotal:</span>
                      <span>{formatCurrency(order.items.reduce((sum, item) => sum + (item.price * item.quantity), 0))}</span>
                    </div>
                  </div>
                </div>
              );
            })}

            {/* Delivery Address */}
            <div className="bg-white rounded-lg shadow-md p-6 mb-6">
              <h3 className="text-xl font-semibold mb-4">Delivery Address</h3>
              <div className="space-y-4">
                <input
                  type="text"
                  placeholder="Full Name"
                  value={customerInfo.full_name}
                  onChange={(e) => setCustomerInfo({...customerInfo, full_name: e.target.value})}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
                <input
                  type="email"
                  placeholder="Email Address"
                  value={customerInfo.email}
                  onChange={(e) => setCustomerInfo({...customerInfo, email: e.target.value})}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
                <input
                  type="tel"
                  placeholder="Phone Number"
                  value={customerInfo.phone}
                  onChange={(e) => setCustomerInfo({...customerInfo, phone: e.target.value})}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Delivery Area *
                  </label>
                  <select
                    value={customerInfo.area}
                    onChange={(e) => handleAreaChange(e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="">Select delivery area...</option>
                    <option value="Walk In (Self Pick Up)"> Walk In (Self Pick Up) - FREE</option>
                    {deliveryAreas.map(areaData => (
                      <option key={areaData.area} value={areaData.area}>
                        {areaData.area} - Delivery: {formatCurrency(areaData.price)}
                      </option>
                    ))}
                  </select>
                  {customerInfo.area === "Walk In (Self Pick Up)" && (
                    <p className="mt-1 text-sm text-green-600">
                       Self Pick Up - No delivery fee
                    </p>
                  )}
                  {customerInfo.area && customerInfo.area !== "Walk In (Self Pick Up)" && customerInfo.delivery_price > 0 && (
                    <p className="mt-1 text-sm text-green-600">
                       Delivery to {customerInfo.area} - {formatCurrency(customerInfo.delivery_price)}
                    </p>
                  )}
                </div>
                <input
                  type="text"
                  placeholder="Street Address"
                  value={customerInfo.street}
                  onChange={(e) => setCustomerInfo({...customerInfo, street: e.target.value})}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            </div>

            {/* Referral Code */}
            <div className="bg-white rounded-lg shadow-md p-6 mb-6">
              <h3 className="text-xl font-semibold mb-4">Have a promo or referral code?</h3>
              
              {!appliedReferral ? (
                <div className="space-y-3">
                  <div className="flex gap-2">
                    <input
                      type="text"
                      placeholder="Enter promo or referral code"
                      value={referralCode}
                      onChange={(e) => {
                        setReferralCode(e.target.value.toUpperCase());
                        setReferralError('');
                      }}
                      className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent uppercase"
                      disabled={applyingReferral}
                    />
                    <button
                      onClick={applyReferralCode}
                      disabled={!referralCode.trim() || applyingReferral}
                      className="px-6 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-semibold"
                    >
                      {applyingReferral ? 'Checking...' : 'Apply'}
                    </button>
                  </div>
                  
                  {referralError && (
                    <p className="text-sm text-red-600">
                       {referralError}
                    </p>
                  )}
                </div>
              ) : (
                <div className="space-y-3">
                  <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-semibold text-green-800 flex items-center gap-2">
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                          Code Applied: <span className="font-mono">{appliedReferral.code}</span>
                        </p>
                        <p className="text-sm text-green-700 mt-1">
                          {appliedReferral.discount_type === 'percentage' && `${appliedReferral.discount_value}% discount`}
                          {appliedReferral.discount_type === 'free_delivery' && 'Free Delivery'}
                          {appliedReferral.discount_type === 'cashback' && `${formatCurrency(appliedReferral.cashback_amount || appliedReferral.discount_value)} cashback`}
                          {appliedReferral.influencer_name && ` - by ${appliedReferral.influencer_name}`}
                        </p>
                      </div>
                      <button
                        onClick={removeReferralCode}
                        className="text-red-600 hover:text-red-800 font-medium text-sm"
                      >
                        Remove
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Grand Total */}
            <div className="bg-blue-50 rounded-lg p-6 mb-6">
              <div className="space-y-2">
                <div className="flex justify-between items-center">
                  <span className="text-lg font-semibold text-gray-700">Food Total:</span>
                  <span className="text-lg font-semibold text-gray-900">
                    {formatCurrency(getGrandTotal() - (customerInfo.delivery_price || 0) - getPackagingTotal())}
                  </span>
                </div>
                {customerInfo.delivery_price > 0 && (
                  <div className="flex justify-between items-center">
                    <span className="text-lg font-semibold text-gray-700">Delivery Fee ({customerInfo.area}):</span>
                    <span className="text-lg font-semibold text-gray-900">
                      {formatCurrency(customerInfo.delivery_price)}
                    </span>
                  </div>
                )}
                {getPackagingTotal() > 0 && (
                  <div className="flex justify-between items-center">
                    <span className="text-lg font-semibold text-gray-700">
                      Packaging Fee ({getNumberOfOrders()} {getNumberOfOrders() === 1 ? 'order' : 'orders'}):
                    </span>
                    <span className="text-lg font-semibold text-gray-900">
                      {formatCurrency(getPackagingTotal())}
                    </span>
                  </div>
                )}
                {/* Service Charge - always show, crossed out if zero */}
                <div className="flex justify-between items-center">
                  <span className={`text-lg font-semibold text-gray-700 ${(currentVendor?.service_charge || 0) === 0 ? 'line-through' : ''}`}>
                    Service Charge:
                  </span>
                  <span className={`text-lg font-semibold text-gray-900 ${(currentVendor?.service_charge || 0) === 0 ? 'line-through' : ''}`}>
                    {formatCurrency(currentVendor?.service_charge || 0)}
                  </span>
                </div>
                
                {/* Referral Discount */}
                {appliedReferral && appliedReferral.discount_amount > 0 && (
                  <div className="flex justify-between items-center text-green-600">
                    <span className="text-lg font-semibold">
                      Referral Discount ({appliedReferral.code}):
                    </span>
                    <span className="text-lg font-semibold">
                      -{formatCurrency(appliedReferral.discount_amount)}
                    </span>
                  </div>
                )}
                
                <div className="border-t pt-2">
                  <div className="flex justify-between items-center">
                    <span className="text-2xl font-bold text-imax-orange">Grand Total:</span>
                    <span className="text-3xl font-bold text-imax-orange-dark">
                      {formatCurrency(Math.max(0, getGrandTotal() - (appliedReferral?.discount_amount || 0)))}
                    </span>
                  </div>
                  {appliedReferral && appliedReferral.discount_amount > 0 && (
                    <p className="text-sm text-green-600 text-right mt-1">
                      You saved {formatCurrency(appliedReferral.discount_amount)}!
                    </p>
                  )}
                </div>
              </div>
              <p className="text-sm text-gray-600 mt-2">
                {getNumberOfOrders()} Order{getNumberOfOrders() !== 1 ? 's' : ''} in cart
                {customerInfo.area && ` + Delivery to ${customerInfo.area}`}
              </p>
            </div>

            {/* Payment methods: normal Pay Now, optional POD, and optional
                admin-enabled direct WhatsApp ordering. */}
            {!whatsappDirectEnabled && podSettings.pod_enabled && (() => {
              const preview = getPodPreview();
              return (
                <div className="bg-white rounded-2xl p-5 mb-4 shadow-sm">
                  <h3 className="text-lg font-bold text-gray-800 mb-3">Payment method</h3>

                  <label className={`flex items-start gap-3 p-3 rounded-xl border-2 cursor-pointer mb-2 ${
                    paymentMethod === 'PAY_NOW' ? 'border-imax-orange bg-cyan-50' : 'border-gray-200'
                  }`}>
                    <input
                      type="radio"
                      name="payment_method"
                      checked={paymentMethod === 'PAY_NOW'}
                      onChange={() => setPaymentMethod('PAY_NOW')}
                      className="mt-1"
                    />
                    <div>
                      <div className="font-semibold text-gray-900">Pay Now / Express</div>
                      <div className="text-sm text-gray-600">
                        Pay {formatCurrency(Math.max(0, getGrandTotal() - (appliedReferral?.discount_amount || 0)))} now
                      </div>
                    </div>
                  </label>

                  {isPodEligible ? (
                    <label className={`flex items-start gap-3 p-3 rounded-xl border-2 cursor-pointer ${
                      paymentMethod === 'POD' ? 'border-imax-orange bg-cyan-50' : 'border-gray-200'
                    }`}>
                      <input
                        type="radio"
                        name="payment_method"
                        checked={paymentMethod === 'POD'}
                        onChange={() => setPaymentMethod('POD')}
                        className="mt-1"
                      />
                      <div>
                        <div className="font-semibold text-gray-900">Pay on Delivery</div>
                        <div className="text-sm text-gray-600">Pay {formatCurrency(preview.amountPayableNow)} now</div>
                        <div className="text-sm text-gray-600">Pay {formatCurrency(preview.amountDueOnDelivery)} on delivery</div>
                      </div>
                    </label>
                  ) : (
                    <div className="flex items-start gap-3 p-3 rounded-xl border-2 border-gray-100 bg-gray-50 opacity-70">
                      <input type="radio" disabled className="mt-1" />
                      <div>
                        <div className="font-semibold text-gray-500">Pay on Delivery</div>
                        <div className="text-sm text-gray-500">
                          Pay on Delivery is available for orders of {formatCurrency(podSettings.pod_minimum_order_value)} or more.
                        </div>
                      </div>
                    </div>
                  )}

                </div>
              );
            })()}

            {whatsappDirectEnabled && (
              <div className="bg-white rounded-2xl p-5 mb-4 shadow-sm border border-green-100">
                <h3 className="text-lg font-bold text-gray-800 mb-1">Order via WhatsApp</h3>
                <p className="text-sm text-gray-600">This vendor accepts orders directly on WhatsApp. No SchoolVerse payment is required.</p>
                <p className="text-xs text-green-700 mt-2 font-medium">Your complete order will be sent to the vendor on WhatsApp.</p>
              </div>
            )}

            {/* Payment Button */}
            <button
              onClick={submitOrder}
              className="w-full bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white py-4 rounded-[35px] text-lg font-semibold transition-all duration-300 hover:shadow-lg hover:transform hover:-translate-y-1 flex items-center justify-center gap-2"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                <rect x="3" y="5" width="18" height="14" rx="2" strokeWidth="2" />
                <path d="M3 10h18M7 15h4" strokeWidth="2" strokeLinecap="round" />
              </svg>
              {paymentMethod === 'WHATSAPP_DIRECT' ? 'Send order on WhatsApp' : paymentMethod === 'POD' ? 'Continue to payment' : 'Pay securely with Paystack'}
            </button>
          </div>
        )}

        {/* Floating Proceed Button - Minimal & Attractive */}
      {currentPage === 'vendor' && orders[activeOrderIndex]?.items.length > 0 && (
        <div className="fixed bottom-6 left-1/2 transform -translate-x-1/2 z-40">
          <button
            onClick={() => {
              setCurrentPage('checkout');
              window.history.pushState({ page: 'checkout' }, '', `${window.location.pathname}#/checkout`);
            }}
            className="bg-imax-orange text-white px-6 py-3 rounded-[35px] shadow-xl hover:bg-imax-orange-dark transition-all duration-300 hover:shadow-2xl hover:scale-105 flex items-center gap-2 text-base font-semibold"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z" />
            </svg>
            <span>Checkout</span>
            <span className="bg-white bg-opacity-20 px-2 py-0.5 rounded-[35px] text-sm font-bold">
              {formatCurrency(getCartTotal())}
            </span>
          </button>
        </div>
      )}

        {/* Category Page - Custom layouts per category type */}
        {currentPage === 'category' && currentCategoryType && (
          <div className="min-h-screen">
            {/* Category Header ? Marketplace style */}
            <div className="bg-white border-b border-gray-200 -mx-4 sm:-mx-6 lg:-mx-8 px-4 sm:px-6 lg:px-8 py-5">
              <div className="max-w-7xl mx-auto">
                {/* Breadcrumb */}
                <nav className="flex items-center gap-2 text-xs md:text-sm text-gray-500 mb-3" data-testid="category-breadcrumb">
                  <button
                    onClick={() => {
                      if (window.history.length > 1) {
                        window.history.back();
                      } else {
                        setCurrentPage('home');
                        window.history.replaceState({ page: 'home' }, '', window.location.pathname);
                      }
                    }}
                    className="flex items-center gap-1 hover:text-cyan-700"
                  >
                    <i className="fa-solid fa-house text-[11px]" />
                    Home
                  </button>
                  <i className="fa-solid fa-chevron-right text-[9px] opacity-60" />
                  <span className="text-gray-900 font-semibold">
                    {vendorTypes.find(v => v.type === currentCategoryType)?.label || currentCategoryType}
                  </span>
                </nav>
                <div className="flex items-center gap-4">
                  <div className="w-14 h-14 md:w-16 md:h-16 rounded-2xl bg-cyan-50 border border-cyan-100 flex items-center justify-center text-3xl md:text-4xl flex-shrink-0">
                    {vendorTypes.find(v => v.type === currentCategoryType)?.icon || ''}
                  </div>
                  <div className="min-w-0">
                    <h1 className="text-xl md:text-3xl font-black text-gray-900 truncate">
                      {vendorTypes.find(v => v.type === currentCategoryType)?.label || currentCategoryType}
                    </h1>
                    <p className="text-xs md:text-sm text-gray-500 mt-0.5">
                      {vendorTypes.find(v => v.type === currentCategoryType)?.description || 'Browse all businesses'}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Search and Filter Bar */}
            <div className="sticky top-[68px] z-10 bg-white border-b border-gray-100 shadow-sm px-4 py-3 -mx-4 sm:-mx-6 lg:-mx-8 sm:px-6 lg:px-8">
              <div className="max-w-7xl mx-auto flex flex-col sm:flex-row gap-3">
                {/* Search Input */}
                <div className="relative flex-1">
                  <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                  <input
                    type="text"
                    placeholder={`Search ${vendorTypes.find(v => v.type === currentCategoryType)?.label || 'vendors'}...`}
                    value={categorySearchTerm}
                    onChange={(e) => setCategorySearchTerm(e.target.value)}
                    data-testid="category-search-input"
                    className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500 outline-none text-sm"
                  />
                  {categorySearchTerm && (
                    <button 
                      onClick={() => setCategorySearchTerm('')}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    >
                      
                    </button>
                  )}
                </div>
                
                {/* Filter Buttons */}
                <div className="flex gap-2">
                  <button
                    onClick={() => setCategoryFilter('all')}
                    className={`px-4 py-2 rounded-xl text-sm font-medium transition-colors ${
                      categoryFilter === 'all' 
                        ? 'bg-cyan-500 text-white' 
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    All
                  </button>
                  <button
                    onClick={() => setCategoryFilter('open')}
                    className={`px-4 py-2 rounded-xl text-sm font-medium transition-colors flex items-center gap-1 ${
                      categoryFilter === 'open' 
                        ? 'bg-green-500 text-white' 
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    <span className="w-2 h-2 rounded-full bg-current"></span> Open
                  </button>
                  <button
                    onClick={() => setCategoryFilter('closed')}
                    className={`px-4 py-2 rounded-xl text-sm font-medium transition-colors flex items-center gap-1 ${
                      categoryFilter === 'closed' 
                        ? 'bg-red-500 text-white' 
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    <span className="w-2 h-2 rounded-full bg-current"></span> Closed
                  </button>
                </div>
              </div>
            </div>

            <div className="max-w-7xl mx-auto px-4 py-6">
              {loading ? (
                <div className="flex justify-center py-12">
                  <div className="animate-spin rounded-full h-12 w-12 border-4 border-imax-orange border-t-transparent"></div>
                </div>
              ) : (
                <>
                  {searchResults.length > 0 && String(searchTerm || '').trim() && (
                    <section className="mb-8" data-testid="category-product-search-results">
                      <div className="flex items-center justify-between mb-4">
                        <div>
                          <h2 className="text-lg md:text-xl font-bold text-gray-900">Products in {vendorTypes.find(v => v.type === currentCategoryType)?.label || 'this category'}</h2>
                          <p className="text-xs text-gray-500">{searchResults.length} matching products</p>
                        </div>
                      </div>
                      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3 md:gap-4">
                        {searchResults.map(product => (
                          <ProductCard key={product.id} product={product} getImageUrl={getImageUrl} onClick={openProductPage} />
                        ))}
                      </div>
                    </section>
                  )}
                  {/* Featured / promoted vendors */}
                  {(() => {
                    const normalizedSearch = categorySearchTerm.trim().toLowerCase();
                    const matchesVendor = (vendor) => {
                      const haystack = [
                        vendor.name,
                        vendor.description,
                        vendor.location,
                        vendor.city,
                        vendor.vendor_type,
                      ]
                        .filter(Boolean)
                        .join(' ')
                        .toLowerCase();
                      const matchesSearch = !normalizedSearch || haystack.includes(normalizedSearch);
                      const matchesFilter =
                        categoryFilter === 'all' ||
                        (categoryFilter === 'open' && vendor.is_open !== false) ||
                        (categoryFilter === 'closed' && vendor.is_open === false);
                      return matchesSearch && matchesFilter;
                    };

                    const visibleFeatured = featuredVendors.filter(matchesVendor);
                    const visibleVendors = categoryVendors.filter(matchesVendor);
                    const totalResults = visibleFeatured.length + visibleVendors.length;
                    const gridClass =
                      currentCategoryType === 'products' || currentCategoryType === 'giftcards'
                        ? 'grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5'
                        : 'grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5';

                    return (
                      <>
                        {visibleFeatured.length > 0 && (
                          <section className="mb-8" data-testid="category-featured-vendors">
                            <div className="flex items-center justify-between mb-4">
                              <div className="flex items-center gap-2">
                                <span className="inline-flex w-8 h-8 rounded-full bg-yellow-50 text-yellow-600 items-center justify-center">
                                  <i className="fa-solid fa-star text-sm" />
                                </span>
                                <div>
                                  <h2 className="text-lg md:text-xl font-bold text-gray-900">Featured vendors</h2>
                                  <p className="text-xs text-gray-500">Handpicked places to start exploring.</p>
                                </div>
                              </div>
                            </div>
                            <div className={`grid ${gridClass} gap-3 md:gap-4`}>
                              {visibleFeatured.map((vendor) => (
                                <VendorCard
                                  key={vendor.id}
                                  vendor={vendor}
                                  getImageUrl={getImageUrl}
                                  onClick={openVendorPage}
                                  onShare={(v, e) => openShareModal('vendor', v, e)}
                                  featured
                                  actionLabel={vendor.is_open === false ? 'Closed' : 'Shop'}
                                />
                              ))}
                            </div>
                          </section>
                        )}

                        <section data-testid="category-all-vendors">
                          <div className="flex items-center justify-between mb-4">
                            <div>
                              <h2 className="text-lg md:text-xl font-bold text-gray-900">
                                {visibleFeatured.length > 0 ? 'More vendors' : 'Vendors'}
                              </h2>
                              <p className="text-xs text-gray-500 mt-0.5">
                                {totalResults} {totalResults === 1 ? 'vendor' : 'vendors'} found
                              </p>
                            </div>
                          </div>

                          {totalResults === 0 ? (
                            <div className="text-center py-12 bg-white border border-gray-200 rounded-2xl">
                              <span className="text-4xl mb-3 block"></span>
                              <p className="text-gray-600 font-semibold">No vendors found</p>
                              <p className="text-sm text-gray-400 mt-1">Try a different search or filter.</p>
                              {(categorySearchTerm || categoryFilter !== 'all') && (
                                <button
                                  type="button"
                                  onClick={() => {
                                    setCategorySearchTerm('');
                                    setCategoryFilter('all');
                                  }}
                                  className="mt-4 px-4 py-2 rounded-lg bg-cyan-50 text-cyan-700 font-semibold text-sm hover:bg-cyan-100"
                                >
                                  Clear filters
                                </button>
                              )}
                            </div>
                          ) : (
                            <div className={`grid ${gridClass} gap-3 md:gap-4`}>
                              {visibleVendors.map((vendor) => (
                                <VendorCard
                                  key={vendor.id}
                                  vendor={vendor}
                                  getImageUrl={getImageUrl}
                                  onClick={openVendorPage}
                                  onShare={(v, e) => openShareModal('vendor', v, e)}
                                  actionLabel={vendor.is_open === false ? 'Closed' : 'Shop'}
                                />
                              ))}
                            </div>
                          )}
                        </section>
                      </>
                    );
                  })()}
                </>
              )}
            </div>
          </div>
        )}

        {/* Vendor Login Page */}
        {currentPage === 'vendor-login' && (
          <VendorLoginPage 
            onLogin={vendorLogin}
            onSwitchToRegister={() => setCurrentPage('vendor-register')}
            onForgotPassword={() => setCurrentPage('forgot-password')}
          />
        )}

        {/* Forgot Password Page */}
        {currentPage === 'forgot-password' && (
          <ForgotPasswordPage 
            backendUrl={backendUrl}
            onBackToLogin={() => setCurrentPage('vendor-login')}
          />
        )}

        {/* Reset Password Page */}
        {currentPage === 'reset-password' && (
          <ResetPasswordPage 
            backendUrl={backendUrl}
            token={resetToken}
            onBackToLogin={() => setCurrentPage('vendor-login')}
            onSuccess={() => {
              setCurrentPage('vendor-login');
              setResetToken('');
            }}
          />
        )}

        {/* Email Verification Page */}
        {currentPage === 'verify-email' && (
          <VerifyEmailPage 
            backendUrl={backendUrl}
            token={verifyToken}
            onBackToLogin={() => setCurrentPage('vendor-login')}
            onSuccess={() => {
              setCurrentPage('vendor-login');
              setVerifyToken('');
            }}
          />
        )}

        {/* Vendor Register Page */}
        {currentPage === 'vendor-register' && (
          <VendorRegisterPage 
            onRegister={vendorRegister}
            onSwitchToLogin={() => setCurrentPage('vendor-login')}
            onRegistrationSuccess={(result) => { setRegistrationResult(result); setCurrentPage('vendor-registration-success'); }}
            backendUrl={backendUrl}
            vendorTypes={vendorTypes}
          />
        )}

        {currentPage === 'vendor-registration-success' && (
          <VendorRegistrationSuccessPage result={registrationResult} onLogin={() => setCurrentPage('vendor-login')} onHome={() => { setCurrentPage('home'); window.location.hash = ''; }} />
        )}

        {/* Vendor Dashboard */}
        {currentPage === 'vendor-dashboard' && vendorAuth.isAuthenticated && (
          <VendorDashboard 
            vendor={vendorAuth.vendor}
            products={vendorProducts}
            services={vendorServices}
            properties={vendorProperties}
            events={vendorEvents}
            giftcards={vendorGiftcards}
            orders={vendorOrders}
            onFetchProducts={fetchVendorProducts}
            onFetchServices={fetchVendorServices}
            onFetchProperties={fetchVendorProperties}
            onFetchEvents={fetchVendorEvents}
            onFetchGiftcards={fetchVendorGiftcards}
            onFetchOrders={fetchVendorOrders}
            onCreateProduct={createProduct}
            onUpdateProduct={updateProduct}
            onDeleteProduct={deleteProduct}
            onCreateService={createService}
            onUpdateService={updateService}
            onDeleteService={deleteService}
            onCreateProperty={createProperty}
            onUpdateProperty={updateProperty}
            onDeleteProperty={deleteProperty}
            onCreateEvent={createEvent}
            onUpdateEvent={updateEvent}
            onDeleteEvent={deleteEvent}
            onCreateGiftcard={createGiftcard}
            onUpdateGiftcard={updateGiftcard}
            onDeleteGiftcard={deleteGiftcard}
            onUpdateProfile={updateVendorProfile}
            getImageUrl={getImageUrl}
            backendUrl={backendUrl}
            token={vendorAuth.token}
            onLogout={vendorLogout}
          />
        )}

        {/* Admin Login Page */}
        {currentPage === 'admin-login' && (
          <AdminLoginPage 
            onLogin={adminLogin}
          />
        )}

        {/* Admin Dashboard */}
        {currentPage === 'admin-dashboard' && adminAuth.isAuthenticated && (
          <AdminDashboard 
            admin={adminAuth.admin}
            adminToken={adminAuth.token}
            restaurants={adminRestaurants}
            stats={adminStats}
            referralCodes={adminReferralCodes}
            vendors={vendors}
            onFetchRestaurants={fetchAdminRestaurants}
            onFetchStats={fetchAdminStats}
            onFetchReferralCodes={fetchAdminReferralCodes}
            onToggleStatus={toggleRestaurantStatus}
            onCreateReferralCode={createReferralCode}
            onDeleteReferralCode={deleteReferralCode}
            onToggleReferralStatus={toggleReferralCodeStatus}
            onLogout={adminLogout}
            getImageUrl={getImageUrl}
            backendUrl={backendUrl}
          />
        )}

        {/* Agent Registration Page */}
        {currentPage === 'agent-register' && (
          <AgentRegisterPage 
            backendUrl={backendUrl}
            onSuccess={(data) => {
              if (data.pending_approval) {
                setRegistrationResult(data);
                setCurrentPage('agent-registration-success');
                window.location.hash = '#/agent-registration-success';
                return;
              }
              setAgentAuth({
                isAuthenticated: true,
                token: data.token,
                agent: data.agent
              });
              localStorage.setItem('agentToken', data.token);
              localStorage.setItem('agentData', JSON.stringify(data.agent));
              setCurrentPage('agent-dashboard');
              window.location.hash = '#/agent-dashboard';
            }}
            onSwitchToLogin={() => {
              setCurrentPage('agent-login');
              window.location.hash = '#/agent-login';
            }}
          />
        )}

        {currentPage === 'agent-registration-success' && (
          <AgentRegistrationSuccessPage result={registrationResult} onLogin={() => { setCurrentPage('agent-login'); window.location.hash = '#/agent-login'; }} onHome={() => { setCurrentPage('home'); window.location.hash = ''; }} />
        )}

        {currentPage === 'agent-forgot-password' && (
          <AgentForgotPasswordPage backendUrl={backendUrl} onBackToLogin={() => setCurrentPage('agent-login')} />
        )}
        {currentPage === 'agent-reset-password' && (
          <AgentResetPasswordPage backendUrl={backendUrl} token={resetToken} onBackToLogin={() => setCurrentPage('agent-login')} onSuccess={() => { setCurrentPage('agent-login'); setResetToken(''); }} />
        )}

        {/* Agent Login Page */}
        {currentPage === 'agent-login' && (
          <AgentLoginPage 
            backendUrl={backendUrl}
            onForgotPassword={() => setCurrentPage('agent-forgot-password')}
            onSuccess={(data) => {
              setAgentAuth({
                isAuthenticated: true,
                token: data.token,
                agent: data.agent
              });
              localStorage.setItem('agentToken', data.token);
              localStorage.setItem('agentData', JSON.stringify(data.agent));
              setCurrentPage('agent-dashboard');
              window.location.hash = '#/agent-dashboard';
            }}
            onSwitchToRegister={() => {
              setCurrentPage('agent-register');
              window.location.hash = '#/agent-register';
            }}
          />
        )}

        {/* Agent Dashboard */}
        {currentPage === 'agent-dashboard' && agentAuth.isAuthenticated && (
          <AgentDashboard 
            agent={agentAuth.agent}
            token={agentAuth.token}
            backendUrl={backendUrl}
            onLogout={() => {
              setAgentAuth({ isAuthenticated: false, token: null, agent: null });
              localStorage.removeItem('agentToken');
              localStorage.removeItem('agentData');
              setCurrentPage('home');
              window.location.hash = '';
            }}
            vendorTypes={vendorTypes}
            locations={locations}
          />
        )}

        {/* Vendor Approval Page (from WhatsApp link) */}
        {currentPage === 'vendor-approval' && (
          <VendorApprovalPage 
            backendUrl={backendUrl}
            onComplete={() => {
              setCurrentPage('home');
              window.location.hash = '';
            }}
          />
        )}
      </main>
      
      {/* One shared marketplace footer for every public shopping page. */}
      {currentPage !== 'search' && currentPage !== 'homepage2' && !['vendor-dashboard', 'admin-dashboard', 'agent-dashboard', 'vendor-login', 'vendor-register', 'admin-login', 'forgot-password', 'reset-password', 'verify-email', 'agent-login', 'agent-register', 'agent-forgot-password', 'agent-reset-password', 'vendor-registration-success', 'agent-registration-success', 'vendor-approval'].includes(currentPage) && (
        <MarketplaceFooter onOpenAgentRegister={() => setCurrentPage('vendor-register')} />
      )}

      {/* Cart / Wishlist Drawer (global) */}
      <CartDrawer
        open={showCartDrawer}
        initialTab={cartDrawerTab}
        onClose={() => setShowCartDrawer(false)}
        orders={orders}
        activeOrderIndex={activeOrderIndex}
        currentVendor={currentVendor}
        updateQuantity={updateQuantity}
        getImageUrl={getImageUrl}
        onOpenProduct={(product) => {
          setShowCartDrawer(false);
          openProductPage(product);
        }}
        onCheckout={() => {
          if (currentVendor && orders[activeOrderIndex]?.items?.length > 0) {
            setCurrentPage('checkout');
          } else if (currentVendor) {
            setCurrentPage('vendor');
          } else {
            // No vendor selected ? take them home to pick a vendor
            setCurrentPage('home');
          }
        }}
      />

      {/* Mobile Bottom Nav ? only on customer-facing pages */}
      {(currentPage === 'home' || currentPage === 'homepage2' || currentPage === 'search' || currentPage === 'category' || currentPage === 'vendor' || currentPage === 'deals') && (
        <MobileBottomNav
          currentPage={currentPage === 'homepage2' ? 'home' : currentPage}
          cartCount={orders[activeOrderIndex]?.items?.reduce((s, i) => s + (i.quantity || 0), 0) || 0}
          onHome={navigateToHome}
          onSearch={navigateToSearch}
          onCategories={() => document.getElementById('top-categories-section')?.scrollIntoView({ behavior: 'smooth' }) || (setCurrentPage('home'))}
          onCart={() => { setCartDrawerTab('cart'); setShowCartDrawer(true); }}
          onDeals={navigateToDeals}
          onAccount={() => setShowAccountDropdown(true)}
        />
      )}

    </div>
  );
}

// Vendor Login Component
function VendorLoginPage({ onLogin, onSwitchToRegister, onForgotPassword }) {
  const [formData, setFormData] = useState({ email: '', password: '' });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    const result = await onLogin(formData.email, formData.password);
    
    if (!result.success) {
      setError(result.error);
    }
    
    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center py-12 px-4">
      <div className="max-w-md w-full space-y-8">
        <div>
          <h2 className="text-3xl font-extrabold text-gray-900 text-center">
            Business Login
          </h2>
          <p className="mt-2 text-center text-sm text-gray-600">
            Sign in to manage your business on SchoolVerse
          </p>
        </div>
        <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
              {error}
            </div>
          )}
          <div className="space-y-4">
            <input
              type="email"
              required
              placeholder="Email address"
              value={formData.email}
              onChange={(e) => setFormData({...formData, email: e.target.value})}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            {/* Password with eye toggle */}
            <div className="relative">
              <input
                type={showPassword ? "text" : "password"}
                required
                placeholder="Password"
                value={formData.password}
                onChange={(e) => setFormData({...formData, password: e.target.value})}
                className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
              >
                {showPassword ? (
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                  </svg>
                ) : (
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                  </svg>
                )}
              </button>
            </div>
            
            {/* Forgot Password Link */}
            <div className="text-right">
              <button
                type="button"
                onClick={onForgotPassword}
                className="text-sm text-gray-600 hover:text-imax-orange"
              >
                Forgot Password?
              </button>
            </div>
          </div>
          <button
            type="submit"
            disabled={loading}
            className="w-full bg-imax-orange text-white py-2 px-4 rounded-md hover:bg-imax-orange-dark disabled:opacity-50"
          >
            {loading ? 'Signing in...' : 'Sign In'}
          </button>
          <div className="text-center">
            <button
              type="button"
              onClick={onSwitchToRegister}
              className="text-imax-orange hover:text-imax-orange-dark"
            >
              Don't have an account? Register here
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// Vendor Register Component
function VendorRegisterPage({ onRegister, onSwitchToLogin, backendUrl, vendorTypes, onRegistrationSuccess }) {
  const [step, setStep] = useState(0);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    locations: [],
    no_physical_location: false,
    address: '',
    email: '',
    phone: '',
    social_media: '',
    vendor_type: '',
    category: '',
    password: '',
    confirmPassword: '',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [availableLocations, setAvailableLocations] = useState([]);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const steps = ['Business details', 'Contact', 'Business info', 'Password'];
  const categoryOptions = {
    food: ['Staples and Swallows', 'Soups, Stews, and Sauces', 'Protein, Meat, and Fish', 'Snacks and Baked Goods', 'Drinks', 'Others'],
    products: ['Electronics', 'Fashion', 'Home & Garden', 'Health & Beauty', 'Groceries', 'Others'],
    services: ['Plumbing', 'Electrical', 'Cleaning', 'Carpentry', 'Painting', 'AC/HVAC', 'Others'],
    property: ['Apartments', 'Houses', 'Land', 'Commercial', 'Others'],
    events: ['Parties', 'Concerts', 'Conferences', 'Workshops', 'Others'],
    giftcards: ['Shopping', 'Dining', 'Entertainment', 'General', 'Others'],
  };

  useEffect(() => {
    fetch(`${backendUrl}/api/locations`)
      .then(r => r.ok ? r.json() : { locations: [] })
      .then(data => setAvailableLocations(data.locations || []))
      .catch(() => setAvailableLocations([]));
  }, [backendUrl]);

  useEffect(() => {
    if (formData.vendor_type && !categoryOptions[formData.vendor_type]?.includes(formData.category)) {
      setFormData(prev => ({ ...prev, category: '' }));
    }
  }, [formData.vendor_type]);

  const update = (key, value) => setFormData(prev => ({ ...prev, [key]: value }));

  const validateStep = (targetStep = step) => {
    setError('');
    if (targetStep === 0) {
      if (!formData.name.trim()) return setError('Business Name is required.'), false;
      if (!formData.description.trim()) return setError('Business Description is required.'), false;
      if (!formData.no_physical_location && formData.locations.length === 0) return setError('Select at least one business location or choose No physical location.'), false;
      if (!formData.address.trim()) return setError('Business Address is required.'), false;
    }
    if (targetStep === 1) {
      if (!formData.email.trim()) return setError('Email is required.'), false;
      if (!/^\S+@\S+\.\S+$/.test(formData.email.trim())) return setError('Enter a valid email address.'), false;
      if (!formData.phone.trim()) return setError('Phone number is required.'), false;
    }
    if (targetStep === 2) {
      if (!formData.vendor_type) return setError('Select a business type.'), false;
      if (!formData.category) return setError('Select a category.'), false;
    }
    if (targetStep === 3) {
      if (formData.password.length < 8) return setError('Password must be at least 8 characters long.'), false;
      if (!/[A-Za-z]/.test(formData.password) || !/\d/.test(formData.password)) return setError('Password must contain at least one letter and one number.'), false;
      if (formData.password !== formData.confirmPassword) return setError('Passwords do not match.'), false;
    }
    return true;
  };

  const nextStep = () => {
    if (validateStep(step)) setStep(prev => Math.min(prev + 1, steps.length - 1));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateStep(3)) return;
    setLoading(true);
    setError('');
    const result = await onRegister({
      ...formData,
      name: formData.name.trim(),
      description: formData.description.trim(),
      email: formData.email.trim().toLowerCase(),
      phone: formData.phone.trim(),
      social_media: formData.social_media.trim(),
      address: formData.address.trim(),
      location: formData.locations[0] || '',
      locations: formData.no_physical_location ? [] : formData.locations,
      no_physical_location: formData.no_physical_location,
    });
    setLoading(false);
    if (!result.success) setError(result.error || 'Registration failed.');
    else if (result.pendingApproval) onRegistrationSuccess?.(result);
  };

  const fieldClass = 'w-full px-4 py-3 border border-gray-300 rounded-xl bg-white text-gray-900 focus:outline-none focus:ring-2 focus:ring-imax-orange focus:border-transparent';
  const labelClass = 'block text-sm font-semibold text-gray-700 mb-2';

  return (
    <div className="min-h-screen bg-gray-50 px-4 py-8 sm:py-12">
      <div className="max-w-2xl mx-auto">
        <div className="text-center mb-8">
          <h1 className="text-3xl sm:text-4xl font-extrabold text-gray-900">Business Registration</h1>
          <p className="mt-2 text-gray-600">Join SchoolVerse and start selling your products or services</p>
        </div>

        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5 sm:p-7">
          <div className="grid grid-cols-4 gap-2 mb-8">
            {steps.map((name, index) => (
              <button key={name} type="button" onClick={() => index < step && setStep(index)} className="text-left" disabled={index > step}>
                <div className={`h-1.5 rounded-full ${index <= step ? 'bg-imax-orange' : 'bg-gray-200'}`} />
                <p className={`mt-2 text-xs sm:text-sm font-semibold ${index === step ? 'text-imax-orange' : 'text-gray-500'}`}>{index + 1}. {name}</p>
              </button>
            ))}
          </div>

          {error && <div className="mb-5 p-3 rounded-xl bg-red-50 border border-red-200 text-red-700 text-sm">{error}</div>}

          <form onSubmit={handleSubmit}>
            {step === 0 && (
              <div className="space-y-5">
                <div>
                  <label className={labelClass}>Business Name <span className="text-red-500">*</span></label>
                  <input className={fieldClass} value={formData.name} onChange={e => update('name', e.target.value)} placeholder="Enter your business name" required />
                </div>
                <div>
                  <label className={labelClass}>Business Description <span className="text-red-500">*</span></label>
                  <textarea className={`${fieldClass} resize-none`} rows={4} value={formData.description} onChange={e => update('description', e.target.value)} placeholder="Tell customers what your business offers" required />
                </div>
                <div>
                  <label className={labelClass}>Select location <span className="text-red-500">*</span></label>
                  {!formData.no_physical_location && (
                    <div className="border border-gray-300 rounded-xl p-3 bg-white max-h-48 overflow-y-auto space-y-2">
                      {availableLocations.length === 0 ? (
                        <p className="text-sm text-gray-500 py-2">No locations are currently available.</p>
                      ) : (
                        availableLocations.map(location => (
                          <label key={location.id} className="flex items-center gap-3 p-2 rounded-lg hover:bg-gray-50 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={formData.locations.includes(location.id)}
                              onChange={e => update('locations', e.target.checked ? [...formData.locations, location.id] : formData.locations.filter(id => id !== location.id))}
                              className="w-4 h-4 accent-cyan-500"
                            />
                            <span className="text-sm text-gray-800">{location.name}</span>
                          </label>
                        ))
                      )}
                    </div>
                  )}
                  {!formData.no_physical_location && <p className="text-xs text-gray-500 mt-1">Select one or more locations.</p>}
                  <label className="mt-3 flex items-center gap-3 text-sm text-gray-700 cursor-pointer">
                    <input type="checkbox" checked={formData.no_physical_location} onChange={e => update('no_physical_location', e.target.checked)} className="w-4 h-4 accent-cyan-500" />
                    <span>No physical location</span>
                  </label>
                </div>
                <div>
                  <label className={labelClass}>Business Address <span className="text-red-500">*</span></label>
                  <textarea className={`${fieldClass} resize-none`} rows={3} value={formData.address} onChange={e => update('address', e.target.value)} placeholder="Enter your business or operating address" required />
                </div>
              </div>
            )}

            {step === 1 && (
              <div className="space-y-5">
                <div>
                  <label className={labelClass}>Email <span className="text-red-500">*</span></label>
                  <input type="email" className={fieldClass} value={formData.email} onChange={e => update('email', e.target.value)} placeholder="Enter your email address" required />
                </div>
                <div>
                  <label className={labelClass}>Phone number <span className="text-red-500">*</span></label>
                  <input type="tel" className={fieldClass} value={formData.phone} onChange={e => update('phone', e.target.value)} placeholder="Enter your phone number" required />
                </div>
                <div>
                  <label className={labelClass}>Social media <span className="font-normal text-gray-400">(optional)</span></label>
                  <input className={fieldClass} value={formData.social_media} onChange={e => update('social_media', e.target.value)} placeholder="Instagram, Facebook, TikTok or other social link" />
                </div>
              </div>
            )}

            {step === 2 && (
              <div className="space-y-5">
                <div>
                  <label className={labelClass}>Select Business type <span className="text-red-500">*</span></label>
                  <select className={fieldClass} value={formData.vendor_type} onChange={e => update('vendor_type', e.target.value)} required>
                    <option value="">Select business type</option>
                    {(vendorTypes || []).map(type => <option key={type.type} value={type.type}>{type.label}</option>)}
                  </select>
                </div>
                <div>
                  <label className={labelClass}>Select Category <span className="text-red-500">*</span></label>
                  <select className={fieldClass} value={formData.category} onChange={e => update('category', e.target.value)} required disabled={!formData.vendor_type}>
                    <option value="">{formData.vendor_type ? 'Select category' : 'Select business type first'}</option>
                    {(categoryOptions[formData.vendor_type] || []).map(category => <option key={category} value={category}>{category}</option>)}
                  </select>
                </div>
              </div>
            )}

            {step === 3 && (
              <div className="space-y-5">
                <div>
                  <label className={labelClass}>Password <span className="text-red-500">*</span></label>
                  <div className="relative">
                    <input type={showPassword ? 'text' : 'password'} className={`${fieldClass} pr-12`} value={formData.password} onChange={e => update('password', e.target.value)} placeholder="Minimum 8 characters, letter + number" required />
                    <button type="button" onClick={() => setShowPassword(v => !v)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500" aria-label="Toggle password visibility"><i className={`fa-solid ${showPassword ? 'fa-eye-slash' : 'fa-eye'}`} /></button>
                  </div>
                </div>
                <div>
                  <label className={labelClass}>Confirm Password <span className="text-red-500">*</span></label>
                  <div className="relative">
                    <input type={showConfirmPassword ? 'text' : 'password'} className={`${fieldClass} pr-12`} value={formData.confirmPassword} onChange={e => update('confirmPassword', e.target.value)} placeholder="Re-enter your password" required />
                    <button type="button" onClick={() => setShowConfirmPassword(v => !v)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500" aria-label="Toggle confirm password visibility"><i className={`fa-solid ${showConfirmPassword ? 'fa-eye-slash' : 'fa-eye'}`} /></button>
                  </div>
                </div>
              </div>
            )}

            <div className="mt-8 flex gap-3">
              {step > 0 && <button type="button" onClick={() => { setError(''); setStep(prev => prev - 1); }} className="flex-1 border border-gray-300 text-gray-700 py-3 rounded-xl font-semibold">Back</button>}
              {step < steps.length - 1 ? (
                <button type="button" onClick={nextStep} className="flex-1 bg-imax-orange text-white py-3 rounded-xl font-semibold">Next</button>
              ) : (
                <button type="submit" disabled={loading} className="flex-1 bg-imax-orange text-white py-3 rounded-xl font-semibold disabled:opacity-50">{loading ? 'Creating account...' : 'Create Account'}</button>
              )}
            </div>
          </form>

          <div className="mt-5 text-center">
            <button type="button" onClick={onSwitchToLogin} className="text-sm text-imax-orange hover:text-imax-orange-dark">Already have a business account? Sign in</button>
          </div>
        </div>
      </div>
    </div>
  );
}


function VendorRegistrationCompletion({ backendUrl, token, vendor }) {
  const toast = useToast();
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [legal, setLegal] = useState({ is_registered_business: null, registration_name: '', registration_name_same_as_business: false });

  const loadStatus = useCallback(async () => {
    setLoading(true);
    try {
      const response = await fetch(`${backendUrl}/api/vendor/registration-completion`, { headers: { Authorization: `Bearer ${token}` } });
      const data = await response.json();
      if (!response.ok) throw new Error(data.detail || 'Unable to load registration details');
      setStatus(data);
      setLegal(data.legal || { is_registered_business: null, registration_name: '', registration_name_same_as_business: false });
    } catch (error) {
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  }, [backendUrl, token, toast]);

  useEffect(() => { if (token) loadStatus(); }, [token, loadStatus]);

  const setRegisteredBusiness = (value) => {
    setLegal(prev => ({
      ...prev,
      is_registered_business: value,
      registration_name: value ? (prev.registration_name_same_as_business ? (vendor?.name || '') : prev.registration_name) : '',
      registration_name_same_as_business: value ? prev.registration_name_same_as_business : false,
    }));
  };

  const saveLegal = async (e) => {
    e.preventDefault();
    if (legal.is_registered_business === null) return toast.error('Select whether your business is a registered business.');
    if (legal.is_registered_business && !legal.registration_name.trim()) return toast.error('Registration name is required.');
    setSaving(true);
    try {
      const response = await fetch(`${backendUrl}/api/vendor/registration-completion/legal`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(legal),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.detail || 'Unable to save legal details');
      toast.success('Legal details saved.');
      await loadStatus();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="bg-white rounded-xl border p-6">Loading registration details...</div>;

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-xl border p-6">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h2 className="text-xl font-bold text-gray-900">Complete registration</h2>
            <p className="text-sm text-gray-600 mt-1">Finish your legal and bank details after your SchoolVerse account has been approved.</p>
          </div>
          <span className={`px-3 py-1 rounded-full text-xs font-semibold ${status?.complete ? 'bg-green-100 text-green-700' : 'bg-cyan-100 text-cyan-800'}`}>{status?.complete ? 'Complete' : 'Incomplete'}</span>
        </div>
      </div>

      <div className="bg-white rounded-xl border p-6">
        <h3 className="text-lg font-bold text-gray-900">Legal details</h3>
        <p className="text-sm text-gray-600 mt-1 mb-5">Tell us whether your business is legally registered.</p>
        <form onSubmit={saveLegal} className="space-y-5">
          <div>
            <p className="block text-sm font-semibold text-gray-700 mb-3">Is your business a registered business? <span className="text-red-500">*</span></p>
            <div className="flex flex-wrap gap-6">
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={legal.is_registered_business === true} onChange={() => setRegisteredBusiness(true)} className="w-4 h-4 accent-cyan-500" />
                <span>Yes</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={legal.is_registered_business === false} onChange={() => setRegisteredBusiness(false)} className="w-4 h-4 accent-cyan-500" />
                <span>No</span>
              </label>
            </div>
          </div>

          {legal.is_registered_business === true && (
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Registration name <span className="text-red-500">*</span></label>
              <input
                className="w-full px-4 py-3 border border-gray-300 rounded-xl disabled:bg-gray-100 disabled:text-gray-500"
                value={legal.registration_name}
                disabled={legal.registration_name_same_as_business}
                onChange={e => setLegal(prev => ({ ...prev, registration_name: e.target.value }))}
                placeholder="Enter your registered business name"
                required
              />
              <label className="mt-3 flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                <input
                  type="checkbox"
                  checked={legal.registration_name_same_as_business}
                  onChange={e => setLegal(prev => ({ ...prev, registration_name_same_as_business: e.target.checked, registration_name: e.target.checked ? (vendor?.name || '') : '' }))}
                  className="w-4 h-4 accent-cyan-500"
                />
                <span>Same as business operation name</span>
              </label>
            </div>
          )}

          <button type="submit" disabled={saving} className="px-5 py-3 rounded-xl bg-imax-orange text-white font-semibold disabled:opacity-50">{saving ? 'Saving...' : 'Save legal details'}</button>
        </form>
      </div>

      <Suspense fallback={<div className="py-3 text-sm text-gray-500">Loading payment settings…</div>}>
        <PaymentAccountSetup backendUrl={backendUrl} token={token} ownerType="vendor" />
      </Suspense>
    </div>
  );
}

function ForgotPasswordPage({ backendUrl, onBackToLogin }) {
  const toast = useToast();
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await fetch(`${backendUrl}/api/vendor/forgot-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email })
      });

      if (response.ok) {
        setSubmitted(true);
        toast.success('If this email exists, you will receive a password reset link.');
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Something went wrong. Please try again.');
      }
    } catch (error) {
      toast.error('Network error. Please check your connection.');
    }
    
    setLoading(false);
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center py-12 px-4">
        <div className="max-w-md w-full space-y-8 text-center">
          <div className="bg-white rounded-lg shadow-lg p-8">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
              </svg>
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Check Your Email</h2>
            <p className="text-gray-600 mb-6">
              If an account exists for <strong>{email}</strong>, you will receive a password reset link shortly.
            </p>
            <p className="text-sm text-gray-500 mb-6">
              The link will expire in 1 hour. Don't forget to check your spam folder.
            </p>
            <button
              onClick={onBackToLogin}
              className="w-full bg-imax-orange text-white py-2 px-4 rounded-md hover:bg-imax-orange-dark"
            >
              Back to Login
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center py-12 px-4">
      <div className="max-w-md w-full space-y-8">
        <div>
          <h2 className="text-3xl font-extrabold text-gray-900 text-center">
            Forgot Password
          </h2>
          <p className="mt-2 text-center text-sm text-gray-600">
            Enter your email address and we'll send you a link to reset your password
          </p>
        </div>
        <form className="mt-8 space-y-6 bg-white rounded-lg shadow-lg p-8" onSubmit={handleSubmit}>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Email Address
            </label>
            <input
              type="email"
              required
              placeholder="Enter your email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-imax-orange"
            />
          </div>
          <button
            type="submit"
            disabled={loading}
            className="w-full bg-imax-orange text-white py-2 px-4 rounded-md hover:bg-imax-orange-dark disabled:opacity-50"
          >
            {loading ? 'Sending...' : 'Send Reset Link'}
          </button>
          <div className="text-center">
            <button
              type="button"
              onClick={onBackToLogin}
              className="text-gray-600 hover:text-imax-orange text-sm"
            >
              ? Back to Login
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// Reset Password Page Component
function ResetPasswordPage({ backendUrl, token, onBackToLogin, onSuccess }) {
  const toast = useToast();
  const [formData, setFormData] = useState({ password: '', confirmPassword: '' });
  const [loading, setLoading] = useState(false);
  const [validating, setValidating] = useState(true);
  const [tokenValid, setTokenValid] = useState(false);
  const [tokenEmail, setTokenEmail] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [success, setSuccess] = useState(false);

  // Validate token on mount
  useEffect(() => {
    const validateToken = async () => {
      try {
        const response = await fetch(`${backendUrl}/api/vendor/check-reset-token?token=${token}`);
        if (response.ok) {
          const data = await response.json();
          setTokenValid(true);
          setTokenEmail(data.email);
        } else {
          setTokenValid(false);
        }
      } catch (error) {
        setTokenValid(false);
      }
      setValidating(false);
    };

    if (token) {
      validateToken();
    } else {
      setValidating(false);
    }
  }, [backendUrl, token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (formData.password !== formData.confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    
    if (formData.password.length < 8) {
      toast.error('Password must be at least 8 characters long');
      return;
    }
    
    if (!/[A-Za-z]/.test(formData.password) || !/\d/.test(formData.password)) {
      toast.error('Password must contain at least one letter and one number');
      return;
    }

    setLoading(true);

    try {
      const response = await fetch(`${backendUrl}/api/vendor/reset-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, new_password: formData.password })
      });

      if (response.ok) {
        setSuccess(true);
        toast.success('Password reset successful!');
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Password reset failed');
      }
    } catch (error) {
      toast.error('Network error. Please try again.');
    }
    
    setLoading(false);
  };

  if (validating) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center py-12 px-4">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-imax-orange mx-auto"></div>
          <p className="mt-4 text-gray-600">Validating reset link...</p>
        </div>
      </div>
    );
  }

  if (!tokenValid) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center py-12 px-4">
        <div className="max-w-md w-full text-center bg-white rounded-lg shadow-lg p-8">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Invalid or Expired Link</h2>
          <p className="text-gray-600 mb-6">
            This password reset link is invalid or has expired. Please request a new one.
          </p>
          <button
            onClick={onBackToLogin}
            className="w-full bg-imax-orange text-white py-2 px-4 rounded-md hover:bg-imax-orange-dark"
          >
            Back to Login
          </button>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center py-12 px-4">
        <div className="max-w-md w-full text-center bg-white rounded-lg shadow-lg p-8">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Password Reset Successful!</h2>
          <p className="text-gray-600 mb-6">
            Your password has been updated. You can now sign in with your new password.
          </p>
          <button
            onClick={onSuccess}
            className="w-full bg-imax-orange text-white py-2 px-4 rounded-md hover:bg-imax-orange-dark"
          >
            Sign In
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center py-12 px-4">
      <div className="max-w-md w-full space-y-8">
        <div>
          <h2 className="text-3xl font-extrabold text-gray-900 text-center">
            Reset Your Password
          </h2>
          <p className="mt-2 text-center text-sm text-gray-600">
            Enter a new password for <strong>{tokenEmail}</strong>
          </p>
        </div>
        <form className="mt-8 space-y-6 bg-white rounded-lg shadow-lg p-8" onSubmit={handleSubmit}>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                New Password
              </label>
              <div className="relative">
                <input
                  type={showPassword ? "text" : "password"}
                  required
                  placeholder="Enter new password"
                  value={formData.password}
                  onChange={(e) => setFormData({...formData, password: e.target.value})}
                  className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-imax-orange"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                >
                  {showPassword ? (
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                    </svg>
                  ) : (
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                    </svg>
                  )}
                </button>
              </div>
              <p className="text-xs text-gray-500 mt-1">At least 8 characters with letters and numbers</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Confirm New Password
              </label>
              <div className="relative">
                <input
                  type={showConfirmPassword ? "text" : "password"}
                  required
                  placeholder="Confirm new password"
                  value={formData.confirmPassword}
                  onChange={(e) => setFormData({...formData, confirmPassword: e.target.value})}
                  className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-imax-orange"
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                >
                  {showConfirmPassword ? (
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                    </svg>
                  ) : (
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                    </svg>
                  )}
                </button>
              </div>
            </div>
          </div>
          <button
            type="submit"
            disabled={loading}
            className="w-full bg-imax-orange text-white py-2 px-4 rounded-md hover:bg-imax-orange-dark disabled:opacity-50"
          >
            {loading ? 'Resetting...' : 'Reset Password'}
          </button>
          <div className="text-center">
            <button
              type="button"
              onClick={onBackToLogin}
              className="text-gray-600 hover:text-imax-orange text-sm"
            >
              ? Back to Login
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// Email Verification Page Component
function VerifyEmailPage({ backendUrl, token, onBackToLogin, onSuccess }) {
  const toast = useToast();
  const [verifying, setVerifying] = useState(true);
  const [verified, setVerified] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    const verifyEmail = async () => {
      try {
        const response = await fetch(`${backendUrl}/api/vendor/verify-email?token=${token}`);
        if (response.ok) {
          setVerified(true);
          toast.success('Email verified successfully!');
        } else {
          const data = await response.json();
          setError(data.detail || 'Verification failed');
        }
      } catch (err) {
        setError('Network error. Please try again.');
      }
      setVerifying(false);
    };

    if (token) {
      verifyEmail();
    } else {
      setError('No verification token provided');
      setVerifying(false);
    }
  }, [backendUrl, token, toast]);

  if (verifying) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center py-12 px-4">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-imax-orange mx-auto"></div>
          <p className="mt-4 text-gray-600">Verifying your email...</p>
        </div>
      </div>
    );
  }

  if (verified) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center py-12 px-4">
        <div className="max-w-md w-full text-center bg-white rounded-lg shadow-lg p-8">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Email Verified!</h2>
          <p className="text-gray-600 mb-6">
            Your email has been successfully verified. You now have full access to all features.
          </p>
          <button
            onClick={onSuccess}
            className="w-full bg-imax-orange text-white py-2 px-4 rounded-md hover:bg-imax-orange-dark"
          >
            Go to Login
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center py-12 px-4">
      <div className="max-w-md w-full text-center bg-white rounded-lg shadow-lg p-8">
        <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </div>
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Verification Failed</h2>
        <p className="text-gray-600 mb-6">{error}</p>
        <button
          onClick={onBackToLogin}
          className="w-full bg-imax-orange text-white py-2 px-4 rounded-md hover:bg-imax-orange-dark"
        >
          Back to Login
        </button>
      </div>
    </div>
  );
}

// Vendor Dashboard Component
function VendorDashboard({ 
  vendor, products, services, properties, events, giftcards, orders,
  onFetchProducts, onFetchServices, onFetchProperties, onFetchEvents, onFetchGiftcards, onFetchOrders,
  onCreateProduct, onUpdateProduct, onDeleteProduct,
  onCreateService, onUpdateService, onDeleteService,
  onCreateProperty, onUpdateProperty, onDeleteProperty,
  onCreateEvent, onUpdateEvent, onDeleteEvent,
  onCreateGiftcard, onUpdateGiftcard, onDeleteGiftcard,
  onUpdateProfile, getImageUrl, backendUrl, token, onLogout
}) {
  const toast = useToast();
  const vendorType = vendor?.vendor_type || 'food';
  // Custom categories the vendor has added for their own products, on top
  // of the platform's built-in ones for their vendor_type.
  const [customCategories, setCustomCategories] = useState(vendor?.custom_categories || []);

  const handleAddCustomCategory = async (name) => {
    try {
      const response = await fetch(`${backendUrl}/api/vendor/custom-categories`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ name })
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.detail || 'Unable to add category');
      setCustomCategories(data.custom_categories || []);
      return true;
    } catch (error) {
      toast.error(error.message);
      return false;
    }
  };

  const handleDeleteCustomCategory = async (name) => {
    try {
      const response = await fetch(`${backendUrl}/api/vendor/custom-categories/${encodeURIComponent(name)}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.detail || 'Unable to remove category');
      setCustomCategories(data.custom_categories || []);
    } catch (error) {
      toast.error(error.message);
    }
  };
  
  // Set default tab based on vendor type
  const getDefaultTab = () => {
    switch(vendorType) {
      case 'services': return 'services';
      case 'property': return 'properties';
      case 'events': return 'events';
      case 'giftcards': return 'giftcards';
      default: return 'products';
    }
  };
  
  const [activeTab, setActiveTab] = useState(getDefaultTab());
  const [registrationComplete, setRegistrationComplete] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editingItem, setEditingItem] = useState(null);

  useEffect(() => {
    // Fetch registration completion status for the approved vendor.
    fetch(`${backendUrl}/api/vendor/registration-completion`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.ok ? r.json() : null)
      .then(data => { if (data) setRegistrationComplete(Boolean(data.complete)); })
      .catch(() => {});

    // Fetch data based on vendor type
    switch(vendorType) {
      case 'services':
        onFetchServices();
        break;
      case 'property':
        onFetchProperties();
        break;
      case 'events':
        onFetchEvents();
        break;
      case 'giftcards':
        onFetchGiftcards();
        break;
      default:
        onFetchProducts();
    }
    onFetchOrders();
  }, [vendorType]);

  // Get the appropriate label and data based on vendor type
  const getVendorTypeConfig = () => {
    switch(vendorType) {
      case 'services':
        return { label: 'Services', icon: '', items: services || [], color: 'green' };
      case 'property':
        return { label: 'Properties', icon: '', items: properties || [], color: 'purple' };
      case 'events':
        return { label: 'Events', icon: '', items: events || [], color: 'pink' };
      case 'giftcards':
        return { label: 'Gift Cards', icon: '', items: giftcards || [], color: 'yellow' };
      default:
        return { label: 'Products', icon: '', items: products || [], color: 'orange' };
    }
  };

  const config = getVendorTypeConfig();

  return (
    <div className="vendor-dashboard-shell min-h-screen bg-gray-50 flex flex-col">
      <PortalHeader role="Seller Dashboard" name={vendor?.name} onHome={() => { window.location.hash = ''; window.location.href = window.location.pathname; }} onLogout={onLogout} />
      <main className="flex-1">
        <div className="max-w-7xl mx-auto px-4 py-8">
          {/* Dashboard Header */}
        <div className="bg-white rounded-lg shadow p-6 mb-8">
          <div className="flex items-center gap-3 mb-2">
            <i className={`fa-solid ${config.icon || 'fa-store'} text-3xl`} aria-hidden="true" />
            <h1 className="text-3xl font-bold text-gray-900">{vendor.name}</h1>
            <span className={`px-3 py-1 rounded-full text-xs font-semibold bg-${config.color}-100 text-${config.color}-700`}>
              {vendorType.charAt(0).toUpperCase() + vendorType.slice(1)}
            </span>
          </div>
          <p className="text-gray-600">{vendor.description}</p>
          <div className="mt-4 flex flex-wrap gap-4 text-sm text-gray-500">
            <span><i className="fa-solid fa-location-dot mr-1" aria-hidden="true" />{vendor.location}</span>
            <span><i className="fa-solid fa-phone mr-1" aria-hidden="true" />{vendor.phone}</span>
            <span><i className="fa-solid fa-tags mr-1" aria-hidden="true" />{vendor.category}</span>
            {Number(vendor.rating) > 0 && <span><UiIcon name="star" size={15} className="text-amber-500" /> {Number(vendor.rating).toFixed(1)}/5</span>}
          </div>
        </div>

        {/* Navigation Tabs */}
        <div className="bg-white rounded-lg shadow mb-8">
          <nav className="flex space-x-8 px-6 overflow-x-auto">
            <button
              onClick={() => setActiveTab(getDefaultTab())}
              className={`py-4 px-2 border-b-2 font-medium text-sm whitespace-nowrap ${
                activeTab === getDefaultTab()
                  ? 'border-purple-600 text-purple-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              <i className={`fa-solid ${config.icon || 'fa-store'} mr-2`} aria-hidden="true" /> {config.label} ({config.items.length})
            </button>
            <button
              onClick={() => setActiveTab('orders')}
              className={`py-4 px-2 border-b-2 font-medium text-sm whitespace-nowrap ${
                activeTab === 'orders'
                  ? 'border-purple-600 text-purple-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
               Orders ({orders.length})
            </button>
            <button
              onClick={() => setActiveTab('profile')}
              className={`py-4 px-2 border-b-2 font-medium text-sm whitespace-nowrap ${
                activeTab === 'profile'
                  ? 'border-purple-600 text-purple-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
               Profile
            </button>
            <button
              onClick={() => setActiveTab('registration')}
              className={`py-4 px-2 border-b-2 font-medium text-sm whitespace-nowrap ${
                activeTab === 'registration'
                  ? 'border-purple-600 text-purple-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              {registrationComplete ? 'Registration' : 'Complete registration'}
            </button>
            <button
              onClick={() => setActiveTab('payment')}
              className={`py-4 px-2 border-b-2 font-medium text-sm whitespace-nowrap ${
                activeTab === 'payment'
                  ? 'border-purple-600 text-purple-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              Payment & Settlement
            </button>
          </nav>
        </div>

        {/* Products/Food Tab */}
        {activeTab === 'products' && (vendorType === 'food' || vendorType === 'products') && (
          <ProductsTab
            products={products}
            showForm={showForm}
            editingProduct={editingItem}
            onShowForm={() => setShowForm(true)}
            onHideForm={() => { setShowForm(false); setEditingItem(null); }}
            onEditProduct={(item) => { setEditingItem(item); setShowForm(true); }}
            onCreateProduct={onCreateProduct}
            onUpdateProduct={onUpdateProduct}
            onDeleteProduct={onDeleteProduct}
            getImageUrl={getImageUrl}
            backendUrl={backendUrl}
            vendorType={vendorType}
            customCategories={customCategories}
            onAddCustomCategory={handleAddCustomCategory}
            onDeleteCustomCategory={handleDeleteCustomCategory}
          />
        )}

        {/* Services Tab */}
        {activeTab === 'services' && vendorType === 'services' && (
          <ServicesTab
            services={services}
            showForm={showForm}
            editingService={editingItem}
            onShowForm={() => setShowForm(true)}
            onHideForm={() => { setShowForm(false); setEditingItem(null); }}
            onEditService={(item) => { setEditingItem(item); setShowForm(true); }}
            onCreateService={onCreateService}
            onUpdateService={onUpdateService}
            onDeleteService={onDeleteService}
            getImageUrl={getImageUrl}
            backendUrl={backendUrl}
            toast={toast}
          />
        )}

        {/* Properties Tab */}
        {activeTab === 'properties' && vendorType === 'property' && (
          <PropertiesTab
            properties={properties}
            showForm={showForm}
            editingProperty={editingItem}
            onShowForm={() => setShowForm(true)}
            onHideForm={() => { setShowForm(false); setEditingItem(null); }}
            onEditProperty={(item) => { setEditingItem(item); setShowForm(true); }}
            onCreateProperty={onCreateProperty}
            onUpdateProperty={onUpdateProperty}
            onDeleteProperty={onDeleteProperty}
            getImageUrl={getImageUrl}
            backendUrl={backendUrl}
            toast={toast}
          />
        )}

        {/* Events Tab */}
        {activeTab === 'events' && vendorType === 'events' && (
          <EventsTab
            events={events}
            showForm={showForm}
            editingEvent={editingItem}
            onShowForm={() => setShowForm(true)}
            onHideForm={() => { setShowForm(false); setEditingItem(null); }}
            onEditEvent={(item) => { setEditingItem(item); setShowForm(true); }}
            onCreateEvent={onCreateEvent}
            onUpdateEvent={onUpdateEvent}
            onDeleteEvent={onDeleteEvent}
            getImageUrl={getImageUrl}
            backendUrl={backendUrl}
            toast={toast}
          />
        )}

        {/* Gift Cards Tab */}
        {activeTab === 'giftcards' && vendorType === 'giftcards' && (
          <GiftcardsTab
            giftcards={giftcards}
            showForm={showForm}
            editingGiftcard={editingItem}
            onShowForm={() => setShowForm(true)}
            onHideForm={() => { setShowForm(false); setEditingItem(null); }}
            onEditGiftcard={(item) => { setEditingItem(item); setShowForm(true); }}
            onCreateGiftcard={onCreateGiftcard}
            onUpdateGiftcard={onUpdateGiftcard}
            onDeleteGiftcard={onDeleteGiftcard}
            getImageUrl={getImageUrl}
            backendUrl={backendUrl}
            toast={toast}
          />
        )}

        {/* Orders Tab */}
        {activeTab === 'orders' && (
          <OrdersTab orders={orders} />
        )}

        {/* Profile Tab */}
        {activeTab === 'profile' && (
          <ProfileTab vendor={vendor} onUpdateProfile={onUpdateProfile} backendUrl={backendUrl} token={token} />
        )}

          {activeTab === 'registration' && (
            <VendorRegistrationCompletion backendUrl={backendUrl} token={token} vendor={vendor} />
          )}

          {activeTab === 'payment' && (
            <Suspense fallback={<div className="py-3 text-sm text-gray-500">Loading payment settings…</div>}>
              <PaymentAccountSetup backendUrl={backendUrl} token={token} ownerType="vendor" />
            </Suspense>
          )}
        </div>
      </main>
      <PortalFooter role="seller" />
    </div>
  );
}

// Products Tab Component
function ProductsTab({ products, showForm, editingProduct, onShowForm, onHideForm, onEditProduct, onCreateProduct, onUpdateProduct, onDeleteProduct, getImageUrl, backendUrl, vendorType, customCategories, onAddCustomCategory, onDeleteCustomCategory }) {
  const [query, setQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  const filteredProducts = (products || [])
    .filter((product) => {
      if (statusFilter === 'available') return product.available !== false;
      if (statusFilter === 'hidden') return product.available === false;
      return true;
    })
    .filter((product) => {
      const q = query.trim().toLowerCase();
      if (!q) return true;
      return [product.name, product.category, product.item_code]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(q));
    })
    .slice()
    .sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0));

  const visibleCount = (products || []).filter((p) => p.available !== false).length;
  const hiddenCount = (products || []).filter((p) => p.available === false).length;

  return (
    <div>
      {!showForm ? (
        <div>
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-5">
            <div>
              <h2 className="text-2xl font-bold text-gray-900">Your Products</h2>
              <p className="text-sm text-gray-500 mt-1">Keep your catalog accurate and easy to manage.</p>
            </div>
            <button
              onClick={onShowForm}
              className="bg-imax-orange text-white px-4 py-2 rounded-lg hover:bg-imax-orange-dark"
            >
              Add New Product
            </button>
          </div>

          <div className="grid grid-cols-3 gap-2 mb-5">
            <div className="bg-white rounded-lg border p-3"><p className="text-xs text-gray-500">Total</p><p className="text-xl font-bold">{products?.length || 0}</p></div>
            <div className="bg-white rounded-lg border p-3"><p className="text-xs text-gray-500">Visible</p><p className="text-xl font-bold text-green-600">{visibleCount}</p></div>
            <div className="bg-white rounded-lg border p-3"><p className="text-xs text-gray-500">Hidden</p><p className="text-xl font-bold text-gray-600">{hiddenCount}</p></div>
          </div>

          {products.length > 0 && (
            <div className="bg-white rounded-lg border p-3 mb-5">
              <div className="flex flex-col sm:flex-row gap-2">
                <input
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Search products, categories or SKU"
                  aria-label="Search products"
                  className="flex-1 px-3 py-2.5 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-200"
                />
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="px-3 py-2.5 border border-gray-200 rounded-lg bg-white"
                  aria-label="Filter products by visibility"
                >
                  <option value="all">All products</option>
                  <option value="available">Visible</option>
                  <option value="hidden">Hidden</option>
                </select>
              </div>
            </div>
          )}

          {products.length === 0 ? (
            <div className="bg-white rounded-lg shadow p-8 text-center">
              <p className="text-gray-500 mb-4">No products yet</p>
              <button
                onClick={onShowForm}
                className="bg-imax-orange text-white px-6 py-2 rounded-lg hover:bg-imax-orange-dark"
              >
                Add Your First Product
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
              {filteredProducts.length > 0 ? filteredProducts.map(product => (
                <VendorManagedProductCard
                  key={product.id}
                  product={product}
                  onEdit={() => onEditProduct(product)}
                  onDelete={() => onDeleteProduct(product.id)}
                  getImageUrl={getImageUrl}
                />
              )) : (
                <div className="md:col-span-2 lg:col-span-3 bg-white rounded-lg border p-8 text-center">
                  <h3 className="font-semibold text-gray-900">No matching products</h3>
                  <p className="text-sm text-gray-500 mt-1">Try a different search or visibility filter.</p>
                  <button onClick={() => { setQuery(''); setStatusFilter('all'); }} className="mt-3 text-cyan-700 font-semibold">Clear filters</button>
                </div>
              )}
            </div>
          )}
        </div>
      ) : (
        <ProductForm
          product={editingProduct}
          products={products}
          onSave={editingProduct ? onUpdateProduct : onCreateProduct}
          onCancel={onHideForm}
          backendUrl={backendUrl}
          token={token}
          vendorType={vendorType}
          customCategories={customCategories}
          onAddCustomCategory={onAddCustomCategory}
          onDeleteCustomCategory={onDeleteCustomCategory}
        />
      )}
    </div>
  );
}


// Vendor-managed catalog card. Kept separate from the customer ProductCard so
// seller actions (Edit/Delete) are always available for existing products.
function VendorManagedProductCard({ product, onEdit, onDelete, getImageUrl }) {
  const image = getImageUrl ? getImageUrl(product?.image_url) : product?.image_url;
  const available = product?.available !== false;
  const status = product?.approval_status || 'pending_admin_approval';
  const statusLabel = status === 'approved' ? 'Approved' : status === 'rejected' ? 'Needs changes' : 'Pending review';
  return (
    <article className="bg-white rounded-2xl border border-gray-200 overflow-hidden shadow-sm">
      <div className="relative aspect-[4/3] bg-gray-100">
        {image ? (
          <img src={image} alt={product?.name || 'Product'} className="w-full h-full object-cover" loading="lazy" />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-gray-400"><i className="fa-solid fa-box text-3xl" /></div>
        )}
        <span className={`absolute top-2 left-2 text-[10px] font-bold px-2 py-1 rounded-full ${available ? 'bg-green-600 text-white' : 'bg-gray-800 text-white'}`}>
          {available ? 'Visible' : 'Hidden'}
        </span>
        <span className={`absolute top-2 right-2 text-[10px] font-bold px-2 py-1 rounded-full ${status === 'approved' ? 'bg-white text-green-700' : status === 'rejected' ? 'bg-white text-red-700' : 'bg-white text-amber-700'}`}>
          {statusLabel}
        </span>
      </div>
      <div className="p-4">
        <h3 className="font-bold text-gray-900 line-clamp-2">{product?.name || 'Unnamed product'}</h3>
        <p className="text-xs text-gray-500 mt-1 line-clamp-1">{product?.category || 'No category'}</p>
        <p className="text-lg font-extrabold text-cyan-700 mt-2">₦{Number(product?.price || 0).toLocaleString('en-NG')}</p>
        <div className="mt-3 flex gap-2">
          <button type="button" onClick={onEdit} className="flex-1 px-3 py-2.5 rounded-lg bg-cyan-500 text-white text-sm font-bold hover:bg-cyan-700">
            <i className="fa-solid fa-pen-to-square mr-1.5" />Edit
          </button>
          <button type="button" onClick={onDelete} className="px-3 py-2.5 rounded-lg border border-red-200 text-red-600 text-sm font-bold hover:bg-red-50" aria-label={`Delete ${product?.name || 'product'}`}>
            <i className="fa-solid fa-trash-can" />
          </button>
        </div>
      </div>
    </article>
  );
}

// Product Form Component
function ProductForm({ product, products = [], onSave, onCancel, backendUrl, vendorType, token, customCategories = [], onAddCustomCategory, onDeleteCustomCategory }) {
  const [formData, setFormData] = useState({
    name: product?.name || '',
    description: product?.description || '',
    price: product?.price || '',
    sale_price: product?.sale_price || '',
    image_url: product?.image_url || '',
    image_urls: product?.image_urls || (product?.image_url ? [product.image_url] : []),
    category: product?.category || '',
    item_code: product?.item_code || '',
    available: product?.available ?? true,
    short_description: product?.short_description || product?.description || '',
    class_options: Array.isArray(product?.class_options) ? product.class_options : [],
    linked_product_ids: Array.isArray(product?.linked_product_ids) ? product.linked_product_ids : [],
    full_description_blocks: Array.isArray(product?.full_description_blocks) ? product.full_description_blocks : [],
    show_reviews: product?.show_reviews !== false
  });
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [newCategoryInput, setNewCategoryInput] = useState('');
  const [addingCategory, setAddingCategory] = useState(false);
  const [error, setError] = useState('');
  const initialImages = product?.image_urls?.length ? product.image_urls : (product?.image_url ? [product.image_url] : []);
  const [imageUrls, setImageUrls] = useState(initialImages);

  const removeImage = (indexToRemove) => {
    const next = imageUrls.filter((_, i) => i !== indexToRemove);
    setImageUrls(next);
    setFormData(prev => ({ ...prev, image_url: next[0] || '', image_urls: next }));
  };

  const handleImageUpload = async (event) => {
    const files = Array.from(event.target.files || []);
    if (!files.length) return;

    const allowedTypes = ['image/jpeg', 'image/png', 'image/webp'];
    const validFiles = files.filter((file) => allowedTypes.includes(file.type) && file.size <= 5 * 1024 * 1024);
    if (!validFiles.length) { setError('Select JPEG, PNG, or WebP images under 5MB each.'); return; }
    setUploading(true);
    setError('');
    try {
      const uploaded = [];
      for (const file of validFiles.slice(0, 8)) {
        const body = new FormData();
        body.append('file', file);
        const response = await fetch(`${backendUrl}/api/upload-image`, { method: 'POST', headers: token ? { Authorization: `Bearer ${token}` } : {}, body });
        if (!response.ok) { const data = await response.json().catch(() => ({})); throw new Error(data.detail || 'Upload failed'); }
        const data = await response.json();
        if (data.image_url) uploaded.push(data.image_url);
      }
      const next = [...imageUrls, ...uploaded].slice(0, 8);
      setImageUrls(next);
      setFormData(prev => ({ ...prev, image_url: next[0] || '', image_urls: next }));
    } catch (error) {
      setError(`Upload failed: ${error.message}`);
    } finally {
      setUploading(false);
    }
  };

  const validateForm = () => {
    const name = formData.name.trim();
    const price = Number(formData.price);
    const salePrice = formData.sale_price === '' ? null : Number(formData.sale_price);

    if (name.length < 2) return 'Enter a product name with at least 2 characters.';
    if (!Number.isFinite(price) || price <= 0) return 'Enter a valid price greater than ₦0.';
    if (salePrice !== null && (!Number.isFinite(salePrice) || salePrice <= 0)) return 'Enter a valid sale price.';
    if (salePrice !== null && salePrice >= price) return 'Sale price must be lower than the regular price.';
    if (!formData.category) return 'Select a product category.';
    if (!imageUrls.length) return 'Please upload at least one product image.';
    return '';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (loading || uploading) return;

    const validationError = validateForm();
    if (validationError) {
      setError(validationError);
      return;
    }

    setLoading(true);
    setError('');

    const payload = {
      ...formData,
      name: formData.name.trim(),
      description: formData.description.trim(),
      short_description: formData.short_description.trim(),
      category: formData.category.trim(),
      item_code: formData.item_code.trim() || null,
      image_urls: imageUrls,
      price: Number(formData.price),
      sale_price: formData.sale_price === '' ? null : Number(formData.sale_price),
      class_options: (formData.class_options || []).map(item => ({ name: String(item.name || '').trim(), options: (item.options || []).map(v => String(v).trim()).filter(Boolean) })).filter(item => item.name && item.options.length),
      linked_product_ids: Array.from(new Set((formData.linked_product_ids || []).filter(Boolean))),
      full_description_blocks: (formData.full_description_blocks || []).filter(block => block && (block.type === 'image' ? block.url : String(block.text || '').trim())),
      show_reviews: formData.show_reviews !== false
    };

    try {
      const result = product
        ? await onSave(product.id, payload)
        : await onSave(payload);

      if (result?.success) {
        onCancel();
      } else {
        setError(result?.error || 'Unable to save product. Please try again.');
      }
    } catch (saveError) {
      setError(saveError.message || 'Unable to save product. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-gray-50 min-h-full">
      {/* Header - WhatsApp Style */}
      <div className="bg-teal-600 text-white px-4 py-3 flex items-center justify-between sticky top-0 z-10">
        <div className="flex items-center gap-3">
          <button onClick={onCancel} className="p-1 hover:bg-teal-700 rounded-full">
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
          <h2 className="text-lg font-medium">
            {product ? 'Edit item' : 'Add item'}
          </h2>
        </div>
        <button
          onClick={handleSubmit}
          disabled={loading || uploading}
          className="text-white font-medium disabled:opacity-50"
        >
          {uploading ? 'Uploading...' : loading ? 'Saving...' : 'Save'}
        </button>
      </div>

      {/* Error Message */}
      {error && (
        <div className="mx-4 mt-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
          {error}
        </div>
      )}

      {/* Image Upload Section - all uploaded images shown as a removable row,
          not just a single preview, so vendors can see and manage every
          photo they've added (up to 8) instead of only ever seeing the
          first one. */}
      <div className="p-4">
        <div className="flex gap-3 overflow-x-auto pb-1">
          {imageUrls.map((url, index) => (
            <div key={`${url}-${index}`} className="relative flex-shrink-0 w-24 h-24 rounded-xl overflow-hidden border border-gray-200">
              <img src={url} alt={`Product ${index + 1}`} className="w-full h-full object-cover" />
              {index === 0 && (
                <span className="absolute bottom-0 left-0 right-0 bg-black/60 text-white text-[10px] text-center py-0.5">Main</span>
              )}
              <button
                type="button"
                onClick={() => removeImage(index)}
                aria-label={`Remove image ${index + 1}`}
                className="absolute top-1 right-1 w-5 h-5 rounded-full bg-black/70 text-white flex items-center justify-center text-xs leading-none"
              >
                &times;
              </button>
            </div>
          ))}

          {imageUrls.length < 8 && (
            <label className={`cursor-pointer flex-shrink-0 w-24 h-24 rounded-xl border-2 border-dashed flex flex-col items-center justify-center text-gray-400 ${
              uploading ? 'border-gray-300 bg-gray-100' : 'border-gray-300 bg-white'
            }`}>
              <input
                type="file"
                accept="image/jpeg,image/png,image/webp"
                multiple
                onChange={handleImageUpload}
                className="hidden"
                disabled={uploading}
              />
              {uploading ? (
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-teal-600"></div>
              ) : (
                <>
                  <svg className="w-6 h-6 mb-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                  </svg>
                  <span className="text-[10px]">Add</span>
                </>
              )}
            </label>
          )}
        </div>
        <p className="text-xs text-gray-500 mt-2">
          {imageUrls.length > 0
            ? `${imageUrls.length}/8 photos added. The first photo is used as the main image.`
            : 'Add up to 8 photos. JPEG, PNG, or WebP, under 5MB each.'}
        </p>
      </div>

      {/* Form Fields - Stacked Vertically */}
      <div className="bg-white mx-4 rounded-xl shadow-sm overflow-hidden">
        {/* Item Name */}
        <div className="px-4 py-3 border-b border-gray-100">
          <label className="block text-xs text-gray-500 mb-1">Item name *</label>
          <input
            type="text"
            required
            placeholder="Enter item name"
            value={formData.name}
            onChange={(e) => setFormData({...formData, name: e.target.value})}
            className="w-full text-base text-gray-900 placeholder-gray-400 focus:outline-none"
          />
        </div>

        {/* Price */}
        <div className="px-4 py-3 border-b border-gray-100">
          <label className="block text-xs text-gray-500 mb-1">Price *</label>
          <div className="flex items-center">
            <span className="text-gray-500 mr-1">₦</span>
            <input
              type="number"
              step="0.01"
              min="0"
              required
              placeholder="0.00"
              value={formData.price}
              onChange={(e) => setFormData({...formData, price: e.target.value})}
              className="flex-1 text-base text-gray-900 placeholder-gray-400 focus:outline-none"
            />
          </div>
        </div>

        {/* Sale Price */}
        <div className="px-4 py-3 border-b border-gray-100">
          <label className="block text-xs text-gray-500 mb-1">Sale price (optional)</label>
          <div className="flex items-center">
            <span className="text-gray-500 mr-1">₦</span>
            <input
              type="number"
              step="0.01"
              min="0"
              placeholder="0.00"
              value={formData.sale_price}
              onChange={(e) => setFormData({...formData, sale_price: e.target.value})}
              className="flex-1 text-base text-gray-900 placeholder-gray-400 focus:outline-none"
            />
          </div>
        </div>

        {/* Description */}
        <div className="px-4 py-3 border-b border-gray-100">
          <label className="block text-xs text-gray-500 mb-1">Description</label>
          <textarea
            placeholder="Add item description"
            value={formData.description}
            onChange={(e) => setFormData({...formData, description: e.target.value})}
            className="w-full text-base text-gray-900 placeholder-gray-400 focus:outline-none resize-none"
            rows="2"
          />
        </div>

        {/* Short description shown directly on the Single Product Page */}
        <div className="px-4 py-3 border-b border-gray-100">
          <label className="block text-xs text-gray-500 mb-1">Short description (optional)</label>
          <textarea
            placeholder="A concise product summary customers see near the price"
            value={formData.short_description}
            onChange={(e) => setFormData({...formData, short_description: e.target.value})}
            className="w-full text-base text-gray-900 placeholder-gray-400 focus:outline-none resize-none"
            rows="3"
            maxLength={500}
          />
          <p className="text-[11px] text-gray-400 mt-1">This is separate from the optional full description below.</p>
        </div>

        {/* Category -- scoped to this vendor's own business type, instead
            of exposing every category from every vendor type on the
            platform (a food vendor no longer sees "Furniture", a products
            vendor no longer sees "Main Course", etc). Vendors can also add
            their own categories below, specific to their vendor. */}
        <div className="px-4 py-3 border-b border-gray-100">
          <label className="block text-xs text-gray-500 mb-1">Category *</label>
          <select
            required
            value={formData.category}
            onChange={(e) => setFormData({...formData, category: e.target.value})}
            className="w-full text-base text-gray-900 focus:outline-none bg-transparent"
          >
            <option value="">Select category</option>
            {vendorType === 'food' ? (
              <optgroup label="Food & Drinks">
                <option value="Main Course">Main Course</option>
                <option value="Appetizer">Appetizer</option>
                <option value="Dessert">Dessert</option>
                <option value="Beverage">Beverage</option>
                <option value="Side Dish">Side Dish</option>
                <option value="Snacks">Snacks</option>
                <option value="Breakfast">Breakfast</option>
                <option value="Lunch">Lunch</option>
                <option value="Dinner">Dinner</option>
              </optgroup>
            ) : (
              <optgroup label="General">
                <option value="Electronics">Electronics</option>
                <option value="Clothing">Clothing</option>
                <option value="Accessories">Accessories</option>
                <option value="Home & Garden">Home & Garden</option>
                <option value="Beauty & Health">Beauty & Health</option>
                <option value="Sports & Outdoors">Sports & Outdoors</option>
                <option value="Books & Stationery">Books & Stationery</option>
                <option value="Toys & Games">Toys & Games</option>
                <option value="Groceries">Groceries</option>
                <option value="Furniture">Furniture</option>
              </optgroup>
            )}
            {customCategories.length > 0 && (
              <optgroup label="My Categories">
                {customCategories.map((cat) => (
                  <option key={cat} value={cat}>{cat}</option>
                ))}
              </optgroup>
            )}
            <optgroup label="Other">
              <option value="Services">Services</option>
              <option value="Digital Products">Digital Products</option>
              <option value="Other">Other</option>
            </optgroup>
          </select>

          {/* Add a category specific to this vendor */}
          {onAddCustomCategory && (
            <div className="mt-2 flex gap-2">
              <input
                type="text"
                value={newCategoryInput}
                onChange={(e) => setNewCategoryInput(e.target.value)}
                placeholder="Add your own category..."
                maxLength={40}
                className="flex-1 text-sm border border-gray-200 rounded-lg px-3 py-1.5 focus:outline-none focus:border-teal-500"
              />
              <button
                type="button"
                disabled={!newCategoryInput.trim() || addingCategory}
                onClick={async () => {
                  setAddingCategory(true);
                  const ok = await onAddCustomCategory(newCategoryInput.trim());
                  if (ok) {
                    setFormData(prev => ({ ...prev, category: newCategoryInput.trim() }));
                    setNewCategoryInput('');
                  }
                  setAddingCategory(false);
                }}
                className="px-3 py-1.5 text-sm bg-teal-600 text-white rounded-lg disabled:opacity-50"
              >
                {addingCategory ? 'Adding...' : 'Add'}
              </button>
            </div>
          )}
          {customCategories.length > 0 && onDeleteCustomCategory && (
            <div className="mt-2 flex flex-wrap gap-1.5">
              {customCategories.map((cat) => (
                <span key={cat} className="inline-flex items-center gap-1 text-xs bg-gray-100 text-gray-600 rounded-full px-2.5 py-1">
                  {cat}
                  <button
                    type="button"
                    onClick={() => onDeleteCustomCategory(cat)}
                    aria-label={`Remove category ${cat}`}
                    className="text-gray-400 hover:text-red-500"
                  >
                    &times;
                  </button>
                </span>
              ))}
            </div>
          )}
        </div>

        {/* Item Code */}
        <div className="px-4 py-3">
          <label className="block text-xs text-gray-500 mb-1">Item code (optional)</label>
          <input
            type="text"
            placeholder="e.g., SKU-12345"
            value={formData.item_code}
            onChange={(e) => setFormData({...formData, item_code: e.target.value})}
            className="w-full text-base text-gray-900 placeholder-gray-400 focus:outline-none"
          />
        </div>
      </div>

      <div className="mx-4 mt-4">
        <Suspense fallback={<div className="py-3 text-sm text-gray-500">Loading product options…</div>}>
          <ProductAdvancedFields
            token={token}
            formData={formData}
            setFormData={setFormData}
            products={products}
            currentProductId={product?.id || null}
            backendUrl={backendUrl}
          />
        </Suspense>
      </div>

      {/* Hide Item Toggle */}
      <div className="bg-white mx-4 mt-4 rounded-xl shadow-sm overflow-hidden">
        <div className="px-4 py-3 flex items-center justify-between">
          <div>
            <p className="text-base text-gray-900">Hide this item</p>
            <p className="text-xs text-gray-500">Item won't be visible to customers</p>
          </div>
          <label className="relative inline-flex items-center cursor-pointer">
            <input
              type="checkbox"
              checked={!formData.available}
              onChange={(e) => setFormData({...formData, available: !e.target.checked})}
              className="sr-only peer"
            />
            <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-teal-600"></div>
          </label>
        </div>
      </div>

      {/* Bottom Padding */}
      <div className="h-8"></div>
    </div>
  );
}

// ============ SERVICES TAB ============
function ServicesTab({ services, showForm, editingService, onShowForm, onHideForm, onEditService, onCreateService, onUpdateService, onDeleteService, getImageUrl, backendUrl, toast }) {
  const [formData, setFormData] = useState({
    name: '', description: '', price: '', duration: '60', category: '', image_url: ''
  });

  useEffect(() => {
    if (editingService) {
      setFormData({
        name: editingService.name || '',
        description: editingService.description || '',
        price: editingService.price || '',
        duration: editingService.duration || '60',
        category: editingService.category || '',
        image_url: editingService.image_url || ''
      });
    } else {
      setFormData({ name: '', description: '', price: '', duration: '60', category: '', image_url: '' });
    }
  }, [editingService]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const result = editingService 
      ? await onUpdateService(editingService.id, formData)
      : await onCreateService(formData);
    if (result.success) {
      toast.success(editingService ? 'Service updated successfully!' : 'Service created successfully!');
      onHideForm();
    } else {
      toast.error(result.error);
    }
  };

  return (
    <div>
      {!showForm ? (
        <div>
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-2xl font-bold text-gray-900"> Your Services</h2>
            <button onClick={onShowForm} className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700">
              + Add Service
            </button>
          </div>
          {services.length === 0 ? (
            <div className="bg-white rounded-lg shadow p-8 text-center">
              <p className="text-gray-500 mb-4">No services yet. Add your first service!</p>
            </div>
          ) : (
            <div className="grid gap-4">
              {services
                .sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0))
                .map(service => (
                <div key={service.id} className="bg-white rounded-lg shadow p-4 flex justify-between items-center">
                  <div>
                    <h3 className="font-semibold">{service.name}</h3>
                    <p className="text-sm text-gray-500">{service.description}</p>
                    <p className="text-green-600 font-bold">₦{Number(service.price).toLocaleString()} ₦ {service.duration} mins</p>
                  </div>
                  <div className="flex gap-2">
                    <button onClick={() => onEditService(service)} className="text-blue-600 hover:text-blue-800">Edit</button>
                    <button onClick={() => onDeleteService(service.id)} className="text-red-600 hover:text-red-800">Delete</button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-xl font-bold mb-4">{editingService ? 'Edit Service' : 'Add New Service'}</h2>
          <form onSubmit={handleSubmit} className="space-y-4">
            <input type="text" required placeholder="Service Name" value={formData.name} onChange={(e) => setFormData({...formData, name: e.target.value})} className="w-full px-3 py-2 border rounded-lg" />
            <textarea placeholder="Description" value={formData.description} onChange={(e) => setFormData({...formData, description: e.target.value})} className="w-full px-3 py-2 border rounded-lg" rows="3" />
            <div className="grid grid-cols-2 gap-4">
              <input type="number" required placeholder="Price (?)" value={formData.price} onChange={(e) => setFormData({...formData, price: e.target.value})} className="w-full px-3 py-2 border rounded-lg" />
              <input type="number" placeholder="Duration (mins)" value={formData.duration} onChange={(e) => setFormData({...formData, duration: e.target.value})} className="w-full px-3 py-2 border rounded-lg" />
            </div>
            <input type="text" placeholder="Category (e.g., Plumbing, Cleaning)" value={formData.category} onChange={(e) => setFormData({...formData, category: e.target.value})} className="w-full px-3 py-2 border rounded-lg" />
            <div className="flex gap-4">
              <button type="button" onClick={onHideForm} className="px-4 py-2 border rounded-lg">Cancel</button>
              <button type="submit" className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700">{editingService ? 'Update' : 'Create'} Service</button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}

// ============ PROPERTIES TAB ============
function PropertiesTab({ properties, showForm, editingProperty, onShowForm, onHideForm, onEditProperty, onCreateProperty, onUpdateProperty, onDeleteProperty, getImageUrl, backendUrl, toast }) {
  const [formData, setFormData] = useState({
    title: '', description: '', price: '', property_type: 'rent', bedrooms: '', bathrooms: '', location: '', image_url: ''
  });

  useEffect(() => {
    if (editingProperty) {
      setFormData({
        title: editingProperty.title || '',
        description: editingProperty.description || '',
        price: editingProperty.price || '',
        property_type: editingProperty.property_type || 'rent',
        bedrooms: editingProperty.bedrooms || '',
        bathrooms: editingProperty.bathrooms || '',
        location: editingProperty.location || '',
        image_url: editingProperty.image_url || ''
      });
    } else {
      setFormData({ title: '', description: '', price: '', property_type: 'rent', bedrooms: '', bathrooms: '', location: '', image_url: '' });
    }
  }, [editingProperty]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const result = editingProperty 
      ? await onUpdateProperty(editingProperty.id, formData)
      : await onCreateProperty(formData);
    if (result.success) {
      toast.success(editingProperty ? 'Property updated successfully!' : 'Property created successfully!');
      onHideForm();
    } else {
      toast.error(result.error);
    }
  };

  return (
    <div>
      {!showForm ? (
        <div>
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-2xl font-bold text-gray-900"> Your Properties</h2>
            <button onClick={onShowForm} className="bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700">
              + Add Property
            </button>
          </div>
          {properties.length === 0 ? (
            <div className="bg-white rounded-lg shadow p-8 text-center">
              <p className="text-gray-500 mb-4">No properties listed yet.</p>
            </div>
          ) : (
            <div className="grid gap-4">
              {properties
                .sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0))
                .map(property => (
                <div key={property.id} className="bg-white rounded-lg shadow p-4">
                  <div className="flex justify-between">
                    <div>
                      <span className={`px-2 py-1 rounded text-xs font-semibold ${property.property_type === 'sale' ? 'bg-red-100 text-red-700' : 'bg-blue-100 text-blue-700'}`}>
                        For {property.property_type === 'sale' ? 'Sale' : 'Rent'}
                      </span>
                      <h3 className="font-semibold mt-2">{property.title}</h3>
                      <p className="text-sm text-gray-500">{property.location}</p>
                      <p className="text-sm"> {property.bedrooms} beds • {property.bathrooms} baths</p>
                      <p className="text-purple-600 font-bold text-lg">₦{Number(property.price).toLocaleString()}{property.property_type === 'rent' ? '/mo' : ''}</p>
                    </div>
                    <div className="flex flex-col gap-2">
                      <button onClick={() => onEditProperty(property)} className="text-blue-600 hover:text-blue-800">Edit</button>
                      <button onClick={() => onDeleteProperty(property.id)} className="text-red-600 hover:text-red-800">Delete</button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-xl font-bold mb-4">{editingProperty ? 'Edit Property' : 'Add New Property'}</h2>
          <form onSubmit={handleSubmit} className="space-y-4">
            <input type="text" required placeholder="Property Title" value={formData.title} onChange={(e) => setFormData({...formData, title: e.target.value})} className="w-full px-3 py-2 border rounded-lg" />
            <textarea placeholder="Description" value={formData.description} onChange={(e) => setFormData({...formData, description: e.target.value})} className="w-full px-3 py-2 border rounded-lg" rows="3" />
            <div className="grid grid-cols-2 gap-4">
              <select value={formData.property_type} onChange={(e) => setFormData({...formData, property_type: e.target.value})} className="w-full px-3 py-2 border rounded-lg">
                <option value="rent">For Rent</option>
                <option value="sale">For Sale</option>
              </select>
              <input type="number" required placeholder="Price (?)" value={formData.price} onChange={(e) => setFormData({...formData, price: e.target.value})} className="w-full px-3 py-2 border rounded-lg" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <input type="number" placeholder="Bedrooms" value={formData.bedrooms} onChange={(e) => setFormData({...formData, bedrooms: e.target.value})} className="w-full px-3 py-2 border rounded-lg" />
              <input type="number" placeholder="Bathrooms" value={formData.bathrooms} onChange={(e) => setFormData({...formData, bathrooms: e.target.value})} className="w-full px-3 py-2 border rounded-lg" />
            </div>
            <input type="text" placeholder="Location" value={formData.location} onChange={(e) => setFormData({...formData, location: e.target.value})} className="w-full px-3 py-2 border rounded-lg" />
            <div className="flex gap-4">
              <button type="button" onClick={onHideForm} className="px-4 py-2 border rounded-lg">Cancel</button>
              <button type="submit" className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700">{editingProperty ? 'Update' : 'Create'} Property</button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}

// ============ EVENTS TAB ============
function EventsTab({ events, showForm, editingEvent, onShowForm, onHideForm, onEditEvent, onCreateEvent, onUpdateEvent, onDeleteEvent, getImageUrl, backendUrl, toast }) {
  const [formData, setFormData] = useState({
    title: '', description: '', date: '', time: '', venue: '', ticket_price: '', total_tickets: '', image_url: ''
  });

  useEffect(() => {
    if (editingEvent) {
      setFormData({
        title: editingEvent.title || '',
        description: editingEvent.description || '',
        date: editingEvent.date || '',
        time: editingEvent.time || '',
        venue: editingEvent.venue || '',
        ticket_price: editingEvent.ticket_price || '',
        total_tickets: editingEvent.total_tickets || '',
        image_url: editingEvent.image_url || ''
      });
    } else {
      setFormData({ title: '', description: '', date: '', time: '', venue: '', ticket_price: '', total_tickets: '', image_url: '' });
    }
  }, [editingEvent]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const result = editingEvent 
      ? await onUpdateEvent(editingEvent.id, formData)
      : await onCreateEvent(formData);
    if (result.success) {
      toast.success(editingEvent ? 'Event updated successfully!' : 'Event created successfully!');
      onHideForm();
    } else {
      toast.error(result.error);
    }
  };

  return (
    <div>
      {!showForm ? (
        <div>
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-2xl font-bold text-gray-900"> Your Events</h2>
            <button onClick={onShowForm} className="bg-pink-600 text-white px-4 py-2 rounded-lg hover:bg-pink-700">
              + Add Event
            </button>
          </div>
          {events.length === 0 ? (
            <div className="bg-white rounded-lg shadow p-8 text-center">
              <p className="text-gray-500 mb-4">No events created yet.</p>
            </div>
          ) : (
            <div className="grid gap-4">
              {events
                .sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0))
                .map(event => (
                <div key={event.id} className="bg-white rounded-lg shadow p-4">
                  <div className="flex justify-between">
                    <div>
                      <h3 className="font-semibold text-lg">{event.title}</h3>
                      <p className="text-sm text-gray-500">{event.description}</p>
                      <div className="flex gap-4 mt-2 text-sm">
                        <span> {event.date}</span>
                        <span> {event.time}</span>
                        <span> {event.venue}</span>
                      </div>
                      <p className="text-pink-600 font-bold mt-1">₦{Number(event.ticket_price).toLocaleString()} ₦ {event.total_tickets} tickets</p>
                    </div>
                    <div className="flex flex-col gap-2">
                      <button onClick={() => onEditEvent(event)} className="text-blue-600 hover:text-blue-800">Edit</button>
                      <button onClick={() => onDeleteEvent(event.id)} className="text-red-600 hover:text-red-800">Delete</button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-xl font-bold mb-4">{editingEvent ? 'Edit Event' : 'Add New Event'}</h2>
          <form onSubmit={handleSubmit} className="space-y-4">
            <input type="text" required placeholder="Event Title" value={formData.title} onChange={(e) => setFormData({...formData, title: e.target.value})} className="w-full px-3 py-2 border rounded-lg" />
            <textarea placeholder="Description" value={formData.description} onChange={(e) => setFormData({...formData, description: e.target.value})} className="w-full px-3 py-2 border rounded-lg" rows="3" />
            <div className="grid grid-cols-2 gap-4">
              <input type="date" required value={formData.date} onChange={(e) => setFormData({...formData, date: e.target.value})} className="w-full px-3 py-2 border rounded-lg" />
              <input type="time" required value={formData.time} onChange={(e) => setFormData({...formData, time: e.target.value})} className="w-full px-3 py-2 border rounded-lg" />
            </div>
            <input type="text" placeholder="Venue" value={formData.venue} onChange={(e) => setFormData({...formData, venue: e.target.value})} className="w-full px-3 py-2 border rounded-lg" />
            <div className="grid grid-cols-2 gap-4">
              <input type="number" required placeholder="Ticket Price (?)" value={formData.ticket_price} onChange={(e) => setFormData({...formData, ticket_price: e.target.value})} className="w-full px-3 py-2 border rounded-lg" />
              <input type="number" placeholder="Total Tickets" value={formData.total_tickets} onChange={(e) => setFormData({...formData, total_tickets: e.target.value})} className="w-full px-3 py-2 border rounded-lg" />
            </div>
            <div className="flex gap-4">
              <button type="button" onClick={onHideForm} className="px-4 py-2 border rounded-lg">Cancel</button>
              <button type="submit" className="px-4 py-2 bg-pink-600 text-white rounded-lg hover:bg-pink-700">{editingEvent ? 'Update' : 'Create'} Event</button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}

// ============ GIFTCARDS TAB ============
function GiftcardsTab({ giftcards, showForm, editingGiftcard, onShowForm, onHideForm, onEditGiftcard, onCreateGiftcard, onUpdateGiftcard, onDeleteGiftcard, getImageUrl, backendUrl, toast }) {
  const [formData, setFormData] = useState({
    name: '', description: '', value: '', price: '', quantity: '', expiry_days: '365', image_url: ''
  });

  useEffect(() => {
    if (editingGiftcard) {
      setFormData({
        name: editingGiftcard.name || '',
        description: editingGiftcard.description || '',
        value: editingGiftcard.value || '',
        price: editingGiftcard.price || '',
        quantity: editingGiftcard.quantity || '',
        expiry_days: editingGiftcard.expiry_days || '365',
        image_url: editingGiftcard.image_url || ''
      });
    } else {
      setFormData({ name: '', description: '', value: '', price: '', quantity: '', expiry_days: '365', image_url: '' });
    }
  }, [editingGiftcard]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const result = editingGiftcard 
      ? await onUpdateGiftcard(editingGiftcard.id, formData)
      : await onCreateGiftcard(formData);
    if (result.success) {
      toast.success(editingGiftcard ? 'Gift card updated successfully!' : 'Gift card created successfully!');
      onHideForm();
    } else {
      toast.error(result.error);
    }
  };

  return (
    <div>
      {!showForm ? (
        <div>
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-2xl font-bold text-gray-900"> Your Gift Cards</h2>
            <button onClick={onShowForm} className="bg-yellow-500 text-white px-4 py-2 rounded-lg hover:bg-yellow-600">
              + Add Gift Card
            </button>
          </div>
          {giftcards.length === 0 ? (
            <div className="bg-white rounded-lg shadow p-8 text-center">
              <p className="text-gray-500 mb-4">No gift cards created yet.</p>
            </div>
          ) : (
            <div className="grid gap-4 md:grid-cols-2">
              {giftcards
                .sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0))
                .map(card => (
                <div key={card.id} className="bg-gradient-to-br from-yellow-100 to-cyan-100 rounded-lg shadow p-4 border border-yellow-200">
                  <div className="flex justify-between">
                    <div>
                      <h3 className="font-semibold text-lg">{card.name}</h3>
                      <p className="text-sm text-gray-600">{card.description}</p>
                      <div className="mt-2">
                        <span className="text-2xl font-bold text-yellow-700">₦{Number(card.value).toLocaleString()}</span>
                        <span className="text-sm text-gray-500 ml-2">value</span>
                      </div>
                      <p className="text-sm">Selling at ₦{Number(card.price).toLocaleString()} ₦ {card.quantity} available</p>
                    </div>
                    <div className="flex flex-col gap-2">
                      <button onClick={() => onEditGiftcard(card)} className="text-blue-600 hover:text-blue-800">Edit</button>
                      <button onClick={() => onDeleteGiftcard(card.id)} className="text-red-600 hover:text-red-800">Delete</button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-xl font-bold mb-4">{editingGiftcard ? 'Edit Gift Card' : 'Add New Gift Card'}</h2>
          <form onSubmit={handleSubmit} className="space-y-4">
            <input type="text" required placeholder="Gift Card Name" value={formData.name} onChange={(e) => setFormData({...formData, name: e.target.value})} className="w-full px-3 py-2 border rounded-lg" />
            <textarea placeholder="Description" value={formData.description} onChange={(e) => setFormData({...formData, description: e.target.value})} className="w-full px-3 py-2 border rounded-lg" rows="2" />
            <div className="grid grid-cols-2 gap-4">
              <input type="number" required placeholder="Card Value (?)" value={formData.value} onChange={(e) => setFormData({...formData, value: e.target.value})} className="w-full px-3 py-2 border rounded-lg" />
              <input type="number" required placeholder="Selling Price (?)" value={formData.price} onChange={(e) => setFormData({...formData, price: e.target.value})} className="w-full px-3 py-2 border rounded-lg" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <input type="number" placeholder="Quantity Available" value={formData.quantity} onChange={(e) => setFormData({...formData, quantity: e.target.value})} className="w-full px-3 py-2 border rounded-lg" />
              <input type="number" placeholder="Valid for (days)" value={formData.expiry_days} onChange={(e) => setFormData({...formData, expiry_days: e.target.value})} className="w-full px-3 py-2 border rounded-lg" />
            </div>
            <div className="flex gap-4">
              <button type="button" onClick={onHideForm} className="px-4 py-2 border rounded-lg">Cancel</button>
              <button type="submit" className="px-4 py-2 bg-yellow-500 text-white rounded-lg hover:bg-yellow-600">{editingGiftcard ? 'Update' : 'Create'} Gift Card</button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}

// Orders Tab Component
function OrdersTab({ orders }) {
  return (
    <div>
      <h2 className="text-2xl font-bold text-gray-900 mb-6">Recent Orders</h2>
      
      {orders.length === 0 ? (
        <div className="bg-white rounded-lg shadow p-8 text-center">
          <p className="text-gray-500">No orders yet</p>
        </div>
      ) : (
        <div className="space-y-4">
          {orders
            .sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0))
            .map(order => (
            <OrderCard key={order.id} order={order} />
          ))}
        </div>
      )}
    </div>
  );
}

// Order Card Component
function OrderCard({ order }) {
  const formatCurrency = (amount) => {
    return `₦${amount.toLocaleString('en-NG', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="bg-white rounded-lg shadow p-6">
      <div className="flex justify-between items-start mb-4">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">Order #{order.order_number}</h3>
          <p className="text-sm text-gray-500">{formatDate(order.created_at)}</p>
        </div>
        <span className="text-2xl font-bold text-green-600">{formatCurrency(order.total)}</span>
      </div>
      
      <div className="border-t pt-4">
        <h4 className="font-medium text-gray-900 mb-2">Items:</h4>
        <ul className="space-y-1">
          {order.items.map((item, index) => (
            <li key={index} className="flex justify-between text-sm">
              <span>{item.product_name} x{item.quantity}</span>
              <span>{formatCurrency(item.price * item.quantity)}</span>
            </li>
          ))}
        </ul>
      </div>
      
      <div className="border-t pt-4 mt-4">
        <h4 className="font-medium text-gray-900 mb-2">Customer:</h4>
        <div className="text-sm text-gray-600">
          <p>{order.customer_info.full_name}</p>
          <p>{order.customer_info.phone}</p>
          <p>{order.customer_info.street}, {order.customer_info.area}</p>
        </div>
      </div>
    </div>
  );
}

// Profile Tab Component
function ProfileTab({ vendor, onUpdateProfile, backendUrl, token }) {
  // Builds the form's initial values from the vendor record -- reused both
  // for the first render and for resetting on Cancel, so cancelling always
  // discards unsaved edits cleanly instead of leaving stray changes.
  const buildFormDataFromVendor = (v) => ({
    name: v.name || '',
    description: v.description || '',
    phone: v.phone || '',
    category: v.category || '',
    location: v.location || '',
    locations: v.locations || [],
    address: v.address || '',
    image_url: v.image_url || '',
    open_time: v.open_time || '09:00',
    close_time: v.close_time || '22:00',
    packaging_fee: v.packaging_fee || 0,
    service_charge: v.service_charge || 0,
    delivery_areas: v.delivery_areas || []
  });

  // The profile now opens read-only -- fields only become editable once
  // "Edit Profile" is clicked, and Save/Cancel both return to view mode.
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState(() => buildFormDataFromVendor(vendor));
  const [availableLocations, setAvailableLocations] = useState([]);
  
  // Fetch available locations on component mount
  useEffect(() => {
    fetchLocations();
  }, []);

  const fetchLocations = async () => {
    try {
      const response = await fetch(`${backendUrl}/api/locations`);
      const data = await response.json();
      setAvailableLocations(data.locations || []);
    } catch (error) {
      console.error('Error fetching locations:', error);
    }
  };

  const handleLocationChange = (e) => {
    const selectedOptions = Array.from(e.target.selectedOptions, option => option.value);
    setFormData({...formData, locations: selectedOptions});
  };
  
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [bannerPreview, setBannerPreview] = useState(vendor.image_url || '');
  const [newArea, setNewArea] = useState({ area: '', price: 0 });

  const addDeliveryArea = () => {
    if (!newArea.area || newArea.price <= 0) {
      setError('Please enter valid area name and price');
      return;
    }
    
    setFormData(prev => ({
      ...prev,
      delivery_areas: [...prev.delivery_areas, { area: newArea.area, price: Number(newArea.price) }]
    }));
    setNewArea({ area: '', price: 0 });
    setError('');
  };

  const removeDeliveryArea = (index) => {
    setFormData(prev => ({
      ...prev,
      delivery_areas: prev.delivery_areas.filter((_, i) => i !== index)
    }));
  };

  const handleBannerUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const allowedTypes = ['image/jpeg', 'image/png', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      setError('Please select a valid image file (JPEG, PNG, or WebP)');
      return;
    }

    const maxSize = 5 * 1024 * 1024;
    if (file.size > maxSize) {
      setError('Image file size must be less than 5MB');
      return;
    }

    setUploading(true);
    setError('');

    try {
      const formDataUpload = new FormData();
      formDataUpload.append('file', file);

      const response = await fetch(`${backendUrl}/api/upload-image`, {
        method: 'POST',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        body: formDataUpload
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Upload failed');
      }

      const data = await response.json();
      // Cloudinary returns full permanent URLs - use directly
      const imageUrl = data.image_url;
      
      setFormData(prev => ({ ...prev, image_url: imageUrl }));
      setBannerPreview(imageUrl);
      
    } catch (error) {
      setError(`Upload failed: ${error.message}`);
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    const result = await onUpdateProfile(formData);
    
    if (result.success) {
      setSuccess('Profile updated successfully!');
      setIsEditing(false);
      setTimeout(() => setSuccess(''), 3000);
    } else {
      setError(result.error || 'Failed to update profile');
    }
    
    setLoading(false);
  };

  const handleCancel = () => {
    setFormData(buildFormDataFromVendor(vendor));
    setError('');
    setIsEditing(false);
  };

  return (
    <div className="bg-white rounded-lg shadow p-6">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-bold text-gray-900">Restaurant Profile</h2>
        {!isEditing && (
          <button
            type="button"
            onClick={() => setIsEditing(true)}
            className="flex items-center gap-2 px-4 py-2 bg-imax-orange text-white rounded-md hover:bg-imax-orange-dark font-medium text-sm"
          >
            <i className="fa-solid fa-pen" /> Edit Profile
          </button>
        )}
      </div>
      
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-4">
          {error}
        </div>
      )}
      
      {success && (
        <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded mb-4">
          {success}
        </div>
      )}
      
      <form onSubmit={handleSubmit} className="space-y-6">
        <fieldset disabled={!isEditing} className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Restaurant Name *
            </label>
            <input
              type="text"
              required
              value={formData.name}
              onChange={(e) => setFormData({...formData, name: e.target.value})}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-imax-orange"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Phone Number *
            </label>
            <input
              type="tel"
              required
              value={formData.phone}
              onChange={(e) => setFormData({...formData, phone: e.target.value})}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-imax-orange"
            />
          </div>
          
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Description *
            </label>
            <textarea
              required
              rows="3"
              value={formData.description}
              onChange={(e) => setFormData({...formData, description: e.target.value})}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-imax-orange"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Category *
            </label>
            <input
              type="text"
              required
              value={formData.category}
              onChange={(e) => setFormData({...formData, category: e.target.value})}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-imax-orange"
            />
          </div>
          
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Location(s) (Hold Ctrl/Cmd to select multiple) *
            </label>
            <select
              required
              multiple
              value={formData.locations || []}
              onChange={handleLocationChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-imax-orange min-h-[120px]"
            >
              {(availableLocations || []).length === 0 ? (
                <option disabled>No locations available. Please contact admin.</option>
              ) : (
                (availableLocations || []).map(location => (
                  <option key={location.id} value={location.id}>
                    {location.name}
                  </option>
                ))
              )}
            </select>
            {(formData.locations || []).length > 0 && (
              <div className="mt-2 flex flex-wrap gap-2">
                {(formData.locations || []).map(locId => {
                  const loc = availableLocations.find(l => l.id === locId);
                  return loc ? (
                    <span key={locId} className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-blue-100 text-blue-800">
                      {loc.name}
                      <button
                        type="button"
                        onClick={() => setFormData({...formData, locations: (formData.locations || []).filter(l => l !== locId)})}
                        className="ml-2 text-blue-600 hover:text-blue-800"
                      >
                        ×
                      </button>
                    </span>
                  ) : null;
                })}
              </div>
            )}
          </div>
          
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Address *
            </label>
            <input
              type="text"
              required
              value={formData.address}
              onChange={(e) => setFormData({...formData, address: e.target.value})}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-imax-orange"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Opening Time *
            </label>
            <input
              type="time"
              required
              value={formData.open_time}
              onChange={(e) => setFormData({...formData, open_time: e.target.value})}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-imax-orange"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Closing Time *
            </label>
            <input
              type="time"
              required
              value={formData.close_time}
              onChange={(e) => setFormData({...formData, close_time: e.target.value})}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-imax-orange"
            />
          </div>
        </div>
        
        {/* Packaging Fee */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Packaging Fee per Order (?) *
          </label>
          <input
            type="number"
            step="0.01"
            min="0"
            required
            value={formData.packaging_fee}
            onChange={(e) => setFormData({...formData, packaging_fee: Number(e.target.value)})}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-imax-orange"
            placeholder="e.g., 1.50"
          />
          <p className="text-xs text-gray-500 mt-1">
            This fee will be applied to each order. If customer makes 2 orders, 2x packaging fee will be charged.
          </p>
        </div>
        
        {/* Service Charge */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Service Charge (?) *
          </label>
          <input
            type="number"
            step="0.01"
            min="0"
            required
            value={formData.service_charge}
            onChange={(e) => setFormData({...formData, service_charge: Number(e.target.value)})}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-imax-orange"
            placeholder="e.g., 2.00"
          />
          <p className="text-xs text-gray-500 mt-1">
            Service charge is applied once per order. Enter 0 if you don't charge service fee.
          </p>
        </div>
        
        {/* Delivery Areas Management */}
        <div className="border-2 border-gray-300 rounded-lg p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Delivery Areas & Fees</h3>
          
          {/* Current Delivery Areas */}
          <div className="mb-4">
            {formData.delivery_areas.length > 0 ? (
              <div className="space-y-2">
                {formData.delivery_areas.map((areaData, index) => (
                  <div key={index} className="flex items-center justify-between bg-gray-50 p-3 rounded-md">
                    <div>
                      <span className="font-medium text-gray-900">{areaData.area}</span>
                      <span className="text-gray-600 ml-3">₦{Number(areaData.price).toFixed(2)}</span>
                    </div>
                    <button
                      type="button"
                      onClick={() => removeDeliveryArea(index)}
                      className="text-red-600 hover:text-red-800 font-medium text-sm"
                    >
                      Remove
                    </button>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-gray-500 text-sm">No delivery areas added yet</p>
            )}
          </div>
          
          {/* Add New Area */}
          <div className="border-t pt-4">
            <p className="text-sm font-medium text-gray-700 mb-3">Add New Delivery Area</p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div className="md:col-span-2">
                <input
                  type="text"
                  value={newArea.area}
                  onChange={(e) => setNewArea({...newArea, area: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-imax-orange"
                  placeholder="Area name (e.g., Downtown)"
                />
              </div>
              <div>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={newArea.price}
                  onChange={(e) => setNewArea({...newArea, price: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-imax-orange"
                  placeholder="Price (?)"
                />
              </div>
            </div>
            <button
              type="button"
              onClick={addDeliveryArea}
              className="mt-3 bg-gray-600 text-white py-2 px-4 rounded-md hover:bg-gray-700 text-sm"
            >
              + Add Area
            </button>
          </div>
        </div>
        
        {/* Banner Image Upload */}
        <div className="border-2 border-dashed border-gray-300 rounded-lg p-6">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Restaurant Banner Image
          </label>
          <p className="text-xs text-gray-500 mb-3">
            Upload a banner image for your restaurant (max 5MB, JPEG/PNG/WebP)
          </p>
          
          {bannerPreview ? (
            <div className="mb-4">
              <img 
                src={bannerPreview} 
                alt="Banner preview" 
                className="w-full h-48 object-cover rounded-lg mb-2"
              />
              <button
                type="button"
                onClick={() => {
                  setFormData({...formData, image_url: ''});
                  setBannerPreview('');
                }}
                className="text-sm text-red-600 hover:text-red-800"
              >
                Remove Image
              </button>
            </div>
          ) : (
            <div className="flex items-center justify-center">
              <label className="cursor-pointer bg-gray-100 hover:bg-gray-200 text-gray-700 py-2 px-4 rounded-md border border-gray-300 transition-colors">
                <input
                  type="file"
                  accept="image/jpeg,image/png,image/webp"
                  onChange={handleBannerUpload}
                  className="hidden"
                />
                {uploading ? 'Uploading...' : 'Choose Banner Image'}
              </label>
            </div>
          )}
        </div>
        </fieldset>
        
        {isEditing && (
        <div className="flex justify-end space-x-4">
          <button
            type="button"
            onClick={handleCancel}
            disabled={loading}
            className="bg-gray-100 text-gray-700 py-2 px-6 rounded-md hover:bg-gray-200 font-medium"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={loading || uploading}
            className="bg-imax-orange text-white py-2 px-6 rounded-md hover:bg-imax-orange-dark disabled:opacity-50 font-medium"
          >
            {loading ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
        )}
      </form>
    </div>
  );
}
// Admin Login Page Component
function AdminLoginPage({ onLogin }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    
    const result = await onLogin(email, password);
    
    if (!result.success) {
      setError(result.error || 'Login failed');
    }
    
    setLoading(false);
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-purple-50 to-purple-100 px-4">
      <div className="max-w-md w-full bg-white rounded-2xl shadow-xl p-8">
        <div className="text-center mb-8">
          <div className="bg-purple-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
            </svg>
          </div>
          <h2 className="text-3xl font-bold text-gray-900">Admin Login</h2>
          <p className="text-gray-600 mt-2">Secure platform management</p>
          <div className="mt-3 inline-flex items-center gap-2 px-3 py-1 bg-purple-50 rounded-full text-xs text-purple-700">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
            </svg>
            Hidden Access Only
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-lg text-sm">
              {error}
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Email Address
            </label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              placeholder="admin@imaxeat.com"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Password
            </label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              placeholder="Enter your password"
            />
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full bg-purple-600 text-white py-3 rounded-lg font-semibold hover:bg-purple-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? 'Logging in...' : 'Login to Admin Panel'}
          </button>

          <div className="text-center pt-4 border-t border-gray-200">
            <p className="text-xs text-gray-500 mb-2"> Access via hidden URL</p>
            <code className="text-xs bg-gray-100 px-3 py-1 rounded text-gray-700">
              yoursite.com/#/admin-login
            </code>
          </div>
        </form>
      </div>
    </div>
  );
}

// Admin Dashboard Component
function AdminOrdersTab({ orders, loading, search, setSearch, status, setStatus, onRefresh }) {
  const [expanded, setExpanded] = useState(null);

  const money = (value) => `₦${Number(value || 0).toLocaleString('en-NG', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  const date = (value) => value ? new Date(value).toLocaleString('en-NG', { dateStyle: 'medium', timeStyle: 'short' }) : '—';
  const text = (value, fallback = '—') => {
    if (value === null || value === undefined || String(value).trim() === '') return fallback;
    return String(value);
  };
  const label = (value) => text(value, '—').replace(/_/g, ' ').replace(/\b\w/g, ch => ch.toUpperCase());
  const customer = (order) => order.customer_info || {};
  const fulfillment = (order) => order.fulfillment || {};
  const normalized = (value) => String(value || '').toLowerCase();
  const filtered = (orders || []).filter(order => {
    const c = customer(order);
    const haystack = [
      order.id, order.order_number, order.vendor_name, order.vendor_phone,
      order.payment_method, order.status, order.order_status, order.payment_status,
      order.payment_reference, order.referral_code, fulfillment(order).channel,
      fulfillment(order).status, c.full_name, c.name, c.phone, c.email,
      c.address, c.street, c.area, c.city, c.location
    ].map(normalized).join(' ');
    const matchesSearch = !search.trim() || haystack.includes(search.trim().toLowerCase());
    const orderStatus = order.order_status || order.status || 'unknown';
    const matchesStatus = status === 'all' || orderStatus === status;
    return matchesSearch && matchesStatus;
  });
  const statuses = [...new Set((orders || []).map(o => o.order_status || o.status).filter(Boolean))];

  return (
    <div className="space-y-5">
      <div className="bg-white rounded-lg shadow-sm p-5">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Customer Orders</h2>
            <p className="text-sm text-gray-600 mt-1">Complete customer order records with customer, vendor, items, charges, payment, fulfillment and timeline information.</p>
          </div>
          <button onClick={onRefresh} disabled={loading} className="px-4 py-2 rounded-lg bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50">
            {loading ? 'Refreshing…' : 'Refresh Orders'}
          </button>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-[1fr_220px] gap-3 mt-4">
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search order, customer, phone, email, vendor or payment reference…" className="w-full px-3 py-2 border rounded-lg" />
          <select value={status} onChange={e => setStatus(e.target.value)} className="w-full px-3 py-2 border rounded-lg">
            <option value="all">All statuses</option>
            {statuses.map(value => <option key={value} value={value}>{label(value)}</option>)}
          </select>
        </div>
        <div className="flex flex-wrap gap-2 mt-3 text-xs text-gray-500">
          <span>{filtered.length} visible order{filtered.length === 1 ? '' : 's'}</span>
          <span>•</span>
          <span>{(orders || []).length} total loaded</span>
        </div>
      </div>

      {loading && <div className="bg-white rounded-lg shadow-sm p-8 text-center text-gray-500">Loading all customer orders…</div>}
      {!loading && filtered.length === 0 && <div className="bg-white rounded-lg shadow-sm p-8 text-center text-gray-500">No orders match the current filters.</div>}

      {!loading && filtered.length > 0 && (
        <div className="space-y-4">
          {filtered.map(order => {
            const c = customer(order);
            const f = fulfillment(order);
            const items = Array.isArray(order.items) ? order.items : [];
            const payments = Array.isArray(order.payments) ? order.payments : [];
            const isOpen = expanded === order.id;
            const itemCount = items.reduce((n, i) => n + Number(i.quantity || 0), 0);
            const orderStatus = order.order_status || order.status || 'unknown';
            const deliveryFee = order.shipping_fee ?? c.delivery_price ?? order.delivery_fee;
            const discount = order.discount ?? order.discount_amount ?? c.discount_amount;
            const addressParts = [c.street, c.address, c.area, c.city, c.state, c.location].filter(Boolean);

            return (
              <div key={order.id} className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
                <button type="button" onClick={() => setExpanded(isOpen ? null : order.id)} className="w-full text-left p-5 hover:bg-gray-50">
                  <div className="flex flex-col xl:flex-row xl:items-center xl:justify-between gap-4">
                    <div className="min-w-0">
                      <div className="flex flex-wrap items-center gap-2">
                        <h3 className="font-bold text-gray-900">Order {text(order.order_number || order.id)}</h3>
                        <span className="px-2 py-1 rounded-full text-xs font-semibold bg-gray-100 text-gray-700">{label(orderStatus)}</span>
                        <span className="px-2 py-1 rounded-full text-xs font-semibold bg-blue-50 text-blue-700">Payment: {label(order.payment_status)}</span>
                        {f.channel && <span className="px-2 py-1 rounded-full text-xs font-semibold bg-purple-50 text-purple-700">{label(f.channel)}</span>}
                      </div>
                      <p className="text-sm text-gray-500 mt-1">{date(order.created_at)} · {text(order.vendor_name, 'Vendor not recorded')}</p>
                    </div>
                    <div className="flex flex-wrap items-center gap-5">
                      <div className="text-right"><p className="text-xs text-gray-500">Customer</p><p className="font-medium text-gray-900">{text(c.full_name || c.name)}</p></div>
                      <div className="text-right"><p className="text-xs text-gray-500">Items</p><p className="font-medium text-gray-900">{itemCount}</p></div>
                      <div className="text-right"><p className="text-xs text-gray-500">Total</p><p className="font-bold text-green-600">{money(order.total)}</p></div>
                      <span className="text-gray-400">{isOpen ? 'â–²' : 'â–¼'}</span>
                    </div>
                  </div>
                </button>

                {isOpen && (
                  <div className="border-t border-gray-200 p-5 space-y-6">
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
                      <div className="border rounded-lg p-4">
                        <h4 className="font-semibold text-gray-900 mb-3">Order information</h4>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
                          <div><p className="text-xs text-gray-500">Order number</p><p className="font-medium break-all">{text(order.order_number || order.id)}</p></div>
                          <div><p className="text-xs text-gray-500">Order ID</p><p className="font-medium break-all">{text(order.id)}</p></div>
                          <div><p className="text-xs text-gray-500">Created</p><p>{date(order.created_at)}</p></div>
                          <div><p className="text-xs text-gray-500">Last updated</p><p>{date(order.updated_at)}</p></div>
                          <div><p className="text-xs text-gray-500">Order status</p><p>{label(orderStatus)}</p></div>
                          <div><p className="text-xs text-gray-500">Currency</p><p>{text(order.currency, 'NGN')}</p></div>
                        </div>
                      </div>

                      <div className="border rounded-lg p-4">
                        <h4 className="font-semibold text-gray-900 mb-3">Customer</h4>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
                          <div><p className="text-xs text-gray-500">Full name</p><p>{text(c.full_name || c.name)}</p></div>
                          <div><p className="text-xs text-gray-500">Phone</p><p>{text(c.phone)}</p></div>
                          <div><p className="text-xs text-gray-500">Email</p><p className="break-all">{text(c.email)}</p></div>
                          <div><p className="text-xs text-gray-500">Customer ID</p><p className="break-all">{text(c.id || c.user_id)}</p></div>
                          <div className="sm:col-span-2"><p className="text-xs text-gray-500">Delivery address</p><p>{addressParts.length ? addressParts.join(', ') : '—'}</p></div>
                          <div><p className="text-xs text-gray-500">Delivery price recorded</p><p>{money(c.delivery_price)}</p></div>
                          <div><p className="text-xs text-gray-500">Referral code</p><p>{text(order.referral_code || c.referral_code)}</p></div>
                        </div>
                      </div>

                      <div className="border rounded-lg p-4">
                        <h4 className="font-semibold text-gray-900 mb-3">Vendor</h4>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
                          <div><p className="text-xs text-gray-500">Vendor name</p><p>{text(order.vendor_name)}</p></div>
                          <div><p className="text-xs text-gray-500">Vendor phone</p><p>{text(order.vendor_phone || f.vendor_phone)}</p></div>
                          <div className="sm:col-span-2"><p className="text-xs text-gray-500">Vendor ID</p><p className="break-all">{text(order.vendor_id)}</p></div>
                        </div>
                      </div>

                      <div className="border rounded-lg p-4">
                        <h4 className="font-semibold text-gray-900 mb-3">Payment</h4>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
                          <div><p className="text-xs text-gray-500">Method</p><p>{label(order.payment_method)}</p></div>
                          <div><p className="text-xs text-gray-500">Status</p><p>{label(order.payment_status)}</p></div>
                          <div><p className="text-xs text-gray-500">Payment reference</p><p className="break-all">{text(order.payment_reference)}</p></div>
                          <div><p className="text-xs text-gray-500">Amount payable now</p><p className="font-semibold">{money(order.amount_payable_now)}</p></div>
                          <div><p className="text-xs text-gray-500">Amount already paid</p><p>{money(order.amount_paid)}</p></div>
                          <div><p className="text-xs text-gray-500">Amount due on delivery</p><p>{money(order.amount_due_on_delivery)}</p></div>
                        </div>
                      </div>
                    </div>

                    <div>
                      <div className="flex items-center justify-between gap-3 mb-3">
                        <h4 className="font-semibold text-gray-900">Items ({itemCount})</h4>
                        <span className="text-sm text-gray-500">{items.length} line item{items.length === 1 ? '' : 's'}</span>
                      </div>
                      <div className="overflow-x-auto border rounded-lg">
                        <table className="min-w-full text-sm">
                          <thead className="bg-gray-50">
                            <tr>
                              <th className="px-4 py-3 text-left">Product</th>
                              <th className="px-4 py-3 text-left">Product ID</th>
                              <th className="px-4 py-3 text-left">Qty</th>
                              <th className="px-4 py-3 text-right">Unit Price</th>
                              <th className="px-4 py-3 text-right">Line Total</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y">
                            {items.map((item, i) => (
                              <tr key={i}>
                                <td className="px-4 py-3 font-medium">{text(item.product_name || item.name || item.title)}</td>
                                <td className="px-4 py-3 text-gray-500 break-all">{text(item.product_id)}</td>
                                <td className="px-4 py-3">{Number(item.quantity || 0)}</td>
                                <td className="px-4 py-3 text-right">{money(item.unit_price ?? item.price)}</td>
                                <td className="px-4 py-3 text-right font-semibold">{money(item.line_total ?? Number(item.unit_price ?? item.price ?? 0) * Number(item.quantity || 0))}</td>
                              </tr>
                            ))}
                            {items.length === 0 && <tr><td colSpan="5" className="px-4 py-6 text-center text-gray-500">No items recorded on this order.</td></tr>}
                          </tbody>
                        </table>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
                      <div className="bg-gray-50 rounded-lg p-4">
                        <h4 className="font-semibold mb-3">Financial breakdown</h4>
                        <div className="space-y-2 text-sm">
                          <p className="flex justify-between gap-4"><span>Merchandise subtotal</span><span>{money(order.subtotal)}</span></p>
                          <p className="flex justify-between gap-4"><span>Delivery / shipping</span><span>{money(deliveryFee)}</span></p>
                          <p className="flex justify-between gap-4"><span>Packaging fee</span><span>{money(order.packaging_fee)}</span></p>
                          <p className="flex justify-between gap-4"><span>Service charge</span><span>{money(order.service_charge)}</span></p>
                          <p className="flex justify-between gap-4"><span>Discount</span><span>-{money(discount)}</span></p>
                          <p className="flex justify-between gap-4"><span>Referral cashback</span><span>{money(order.referral_cashback)}</span></p>
                          <p className="flex justify-between gap-4 font-bold border-t mt-3 pt-3"><span>Order total</span><span>{money(order.total)}</span></p>
                          <p className="flex justify-between gap-4"><span>Payable now</span><span>{money(order.amount_payable_now)}</span></p>
                          <p className="flex justify-between gap-4"><span>Due on delivery</span><span>{money(order.amount_due_on_delivery)}</span></p>
                        </div>
                      </div>

                      <div className="bg-gray-50 rounded-lg p-4">
                        <h4 className="font-semibold mb-3">Fulfillment</h4>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
                          <div><p className="text-xs text-gray-500">Channel</p><p>{label(f.channel)}</p></div>
                          <div><p className="text-xs text-gray-500">Mode</p><p>{label(f.mode)}</p></div>
                          <div><p className="text-xs text-gray-500">Status</p><p>{label(f.status)}</p></div>
                          <div><p className="text-xs text-gray-500">Direct WhatsApp</p><p>{order.direct_whatsapp ? 'Yes' : 'No'}</p></div>
                          <div><p className="text-xs text-gray-500">Vendor phone</p><p>{text(f.vendor_phone || order.vendor_phone)}</p></div>
                          <div><p className="text-xs text-gray-500">Fulfillment reference</p><p className="break-all">{text(f.reference)}</p></div>
                        </div>
                      </div>
                    </div>

                    {payments.length > 0 && (
                      <div>
                        <h4 className="font-semibold text-gray-900 mb-3">Payment records</h4>
                        <div className="overflow-x-auto border rounded-lg">
                          <table className="min-w-full text-sm">
                            <thead className="bg-gray-50"><tr><th className="px-4 py-3 text-left">Type</th><th className="px-4 py-3 text-left">Status</th><th className="px-4 py-3 text-left">Reference</th><th className="px-4 py-3 text-right">Amount</th><th className="px-4 py-3 text-left">Date</th></tr></thead>
                            <tbody className="divide-y">
                              {payments.map((payment, i) => (
                                <tr key={i}>
                                  <td className="px-4 py-3">{label(payment.type || payment.payment_type)}</td>
                                  <td className="px-4 py-3">{label(payment.status)}</td>
                                  <td className="px-4 py-3 break-all">{text(payment.reference)}</td>
                                  <td className="px-4 py-3 text-right font-medium">{money(payment.amount)}</td>
                                  <td className="px-4 py-3">{date(payment.created_at || payment.timestamp || payment.at)}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )}

                    {Array.isArray(order.timeline) && order.timeline.length > 0 && (
                      <div>
                        <h4 className="font-semibold text-gray-900 mb-3">Order timeline</h4>
                        <div className="border rounded-lg divide-y">
                          {order.timeline.map((event, i) => (
                            <div key={i} className="p-3 flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4 text-sm">
                              <div className="sm:w-44 text-gray-500">{date(event.timestamp || event.at || event.created_at)}</div>
                              <div className="flex items-center gap-2">
                                <span className={`inline-block w-2 h-2 rounded-full ${event.current ? 'bg-blue-600' : event.completed ? 'bg-green-500' : 'bg-gray-300'}`} />
                                <span className={`font-medium ${event.current ? 'text-blue-700' : 'text-gray-900'}`}>{text(event.label || event.status || event.event, 'Update')}</span>
                              </div>
                              <span className="text-xs text-gray-500">{event.current ? 'Current' : event.completed ? 'Completed' : 'Pending'}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
function AdminDashboard({ admin, restaurants, stats, referralCodes, vendors, adminToken, onFetchRestaurants, onFetchStats, onFetchReferralCodes, onToggleStatus, onCreateReferralCode, onDeleteReferralCode, onToggleReferralStatus, onLogout, getImageUrl, backendUrl }) {
  const toast = useToast();
  const [activeTab, setActiveTab] = useState('restaurants');
  const [loading, setLoading] = useState(false);
  const [showCodeForm, setShowCodeForm] = useState(false);
  const [newCode, setNewCode] = useState({
    code: '',
    code_type: 'promo',
    influencer_name: '',
    discount_type: 'percentage',
    discount_value: 0,
    target_type: 'global',
    vendor_ids: [],
    vendor_types: [],
    categories: [],
    product_ids: [],
    min_order_amount: 0,
    max_discount_amount: '',
    max_uses: '',
    per_customer_limit: '',
    valid_from: '',
    expires_at: '',
    description: '',
    terms_and_conditions: ''
  });
  const [banners, setBanners] = useState([]);
  const [showBannerForm, setShowBannerForm] = useState(false);
  const [newBanner, setNewBanner] = useState({
    title: '',
    image_url: '',
    banner_type: 'top_header',
    link_url: '',
    start_date: '',
    end_date: '',
    display_order: 0,
    display_position: 4,
    location_scope: 'all',
    location_id: '',
    location_name: '',
    target_section_id: ''
  });
  const [uploadingImage, setUploadingImage] = useState(false);
  
  // Locations state
  const [locations, setLocations] = useState([]);
  const [showLocationForm, setShowLocationForm] = useState(false);
  const [editingLocation, setEditingLocation] = useState(null);
  const [newLocation, setNewLocation] = useState({
    name: '',
    delivery_zones: '',
    special_notes: ''
  });
  
  // Categories state
  const [categories, setCategories] = useState([]);
  const [showCategoryForm, setShowCategoryForm] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [newCategory, setNewCategory] = useState({
    name: '',
    description: '',
    icon: '',
    image_url: '',
    vendor_type: 'food',
    order: 0,
    is_active: true
  });
  
  // Homepage Sections state
  const [homepageSections, setHomepageSections] = useState([]);
  const [showSectionForm, setShowSectionForm] = useState(false);
  const [editingSection, setEditingSection] = useState(null);
  const [newSection, setNewSection] = useState({
    title: '',
    description: '',
    icon: '',
    vendor_ids: [],
    product_ids: [],
    order: 0,
    is_active: true,
    background_color: '',
    show_view_all: true,
    display_mode: 'full',
    max_items: 12,
    location_mode: 'all'
  });
  const [homepageVendorSettings, setHomepageVendorSettings] = useState({
    featured_vendor_ids: [],
    featured_vendor_limit: 12,
    featured_vendor_location_mode: 'selected_location',
    featured_vendor_title: 'Featured Vendors',
    featured_vendor_show_view_all: true
  });
  const [sectionVendorSearch, setSectionVendorSearch] = useState('');
  const [sectionProductSearch, setSectionProductSearch] = useState(''); // NEW: Product search
  const [availableVendorsForSection, setAvailableVendorsForSection] = useState([]);
  const [availableProductsForSection, setAvailableProductsForSection] = useState([]); // NEW: Available products
  const [sectionContentType, setSectionContentType] = useState('vendors'); // NEW: Toggle between vendors/products
  
  // Browse Categories (Vendor Types) state
  const [browseCategories, setBrowseCategories] = useState([]);
  const [showBrowseCategoryForm, setShowBrowseCategoryForm] = useState(false);
  const [editingBrowseCategory, setEditingBrowseCategory] = useState(null);
  const [newBrowseCategory, setNewBrowseCategory] = useState({
    type: '',
    label: '',
    icon: '',
    description: '',
    color: 'bg-gray-500',
    is_active: true
  });
  
  // Marquee/Moving Text state
  const [marqueeSettings, setMarqueeSettings] = useState({
    texts: [],
    separator: '',
    speed: 30,
    is_active: true,
    color_from: '#e11d48',   // rose-600
    color_via: '#c026d3',    // fuchsia-600
    color_to: '#6d28d9'      // violet-700
  });
  const [newMarqueeText, setNewMarqueeText] = useState('');
  
  // Agent Management state
  const [pendingVendors, setPendingVendors] = useState([]);
  // Self-registered and agent-referred vendors are genuinely separate
  // approval workflows (see PASS notes) -- this tab keeps them as two
  // distinct lists in the UI instead of one merged list, so admin never
  // has to visually pick out which is which.
  const [pendingVendorTab, setPendingVendorTab] = useState('self');
  const [agents, setAgents] = useState([]);
  const [pendingProducts, setPendingProducts] = useState([]);
  // All-products admin tab: every product on the platform, across every
  // vendor, regardless of approval status -- distinct from pendingProducts
  // (only unreviewed ones) and from availableProductsForSection (used only
  // for the homepage-section product picker).
  const [allPlatformProducts, setAllPlatformProducts] = useState([]);
  const [loadingAllPlatformProducts, setLoadingAllPlatformProducts] = useState(false);
  const [allProductsSearch, setAllProductsSearch] = useState('');
  // Admin account management (super admin only)
  const [adminsList, setAdminsList] = useState([]);
  const [loadingAdmins, setLoadingAdmins] = useState(false);
  const [newAdminForm, setNewAdminForm] = useState({ email: '', password: '', role: 'admin' });
  const [creatingAdmin, setCreatingAdmin] = useState(false);
  const [pendingReviews, setPendingReviews] = useState([]);
  const [agentSettings, setAgentSettings] = useState({
    default_commission_percentage: 5.0,
    default_commission_value: 5.0,
    commission_type: 'percentage',
    commission_fixed_amount: 0.0,
    min_commission: 0.0,
    max_commission: 100.0
  });
  const [paymentSettings, setPaymentSettings] = useState({
    commission_type: 'percentage',
    commission_value: 5.0,
    fixed_amount: 0.0
  });
  const [podAdminSettings, setPodAdminSettings] = useState({
    pod_enabled: true,
    pod_minimum_order_value: 10000,
    pod_upfront_percentage: 20,
  });
  const [settlementData, setSettlementData] = useState({ summary: {}, transactions: [] });
  const [adminOrders, setAdminOrders] = useState([]);
  const [adminOrdersLoading, setAdminOrdersLoading] = useState(false);
  const [adminOrderSearch, setAdminOrderSearch] = useState('');
  const [adminOrderStatus, setAdminOrderStatus] = useState('all');
  const [settlementStatus, setSettlementStatus] = useState('all');
  const [approvingVendor, setApprovingVendor] = useState(null);
  const [showApprovalModal, setShowApprovalModal] = useState(false);
  const [selectedVendorForApproval, setSelectedVendorForApproval] = useState(null);
  const [approvalCommission, setApprovalCommission] = useState({
    commission_type: 'percentage',
    commission_value: 5.0
  });
  const [showCredentialsModal, setShowCredentialsModal] = useState(false);
  const [approvedCredentials, setApprovedCredentials] = useState(null);
  
  // Change password state
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [passwordData, setPasswordData] = useState({
    old_password: '',
    new_password: '',
    confirm_password: ''
  });
  const [passwordError, setPasswordError] = useState('');
  const [passwordSuccess, setPasswordSuccess] = useState('');

  const fetchPendingReviews = async () => {
    if (!adminToken) return;
    try {
      const response = await fetch(`${backendUrl}/api/admin/reviews`, { headers: { Authorization: `Bearer ${adminToken}` } });
      if (response.ok) {
        const data = await response.json();
        setPendingReviews((data.reviews || []).filter(r => r.status === 'pending'));
      }
    } catch (error) {
      console.error('Error fetching reviews:', error);
    }
  };

  const handleReviewModeration = async (reviewId, action) => {
    try {
      const response = await fetch(`${backendUrl}/api/admin/reviews/${reviewId}/moderation?action=${action}`, { method: 'POST', headers: { Authorization: `Bearer ${adminToken}` } });
      if (!response.ok) { const data = await response.json().catch(() => ({})); throw new Error(data.detail || 'Review moderation failed'); }
      fetchPendingReviews();
    } catch (error) { toast.error(error.message); }
  };

  // Fetch pending vendors for agent approvals
  const fetchPendingVendors = async () => {
    if (!adminToken) return;
    try {
      const response = await fetch(`${backendUrl}/api/admin/pending-vendors`, {
        headers: { 'Authorization': `Bearer ${adminToken}` }
      });
      if (response.ok) {
        const data = await response.json();
        setPendingVendors(data.pending_vendors || []);
      }
    } catch (error) {
      console.error('Error fetching pending vendors:', error);
    }
  };

  // Fetch all agents
  const fetchAgents = async () => {
    if (!adminToken) return;
    try {
      const response = await fetch(`${backendUrl}/api/admin/agents`, {
        headers: { 'Authorization': `Bearer ${adminToken}` }
      });
      if (response.ok) {
        const data = await response.json();
        setAgents(data.agents || []);
      }
    } catch (error) {
      console.error('Error fetching agents:', error);
    }
  };

  // Fetch agent settings
  const fetchAgentSettings = async () => {
    if (!adminToken) return;
    try {
      const response = await fetch(`${backendUrl}/api/admin/agent-settings`, {
        headers: { 'Authorization': `Bearer ${adminToken}` }
      });
      if (response.ok) {
        const data = await response.json();
        setAgentSettings(data.settings || data);
      }
    } catch (error) {
      console.error('Error fetching agent settings:', error);
    }
  };

  // Approve or reject vendor
  const handleVendorApproval = async (vendorId, action, commissionData = null) => {
    setApprovingVendor(vendorId);
    try {
      const options = {
        method: 'POST',
        headers: { 
          'Authorization': `Bearer ${adminToken}`,
          'Content-Type': 'application/json'
        }
      };
      
      // Include commission data for approval
      if (action === 'approve' && commissionData) {
        options.body = JSON.stringify(commissionData);
      }
      
      const response = await fetch(`${backendUrl}/api/admin/vendor-approval/${vendorId}?action=${action}`, options);
      if (response.ok) {
        const data = await response.json();
        if (action === 'approve') {
          // Show credentials modal instead of toast
          setApprovedCredentials({
            vendorName: selectedVendorForApproval?.name || 'Vendor',
            email: data.login_email,
            password: data.temp_password,
            commissionType: data.commission_type,
            commissionValue: data.commission_value
          });
          setShowCredentialsModal(true);
        } else {
          toast.success('Vendor rejected');
        }
        fetchPendingVendors();
        onFetchRestaurants(); // Refresh restaurant list
        setShowApprovalModal(false);
        setSelectedVendorForApproval(null);
      } else {
        const data = await response.json();
        toast.error(data.detail || 'Action failed');
      }
    } catch (error) {
      toast.error('Network error');
    } finally {
      setApprovingVendor(null);
    }
  };
  
  // Open approval modal with vendor
  const openApprovalModal = (vendor) => {
    setSelectedVendorForApproval(vendor);
    // Set default commission based on vendor type
    const defaultType = vendor.vendor_type === 'food' ? 'fixed' : 'percentage';
    const defaultValue = vendor.vendor_type === 'food' ? 200 : 5;
    setApprovalCommission({
      commission_type: defaultType,
      commission_value: defaultValue
    });
    setShowApprovalModal(true);
  };

  const fetchAdminOrders = async () => {
    if (!adminToken) return;
    setAdminOrdersLoading(true);
    try {
      const response = await fetch(`${backendUrl}/api/admin/orders`, {
        headers: { Authorization: `Bearer ${adminToken}` }
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.detail || 'Unable to load orders');
      setAdminOrders(Array.isArray(data.orders) ? data.orders : []);
    } catch (error) {
      console.error('Error fetching admin orders:', error);
      toast.error(error.message || 'Unable to load orders');
    } finally {
      setAdminOrdersLoading(false);
    }
  };

  const fetchPaymentSettings = async () => {
    if (!adminToken) return;
    try {
      const response = await fetch(`${backendUrl}/api/admin/payment-settings`, { headers: { 'Authorization': `Bearer ${adminToken}` } });
      if (response.ok) {
        const data = await response.json();
        const s = data.settings || {};
        setPaymentSettings({ commission_type: s.platform_commission_type || 'percentage', commission_value: Number(s.platform_commission_value ?? 5), fixed_amount: Number(s.platform_commission_fixed_amount ?? 0) });
      }
    } catch (error) { console.error('Error fetching payment settings:', error); }
  };

  const handleUpdatePaymentSettings = async () => {
    try {
      const response = await fetch(`${backendUrl}/api/admin/payment-settings`, { method: 'PUT', headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${adminToken}` }, body: JSON.stringify(paymentSettings) });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.detail || 'Failed to update payment settings');
      toast.success('SchoolVerse platform commission settings saved');
    } catch (error) { toast.error(error.message || 'Network error'); }
  };

  const fetchPodAdminSettings = async () => {
    if (!adminToken) return;
    try {
      const response = await fetch(`${backendUrl}/api/pod-settings`);
      if (response.ok) {
        const data = await response.json();
        setPodAdminSettings(data);
      }
    } catch (error) { console.error('Error fetching POD settings:', error); }
  };

  const handleUpdatePodSettings = async () => {
    try {
      const response = await fetch(`${backendUrl}/api/admin/pod-settings`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${adminToken}` },
        body: JSON.stringify(podAdminSettings)
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.detail || 'Failed to update Pay on Delivery settings');
      toast.success('Pay on Delivery settings saved');
    } catch (error) { toast.error(error.message || 'Network error'); }
  };

  // Update agent settings
  const fetchSettlements = async (status = settlementStatus) => {
    if (!adminToken) return;
    try {
      const response = await fetch(`${backendUrl}/api/admin/settlements?status=${encodeURIComponent(status)}&limit=100`, {
        headers: { Authorization: `Bearer ${adminToken}` }
      });
      if (!response.ok) throw new Error('Unable to load settlements');
      setSettlementData(await response.json());
    } catch (error) {
      console.error('Error fetching settlements:', error);
      toast.error('Unable to load settlement ledger');
    }
  };

  const handleUpdateAgentSettings = async () => {
    try {
      const response = await fetch(`${backendUrl}/api/admin/agent-settings`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${adminToken}`
        },
        body: JSON.stringify(agentSettings)
      });
      if (response.ok) {
        toast.success('Agent commission settings updated!');
      } else {
        const data = await response.json();
        toast.error(data.detail || 'Failed to update settings');
      }
    } catch (error) {
      toast.error('Network error');
    }
  };

  // Fetch available vendors for section selection
  const fetchAvailableVendors = async () => {
    try {
      const response = await fetch(`${backendUrl}/api/vendors`);
      if (response.ok) {
        const data = await response.json();
        setAvailableVendorsForSection(data.vendors || []);
      }
    } catch (error) {
      console.error('Error fetching vendors:', error);
    }
  };

  // Fetch available products for section selection
  const fetchAvailableProducts = async () => {
    try {
      const response = await fetch(`${backendUrl}/api/admin/all-products`, {
        headers: {
          'Authorization': `Bearer ${adminToken}`
        }
      });
      if (response.ok) {
        const data = await response.json();
        setAvailableProductsForSection(data.products || []);
      }
    } catch (error) {
      console.error('Error fetching products:', error);
    }
  };

  const fetchPendingProducts = async () => {
    if (!adminToken) return;
    try {
      const response = await fetch(`${backendUrl}/api/admin/pending-products`, { headers: { Authorization: `Bearer ${adminToken}` } });
      if (response.ok) { const data = await response.json(); setPendingProducts(data.products || []); }
    } catch (error) { console.error('Error fetching pending products:', error); }
  };

  const fetchAllPlatformProducts = async () => {
    if (!adminToken) return;
    setLoadingAllPlatformProducts(true);
    try {
      const response = await fetch(`${backendUrl}/api/admin/all-products`, { headers: { Authorization: `Bearer ${adminToken}` } });
      if (response.ok) { const data = await response.json(); setAllPlatformProducts(data.products || []); }
    } catch (error) {
      console.error('Error fetching all products:', error);
      toast.error('Unable to load products right now.');
    } finally {
      setLoadingAllPlatformProducts(false);
    }
  };

  const fetchAdminsList = async () => {
    if (!adminToken) return;
    setLoadingAdmins(true);
    try {
      const response = await fetch(`${backendUrl}/api/admin/admins`, { headers: { Authorization: `Bearer ${adminToken}` } });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.detail || 'Unable to load admins');
      setAdminsList(data.admins || []);
    } catch (error) {
      toast.error(error.message);
    } finally {
      setLoadingAdmins(false);
    }
  };

  const handleToggleVendorWhatsAppDirect = async (restaurantId, enabled) => {
    try {
      const response = await fetch(`${backendUrl}/api/admin/restaurants/${restaurantId}/whatsapp-direct`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${adminToken}` },
        body: JSON.stringify({ enabled })
      });
      const result = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(result.detail || 'Unable to update WhatsApp setting');
      toast.success(enabled ? 'Direct WhatsApp ordering enabled' : 'Direct WhatsApp ordering disabled');
      await onFetchRestaurants();
    } catch (error) {
      toast.error(error?.message || 'Unable to update WhatsApp setting');
    }
  };

  const handleSetVendorSortPriority = async (restaurantId, rawValue) => {
    try {
      const priority = rawValue === '' ? null : parseInt(rawValue, 10);
      if (rawValue !== '' && Number.isNaN(priority)) {
        toast.error('Enter a whole number, or leave blank for default order.');
        return;
      }
      const response = await fetch(`${backendUrl}/api/admin/restaurants/${restaurantId}/sort-priority`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${adminToken}` },
        body: JSON.stringify({ priority })
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.detail || 'Unable to update homepage order');
      toast.success(data.is_customized ? 'Vendor moved on the homepage.' : 'Reset to default (oldest-first) order.');
      await onFetchRestaurants();
    } catch (error) {
      toast.error(error.message);
    }
  };

  const handleCreateAdmin = async (e) => {
    e.preventDefault();
    setCreatingAdmin(true);
    try {
      const response = await fetch(`${backendUrl}/api/admin/admins`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${adminToken}` },
        body: JSON.stringify(newAdminForm)
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.detail || 'Unable to create admin');
      toast.success(`Admin account created for ${newAdminForm.email}`);
      setNewAdminForm({ email: '', password: '', role: 'admin' });
      await fetchAdminsList();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setCreatingAdmin(false);
    }
  };

  const handleUpdateAdminRole = async (targetAdminId, role) => {
    try {
      const response = await fetch(`${backendUrl}/api/admin/admins/${targetAdminId}/role`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${adminToken}` },
        body: JSON.stringify({ role })
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.detail || 'Unable to update role');
      await fetchAdminsList();
    } catch (error) {
      toast.error(error.message);
    }
  };

  const handleDeleteAdmin = async (targetAdminId, email) => {
    if (!window.confirm(`Remove admin access for ${email}? This cannot be undone.`)) return;
    try {
      const response = await fetch(`${backendUrl}/api/admin/admins/${targetAdminId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${adminToken}` }
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.detail || 'Unable to remove admin');
      toast.success('Admin removed');
      await fetchAdminsList();
    } catch (error) {
      toast.error(error.message);
    }
  };

  const handleProductApproval = async (productId, action) => {
    try {
      const response = await fetch(`${backendUrl}/api/admin/products/${productId}/approval?action=${action}`, { method: 'POST', headers: { Authorization: `Bearer ${adminToken}` } });
      if (!response.ok) { const data = await response.json().catch(() => ({})); throw new Error(data.detail || 'Approval failed'); }
      await fetchPendingProducts();
    } catch (error) { toast.error(error.message); }
  };

  const handleAgentApproval = async (agentId, action) => {
    try {
      const response = await fetch(`${backendUrl}/api/admin/agents/${agentId}/approval?action=${action}`, { method: 'POST', headers: { Authorization: `Bearer ${adminToken}` } });
      if (!response.ok) { const data = await response.json().catch(() => ({})); throw new Error(data.detail || 'Agent approval failed'); }
      await fetchAgents();
    } catch (error) { toast.error(error.message); }
  };

  // Fetch homepage sections
  const fetchHomepageSections = async () => {
    if (!adminToken) return;
    
    setLoading(true);
    try {
      const response = await fetch(`${backendUrl}/api/admin/homepage-sections`, {
        headers: {
          'Authorization': `Bearer ${adminToken}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setHomepageSections(data.sections || []);
      }
    } catch (error) {
      console.error('Error fetching homepage sections:', error);
    }
    setLoading(false);
  };

  const fetchHomepageVendorSettings = async () => {
    if (!adminToken) return;
    try {
      const response = await fetch(`${backendUrl}/api/admin/homepage-settings`, { headers: { Authorization: `Bearer ${adminToken}` } });
      if (response.ok) {
        const data = await response.json();
        setHomepageVendorSettings(prev => ({ ...prev, ...(data.settings || {}) }));
      }
    } catch (error) {
      console.error('Error fetching homepage vendor settings:', error);
    }
  };

  const saveHomepageVendorSettings = async () => {
    try {
      const response = await fetch(`${backendUrl}/api/admin/homepage-settings`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${adminToken}` },
        body: JSON.stringify(homepageVendorSettings)
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.detail || 'Failed to save homepage vendor settings');
      setHomepageVendorSettings(data.settings || homepageVendorSettings);
      toast.success('Homepage vendor display settings saved');
    } catch (error) {
      toast.error(error.message);
    }
  };

  // Create homepage section
  const handleCreateSection = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const response = await fetch(`${backendUrl}/api/admin/homepage-sections`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${adminToken}`
        },
        body: JSON.stringify(newSection)
      });
      
      if (response.ok) {
        setShowSectionForm(false);
        setNewSection({
          title: '',
          description: '',
          icon: '',
          vendor_ids: [],
          product_ids: [],
          order: 0,
          is_active: true,
          background_color: '',
          show_view_all: true,
          display_mode: 'full',
          max_items: 12,
          location_mode: 'all'
        });
        setSectionContentType('vendors');
        fetchHomepageSections();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to create section');
      }
    } catch (error) {
      console.error('Error creating section:', error);
      toast.error('Failed to create section');
    }
    setLoading(false);
  };

  // Update homepage section
  const handleUpdateSection = async (e) => {
    e.preventDefault();
    if (!editingSection) return;
    
    setLoading(true);
    try {
      const response = await fetch(`${backendUrl}/api/admin/homepage-sections/${editingSection.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${adminToken}`
        },
        body: JSON.stringify(newSection)
      });
      
      if (response.ok) {
        setShowSectionForm(false);
        setEditingSection(null);
        setNewSection({
          title: '',
          description: '',
          icon: '',
          vendor_ids: [],
          product_ids: [],
          order: 0,
          is_active: true,
          background_color: '',
          show_view_all: true,
          display_mode: 'full',
          max_items: 12,
          location_mode: 'all'
        });
        setSectionContentType('vendors');
        fetchHomepageSections();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to update section');
      }
    } catch (error) {
      console.error('Error updating section:', error);
      toast.error('Failed to update section');
    }
    setLoading(false);
  };

  // Delete homepage section
  const handleDeleteSection = async (sectionId) => {
    if (!window.confirm('Are you sure you want to delete this section?')) return;
    
    setLoading(true);
    try {
      const response = await fetch(`${backendUrl}/api/admin/homepage-sections/${sectionId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${adminToken}`
        }
      });
      
      if (response.ok) {
        fetchHomepageSections();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to delete section');
      }
    } catch (error) {
      console.error('Error deleting section:', error);
      toast.error('Failed to delete section');
    }
    setLoading(false);
  };

  // Move section up/down
  const handleMoveSection = async (sectionId, direction) => {
    const currentIndex = homepageSections.findIndex(s => s.id === sectionId);
    if (currentIndex === -1) return;
    
    const newIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;
    if (newIndex < 0 || newIndex >= homepageSections.length) return;
    
    const newSections = [...homepageSections];
    const temp = newSections[currentIndex];
    newSections[currentIndex] = newSections[newIndex];
    newSections[newIndex] = temp;
    
    const sectionOrders = newSections.map((sec, idx) => ({
      id: sec.id,
      order: idx + 1
    }));
    
    try {
      await fetch(`${backendUrl}/api/admin/homepage-sections/reorder`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${adminToken}`
        },
        body: JSON.stringify(sectionOrders)
      });
      
      fetchHomepageSections();
    } catch (error) {
      console.error('Error reordering sections:', error);
    }
  };

  // Add vendor to section
  const addVendorToSection = (vendorId) => {
    if (!newSection.vendor_ids.includes(vendorId)) {
      setNewSection({
        ...newSection,
        vendor_ids: [...newSection.vendor_ids, vendorId]
      });
    }
  };

  // Remove vendor from section
  const removeVendorFromSection = (vendorId) => {
    setNewSection({
      ...newSection,
      vendor_ids: newSection.vendor_ids.filter(id => id !== vendorId)
    });
  };

  // Fetch categories
  const fetchCategories = async () => {
    if (!adminToken) return;
    
    setLoading(true);
    try {
      const response = await fetch(`${backendUrl}/api/admin/categories`, {
        headers: {
          'Authorization': `Bearer ${adminToken}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setCategories(data.categories || []);
      }
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
    setLoading(false);
  };

  // Create category
  const handleCreateCategory = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const response = await fetch(`${backendUrl}/api/admin/categories`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${adminToken}`
        },
        body: JSON.stringify(newCategory)
      });
      
      if (response.ok) {
        setShowCategoryForm(false);
        setNewCategory({
          name: '',
          description: '',
          icon: '',
          image_url: '',
          vendor_type: 'food',
          order: 0,
          is_active: true
        });
        fetchCategories();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to create category');
      }
    } catch (error) {
      console.error('Error creating category:', error);
      toast.error('Failed to create category');
    }
    setLoading(false);
  };

  // Update category
  const handleUpdateCategory = async (e) => {
    e.preventDefault();
    if (!editingCategory) return;
    
    setLoading(true);
    try {
      const response = await fetch(`${backendUrl}/api/admin/categories/${editingCategory.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${adminToken}`
        },
        body: JSON.stringify(newCategory)
      });
      
      if (response.ok) {
        setShowCategoryForm(false);
        setEditingCategory(null);
        setNewCategory({
          name: '',
          description: '',
          icon: '',
          image_url: '',
          vendor_type: 'food',
          order: 0,
          is_active: true
        });
        fetchCategories();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to update category');
      }
    } catch (error) {
      console.error('Error updating category:', error);
      toast.error('Failed to update category');
    }
    setLoading(false);
  };

  // Delete category
  const handleDeleteCategory = async (categoryId) => {
    if (!window.confirm('Are you sure you want to delete this category?')) return;
    
    setLoading(true);
    try {
      const response = await fetch(`${backendUrl}/api/admin/categories/${categoryId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${adminToken}`
        }
      });
      
      if (response.ok) {
        fetchCategories();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to delete category');
      }
    } catch (error) {
      console.error('Error deleting category:', error);
      toast.error('Failed to delete category');
    }
    setLoading(false);
  };

  // Move category up/down
  const handleMoveCategory = async (categoryId, direction) => {
    const currentIndex = categories.findIndex(c => c.id === categoryId);
    if (currentIndex === -1) return;
    
    const newIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;
    if (newIndex < 0 || newIndex >= categories.length) return;
    
    // Swap orders
    const newCategories = [...categories];
    const temp = newCategories[currentIndex];
    newCategories[currentIndex] = newCategories[newIndex];
    newCategories[newIndex] = temp;
    
    // Update orders
    const categoryOrders = newCategories.map((cat, idx) => ({
      id: cat.id,
      order: idx + 1
    }));
    
    try {
      await fetch(`${backendUrl}/api/admin/categories/reorder`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${adminToken}`
        },
        body: JSON.stringify(categoryOrders)
      });
      
      fetchCategories();
    } catch (error) {
      console.error('Error reordering categories:', error);
    }
  };

  const fetchBanners = async () => {
    if (!adminToken) return;
    
    setLoading(true);
    try {
      const response = await fetch(`${backendUrl}/api/admin/banners`, {
        headers: {
          'Authorization': `Bearer ${adminToken}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setBanners(data.banners || []);
      } else {
        console.error('Error fetching banners:', response.status);
      }
    } catch (error) {
      console.error('Error fetching banners:', error);
    }
    setLoading(false);
  };

  const handleToggle = async (restaurantId) => {
    setLoading(true);
    await onToggleStatus(restaurantId);
    setLoading(false);
  };

  const handleCreateCode = async (e) => {
    e.preventDefault();
    setLoading(true);
    const codeData = {
      code: newCode.code,
      code_type: newCode.code_type,
      influencer_name: newCode.influencer_name || null,
      discount_type: newCode.discount_type,
      discount_value: parseFloat(newCode.discount_value) || 0,
      target_type: newCode.target_type,
      vendor_ids: newCode.vendor_ids || [],
      vendor_types: newCode.vendor_types || [],
      categories: newCode.categories || [],
      product_ids: newCode.product_ids || [],
      min_order_amount: parseFloat(newCode.min_order_amount) || 0,
      max_discount_amount: newCode.max_discount_amount === '' ? null : parseFloat(newCode.max_discount_amount),
      max_uses: newCode.max_uses === '' ? null : parseInt(newCode.max_uses, 10),
      per_customer_limit: newCode.per_customer_limit === '' ? null : parseInt(newCode.per_customer_limit, 10),
      description: newCode.description || '',
      terms_and_conditions: newCode.terms_and_conditions || ''
    };
    if (newCode.valid_from) codeData.valid_from = new Date(newCode.valid_from).toISOString();
    if (newCode.expires_at) codeData.expires_at = new Date(newCode.expires_at).toISOString();

    const result = await onCreateReferralCode(codeData);
    if (result.success) {
      setShowCodeForm(false);
      setNewCode({
        code: '', code_type: 'promo', influencer_name: '', discount_type: 'percentage',
        discount_value: 0, target_type: 'global', vendor_ids: [], vendor_types: [],
        categories: [], product_ids: [], min_order_amount: 0, max_discount_amount: '',
        max_uses: '', per_customer_limit: '', valid_from: '', expires_at: '',
        description: '', terms_and_conditions: ''
      });
      toast.success('Promo/referral code created successfully!');
    } else {
      toast.error(`Error: ${result.error}`);
    }
    setLoading(false);
  };

  const handleDeleteCode = async (codeId) => {
    if (!confirm('Are you sure you want to delete this referral code?')) return;
    
    setLoading(true);
    const result = await onDeleteReferralCode(codeId);
    
    if (!result.success) {
      toast.error(`Error: ${result.error}`);
    } else {
      toast.success('Referral code deleted successfully!');
    }
    
    setLoading(false);
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    setPasswordError('');
    setPasswordSuccess('');
    
    // Validate passwords
    if (passwordData.new_password.length < 6) {
      setPasswordError('New password must be at least 6 characters long');
      return;
    }
    
    if (passwordData.new_password !== passwordData.confirm_password) {
      setPasswordError('New passwords do not match');
      return;
    }
    
    setLoading(true);
    
    try {
      const response = await fetch(`${backendUrl}/api/admin/change-password`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${adminToken}`
        },
        body: JSON.stringify({
          old_password: passwordData.old_password,
          new_password: passwordData.new_password
        })
      });
      
      const data = await response.json();
      
      if (response.ok) {
        setPasswordSuccess('Password changed successfully!');
        setPasswordData({ old_password: '', new_password: '', confirm_password: '' });
        setTimeout(() => {
          setShowChangePassword(false);
          setPasswordSuccess('');
        }, 2000);
      } else {
        setPasswordError(data.detail || 'Failed to change password');
      }
    } catch (error) {
      console.error('Error changing password:', error);
      setPasswordError('Error changing password. Please try again.');
    }
    
    setLoading(false);
  };

  const handleToggleCodeStatus = async (codeId, currentStatus) => {
    setLoading(true);
    const result = await onToggleReferralStatus(codeId, currentStatus);
    
    if (!result.success) {
      toast.error(`Error: ${result.error}`);
    }
    
    setLoading(false);
  };

  // Location Management Functions
  const fetchLocations = async () => {
    if (!adminToken) return;
    
    setLoading(true);
    try {
      const response = await fetch(`${backendUrl}/api/admin/locations`, {
        headers: {
          'Authorization': `Bearer ${adminToken}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setLocations(data.locations || []);
      } else {
        console.error('Error fetching locations:', response.status);
      }
    } catch (error) {
      console.error('Error fetching locations:', error);
    }
    setLoading(false);
  };

  const handleCreateLocation = async (e) => {
    e.preventDefault();
    if (!newLocation.name.trim()) {
      toast.error('Location name is required');
      return;
    }
    
    setLoading(true);
    try {
      const response = await fetch(`${backendUrl}/api/admin/locations`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${adminToken}`
        },
        body: JSON.stringify({
          name: newLocation.name,
          delivery_zones: newLocation.delivery_zones ? newLocation.delivery_zones.split(',').map(z => z.trim()) : [],
          special_notes: newLocation.special_notes || null
        })
      });
      
      const data = await response.json();
      
      if (response.ok) {
        await fetchLocations();
        setNewLocation({ name: '', delivery_zones: '', special_notes: '' });
        setShowLocationForm(false);
        toast.success('Location created successfully!');
      } else {
        toast.error(data.detail || 'Failed to create location');
      }
    } catch (error) {
      console.error('Error creating location:', error);
      toast.error('Error creating location. Please try again.');
    }
    setLoading(false);
  };

  const handleUpdateLocation = async (e) => {
    e.preventDefault();
    if (!editingLocation || !newLocation.name.trim()) {
      toast.error('Location name is required');
      return;
    }
    
    setLoading(true);
    try {
      const response = await fetch(`${backendUrl}/api/admin/locations/${editingLocation.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${adminToken}`
        },
        body: JSON.stringify({
          name: newLocation.name,
          delivery_zones: newLocation.delivery_zones ? newLocation.delivery_zones.split(',').map(z => z.trim()) : [],
          special_notes: newLocation.special_notes || null
        })
      });
      
      const data = await response.json();
      
      if (response.ok) {
        await fetchLocations();
        setNewLocation({ name: '', delivery_zones: '', special_notes: '' });
        setEditingLocation(null);
        setShowLocationForm(false);
        toast.success('Location updated successfully!');
      } else {
        toast.error(data.detail || 'Failed to update location');
      }
    } catch (error) {
      console.error('Error updating location:', error);
      toast.error('Error updating location. Please try again.');
    }
    setLoading(false);
  };

  const handleDeleteLocation = async (locationId) => {
    if (!confirm('Are you sure you want to delete this location? This action cannot be undone.')) {
      return;
    }
    
    setLoading(true);
    try {
      const response = await fetch(`${backendUrl}/api/admin/locations/${locationId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${adminToken}`
        }
      });
      
      const data = await response.json();
      
      if (response.ok) {
        await fetchLocations();
        toast.success('Location deleted successfully!');
      } else {
        toast.error(data.detail || 'Failed to delete location');
      }
    } catch (error) {
      console.error('Error deleting location:', error);
      toast.error('Error deleting location. Please try again.');
    }
    setLoading(false);
  };

  const handleEditLocation = (location) => {
    setEditingLocation(location);
    setNewLocation({
      name: location.name,
      delivery_zones: (location.delivery_zones || []).join(', '),
      special_notes: location.special_notes || ''
    });
    setShowLocationForm(true);
  };

  const handleToggleLocationStatus = async (locationId, currentStatus) => {
    setLoading(true);
    try {
      const response = await fetch(`${backendUrl}/api/admin/locations/${locationId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${adminToken}`
        },
        body: JSON.stringify({
          is_active: !currentStatus
        })
      });
      
      if (response.ok) {
        await fetchLocations();
      } else {
        const data = await response.json();
        toast.error(data.detail || 'Failed to toggle location status');
      }
    } catch (error) {
      console.error('Error toggling location status:', error);
      toast.error('Error toggling location status. Please try again.');
    }
    setLoading(false);
  };

  // Fetch browse categories (vendor types)
  const fetchBrowseCategories = async () => {
    try {
      const response = await fetch(`${backendUrl}/api/admin/vendor-types`, {
        headers: {
          'Authorization': `Bearer ${adminToken}`
        }
      });
      if (response.ok) {
        const data = await response.json();
        setBrowseCategories(data.vendor_types || []);
      }
    } catch (error) {
      console.error('Error fetching browse categories:', error);
    }
  };

  // Create browse category
  const handleCreateBrowseCategory = async (e) => {
    e.preventDefault();
    if (!newBrowseCategory.label.trim()) {
      toast.error('Label is required');
      return;
    }
    
    setLoading(true);
    try {
      const response = await fetch(`${backendUrl}/api/admin/vendor-types/create`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${adminToken}`
        },
        body: JSON.stringify({
          type: newBrowseCategory.type || newBrowseCategory.label.toLowerCase().replace(/\s+/g, '_'),
          label: newBrowseCategory.label,
          icon: newBrowseCategory.icon,
          description: newBrowseCategory.description,
          color: newBrowseCategory.color,
          is_active: newBrowseCategory.is_active
        })
      });
      
      const data = await response.json();
      
      if (response.ok) {
        setShowBrowseCategoryForm(false);
        setNewBrowseCategory({ type: '', label: '', icon: '', description: '', color: 'bg-gray-500', is_active: true });
        fetchBrowseCategories();
        toast.success('Browse category created successfully!');
      } else {
        toast.error(data.detail || 'Failed to create browse category');
      }
    } catch (error) {
      console.error('Error creating browse category:', error);
      toast.error('Error creating browse category');
    }
    setLoading(false);
  };

  // Update browse category
  const handleUpdateBrowseCategory = async (e) => {
    e.preventDefault();
    if (!editingBrowseCategory) return;
    
    setLoading(true);
    try {
      const response = await fetch(`${backendUrl}/api/admin/vendor-types/${editingBrowseCategory.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${adminToken}`
        },
        body: JSON.stringify({
          label: newBrowseCategory.label,
          icon: newBrowseCategory.icon,
          description: newBrowseCategory.description,
          color: newBrowseCategory.color,
          is_active: newBrowseCategory.is_active
        })
      });
      
      const data = await response.json();
      
      if (response.ok) {
        setEditingBrowseCategory(null);
        setShowBrowseCategoryForm(false);
        setNewBrowseCategory({ type: '', label: '', icon: '', description: '', color: 'bg-gray-500', is_active: true });
        fetchBrowseCategories();
        toast.success('Browse category updated successfully!');
      } else {
        toast.error(data.detail || 'Failed to update browse category');
      }
    } catch (error) {
      console.error('Error updating browse category:', error);
      toast.error('Error updating browse category');
    }
    setLoading(false);
  };

  // Delete browse category
  const handleDeleteBrowseCategory = async (categoryId) => {
    if (!window.confirm('Are you sure you want to delete this browse category?')) return;
    
    setLoading(true);
    try {
      const response = await fetch(`${backendUrl}/api/admin/vendor-types/${categoryId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${adminToken}`
        }
      });
      
      const data = await response.json();
      
      if (response.ok) {
        fetchBrowseCategories();
        toast.success('Browse category deleted successfully!');
      } else {
        toast.error(data.detail || 'Failed to delete browse category');
      }
    } catch (error) {
      console.error('Error deleting browse category:', error);
      toast.error('Error deleting browse category');
    }
    setLoading(false);
  };

  // Reorder browse category (move up or down)
  const handleReorderBrowseCategory = async (index, direction) => {
    const newCategories = [...browseCategories];
    const targetIndex = direction === 'up' ? index - 1 : index + 1;
    
    // Boundary check
    if (targetIndex < 0 || targetIndex >= newCategories.length) return;
    
    // Swap the items
    [newCategories[index], newCategories[targetIndex]] = [newCategories[targetIndex], newCategories[index]];
    
    // Update local state immediately for responsive UI
    setBrowseCategories(newCategories);
    
    // Prepare order data
    const orderData = newCategories.map((cat, idx) => ({
      id: cat.id,
      order: idx + 1
    }));
    
    try {
      const response = await fetch(`${backendUrl}/api/admin/vendor-types/reorder`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${adminToken}`
        },
        body: JSON.stringify(orderData)
      });
      
      if (response.ok) {
        toast.success('Category order updated!');
      } else {
        // Revert on error
        fetchBrowseCategories();
        toast.error('Failed to reorder categories');
      }
    } catch (error) {
      console.error('Error reordering browse categories:', error);
      fetchBrowseCategories();
      toast.error('Error reordering categories');
    }
  };

  // Edit browse category
  const handleEditBrowseCategory = (category) => {
    setEditingBrowseCategory(category);
    setNewBrowseCategory({
      type: category.type,
      label: category.label,
      icon: category.icon || '',
      description: category.description || '',
      color: category.color || 'bg-gray-500',
      is_active: category.is_active !== false
    });
    setShowBrowseCategoryForm(true);
  };

  // Fetch marquee settings
  const fetchMarqueeSettings = async () => {
    try {
      const response = await fetch(`${backendUrl}/api/admin/marquee-settings`, {
        headers: {
          'Authorization': `Bearer ${adminToken}`
        }
      });
      if (response.ok) {
        const data = await response.json();
        setMarqueeSettings(data);
      }
    } catch (error) {
      console.error('Error fetching marquee settings:', error);
    }
  };

  // Update marquee settings
  const handleUpdateMarqueeSettings = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${backendUrl}/api/admin/marquee-settings`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${adminToken}`
        },
        body: JSON.stringify(marqueeSettings)
      });
      
      if (response.ok) {
        toast.success('Marquee settings saved successfully!');
      } else {
        toast.error('Failed to save settings');
      }
    } catch (error) {
      console.error('Error saving marquee settings:', error);
      toast.error('Error saving settings');
    }
    setLoading(false);
  };

  // Add marquee text
  const handleAddMarqueeText = () => {
    if (!newMarqueeText.trim()) return;
    
    const newText = {
      id: Date.now().toString(),
      text: newMarqueeText.trim()
    };
    
    setMarqueeSettings(prev => ({
      ...prev,
      texts: [...prev.texts, newText]
    }));
    setNewMarqueeText('');
  };

  // Delete marquee text
  const handleDeleteMarqueeText = (textId) => {
    setMarqueeSettings(prev => ({
      ...prev,
      texts: prev.texts.filter(t => t.id !== textId)
    }));
  };

  // Effect to handle tab changes for tabs with late-declared functions
  useEffect(() => {
    if (activeTab === 'orders') {
      fetchAdminOrders();
    } else if (activeTab === 'restaurants') {
      onFetchRestaurants();
    } else if (activeTab === 'stats') {
      onFetchStats();
    } else if (activeTab === 'referral-codes') {
      onFetchReferralCodes();
      fetchAvailableProducts();
    } else if (activeTab === 'banners') {
      fetchBanners();
    } else if (activeTab === 'locations') {
      fetchLocations();
    } else if (activeTab === 'categories') {
      fetchCategories();
    } else if (activeTab === 'homepage-sections') {
      fetchHomepageSections();
      fetchHomepageVendorSettings();
      fetchAvailableVendors();
      fetchAvailableProducts(); // NEW: Fetch products for section selection
    } else if (activeTab === 'flash-deals') {
      fetchAvailableProducts();
    } else if (activeTab === 'deals-promotions') {
      fetchAvailableProducts();
      fetchAvailableVendors();
    } else if (activeTab === 'browse-categories') {
      fetchBrowseCategories();
    } else if (activeTab === 'marquee') {
      fetchMarqueeSettings();
    } else if (activeTab === 'agent-approvals') {
      fetchPendingVendors();
      fetchAgents();
    } else if (activeTab === 'product-approvals') {
      fetchPendingProducts();
    } else if (activeTab === 'all-products') {
      fetchAllPlatformProducts();
    } else if (activeTab === 'admins') {
      fetchAdminsList();
    } else if (activeTab === 'reviews') {
      fetchPendingReviews();
    } else if (activeTab === 'agent-settings') {
      fetchAgentSettings();
      fetchAgents();
    }
  }, [activeTab]);

  // Fetch pending vendors count on component mount (for badge display)
  useEffect(() => {
    if (adminToken) {
      fetchPendingVendors();
    }
  }, [adminToken]);

  return (
    <div className="admin-dashboard-shell min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
                <svg className="w-8 h-8 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                </svg>
                Admin Dashboard
              </h1>
              <p className="text-gray-600 mt-1">Manage restaurants and track platform activity</p>
            </div>
            <div className="flex gap-3">
              <button
                onClick={() => setShowChangePassword(true)}
                className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
                </svg>
                Change Password
              </button>
              <button
                onClick={onLogout}
                className="bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 transition-colors"
              >
                Logout
              </button>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="bg-white rounded-lg shadow-sm mb-6">
          <div className="flex border-b overflow-x-auto">
            <button
              onClick={() => setActiveTab('orders')}
              className={`px-6 py-4 font-medium transition-colors whitespace-nowrap ${activeTab === 'orders' ? 'border-b-2 border-blue-600 text-blue-600' : 'text-gray-600 hover:text-gray-900'}`}
            >
              <span className="flex items-center gap-2"><UiIcon name="receipt" size={17} /> Orders {adminOrders.length > 0 && <span className="px-2 py-0.5 text-xs bg-blue-500 text-white rounded-full">{adminOrders.length}</span>}</span>
            </button>
            <button
              onClick={() => setActiveTab('restaurants')}
              className={`px-6 py-4 font-medium transition-colors whitespace-nowrap ${
                activeTab === 'restaurants'
                  ? 'border-b-2 border-purple-600 text-purple-600'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              <span className="flex items-center gap-2">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                </svg>
                Vendors
              </span>
            </button>
            <button
              onClick={() => setActiveTab('fulfillment')}
              className={`px-6 py-4 font-medium transition-colors whitespace-nowrap ${
                activeTab === 'fulfillment'
                  ? 'border-b-2 border-green-600 text-green-600'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              <span className="flex items-center gap-2">
                <UiIcon name="phone" size={17} />
                Fulfillment
              </span>
            </button>
            <button
              onClick={() => setActiveTab('homepage-sections')}
              className={`px-6 py-4 font-medium transition-colors whitespace-nowrap ${
                activeTab === 'homepage-sections'
                  ? 'border-b-2 border-purple-600 text-purple-600'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              <span className="flex items-center gap-2">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 5a1 1 0 011-1h14a1 1 0 011 1v2a1 1 0 01-1 1H5a1 1 0 01-1-1V5zM4 13a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H5a1 1 0 01-1-1v-6zM16 13a1 1 0 011-1h2a1 1 0 011 1v6a1 1 0 01-1 1h-2a1 1 0 01-1-1v-6z" />
                </svg>
                Homepage
              </span>
            </button>
            <button
              onClick={() => setActiveTab('page-experience')}
              data-testid="admin-tab-page-experience"
              className={`px-6 py-4 font-medium transition-colors whitespace-nowrap ${activeTab === 'page-experience' ? 'border-b-2 border-blue-600 text-blue-600' : 'text-gray-600 hover:text-gray-900'}`}
            >
              <span className="flex items-center gap-2"><UiIcon name="home" size={17} /> Page Experience</span>
            </button>
            <button
              onClick={() => setActiveTab('analytics')}
              data-testid="admin-tab-analytics"
              className={`px-6 py-4 font-medium transition-colors whitespace-nowrap ${activeTab === 'analytics' ? 'border-b-2 border-indigo-600 text-indigo-600' : 'text-gray-600 hover:text-gray-900'}`}
            >
              <span className="flex items-center gap-2"><UiIcon name="chart" size={17} /> Analytics</span>
            </button>
            <button
              onClick={() => setActiveTab('deals-promotions')}
              data-testid="admin-tab-deals-promotions"
              className={`px-6 py-4 font-medium transition-colors whitespace-nowrap ${activeTab === 'deals-promotions' ? 'border-b-2 border-cyan-700 text-cyan-700' : 'text-gray-600 hover:text-gray-900'}`}
            >
              <span className="flex items-center gap-2"><span>🔥</span> Deals & Promotions</span>
            </button>
            <button
              onClick={() => setActiveTab('flash-deals')}
              data-testid="admin-tab-flash-deals"
              className={`px-6 py-4 font-medium transition-colors whitespace-nowrap ${
                activeTab === 'flash-deals'
                  ? 'border-b-2 border-red-600 text-red-600'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              <span className="flex items-center gap-2">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
                Flash Deals
              </span>
            </button>
            <button onClick={() => setActiveTab('product-approvals')} className={`px-6 py-4 font-medium transition-colors ${activeTab === 'product-approvals' ? 'border-b-2 border-red-600 text-red-600' : 'text-gray-600 hover:text-gray-900'}`}>
              <span className="flex items-center gap-2"><UiIcon name="check" size={17} /> Product Approvals {pendingProducts.length > 0 && <span className="px-2 py-0.5 text-xs bg-red-500 text-white rounded-full">{pendingProducts.length}</span>}</span>
            </button>
            <button onClick={() => setActiveTab('all-products')} data-testid="admin-tab-all-products" className={`px-6 py-4 font-medium transition-colors whitespace-nowrap ${activeTab === 'all-products' ? 'border-b-2 border-purple-600 text-purple-600' : 'text-gray-600 hover:text-gray-900'}`}>
              <span className="flex items-center gap-2"><UiIcon name="box" size={17} /> All Products</span>
            </button>
            {/* Admin account management -- super admins only */}
            {admin?.role === 'super_admin' && (
              <button onClick={() => setActiveTab('admins')} data-testid="admin-tab-admins" className={`px-6 py-4 font-medium transition-colors whitespace-nowrap ${activeTab === 'admins' ? 'border-b-2 border-indigo-600 text-indigo-600' : 'text-gray-600 hover:text-gray-900'}`}>
                <span className="flex items-center gap-2"><UiIcon name="shield" size={17} /> Admins</span>
              </button>
            )}
            <button onClick={() => setActiveTab('reviews')} className={`px-6 py-4 font-medium transition-colors ${activeTab === 'reviews' ? 'border-b-2 border-amber-600 text-amber-600' : 'text-gray-600 hover:text-gray-900'}`}>
              <span className="flex items-center gap-2"><UiIcon name="star" size={17} /> Reviews {pendingReviews.length > 0 && <span className="px-2 py-0.5 text-xs bg-amber-500 text-white rounded-full">{pendingReviews.length}</span>}</span>
            </button>
            <button
              onClick={() => setActiveTab('stats')}
              className={`px-6 py-4 font-medium transition-colors whitespace-nowrap ${
                activeTab === 'stats'
                  ? 'border-b-2 border-purple-600 text-purple-600'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              <span className="flex items-center gap-2">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                </svg>
                Stats
              </span>
            </button>
            <button
              onClick={() => setActiveTab('referral-codes')}
              className={`px-6 py-4 font-medium transition-colors whitespace-nowrap ${
                activeTab === 'referral-codes'
                  ? 'border-b-2 border-purple-600 text-purple-600'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              <span className="flex items-center gap-2">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v13m0-13V6a2 2 0 112 2h-2zm0 0V5.5A2.5 2.5 0 109.5 8H12zm-7 4h14M5 12a2 2 0 110-4h14a2 2 0 110 4M5 12v7a2 2 0 002 2h10a2 2 0 002-2v-7" />
                </svg>
                Promo & Referrals
              </span>
            </button>
            <button
              onClick={() => setActiveTab('banners')}
              className={`px-6 py-4 font-medium transition-colors whitespace-nowrap ${
                activeTab === 'banners'
                  ? 'border-b-2 border-purple-600 text-purple-600'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              <span className="flex items-center gap-2">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
                Banners
              </span>
            </button>
            <button
              onClick={() => setActiveTab('locations')}
              className={`px-6 py-4 font-medium transition-colors ${
                activeTab === 'locations'
                  ? 'border-b-2 border-purple-600 text-purple-600'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              <span className="flex items-center gap-2">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
                Locations
              </span>
            </button>
            <button
              onClick={() => setActiveTab('categories')}
              className={`px-6 py-4 font-medium transition-colors ${
                activeTab === 'categories'
                  ? 'border-b-2 border-purple-600 text-purple-600'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              <span className="flex items-center gap-2">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
                </svg>
                Categories
              </span>
            </button>
            <button
              onClick={() => setActiveTab('browse-categories')}
              className={`px-6 py-4 font-medium transition-colors ${
                activeTab === 'browse-categories'
                  ? 'border-b-2 border-purple-600 text-purple-600'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              <span className="flex items-center gap-2">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
                </svg>
                Browse Types
              </span>
            </button>
            <button
              onClick={() => setActiveTab('marquee')}
              className={`px-6 py-4 font-medium transition-colors ${
                activeTab === 'marquee'
                  ? 'border-b-2 border-yellow-600 text-yellow-600'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              <span className="flex items-center gap-2">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
                </svg>
                Moving Text
              </span>
            </button>
            <button
              onClick={() => setActiveTab('agent-approvals')}
              className={`px-6 py-4 font-medium transition-colors relative ${
                activeTab === 'agent-approvals'
                  ? 'border-b-2 border-green-600 text-green-600'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              <span className="flex items-center gap-2">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                Agent Approvals
                {(pendingVendors.length + agents.filter(a => a.approval_status === 'pending_admin_approval').length) > 0 && (
                  <span className="ml-1 px-2 py-0.5 text-xs bg-red-500 text-white rounded-full">
                    {pendingVendors.length + agents.filter(a => a.approval_status === 'pending_admin_approval').length}
                  </span>
                )}
              </span>
            </button>
            <button
              onClick={() => setActiveTab('agent-settings')}
              className={`px-6 py-4 font-medium transition-colors ${
                activeTab === 'agent-settings'
                  ? 'border-b-2 border-emerald-600 text-emerald-600'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              <span className="flex items-center gap-2">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                Agent Commission
              </span>
            </button>
            <button
              onClick={() => { setActiveTab('payment-settings'); fetchPaymentSettings(); fetchPodAdminSettings(); }}
              className={`px-6 py-4 font-medium transition-colors ${activeTab === 'payment-settings' ? 'border-b-2 border-cyan-700 text-cyan-700' : 'text-gray-600 hover:text-gray-900'}`}
            >
              <span className="flex items-center gap-2">
                <span>₦</span>
                Payment & Commission
              </span>
            </button>
            <button
              onClick={() => { setActiveTab('settlements'); fetchSettlements('all'); }}
              className={`px-6 py-4 font-medium transition-colors ${activeTab === 'settlements' ? 'border-b-2 border-emerald-600 text-emerald-600' : 'text-gray-600 hover:text-gray-900'}`}
            >
              <span className="flex items-center gap-2"><span>â–£</span> Settlements</span>
            </button>

            <button
              onClick={() => setActiveTab('ai-shopi')}
              className={`px-6 py-4 font-medium transition-colors whitespace-nowrap ${
                activeTab === 'ai-shopi'
                  ? 'border-b-2 border-cyan-500 text-cyan-700'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              <span className="flex items-center gap-2">
                <span>âœ¦</span> Shopi AI
              </span>
            </button>

            <button
              onClick={() => setActiveTab('import-export')}
              className={`px-6 py-4 font-medium transition-colors ${
                activeTab === 'import-export'
                  ? 'border-b-2 border-purple-600 text-purple-600'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              <span className="flex items-center gap-2">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-3m-1 4l-3 3m0 0l-3-3m3 3V4" />
                </svg>
                Import/Export
              </span>
            </button>
          </div>
        </div>

        {/* Shopi AI Tab */}
        {activeTab === 'ai-shopi' && (
          <Suspense fallback={<div className="p-6 text-sm text-gray-500">Loading AI tools…</div>}>
            <AIAdminTab backendUrl={backendUrl} adminToken={adminToken} />
          </Suspense>
        )}

        {/* Restaurants Tab */}
        {activeTab === 'orders' && (
          <AdminOrdersTab
            orders={adminOrders}
            loading={adminOrdersLoading}
            search={adminOrderSearch}
            setSearch={setAdminOrderSearch}
            status={adminOrderStatus}
            setStatus={setAdminOrderStatus}
            onRefresh={fetchAdminOrders}
          />
        )}

        {activeTab === 'restaurants' && (
          <div className="bg-white rounded-lg shadow-sm overflow-hidden">
            <div className="p-6 border-b border-gray-200">
              <h2 className="text-xl font-semibold text-gray-900">Restaurant Management</h2>
              <p className="text-gray-600 text-sm mt-1">Enable or disable restaurants on the platform</p>
              <p className="text-gray-500 text-xs mt-1">
                Homepage order: vendors show oldest-first by default. To move one up, give it a lower "Homepage Order" number below (e.g. 1 shows before 2). Leave the field blank to reset a vendor back to normal order.
              </p>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Restaurant</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Category</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Location</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Contact</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Homepage Order</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Orders</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Revenue</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Credentials</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">WhatsApp direct</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Action</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {restaurants
                    .sort((a, b) => {
                      // Sort enabled restaurants first, disabled at bottom
                      const aEnabled = a.enabled !== false;
                      const bEnabled = b.enabled !== false;
                      if (aEnabled !== bEnabled) return aEnabled ? -1 : 1;
                      // Within same enabled status, sort by created_at (newest first)
                      const aDate = new Date(a.created_at || 0);
                      const bDate = new Date(b.created_at || 0);
                      return bDate - aDate;
                    })
                    .map((restaurant) => (
                    <tr key={restaurant.id} className={!restaurant.enabled ? 'bg-gray-50 opacity-75' : ''}>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="flex-shrink-0 h-10 w-10">
                            <img className="h-10 w-10 rounded-full object-cover" src={getImageUrl(restaurant.image_url) || 'https://via.placeholder.com/40'} alt="" />
                          </div>
                          <div className="ml-4">
                            <div className="text-sm font-medium text-gray-900">{restaurant.name}</div>
                            <div className="text-sm text-gray-500"><UiIcon name="star" size={15} className="text-amber-500" /> {restaurant.rating}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-blue-100 text-blue-800">
                          {restaurant.category}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {restaurant.location}
                      </td>
                      <td className="px-6 py-4 text-xs text-gray-600 max-w-[220px]">
                        {restaurant.email && (
                          <div className="truncate" title={restaurant.email}>
                            <UiIcon name="mail" size={15} className="text-gray-400 mr-1.5 inline-block" />{restaurant.email}
                          </div>
                        )}
                        {restaurant.phone && (
                          <div className="truncate" title={restaurant.phone}>
                            <UiIcon name="phone" size={15} className="text-gray-400 mr-1.5 inline-block" />{restaurant.phone}
                          </div>
                        )}
                        {restaurant.address && (
                          <div className="truncate" title={restaurant.address}>
                            <UiIcon name="location" size={15} className="text-gray-400 mr-1.5 inline-block" />{restaurant.address}
                          </div>
                        )}
                        {!restaurant.email && !restaurant.phone && !restaurant.address && (
                          <span className="text-gray-400 italic">No contact info</span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm">
                        <input
                          type="number"
                          defaultValue={
                            restaurant.admin_sort_priority && restaurant.admin_sort_priority !== 999999
                              ? restaurant.admin_sort_priority
                              : ''
                          }
                          placeholder="Default"
                          title="Lower number = appears earlier on the homepage. Leave blank for default (oldest vendors first)."
                          onBlur={(e) => {
                            const original = restaurant.admin_sort_priority && restaurant.admin_sort_priority !== 999999
                              ? String(restaurant.admin_sort_priority) : '';
                            if (e.target.value !== original) {
                              handleSetVendorSortPriority(restaurant.id, e.target.value);
                            }
                          }}
                          className="w-20 px-2 py-1 border border-gray-300 rounded-lg text-xs"
                        />
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {restaurant.order_count || 0}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 font-medium">
                        {formatCurrency(restaurant.total_revenue || 0)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {restaurant.login_email ? (
                          <div className="text-xs">
                            <div className="text-gray-600 truncate max-w-[150px]" title={restaurant.login_email}>
                               {restaurant.login_email}
                            </div>
                            {restaurant.temp_password && (
                              <button
                                onClick={() => {
                                  navigator.clipboard.writeText(`Email: ${restaurant.login_email}\nPassword: ${restaurant.temp_password}`);
                                  toast.success('Credentials copied!');
                                }}
                                className="text-blue-600 hover:text-blue-800 flex items-center gap-1 mt-1"
                              >
                                 Copy Credentials
                              </button>
                            )}
                          </div>
                        ) : (
                          <span className="text-gray-400 text-xs">—</span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {restaurant.enabled !== false ? (
                          <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800">
                            Enabled
                          </span>
                        ) : (
                          <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-red-100 text-red-800">
                            Disabled
                          </span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm">
                        <button
                          type="button"
                          onClick={() => handleToggleVendorWhatsAppDirect(restaurant.id, !restaurant.whatsapp_direct_fulfillment_enabled)}
                          className={`px-3 py-1 rounded-md transition-colors ${restaurant.whatsapp_direct_fulfillment_enabled ? 'bg-green-100 text-green-700 hover:bg-green-200' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
                        >
                          {restaurant.whatsapp_direct_fulfillment_enabled ? 'Enabled' : 'Off'}
                        </button>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <div className="flex gap-2">
                          <button
                            onClick={() => handleToggle(restaurant.id)}
                            disabled={loading}
                            className={`px-3 py-1 rounded-md transition-colors ${
                              restaurant.enabled !== false
                                ? 'bg-red-100 text-red-700 hover:bg-red-200'
                                : 'bg-green-100 text-green-700 hover:bg-green-200'
                            } disabled:opacity-50`}
                          >
                            {restaurant.enabled !== false ? 'Disable' : 'Enable'}
                          </button>
                          
                          {/* Show Remove button only for disabled restaurants */}
                          {restaurant.enabled === false && (
                            <button
                              onClick={async () => {
                                if (!confirm(`Are you sure you want to permanently remove "${restaurant.name}"? This action cannot be undone.`)) return;
                                
                                setLoading(true);
                                try {
                                  const response = await fetch(`${backendUrl}/api/admin/vendors/${restaurant.id}`, {
                                    method: 'DELETE',
                                    headers: {
                                      'Authorization': `Bearer ${adminToken}`
                                    }
                                  });
                                  
                                  if (response.ok) {
                                    toast.success('Restaurant removed successfully!');
                                    onFetchRestaurants();
                                  } else {
                                    const error = await response.json();
                                    toast.error(`Error: ${error.detail}`);
                                  }
                                } catch (error) {
                                  console.error('Error removing restaurant:', error);
                                  toast.error('Error removing restaurant');
                                }
                                setLoading(false);
                              }}
                              disabled={loading}
                              className="px-3 py-1 rounded-md bg-red-600 text-white hover:bg-red-700 transition-colors disabled:opacity-50"
                            >
                              Remove
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Statistics Tab */}
        {activeTab === 'stats' && stats && (
          <div className="space-y-6">
            {/* Overview Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="bg-white rounded-lg shadow-sm p-6">
                <div className="flex items-center">
                  <div className="bg-blue-100 p-3 rounded-full">
                    <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                  </div>
                  <div className="ml-4">
                    <p className="text-sm font-medium text-gray-600">Total Orders</p>
                    <p className="text-2xl font-bold text-gray-900">{stats.total_orders}</p>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-lg shadow-sm p-6">
                <div className="flex items-center">
                  <div className="bg-green-100 p-3 rounded-full">
                    <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <div className="ml-4">
                    <p className="text-sm font-medium text-gray-600">Total Revenue</p>
                    <p className="text-2xl font-bold text-gray-900">{formatCurrency(stats.total_revenue)}</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Restaurant Statistics */}
            <div className="bg-white rounded-lg shadow-sm overflow-hidden">
              <div className="p-6 border-b border-gray-200">
                <h2 className="text-xl font-semibold text-gray-900">Revenue by Restaurant</h2>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Restaurant</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Orders</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Revenue</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Avg Order Value</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {stats.restaurant_stats.map((stat) => (
                      <tr key={stat.vendor_id}>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                          {stat.vendor_name}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {stat.order_count}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 font-medium">
                          {formatCurrency(stat.total_revenue)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                          {formatCurrency(stat.total_revenue / stat.order_count)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {/* Referral / Promo Codes Tab */}
        {activeTab === 'referral-codes' && (
          <div className="space-y-6">
            <div className="bg-white rounded-lg shadow-sm p-6 flex justify-between items-center">
              <div>
                <h2 className="text-xl font-semibold text-gray-900">Promo & Referral Codes</h2>
                <p className="text-gray-600 text-sm mt-1">Create flexible codes for the whole marketplace, vendors, categories, or specific products.</p>
              </div>
              <button onClick={() => setShowCodeForm(!showCodeForm)} className="bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700">Create Code</button>
            </div>

            {showCodeForm && (
              <div className="bg-white rounded-lg shadow-sm p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Create Promo / Referral Code</h3>
                <form onSubmit={handleCreateCode} className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div><label className="block text-sm font-medium mb-2">Code *</label><input required value={newCode.code} onChange={e => setNewCode({...newCode, code:e.target.value.toUpperCase()})} className="w-full px-3 py-2 border rounded-lg" placeholder="e.g. WELCOME10" /></div>
                  <div><label className="block text-sm font-medium mb-2">Code type</label><select value={newCode.code_type} onChange={e => setNewCode({...newCode, code_type:e.target.value})} className="w-full px-3 py-2 border rounded-lg"><option value="promo">Promo code</option><option value="referral">Referral / influencer</option></select></div>
                  {newCode.code_type === 'referral' && <div><label className="block text-sm font-medium mb-2">Referrer / influencer</label><input value={newCode.influencer_name} onChange={e => setNewCode({...newCode, influencer_name:e.target.value})} className="w-full px-3 py-2 border rounded-lg" placeholder="Optional name" /></div>}
                  <div><label className="block text-sm font-medium mb-2">Discount type *</label><select value={newCode.discount_type} onChange={e => setNewCode({...newCode, discount_type:e.target.value})} className="w-full px-3 py-2 border rounded-lg"><option value="percentage">Percentage discount</option><option value="fixed_amount">Fixed amount off</option><option value="free_delivery">Free delivery</option><option value="cashback">Cashback</option><option value="none">Tracking only</option></select></div>
                  {newCode.discount_type !== 'free_delivery' && newCode.discount_type !== 'none' && <div><label className="block text-sm font-medium mb-2">Value {newCode.discount_type === 'percentage' ? '(%)' : '(₦)'}</label><input type="number" step="0.01" min="0" max={newCode.discount_type === 'percentage' ? 100 : undefined} value={newCode.discount_value} onChange={e => setNewCode({...newCode, discount_value:e.target.value})} className="w-full px-3 py-2 border rounded-lg" /></div>}
                  <div><label className="block text-sm font-medium mb-2">Applies to</label><select value={newCode.target_type} onChange={e => setNewCode({...newCode, target_type:e.target.value, vendor_ids:[], vendor_types:[], categories:[], product_ids:[]})} className="w-full px-3 py-2 border rounded-lg"><option value="global">Entire SchoolVerse</option><option value="vendor">Specific vendor(s)</option><option value="vendor_type">Vendor type</option><option value="category">Category</option><option value="product">Specific product(s)</option></select></div>

                  {newCode.target_type === 'vendor' && <div><label className="block text-sm font-medium mb-2">Vendors</label><select multiple value={newCode.vendor_ids} onChange={e => setNewCode({...newCode, vendor_ids:Array.from(e.target.selectedOptions, o=>o.value)})} className="w-full px-3 py-2 border rounded-lg h-28">{vendors.map(v=><option key={v.id} value={v.id}>{v.name}</option>)}</select></div>}
                  {newCode.target_type === 'vendor_type' && <div><label className="block text-sm font-medium mb-2">Vendor types</label><select multiple value={newCode.vendor_types} onChange={e => setNewCode({...newCode, vendor_types:Array.from(e.target.selectedOptions, o=>o.value)})} className="w-full px-3 py-2 border rounded-lg h-28">{[...new Set(vendors.map(v=>v.vendor_type).filter(Boolean))].map(t=><option key={t} value={t}>{t}</option>)}</select></div>}
                  {newCode.target_type === 'category' && <div><label className="block text-sm font-medium mb-2">Categories</label><select multiple value={newCode.categories} onChange={e => setNewCode({...newCode, categories:Array.from(e.target.selectedOptions, o=>o.value)})} className="w-full px-3 py-2 border rounded-lg h-28">{[...new Set(availableProductsForSection.map(p=>p.category).filter(Boolean))].sort().map(c=><option key={c} value={c}>{c}</option>)}</select></div>}
                  {newCode.target_type === 'product' && <div><label className="block text-sm font-medium mb-2">Products</label><select multiple value={newCode.product_ids} onChange={e => setNewCode({...newCode, product_ids:Array.from(e.target.selectedOptions, o=>o.value)})} className="w-full px-3 py-2 border rounded-lg h-36">{availableProductsForSection.map(p=><option key={p.id} value={p.id}>{p.name} — {p.vendor_name || vendors.find(v=>v.id===p.vendor_id)?.name || 'Vendor'}</option>)}</select></div>}

                  <div><label className="block text-sm font-medium mb-2">Minimum order (₦)</label><input type="number" min="0" value={newCode.min_order_amount} onChange={e=>setNewCode({...newCode,min_order_amount:e.target.value})} className="w-full px-3 py-2 border rounded-lg" /></div>
                  <div><label className="block text-sm font-medium mb-2">Maximum discount (₦)</label><input type="number" min="0" value={newCode.max_discount_amount} onChange={e=>setNewCode({...newCode,max_discount_amount:e.target.value})} className="w-full px-3 py-2 border rounded-lg" placeholder="Optional" /></div>
                  <div><label className="block text-sm font-medium mb-2">Maximum total uses</label><input type="number" min="1" value={newCode.max_uses} onChange={e=>setNewCode({...newCode,max_uses:e.target.value})} className="w-full px-3 py-2 border rounded-lg" placeholder="Unlimited" /></div>
                  <div><label className="block text-sm font-medium mb-2">Uses per customer</label><input type="number" min="1" value={newCode.per_customer_limit} onChange={e=>setNewCode({...newCode,per_customer_limit:e.target.value})} className="w-full px-3 py-2 border rounded-lg" placeholder="Unlimited" /></div>
                  <div><label className="block text-sm font-medium mb-2">Starts</label><input type="datetime-local" value={newCode.valid_from} onChange={e=>setNewCode({...newCode,valid_from:e.target.value})} className="w-full px-3 py-2 border rounded-lg" /></div>
                  <div><label className="block text-sm font-medium mb-2">Expires</label><input type="datetime-local" value={newCode.expires_at} onChange={e=>setNewCode({...newCode,expires_at:e.target.value})} className="w-full px-3 py-2 border rounded-lg" /></div>
                  <div className="md:col-span-2"><label className="block text-sm font-medium mb-2">Description</label><input value={newCode.description} onChange={e=>setNewCode({...newCode,description:e.target.value})} className="w-full px-3 py-2 border rounded-lg" placeholder="What is this promotion for?" /></div>
                  <div className="md:col-span-2"><label className="block text-sm font-medium mb-2">Terms</label><textarea value={newCode.terms_and_conditions} onChange={e=>setNewCode({...newCode,terms_and_conditions:e.target.value})} className="w-full px-3 py-2 border rounded-lg" rows="2" placeholder="Optional customer-facing terms" /></div>
                  <div className="md:col-span-2 flex gap-3"><button type="submit" disabled={loading} className="bg-purple-600 text-white px-6 py-2 rounded-lg disabled:opacity-50">{loading?'Creating...':'Create Code'}</button><button type="button" onClick={()=>setShowCodeForm(false)} className="bg-gray-200 px-6 py-2 rounded-lg">Cancel</button></div>
                </form>
              </div>
            )}

            <div className="bg-white rounded-lg shadow-sm overflow-hidden"><div className="overflow-x-auto"><table className="w-full"><thead className="bg-gray-50"><tr>
              {['Code','Type','Applies to','Discount','Limits','Status','Actions'].map(h=><th key={h} className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">{h}</th>)}
            </tr></thead><tbody className="divide-y divide-gray-200">
              {referralCodes.length===0 ? <tr><td colSpan="7" className="px-6 py-8 text-center text-gray-500">No promo or referral codes yet.</td></tr> : referralCodes.map(code=><tr key={code.id}>
                <td className="px-6 py-4"><span className="font-mono font-bold text-purple-600">{code.code}</span>{code.description && <div className="text-xs text-gray-500 mt-1">{code.description}</div>}</td>
                <td className="px-6 py-4 text-sm">{code.code_type === 'referral' ? `Referral${code.influencer_name ? ` · ${code.influencer_name}` : ''}` : 'Promo'}</td>
                <td className="px-6 py-4 text-sm">{code.target_type === 'vendor' ? `${(code.vendor_ids||[]).length} vendor(s)` : code.target_type === 'vendor_type' ? (code.vendor_types||[]).join(', ') : code.target_type === 'category' ? (code.categories||[]).join(', ') : code.target_type === 'product' ? `${(code.product_ids||[]).length} product(s)` : 'Entire SchoolVerse'}</td>
                <td className="px-6 py-4 text-sm">{code.discount_type==='percentage' ? `${code.discount_value}%` : code.discount_type==='fixed_amount' ? formatCurrency(code.discount_value) : code.discount_type==='free_delivery' ? 'Free delivery' : code.discount_type==='cashback' ? `${formatCurrency(code.discount_value)} cashback` : 'Tracking only'}</td>
                <td className="px-6 py-4 text-sm">{code.usage_count || 0}{code.max_uses ? ` / ${code.max_uses}` : ' / âˆž'}{code.min_order_amount ? <div className="text-xs text-gray-500">Min {formatCurrency(code.min_order_amount)}</div> : null}</td>
                <td className="px-6 py-4">{code.is_active ? <span className="px-2 py-1 text-xs rounded-full bg-green-100 text-green-800">Active</span> : <span className="px-2 py-1 text-xs rounded-full bg-red-100 text-red-800">Inactive</span>}</td>
                <td className="px-6 py-4 text-sm whitespace-nowrap"><button onClick={()=>handleToggleCodeStatus(code.id,code.is_active)} disabled={loading} className="px-3 py-1 rounded-md bg-gray-100 hover:bg-gray-200 mr-2">{code.is_active?'Deactivate':'Activate'}</button><button onClick={()=>handleDeleteCode(code.id)} disabled={loading} className="px-3 py-1 rounded-md bg-red-100 text-red-700">Delete</button></td>
              </tr>)}
            </tbody></table></div></div>
          </div>
        )}

        {/* Banners Tab */}
        {activeTab === 'banners' && (
          <div className="space-y-6">
            {/* Header with Add Button */}
            <div className="bg-white rounded-lg shadow-sm p-6 flex justify-between items-center">
              <div>
                <h2 className="text-xl font-semibold text-gray-900">Banner Management</h2>
                <p className="text-gray-600 text-sm mt-1">Create and manage promotional banners for the platform</p>
              </div>
              <button
                onClick={() => { const opening = !showBannerForm; setShowBannerForm(opening); if (opening) fetchHomepageSections(); }}
                className="bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700 transition-colors flex items-center gap-2"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                Create Banner
              </button>
            </div>

            {/* Create Banner Form */}
            {showBannerForm && (
              <div className="bg-white rounded-lg shadow-sm p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Create New Banner</h3>
                <form onSubmit={async (e) => {
                  e.preventDefault();
                  if (newBanner.location_scope === 'location' && !newBanner.location_id) {
                    toast.error('Choose a target location or select All locations.');
                    return;
                  }
                  setLoading(true);
                  
                  try {
                    const bannerData = {
                      ...newBanner,
                      display_order: parseInt(newBanner.display_order),
                      display_position: Math.max(1, parseInt(newBanner.display_position || 4, 10)),
                      start_date: newBanner.start_date || null,
                      end_date: newBanner.end_date || null,
                      location_scope: newBanner.location_scope || 'all',
                      location_id: newBanner.location_scope === 'location' ? newBanner.location_id : '',
                      location_name: newBanner.location_scope === 'location' ? newBanner.location_name : '' 
                    };
                    
                    const response = await fetch(`${backendUrl}/api/admin/banners`, {
                      method: 'POST',
                      headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${adminToken}`
                      },
                      body: JSON.stringify(bannerData)
                    });
                    
                    if (response.ok) {
                      const result = await response.json();
                      setShowBannerForm(false);
                      setNewBanner({
                        title: '',
                        image_url: '',
                        banner_type: 'top_header',
                        link_url: '',
                        start_date: '',
                        end_date: '',
                        display_order: 0,
                        display_position: 4,
                        location_scope: 'all',
                        location_id: '',
                        location_name: '',
                        target_section_id: ''
                      });
                      toast.success('Banner created successfully!');
                      await fetchBanners();
                    } else {
                      const error = await response.json();
                      toast.error(`Error: ${error.detail}`);
                    }
                  } catch (error) {
                    console.error('Error creating banner:', error);
                    toast.error('Error creating banner');
                  }
                  
                  setLoading(false);
                }} className="space-y-4">
                  
                  {/* Image Upload */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Banner Image *</label>
                    <div className="space-y-2">
                      <input
                        type="file"
                        accept="image/*"
                        onChange={async (e) => {
                          const file = e.target.files[0];
                          if (!file) return;
                          
                          setUploadingImage(true);
                          try {
                            const formData = new FormData();
                            formData.append('file', file);
                            
                            const response = await fetch(`${backendUrl}/api/upload-image`, {
                              method: 'POST',
                              headers: { Authorization: `Bearer ${adminToken}` },
                              body: formData
                            });
                            
                            if (response.ok) {
                              const data = await response.json();
                              setNewBanner({...newBanner, image_url: data.image_url});
                              toast.success('Image uploaded successfully!');
                            } else {
                              const error = await response.json().catch(() => ({}));
                              toast.error(`Upload failed: ${error.detail || `HTTP ${response.status}`}`);
                            }
                          } catch (error) {
                            console.error('Error uploading image:', error);
                            toast.error('Error uploading image');
                          }
                          setUploadingImage(false);
                        }}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                      />
                      {uploadingImage && <p className="text-sm text-blue-600">Uploading image...</p>}
                      {newBanner.image_url && (
                        <div className="mt-2">
                          <img src={getImageUrl(newBanner.image_url)} alt="Preview" className="h-32 rounded-lg object-cover" />
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Title *</label>
                      <input
                        type="text"
                        required
                        value={newBanner.title}
                        onChange={(e) => setNewBanner({...newBanner, title: e.target.value})}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                        placeholder="e.g., Summer Sale"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Banner Type *</label>
                      <select
                        required
                        value={newBanner.banner_type}
                        onChange={(e) => setNewBanner({...newBanner, banner_type: e.target.value})}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                      >
                        <option value="top_header">Top Header (Static)</option>
                        <option value="header_carousel">Header Carousel (Rotating)</option>
                        <option value="slideshow">Restaurant Slideshow</option>
                        <option value="between_cards">Between Restaurant Cards</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Link URL (Optional)</label>
                      <input
                        type="url"
                        value={newBanner.link_url}
                        onChange={(e) => setNewBanner({...newBanner, link_url: e.target.value})}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                        placeholder="https://example.com"
                      />
                    </div>

                    {newBanner.banner_type === 'between_cards' && <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Show after vendor card</label>
                      <input type="number" min="1" max="100" value={newBanner.display_position || 4} onChange={(e) => setNewBanner({...newBanner, display_position: e.target.value})} className="w-full px-3 py-2 border border-gray-300 rounded-lg" />
                      <p className="text-xs text-gray-500 mt-1">For example, 2 places this banner after the second displayed vendor.</p>
                    </div>}
                    {newBanner.banner_type === 'between_cards' && <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Place inside curated homepage section (optional)</label>
                      <select value={newBanner.target_section_id || ''} onChange={(e) => setNewBanner({...newBanner, target_section_id: e.target.value})} className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-white">
                        <option value="">Featured Vendors section (legacy/default)</option>
                        {homepageSections.map((section) => <option key={section.id} value={section.id}>{section.title || 'Untitled section'} · {(section.vendor_ids || []).length ? 'Vendors' : 'Products'}</option>)}
                      </select>
                      <p className="text-xs text-gray-500 mt-1">When selected, the banner is inserted into that admin-curated section after the chosen card position.</p>
                    </div>}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Display Order</label>
                      <input
                        type="number"
                        min="0"
                        value={newBanner.display_order}
                        onChange={(e) => setNewBanner({...newBanner, display_order: e.target.value})}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                        placeholder="0"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Start Date (Optional)</label>
                      <input
                        type="datetime-local"
                        value={newBanner.start_date}
                        onChange={(e) => setNewBanner({...newBanner, start_date: e.target.value})}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">End Date (Optional)</label>
                      <input
                        type="datetime-local"
                        value={newBanner.end_date}
                        onChange={(e) => setNewBanner({...newBanner, end_date: e.target.value})}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 rounded-lg bg-purple-50 p-4 mb-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Banner visibility</label>
                      <select value={newBanner.location_scope || 'all'} onChange={(e) => setNewBanner({...newBanner, location_scope: e.target.value, location_id: e.target.value === 'all' ? '' : newBanner.location_id, location_name: e.target.value === 'all' ? '' : newBanner.location_name})} className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-white">
                        <option value="all">All locations</option><option value="location">Selected location only</option>
                      </select>
                    </div>
                    {newBanner.location_scope === 'location' && <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Target location</label>
                      <select required value={newBanner.location_id || ''} onChange={(e) => { const loc = locations.find((x) => x.id === e.target.value); setNewBanner({...newBanner, location_id: e.target.value, location_name: loc?.name || ''}); }} className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-white">
                        <option value="">Choose a managed location</option>{locations.map((loc) => <option key={loc.id} value={loc.id}>{loc.name}</option>)}
                      </select>
                      {locations.length === 0 && <p className="text-xs text-amber-700 mt-1">No active locations found. Add a location in Location Management first.</p>}
                    </div>}
                  </div>

                  <div className="flex gap-3">
                    <button
                      type="submit"
                      disabled={loading || uploadingImage || !newBanner.image_url}
                      className="bg-purple-600 text-white px-6 py-2 rounded-lg hover:bg-purple-700 transition-colors disabled:opacity-50"
                    >
                      {loading ? 'Creating...' : 'Create Banner'}
                    </button>
                    <button
                      type="button"
                      onClick={() => setShowBannerForm(false)}
                      className="bg-gray-200 text-gray-700 px-6 py-2 rounded-lg hover:bg-gray-300 transition-colors"
                    >
                      Cancel
                    </button>
                  </div>
                </form>
              </div>
            )}

            {/* Banners List */}
            <div className="bg-white rounded-lg shadow-sm overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Preview</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Title</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Type</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Schedule</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Order</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {banners.length === 0 ? (
                      <tr>
                        <td colSpan="7" className="px-6 py-8 text-center text-gray-500">
                          No banners yet. Create your first one!
                        </td>
                      </tr>
                    ) : (
                      banners.map((banner) => (
                        <tr key={banner.id}>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <img 
                              src={getImageUrl(banner.image_url)} 
                              alt={banner.title} 
                              className="h-12 w-20 object-cover rounded"
                            />
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                            {banner.title}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm">
                            <span className={`px-2 py-1 rounded-full text-xs font-semibold ${
                              banner.banner_type === 'top_header' ? 'bg-blue-100 text-blue-800' :
                              banner.banner_type === 'header_carousel' ? 'bg-purple-100 text-purple-800' :
                              banner.banner_type === 'slideshow' ? 'bg-cyan-100 text-cyan-900' :
                              'bg-green-100 text-green-800'
                            }`}>
                              {banner.banner_type === 'top_header' ? 'Top Header' :
                               banner.banner_type === 'header_carousel' ? 'Carousel' :
                               banner.banner_type === 'slideshow' ? 'Restaurant Slideshow' : 'Between Cards'}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-xs text-gray-600">
                            {banner.start_date && banner.end_date ? (
                              <div>
                                <div>{new Date(banner.start_date).toLocaleDateString()}</div>
                                <div>to {new Date(banner.end_date).toLocaleDateString()}</div>
                              </div>
                            ) : banner.start_date ? (
                              `From ${new Date(banner.start_date).toLocaleDateString()}`
                            ) : banner.end_date ? (
                              `Until ${new Date(banner.end_date).toLocaleDateString()}`
                            ) : (
                              'Always active'
                            )}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {banner.display_order}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            {banner.is_active ? (
                              <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800">
                                Active
                              </span>
                            ) : (
                              <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-red-100 text-red-800">
                                Inactive
                              </span>
                            )}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium flex gap-2">
                            <button
                              onClick={async () => {
                                setLoading(true);
                                try {
                                  const response = await fetch(`${backendUrl}/api/admin/banners/${banner.id}/toggle-status`, {
                                    method: 'PUT',
                                    headers: {
                                      'Authorization': `Bearer ${adminToken}`
                                    }
                                  });
                                  
                                  if (response.ok) {
                                    fetchBanners();
                                  } else {
                                    const error = await response.json();
                                    toast.error(`Error: ${error.detail}`);
                                  }
                                } catch (error) {
                                  console.error('Error toggling banner:', error);
                                  toast.error('Error toggling banner status');
                                }
                                setLoading(false);
                              }}
                              disabled={loading}
                              className={`px-3 py-1 rounded-md transition-colors ${
                                banner.is_active
                                  ? 'bg-cyan-100 text-cyan-800 hover:bg-cyan-200'
                                  : 'bg-green-100 text-green-700 hover:bg-green-200'
                              } disabled:opacity-50`}
                            >
                              {banner.is_active ? 'Deactivate' : 'Activate'}
                            </button>
                            <button
                              onClick={async () => {
                                if (!confirm('Are you sure you want to delete this banner?')) return;
                                
                                setLoading(true);
                                try {
                                  const response = await fetch(`${backendUrl}/api/admin/banners/${banner.id}`, {
                                    method: 'DELETE',
                                    headers: {
                                      'Authorization': `Bearer ${adminToken}`
                                    }
                                  });
                                  
                                  if (response.ok) {
                                    toast.success('Banner deleted successfully!');
                                    fetchBanners();
                                  } else {
                                    const error = await response.json();
                                    toast.error(`Error: ${error.detail}`);
                                  }
                                } catch (error) {
                                  console.error('Error deleting banner:', error);
                                  toast.error('Error deleting banner');
                                }
                                setLoading(false);
                              }}
                              disabled={loading}
                              className="px-3 py-1 rounded-md bg-red-100 text-red-700 hover:bg-red-200 transition-colors disabled:opacity-50"
                            >
                              Delete
                            </button>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}


        {/* Locations Tab */}
        {activeTab === 'locations' && (
          <div className="bg-white rounded-lg shadow-sm overflow-hidden">
            <div className="p-6 border-b border-gray-200 flex justify-between items-center">
              <div>
                <h2 className="text-xl font-semibold text-gray-900">Location Management</h2>
                <p className="text-gray-600 text-sm mt-1">Manage locations where restaurants operate</p>
              </div>
              <button
                onClick={() => {
                  setShowLocationForm(true);
                  setEditingLocation(null);
                  setNewLocation({ name: '', delivery_zones: '', special_notes: '' });
                }}
                className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors flex items-center gap-2"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                Add Location
              </button>
            </div>
            
            {/* Location Form Modal */}
            {showLocationForm && (
              <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md">
                  <h3 className="text-lg font-semibold mb-4">{editingLocation ? 'Edit Location' : 'Add New Location'}</h3>
                  <form onSubmit={editingLocation ? handleUpdateLocation : handleCreateLocation} className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Location Name *</label>
                      <input
                        type="text"
                        value={newLocation.name}
                        onChange={(e) => setNewLocation({...newLocation, name: e.target.value})}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                        placeholder="e.g., Lagos Island, Lekki Phase 1"
                        required
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Delivery Zones</label>
                      <input
                        type="text"
                        value={newLocation.delivery_zones}
                        onChange={(e) => setNewLocation({...newLocation, delivery_zones: e.target.value})}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                        placeholder="e.g., Victoria Island, Ikoyi (comma-separated)"
                      />
                      <p className="text-xs text-gray-500 mt-1">Separate multiple zones with commas</p>
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Special Notes</label>
                      <textarea
                        value={newLocation.special_notes}
                        onChange={(e) => setNewLocation({...newLocation, special_notes: e.target.value})}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                        rows="3"
                        placeholder="Any special notes about this location..."
                      ></textarea>
                    </div>
                    
                    <div className="flex gap-3 pt-4">
                      <button
                        type="submit"
                        disabled={loading}
                        className="flex-1 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors disabled:opacity-50"
                      >
                        {loading ? 'Saving...' : (editingLocation ? 'Update Location' : 'Create Location')}
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          setShowLocationForm(false);
                          setEditingLocation(null);
                          setNewLocation({ name: '', delivery_zones: '', special_notes: '' });
                        }}
                        className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                      >
                        Cancel
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            )}
            
            {/* Locations List */}
            <div className="p-6">
              {loading && (locations || []).length === 0 ? (
                <div className="text-center py-8 text-gray-500">Loading locations...</div>
              ) : (locations || []).length === 0 ? (
                <div className="text-center py-12">
                  <svg className="w-16 h-16 mx-auto text-gray-300 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                  <p className="text-gray-500 text-lg">No locations yet</p>
                  <p className="text-gray-400 text-sm mt-1">Click "Add Location" to create your first location</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-gray-200">
                        <th className="text-left py-3 px-4 font-semibold text-gray-700">Location Name</th>
                        <th className="text-left py-3 px-4 font-semibold text-gray-700">Delivery Zones</th>
                        <th className="text-left py-3 px-4 font-semibold text-gray-700">Special Notes</th>
                        <th className="text-left py-3 px-4 font-semibold text-gray-700">Status</th>
                        <th className="text-right py-3 px-4 font-semibold text-gray-700">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(locations || []).map((location) => (
                        <tr key={location.id} className="border-b border-gray-100 hover:bg-gray-50">
                          <td className="py-3 px-4 font-medium text-gray-900">{location.name}</td>
                          <td className="py-3 px-4 text-gray-600">
                            {location.delivery_zones && location.delivery_zones.length > 0 ? (
                              <span className="text-sm">{location.delivery_zones.join(', ')}</span>
                            ) : (
                              <span className="text-gray-400 text-sm">No zones specified</span>
                            )}
                          </td>
                          <td className="py-3 px-4 text-gray-600 max-w-xs truncate">
                            {location.special_notes || '-'}
                          </td>
                          <td className="py-3 px-4">
                            <button
                              onClick={() => handleToggleLocationStatus(location.id, location.is_active)}
                              className={`px-3 py-1 rounded-full text-xs font-medium ${
                                location.is_active
                                  ? 'bg-green-100 text-green-700 hover:bg-green-200'
                                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                              }`}
                            >
                              {location.is_active ? 'Active' : 'Inactive'}
                            </button>
                          </td>
                          <td className="py-3 px-4">
                            <div className="flex items-center justify-end gap-2">
                              <button
                                onClick={() => handleEditLocation(location)}
                                className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                                title="Edit"
                              >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                </svg>
                              </button>
                              <button
                                onClick={() => handleDeleteLocation(location.id)}
                                className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                                title="Delete"
                              >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                </svg>
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Categories Tab */}
        {activeTab === 'categories' && (
          <div className="bg-white rounded-lg shadow-sm overflow-hidden">
            <div className="p-6 border-b border-gray-200 flex items-center justify-between">
              <div>
                <h2 className="text-xl font-semibold text-gray-900">Category Management</h2>
                <p className="text-gray-600 text-sm mt-1">Create and manage categories for vendors. These categories will appear on the homepage.</p>
              </div>
              <button
                onClick={() => {
                  setEditingCategory(null);
                  setNewCategory({
                    name: '',
                    description: '',
                    icon: '',
                    image_url: '',
                    vendor_type: 'food',
                    order: 0,
                    is_active: true
                  });
                  setShowCategoryForm(true);
                }}
                className="bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700 flex items-center gap-2"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                </svg>
                Add Category
              </button>
            </div>

            {/* Category Form Modal */}
            {showCategoryForm && (
              <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                <div className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4 max-h-[90vh] overflow-y-auto">
                  <div className="p-6 border-b border-gray-200">
                    <h3 className="text-lg font-semibold text-gray-900">
                      {editingCategory ? 'Edit Category' : 'Add New Category'}
                    </h3>
                  </div>
                  <form onSubmit={editingCategory ? handleUpdateCategory : handleCreateCategory} className="p-6 space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Category Name *</label>
                      <input
                        type="text"
                        required
                        value={newCategory.name}
                        onChange={(e) => setNewCategory({...newCategory, name: e.target.value})}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                        placeholder="e.g., Nigerian Food"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                      <textarea
                        value={newCategory.description}
                        onChange={(e) => setNewCategory({...newCategory, description: e.target.value})}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                        rows={2}
                        placeholder="Brief description of this category"
                      />
                    </div>
                    
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Icon (optional)</label>
                        <select
                          value={newCategory.icon || ''}
                          onChange={(e) => setNewCategory({...newCategory, icon: e.target.value})}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                        >
                          <option value="">No icon</option>
                          <option value="store">Vendor</option>
                          <option value="star">Featured</option>
                          <option value="box">Products</option>
                          <option value="grid">Collection</option>
                        </select>
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Display Order</label>
                        <input
                          type="number"
                          value={newCategory.order}
                          onChange={(e) => setNewCategory({...newCategory, order: parseInt(e.target.value) || 0})}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                          min={0}
                        />
                      </div>
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Vendor Type</label>
                      <select
                        value={newCategory.vendor_type}
                        onChange={(e) => setNewCategory({...newCategory, vendor_type: e.target.value})}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                      >
                        <option value="food"> Food & Restaurants</option>
                        <option value="products"> Products & Commodities</option>
                        <option value="services"> Services & Handymen</option>
                        <option value="property"> Property Listings</option>
                        <option value="events"> Events & Tickets</option>
                        <option value="giftcards"> Gift Cards</option>
                      </select>
                    </div>
                    
                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        id="category-active"
                        checked={newCategory.is_active}
                        onChange={(e) => setNewCategory({...newCategory, is_active: e.target.checked})}
                        className="h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300 rounded"
                      />
                      <label htmlFor="category-active" className="ml-2 block text-sm text-gray-900">
                        Category is Active
                      </label>
                    </div>
                    
                    <div className="flex gap-3 pt-4">
                      <button
                        type="button"
                        onClick={() => {
                          setShowCategoryForm(false);
                          setEditingCategory(null);
                        }}
                        className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                      >
                        Cancel
                      </button>
                      <button
                        type="submit"
                        disabled={loading}
                        className="flex-1 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50"
                      >
                        {loading ? 'Saving...' : (editingCategory ? 'Update' : 'Create')}
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            )}

            <div className="p-6">
              {categories.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  <svg className="w-16 h-16 mx-auto mb-4 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
                  </svg>
                  <p className="text-gray-500 text-lg">No categories yet</p>
                  <p className="text-gray-400 text-sm mt-1">Click "Add Category" to create your first category</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {categories.map((category, index) => (
                    <div key={category.id} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                      <div className="flex items-center gap-4">
                        <div className="flex flex-col gap-1">
                          <button
                            onClick={() => handleMoveCategory(category.id, 'up')}
                            disabled={index === 0}
                            className={`p-1 rounded ${index === 0 ? 'text-gray-300 cursor-not-allowed' : 'text-gray-500 hover:bg-gray-200'}`}
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                            </svg>
                          </button>
                          <button
                            onClick={() => handleMoveCategory(category.id, 'down')}
                            disabled={index === categories.length - 1}
                            className={`p-1 rounded ${index === categories.length - 1 ? 'text-gray-300 cursor-not-allowed' : 'text-gray-500 hover:bg-gray-200'}`}
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                            </svg>
                          </button>
                        </div>
                        
                        <UiIcon name={category.icon} size={24} className="text-gray-700" />
                        
                        <div>
                          <h4 className="font-semibold text-gray-900">{category.name}</h4>
                          <p className="text-sm text-gray-500">
                            {category.vendor_type === 'food' && ' Food'}
                            {category.vendor_type === 'products' && ' Products'}
                            {category.vendor_type === 'services' && ' Services'}
                            {category.vendor_type === 'property' && ' Property'}
                            {category.vendor_type === 'events' && ' Events'}
                            {category.vendor_type === 'giftcards' && ' Gift Cards'}
                            {category.description && ` ? ${category.description}`}
                          </p>
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-3">
                        <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                          category.is_active 
                            ? 'bg-green-100 text-green-700' 
                            : 'bg-gray-100 text-gray-600'
                        }`}>
                          {category.is_active ? 'Active' : 'Inactive'}
                        </span>
                        
                        <button
                          onClick={() => {
                            setEditingCategory(category);
                            setNewCategory({
                              name: category.name,
                              description: category.description || '',
                              icon: category.icon || '',
                              image_url: category.image_url || '',
                              vendor_type: category.vendor_type || 'food',
                              order: category.order || 0,
                              is_active: category.is_active !== false
                            });
                            setShowCategoryForm(true);
                          }}
                          className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                          title="Edit"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                          </svg>
                        </button>
                        
                        <button
                          onClick={() => handleDeleteCategory(category.id)}
                          className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                          title="Delete"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Deals & Promotions Admin Tab */}
        {activeTab === 'page-experience' && (
          <Suspense fallback={<div className="p-6 text-sm text-gray-500">Loading page settings…</div>}>
            <AdminPageExperienceTab backendUrl={backendUrl} adminToken={adminToken} />
          </Suspense>
        )}

        {activeTab === 'analytics' && (
          <Suspense fallback={<div className="p-6 text-sm text-gray-500">Loading analytics…</div>}>
            <AdminAnalyticsTab backendUrl={backendUrl} adminToken={adminToken} />
          </Suspense>
        )}

        {activeTab === 'deals-promotions' && (
          <Suspense fallback={<div className="p-6 text-sm text-gray-500">Loading promotions…</div>}>
            <DealsPromotionsAdminTab
              backendUrl={backendUrl}
              adminToken={adminToken}
              availableProducts={availableProductsForSection}
              availableVendors={availableVendorsForSection}
            />
          </Suspense>
        )}

        {/* Flash Deals Admin Tab */}
        {activeTab === 'flash-deals' && (
          <Suspense fallback={<div className="p-6 text-sm text-gray-500">Loading flash deals…</div>}>
            <FlashDealsAdminTab
              backendUrl={backendUrl}
              adminToken={adminToken}
              availableProducts={availableProductsForSection}
              onRefresh={fetchAvailableProducts}
            />
          </Suspense>
        )}


        {/* Fulfillment Tab */}
        {activeTab === 'fulfillment' && (
          <div className="space-y-6">
            <div className="bg-white rounded-lg shadow-sm p-6 border border-gray-200">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <h2 className="text-xl font-semibold text-gray-900">WhatsApp Direct Fulfillment</h2>
                  <p className="text-gray-600 text-sm mt-1">Control which vendors are allowed to receive direct customer orders through WhatsApp.</p>
                </div>
                <span className="px-3 py-1 rounded-full bg-green-50 text-green-700 text-xs font-semibold whitespace-nowrap">Admin controlled</span>
              </div>
              <div className="mt-4 p-4 rounded-lg bg-gray-50 border border-gray-200 text-sm text-gray-600">
                A vendor must have a WhatsApp/business phone number before this can be enabled. This setting is independent for each vendor.
              </div>
            </div>

            <div className="bg-white rounded-lg shadow-sm overflow-hidden border border-gray-200">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Vendor</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Vendor type</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">WhatsApp / phone</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Direct fulfillment</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {restaurants.map((restaurant) => {
                      const phone = restaurant.business_phone || restaurant.phone || '';
                      const enabled = restaurant.whatsapp_direct_fulfillment_enabled === true;
                      return (
                        <tr key={restaurant.id}>
                          <td className="px-6 py-4">
                            <div className="font-medium text-gray-900">{restaurant.name}</div>
                            <div className="text-xs text-gray-500 mt-1">{restaurant.enabled === false ? 'Vendor disabled' : 'Vendor active'}</div>
                          </td>
                          <td className="px-6 py-4 text-sm text-gray-600">{restaurant.vendor_type || restaurant.category || '—'}</td>
                          <td className="px-6 py-4 text-sm text-gray-600">{phone || 'No phone number'}</td>
                          <td className="px-6 py-4">
                            <button
                              type="button"
                              disabled={!phone}
                              onClick={() => handleToggleVendorWhatsAppDirect(restaurant.id, !enabled)}
                              className={`px-4 py-2 rounded-lg text-sm font-semibold transition-colors ${
                                enabled
                                  ? 'bg-green-100 text-green-700 hover:bg-green-200'
                                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                              } disabled:opacity-50 disabled:cursor-not-allowed`}
                            >
                              {enabled ? 'Enabled' : 'Off'}
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {/* Homepage Sections Tab */}
        {activeTab === 'homepage-sections' && (
          <div className="bg-white rounded-lg shadow-sm overflow-hidden">
            <div className="p-6 border-b border-gray-200 flex items-center justify-between">
              <div>
                <h2 className="text-xl font-semibold text-gray-900">Homepage Sections</h2>
                <p className="text-gray-600 text-sm mt-1">Create and manage custom sections for your homepage. Add vendors to each section.</p>
              </div>
              <button
                onClick={() => {
                  setEditingSection(null);
                  setNewSection({
                    title: '',
                    description: '',
                    icon: '',
                    vendor_ids: [],
                    product_ids: [],
                    order: 0,
                    is_active: true,
                    background_color: '',
                    show_view_all: true,
                    display_mode: 'full',
                    max_items: 12,
                    location_mode: 'all'
                  });
                  setShowSectionForm(true);
                }}
                className="bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700 flex items-center gap-2"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                </svg>
                Add Section
              </button>
            </div>

            {/* Explicit homepage vendor control */}
            <div className="p-6 border-b border-gray-200 bg-slate-50">
              <div className="flex items-start justify-between gap-4 mb-4">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">Homepage vendor visibility</h3>
                  <p className="text-sm text-gray-600 mt-1">Approved vendors are not shown on the homepage unless you explicitly feature them here.</p>
                </div>
                <button onClick={saveHomepageVendorSettings} className="bg-slate-900 text-white px-4 py-2 rounded-lg hover:bg-slate-800">Save vendor display</button>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Homepage section title</label>
                  <input value={homepageVendorSettings.featured_vendor_title || ''} onChange={(e) => setHomepageVendorSettings({...homepageVendorSettings, featured_vendor_title: e.target.value})} className="w-full px-3 py-2 border border-gray-300 rounded-lg" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Maximum vendors</label>
                  <input type="number" min="1" max="50" value={homepageVendorSettings.featured_vendor_limit || 12} onChange={(e) => setHomepageVendorSettings({...homepageVendorSettings, featured_vendor_limit: Number(e.target.value) || 1})} className="w-full px-3 py-2 border border-gray-300 rounded-lg" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Location behavior</label>
                  <select value={homepageVendorSettings.featured_vendor_location_mode || 'selected_location'} onChange={(e) => setHomepageVendorSettings({...homepageVendorSettings, featured_vendor_location_mode: e.target.value})} className="w-full px-3 py-2 border border-gray-300 rounded-lg">
                    <option value="selected_location">Prioritize selected location</option>
                    <option value="all">All locations</option>
                  </select>
                </div>
              </div>
              <label className="flex items-center gap-2 text-sm text-gray-700 mb-4">
                <input type="checkbox" checked={homepageVendorSettings.featured_vendor_show_view_all !== false} onChange={(e) => setHomepageVendorSettings({...homepageVendorSettings, featured_vendor_show_view_all: e.target.checked})} />
                Show “See more” link
              </label>
              <div className="border border-gray-200 rounded-lg bg-white max-h-64 overflow-y-auto">
                {availableVendorsForSection.map(vendor => {
                  const selected = (homepageVendorSettings.featured_vendor_ids || []).includes(vendor.id);
                  return (
                    <label key={vendor.id} className="flex items-center gap-3 px-4 py-3 border-b last:border-b-0 cursor-pointer hover:bg-gray-50">
                      <input type="checkbox" checked={selected} onChange={(e) => setHomepageVendorSettings({...homepageVendorSettings, featured_vendor_ids: e.target.checked ? [...(homepageVendorSettings.featured_vendor_ids || []), vendor.id] : (homepageVendorSettings.featured_vendor_ids || []).filter(id => id !== vendor.id)})} />
                      <img src={getImageUrl(vendor.image_url)} alt="" className="w-9 h-9 rounded-lg object-cover" />
                      <span className="flex-1 font-medium text-gray-900">{vendor.name}</span>
                      <span className="text-xs text-gray-500">{vendor.location || 'Location not set'}</span>
                    </label>
                  );
                })}
                {availableVendorsForSection.length === 0 && <p className="p-4 text-sm text-gray-500">No approved vendors available.</p>}
              </div>
              <p className="text-xs text-gray-500 mt-2">Selected: {(homepageVendorSettings.featured_vendor_ids || []).length}. Order follows the selection list; customers in a selected location see matching vendors first.</p>
            </div>

            {/* Section Form Modal */}
            {showSectionForm && (
              <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl mx-4 max-h-[90vh] overflow-y-auto">
                  <div className="p-6 border-b border-gray-200">
                    <h3 className="text-lg font-semibold text-gray-900">
                      {editingSection ? 'Edit Section' : 'Create New Section'}
                    </h3>
                  </div>
                  <form onSubmit={editingSection ? handleUpdateSection : handleCreateSection} className="p-6 space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div className="col-span-2 md:col-span-1">
                        <label className="block text-sm font-medium text-gray-700 mb-1">Section Title *</label>
                        <input
                          type="text"
                          required
                          value={newSection.title}
                          onChange={(e) => setNewSection({...newSection, title: e.target.value})}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                          placeholder="e.g., Trending Now, Featured, Top Picks"
                        />
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Section icon (optional)</label>
                        <select
                          value={newSection.icon || ''}
                          onChange={(e) => setNewSection({...newSection, icon: e.target.value})}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                        >
                          <option value="">No icon</option>
                          <option value="store">Vendors</option>
                          <option value="star">Featured</option>
                          <option value="box">Products</option>
                          <option value="grid">Collection</option>
                        </select>
                      </div>
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Description (Optional)</label>
                      <input
                        type="text"
                        value={newSection.description}
                        onChange={(e) => setNewSection({...newSection, description: e.target.value})}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                        placeholder="Brief description of this section"
                      />
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Display layout</label>
                        <select value={newSection.display_mode} onChange={(e) => setNewSection({...newSection, display_mode: e.target.value})} className="w-full px-3 py-2 border border-gray-300 rounded-lg">
                          <option value="full">Show all</option>
                          <option value="slider">One-line slider</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Maximum items</label>
                        <input type="number" min="1" max="50" value={newSection.max_items} onChange={(e) => setNewSection({...newSection, max_items: Number(e.target.value) || 1})} className="w-full px-3 py-2 border border-gray-300 rounded-lg" />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Location behavior</label>
                        <select value={newSection.location_mode} onChange={(e) => setNewSection({...newSection, location_mode: e.target.value})} className="w-full px-3 py-2 border border-gray-300 rounded-lg">
                          <option value="all">All locations</option>
                          <option value="selected_location">Prioritize selected location</option>
                        </select>
                      </div>
                    </div>
                    
                    {/* Content Type Toggle - Vendors or Products */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Section Content Type</label>
                      <div className="flex gap-2 mb-4">
                        <button
                          type="button"
                          onClick={() => setSectionContentType('vendors')}
                          className={`flex-1 py-2 px-4 rounded-lg font-medium transition-colors ${
                            sectionContentType === 'vendors'
                              ? 'bg-purple-600 text-white'
                              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                          }`}
                        >
                           Vendors ({newSection.vendor_ids.length})
                        </button>
                        <button
                          type="button"
                          onClick={() => setSectionContentType('products')}
                          className={`flex-1 py-2 px-4 rounded-lg font-medium transition-colors ${
                            sectionContentType === 'products'
                              ? 'bg-cyan-500 text-white'
                              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                          }`}
                        >
                           Products ({newSection.product_ids?.length || 0})
                        </button>
                      </div>
                    </div>
                    
                    {/* Vendor Selection - Show when vendors tab is active */}
                    {sectionContentType === 'vendors' && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Select Vendors for this Section ({newSection.vendor_ids.length} selected)
                        </label>
                        
                        {/* Selected Vendors */}
                        {newSection.vendor_ids.length > 0 && (
                          <div className="mb-3 flex flex-wrap gap-2">
                            {newSection.vendor_ids.map(vendorId => {
                              const vendor = availableVendorsForSection.find(v => v.id === vendorId);
                              return vendor ? (
                                <span key={vendorId} className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-purple-100 text-purple-700">
                                  {vendor.name}
                                  <button
                                    type="button"
                                    onClick={() => removeVendorFromSection(vendorId)}
                                    className="ml-2 text-purple-500 hover:text-purple-700"
                                  >
                                    ×
                                  </button>
                                </span>
                              ) : null;
                            })}
                          </div>
                        )}
                        
                        {/* Search and Add Vendors */}
                        <input
                          type="text"
                          value={sectionVendorSearch}
                          onChange={(e) => setSectionVendorSearch(e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent mb-2"
                          placeholder="Search vendors to add..."
                        />
                        
                        <div className="max-h-48 overflow-y-auto border border-gray-200 rounded-lg">
                          {availableVendorsForSection
                            .filter(v => 
                              !newSection.vendor_ids.includes(v.id) &&
                              (sectionVendorSearch === '' || 
                               v.name.toLowerCase().includes(sectionVendorSearch.toLowerCase()))
                            )
                            .slice(0, 20)
                            .map(vendor => (
                              <div
                                key={vendor.id}
                                onClick={() => addVendorToSection(vendor.id)}
                                className="flex items-center gap-3 p-3 hover:bg-gray-50 cursor-pointer border-b border-gray-100 last:border-b-0"
                              >
                                <img
                                  src={vendor.image_url || 'https://via.placeholder.com/40'}
                                  alt={vendor.name}
                                  className="w-10 h-10 rounded-lg object-cover"
                                />
                                <div className="flex-1">
                                  <p className="font-medium text-gray-900">{vendor.name}</p>
                                  <p className="text-xs text-gray-500">{vendor.category}</p>
                                </div>
                                <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                                </svg>
                              </div>
                            ))
                          }
                          {availableVendorsForSection.filter(v => 
                            !newSection.vendor_ids.includes(v.id) &&
                            (sectionVendorSearch === '' || v.name.toLowerCase().includes(sectionVendorSearch.toLowerCase()))
                          ).length === 0 && (
                            <p className="p-4 text-center text-gray-500">No vendors found</p>
                          )}
                        </div>
                      </div>
                    )}
                    
                    {/* Product Selection - Show when products tab is active */}
                    {sectionContentType === 'products' && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Select Products for this Section ({newSection.product_ids?.length || 0} selected)
                        </label>
                        
                        {/* Selected Products */}
                        {newSection.product_ids?.length > 0 && (
                          <div className="mb-3 flex flex-wrap gap-2">
                            {newSection.product_ids.map(productId => {
                              const product = availableProductsForSection.find(p => p.id === productId);
                              return product ? (
                                <span key={productId} className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-cyan-100 text-cyan-800">
                                  {product.name}
                                  <button
                                    type="button"
                                    onClick={() => setNewSection({
                                      ...newSection,
                                      product_ids: newSection.product_ids.filter(id => id !== productId)
                                    })}
                                    className="ml-2 text-cyan-500 hover:text-cyan-800"
                                  >
                                    ×
                                  </button>
                                </span>
                              ) : null;
                            })}
                          </div>
                        )}
                        
                        {/* Search and Add Products */}
                        <input
                          type="text"
                          value={sectionProductSearch}
                          onChange={(e) => setSectionProductSearch(e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-transparent mb-2"
                          placeholder="Search products to add..."
                        />
                        
                        <div className="max-h-48 overflow-y-auto border border-gray-200 rounded-lg">
                          {availableProductsForSection
                            .filter(p => 
                              !(newSection.product_ids || []).includes(p.id) &&
                              (sectionProductSearch === '' || 
                               p.name.toLowerCase().includes(sectionProductSearch.toLowerCase()))
                            )
                            .slice(0, 20)
                            .map(product => (
                              <div
                                key={product.id}
                                onClick={() => setNewSection({
                                  ...newSection,
                                  product_ids: [...(newSection.product_ids || []), product.id]
                                })}
                                className="flex items-center gap-3 p-3 hover:bg-cyan-50 cursor-pointer border-b border-gray-100 last:border-b-0"
                              >
                                <img
                                  src={product.image_url || 'https://via.placeholder.com/40'}
                                  alt={product.name}
                                  className="w-10 h-10 rounded-lg object-cover"
                                />
                                <div className="flex-1">
                                  <p className="font-medium text-gray-900">{product.name}</p>
                                  <p className="text-xs text-gray-500">{product.vendor_name || 'Unknown vendor'} • ₦{product.price}</p>
                                </div>
                                <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                                </svg>
                              </div>
                            ))
                          }
                          {availableProductsForSection.filter(p => 
                            !(newSection.product_ids || []).includes(p.id) &&
                            (sectionProductSearch === '' || p.name.toLowerCase().includes(sectionProductSearch.toLowerCase()))
                          ).length === 0 && (
                            <p className="p-4 text-center text-gray-500">No products found</p>
                          )}
                        </div>
                      </div>
                    )}
                    
                    <div className="flex items-center gap-4">
                      <label className="flex items-center">
                        <input
                          type="checkbox"
                          checked={newSection.is_active}
                          onChange={(e) => setNewSection({...newSection, is_active: e.target.checked})}
                          className="h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300 rounded"
                        />
                        <span className="ml-2 text-sm text-gray-900">Section is Active</span>
                      </label>
                      
                      <label className="flex items-center">
                        <input
                          type="checkbox"
                          checked={newSection.show_view_all}
                          onChange={(e) => setNewSection({...newSection, show_view_all: e.target.checked})}
                          className="h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300 rounded"
                        />
                        <span className="ml-2 text-sm text-gray-900">Show "View All" link</span>
                      </label>
                    </div>
                    
                    <div className="flex gap-3 pt-4">
                      <button
                        type="button"
                        onClick={() => {
                          setShowSectionForm(false);
                          setEditingSection(null);
                          setSectionVendorSearch('');
                          setSectionProductSearch('');
                        }}
                        className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                      >
                        Cancel
                      </button>
                      <button
                        type="submit"
                        disabled={loading || (newSection.vendor_ids.length === 0 && (newSection.product_ids?.length || 0) === 0)}
                        className="flex-1 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50"
                      >
                        {loading ? 'Saving...' : (editingSection ? 'Update Section' : 'Create Section')}
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            )}

            <div className="p-6">
              {homepageSections.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  <svg className="w-16 h-16 mx-auto mb-4 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 5a1 1 0 011-1h14a1 1 0 011 1v2a1 1 0 01-1 1H5a1 1 0 01-1-1V5zM4 13a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H5a1 1 0 01-1-1v-6z" />
                  </svg>
                  <p className="text-gray-500 text-lg">No homepage sections yet</p>
                  <p className="text-gray-400 text-sm mt-1">Create sections like "Trending", "Featured", "Top Picks" and add vendors to them</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {homepageSections.map((section, index) => (
                    <div key={section.id} className="border border-gray-200 rounded-lg overflow-hidden">
                      <div className="flex items-center justify-between p-4 bg-gray-50">
                        <div className="flex items-center gap-4">
                          <div className="flex flex-col gap-1">
                            <button
                              onClick={() => handleMoveSection(section.id, 'up')}
                              disabled={index === 0}
                              className={`p-1 rounded ${index === 0 ? 'text-gray-300 cursor-not-allowed' : 'text-gray-500 hover:bg-gray-200'}`}
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                              </svg>
                            </button>
                            <button
                              onClick={() => handleMoveSection(section.id, 'down')}
                              disabled={index === homepageSections.length - 1}
                              className={`p-1 rounded ${index === homepageSections.length - 1 ? 'text-gray-300 cursor-not-allowed' : 'text-gray-500 hover:bg-gray-200'}`}
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                              </svg>
                            </button>
                          </div>
                          
                          <UiIcon name={section.icon} size={24} className="text-gray-700" />
                          
                          <div>
                            <h4 className="font-semibold text-gray-900 text-lg">{section.title}</h4>
                            <p className="text-sm text-gray-500">
                              {(section.vendor_count || section.vendor_ids?.length || 0) > 0 && (
                                <span className="text-purple-600">{section.vendor_count || section.vendor_ids?.length || 0} vendors</span>
                              )}
                              {(section.vendor_count || section.vendor_ids?.length || 0) > 0 && (section.product_count || section.product_ids?.length || 0) > 0 && ' ? '}
                              {(section.product_count || section.product_ids?.length || 0) > 0 && (
                                <span className="text-cyan-700">{section.product_count || section.product_ids?.length || 0} products</span>
                              )}
                              {section.description && ` ? ${section.description}`}
                            </p>
                          </div>
                        </div>
                        
                        <div className="flex items-center gap-3">
                          <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                            section.is_active 
                              ? 'bg-green-100 text-green-700' 
                              : 'bg-gray-100 text-gray-600'
                          }`}>
                            {section.is_active ? 'Active' : 'Inactive'}
                          </span>
                          
                          <button
                            onClick={() => {
                              setEditingSection(section);
                              setNewSection({
                                title: section.title,
                                description: section.description || '',
                                icon: section.icon || '',
                                vendor_ids: section.vendor_ids || [],
                                product_ids: section.product_ids || [],
                                order: section.order || 0,
                                is_active: section.is_active !== false,
                                background_color: section.background_color || '',
                                show_view_all: section.show_view_all !== false,
                                display_mode: section.display_mode || 'full',
                                max_items: section.max_items || 12,
                                location_mode: section.location_mode || 'all'
                              });
                              setSectionContentType(section.product_ids?.length > 0 ? 'products' : 'vendors');
                              setShowSectionForm(true);
                            }}
                            className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                            title="Edit"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                            </svg>
                          </button>
                          
                          <button
                            onClick={() => handleDeleteSection(section.id)}
                            className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                            title="Delete"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}


        {/* Browse Categories (Vendor Types) Tab */}
        {activeTab === 'browse-categories' && (
          <div className="bg-white rounded-lg shadow-sm overflow-hidden">
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-xl font-semibold text-gray-900">Browse Categories</h2>
                  <p className="text-gray-600 text-sm mt-1">Manage the category buttons shown on the homepage (All, Food, Products, Services, etc.)</p>
                </div>
                <button
                  onClick={() => {
                    setEditingBrowseCategory(null);
                    setNewBrowseCategory({ type: '', label: '', icon: '', description: '', color: 'bg-gray-500', is_active: true });
                    setShowBrowseCategoryForm(true);
                  }}
                  className="flex items-center gap-2 bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                  </svg>
                  Add Category
                </button>
              </div>
            </div>
            
            {/* Add/Edit Form */}
            {showBrowseCategoryForm && (
              <div className="p-6 bg-gray-50 border-b border-gray-200">
                <form onSubmit={editingBrowseCategory ? handleUpdateBrowseCategory : handleCreateBrowseCategory} className="space-y-4">
                  <h3 className="text-lg font-semibold text-gray-900">
                    {editingBrowseCategory ? 'Edit Category' : 'Add New Category'}
                  </h3>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Label *</label>
                      <input
                        type="text"
                        value={newBrowseCategory.label}
                        onChange={(e) => setNewBrowseCategory({...newBrowseCategory, label: e.target.value})}
                        placeholder="e.g., Electronics"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                        required
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Icon (optional)</label>
                      <select
                        value={newBrowseCategory.icon || ''}
                        onChange={(e) => setNewBrowseCategory({...newBrowseCategory, icon: e.target.value})}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                      >
                        <option value="">No icon</option>
                        <option value="store">Vendor</option>
                        <option value="star">Featured</option>
                        <option value="box">Products</option>
                        <option value="grid">Collection</option>
                      </select>
                    </div>
                    
                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                      <input
                        type="text"
                        value={newBrowseCategory.description}
                        onChange={(e) => setNewBrowseCategory({...newBrowseCategory, description: e.target.value})}
                        placeholder="Short description of this category"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Color</label>
                      <select
                        value={newBrowseCategory.color}
                        onChange={(e) => setNewBrowseCategory({...newBrowseCategory, color: e.target.value})}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                      >
                        <option value="bg-cyan-500">Orange</option>
                        <option value="bg-blue-500">Blue</option>
                        <option value="bg-green-500">Green</option>
                        <option value="bg-purple-500">Purple</option>
                        <option value="bg-pink-500">Pink</option>
                        <option value="bg-yellow-500">Yellow</option>
                        <option value="bg-red-500">Red</option>
                        <option value="bg-gray-500">Gray</option>
                        <option value="bg-teal-500">Teal</option>
                        <option value="bg-indigo-500">Indigo</option>
                      </select>
                    </div>
                    
                    <div className="flex items-center">
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={newBrowseCategory.is_active}
                          onChange={(e) => setNewBrowseCategory({...newBrowseCategory, is_active: e.target.checked})}
                          className="w-4 h-4 text-green-600 rounded focus:ring-green-500"
                        />
                        <span className="text-sm font-medium text-gray-700">Active (visible on homepage)</span>
                      </label>
                    </div>
                  </div>
                  
                  <div className="flex gap-3 pt-2">
                    <button
                      type="submit"
                      disabled={loading}
                      className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50"
                    >
                      {loading ? 'Saving...' : (editingBrowseCategory ? 'Update Category' : 'Create Category')}
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setShowBrowseCategoryForm(false);
                        setEditingBrowseCategory(null);
                      }}
                      className="px-4 py-2 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400 transition-colors"
                    >
                      Cancel
                    </button>
                  </div>
                </form>
              </div>
            )}
            
            {/* Categories List */}
            <div className="p-6">
              {browseCategories.length === 0 ? (
                <div className="text-center py-12">
                  <div className="text-gray-400 text-4xl mb-3"></div>
                  <p className="text-gray-500 text-lg">No browse categories yet</p>
                  <p className="text-gray-400 text-sm mt-1">Click "Add Category" to create your first category</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {browseCategories.map((category, index) => (
                    <div 
                      key={category.id} 
                      className={`flex items-center justify-between p-4 rounded-lg border ${category.is_active ? 'bg-white border-gray-200' : 'bg-gray-50 border-gray-300 opacity-60'}`}
                    >
                      <div className="flex items-center gap-4">
                        <div className="flex flex-col items-center gap-1">
                          <button
                            onClick={() => handleReorderBrowseCategory(index, 'up')}
                            disabled={index === 0}
                            className={`p-1 rounded ${index === 0 ? 'text-gray-300 cursor-not-allowed' : 'text-gray-500 hover:bg-gray-200'}`}
                            title="Move up"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                            </svg>
                          </button>
                          <button
                            onClick={() => handleReorderBrowseCategory(index, 'down')}
                            disabled={index === browseCategories.length - 1}
                            className={`p-1 rounded ${index === browseCategories.length - 1 ? 'text-gray-300 cursor-not-allowed' : 'text-gray-500 hover:bg-gray-200'}`}
                            title="Move down"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                            </svg>
                          </button>
                        </div>
                        
                        <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${category.color || 'bg-gray-500'} text-white`}>
                          <UiIcon name={category.icon} size={24} className="text-white" />
                        </div>
                        
                        <div>
                          <div className="flex items-center gap-2">
                            <h3 className="font-semibold text-gray-900">{category.label}</h3>
                            <span className={`px-2 py-0.5 text-xs rounded-full ${category.is_active ? 'bg-green-100 text-green-700' : 'bg-gray-200 text-gray-600'}`}>
                              {category.is_active ? 'Active' : 'Inactive'}
                            </span>
                          </div>
                          <p className="text-sm text-gray-500">{category.description || 'No description'}</p>
                          <p className="text-xs text-gray-400 mt-1">Type: {category.type}</p>
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => handleEditBrowseCategory(category)}
                          className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                          title="Edit"
                        >
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                          </svg>
                        </button>
                        <button
                          onClick={() => handleDeleteBrowseCategory(category.id)}
                          className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                          title="Delete"
                        >
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}


        {/* Marquee/Moving Text Tab */}
        {activeTab === 'marquee' && (
          <div className="bg-white rounded-lg shadow-sm overflow-hidden">
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-xl font-semibold text-gray-900">Moving Text (Marquee)</h2>
                  <p className="text-gray-600 text-sm mt-1">Customize the scrolling text displayed on vendor pages</p>
                </div>
                <button
                  onClick={handleUpdateMarqueeSettings}
                  disabled={loading}
                  className="flex items-center gap-2 bg-yellow-600 text-white px-4 py-2 rounded-lg hover:bg-yellow-700 transition-colors disabled:opacity-50"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  {loading ? 'Saving...' : 'Save Settings'}
                </button>
              </div>
            </div>
            
            <div className="p-6 space-y-6">
              {/* Preview */}
              <div className="bg-gray-100 rounded-lg p-4">
                <h3 className="text-sm font-medium text-gray-700 mb-2">Preview</h3>
                <div
                  className="rounded-lg py-3 overflow-hidden"
                  style={{
                    background: `linear-gradient(to right, ${marqueeSettings.color_from}, ${marqueeSettings.color_via}, ${marqueeSettings.color_to})`
                  }}
                >
                  <div 
                    className="whitespace-nowrap text-white font-medium"
                    style={{
                      animation: `marquee ${marqueeSettings.speed}s linear infinite`
                    }}
                  >
                    {marqueeSettings.texts.map((item, index) => (
                      <span key={item.id}>
                        {item.text}
                        {index < marqueeSettings.texts.length - 1 && (
                          <span className="mx-6 text-white/70">{marqueeSettings.separator}</span>
                        )}
                      </span>
                    ))}
                    {marqueeSettings.texts.length === 0 && (
                      <span className="text-white/70 italic">No texts added yet. Add some below!</span>
                    )}
                  </div>
                </div>
              </div>
              
              {/* Colors -- these drive the gradient shown on the actual vendor page */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Section Colors</label>
                <div className="grid grid-cols-3 gap-4 max-w-md">
                  {[
                    { key: 'color_from', label: 'Start' },
                    { key: 'color_via', label: 'Middle' },
                    { key: 'color_to', label: 'End' },
                  ].map(({ key, label }) => (
                    <div key={key} className="flex flex-col items-center gap-1">
                      <input
                        type="color"
                        value={marqueeSettings[key]}
                        onChange={(e) => setMarqueeSettings(prev => ({ ...prev, [key]: e.target.value }))}
                        className="w-14 h-10 rounded border border-gray-300 cursor-pointer"
                        aria-label={`Marquee ${label} color`}
                      />
                      <span className="text-xs text-gray-500">{label}</span>
                    </div>
                  ))}
                </div>
              </div>
              
              {/* Settings */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Separator */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Separator Symbol</label>
                  <div className="flex gap-2">
                    {['', '•', '', '', '|', '~', 'âŸ¡', 'â—†'].map(sep => (
                      <button
                        key={sep}
                        type="button"
                        onClick={() => setMarqueeSettings(prev => ({...prev, separator: sep}))}
                        className={`w-10 h-10 rounded-lg border-2 flex items-center justify-center text-lg transition-all ${
                          marqueeSettings.separator === sep 
                            ? 'border-yellow-500 bg-yellow-50 text-yellow-700' 
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        {sep}
                      </button>
                    ))}
                  </div>
                </div>
                
                {/* Speed */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Speed: {marqueeSettings.speed}s (Lower = Faster)
                  </label>
                  <input
                    type="range"
                    min="10"
                    max="60"
                    value={marqueeSettings.speed}
                    onChange={(e) => setMarqueeSettings(prev => ({...prev, speed: parseInt(e.target.value)}))}
                    className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-yellow-500"
                  />
                  <div className="flex justify-between text-xs text-gray-500 mt-1">
                    <span>Fast (10s)</span>
                    <span>Slow (60s)</span>
                  </div>
                </div>
              </div>
              
              {/* Active Toggle */}
              <div className="flex items-center gap-3">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={marqueeSettings.is_active}
                    onChange={(e) => setMarqueeSettings(prev => ({...prev, is_active: e.target.checked}))}
                    className="w-5 h-5 text-yellow-600 rounded focus:ring-yellow-500"
                  />
                  <span className="font-medium text-gray-900">Display moving text on vendor pages</span>
                </label>
              </div>
              
              {/* Add New Text */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Add New Text</label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={newMarqueeText}
                    onChange={(e) => setNewMarqueeText(e.target.value)}
                    placeholder='e.g., Great service - Customer Name'
                    className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
                    onKeyPress={(e) => e.key === 'Enter' && handleAddMarqueeText()}
                  />
                  <button
                    onClick={handleAddMarqueeText}
                    className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                  >
                    + Add
                  </button>
                </div>
              </div>
              
              {/* Text List */}
              <div>
                <h3 className="text-sm font-medium text-gray-700 mb-3">
                  Current Texts ({marqueeSettings.texts.length})
                </h3>
                {marqueeSettings.texts.length === 0 ? (
                  <div className="text-center py-8 bg-gray-50 rounded-lg">
                    <span className="text-3xl mb-2 block"></span>
                    <p className="text-gray-500">No texts added yet</p>
                    <p className="text-gray-400 text-sm">Add testimonials, announcements, or promotions above</p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {marqueeSettings.texts.map((item, index) => (
                      <div 
                        key={item.id}
                        className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg group"
                      >
                        <span className="text-gray-400 text-sm font-mono">{index + 1}.</span>
                        <span className="flex-1 text-gray-800">{item.text}</span>
                        <button
                          onClick={() => handleDeleteMarqueeText(item.id)}
                          className="p-2 text-red-500 hover:bg-red-50 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity"
                        >
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}


        {/* Product Approvals Tab */}
        {activeTab === 'product-approvals' && (
          <div className="bg-white rounded-lg shadow-sm overflow-hidden">
            <div className="p-6 border-b border-gray-200"><h2 className="text-xl font-semibold text-gray-900">Product Approvals</h2><p className="text-gray-600 text-sm mt-1">Approve every vendor product before it becomes visible to customers.</p></div>
            {pendingProducts.length === 0 ? <div className="p-10 text-center text-gray-500">No products waiting for approval.</div> : <div className="divide-y divide-gray-200">
              {pendingProducts.map(product => <div key={product.id} className="p-5 flex flex-col md:flex-row gap-4 md:items-center">
                <img src={product.image_url || product.image_urls?.[0]} alt={product.name} className="w-20 h-20 object-cover rounded-lg bg-gray-100" />
                <div className="flex-1"><h3 className="font-semibold text-gray-900">{product.name}</h3><p className="text-sm text-gray-500">{product.vendor_name || 'Unknown vendor'} · {product.category}</p><p className="font-bold text-cyan-700 mt-1">{formatCurrency(product.price)}</p></div>
                <div className="flex gap-2"><button onClick={() => handleProductApproval(product.id, 'approve')} className="px-4 py-2 bg-green-600 text-white rounded-lg">Approve</button><button onClick={() => handleProductApproval(product.id, 'reject')} className="px-4 py-2 bg-red-600 text-white rounded-lg">Reject</button></div>
              </div>)}
            </div>}
          </div>
        )}

        {/* All Products Tab -- every product on the platform, across every vendor */}
        {activeTab === 'all-products' && (
          <div className="bg-white rounded-lg shadow-sm overflow-hidden">
            <div className="p-6 border-b border-gray-200 flex flex-col md:flex-row md:items-center justify-between gap-3">
              <div>
                <h2 className="text-xl font-semibold text-gray-900">All Products</h2>
                <p className="text-gray-600 text-sm mt-1">Every product listed by every vendor on the platform ({allPlatformProducts.length} total).</p>
              </div>
              <input
                type="text"
                value={allProductsSearch}
                onChange={(e) => setAllProductsSearch(e.target.value)}
                placeholder="Search by product or vendor name..."
                className="px-4 py-2 border border-gray-300 rounded-lg text-sm w-full md:w-72"
              />
            </div>
            {loadingAllPlatformProducts ? (
              <div className="p-10 text-center text-gray-500">Loading products...</div>
            ) : (() => {
              const q = allProductsSearch.trim().toLowerCase();
              const filtered = q
                ? allPlatformProducts.filter(p =>
                    (p.name || '').toLowerCase().includes(q) ||
                    (p.vendor_name || '').toLowerCase().includes(q) ||
                    (p.category || '').toLowerCase().includes(q))
                : allPlatformProducts;
              if (filtered.length === 0) {
                return <div className="p-10 text-center text-gray-500">{q ? 'No products match your search.' : 'No products on the platform yet.'}</div>;
              }
              return (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50 text-left text-gray-500">
                      <tr>
                        <th className="px-5 py-3 font-medium">Product</th>
                        <th className="px-5 py-3 font-medium">Vendor</th>
                        <th className="px-5 py-3 font-medium">Category</th>
                        <th className="px-5 py-3 font-medium">Price</th>
                        <th className="px-5 py-3 font-medium">Status</th>
                        <th className="px-5 py-3 font-medium">Visible</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {filtered.map(product => (
                        <tr key={product.id} className="hover:bg-gray-50">
                          <td className="px-5 py-3">
                            <div className="flex items-center gap-3">
                              <img src={product.image_url || product.image_urls?.[0]} alt={product.name} className="w-10 h-10 object-cover rounded-lg bg-gray-100 flex-shrink-0" />
                              <div className="min-w-0">
                                <span className="font-medium text-gray-900 block truncate">{product.name}</span>
                                <div className="flex flex-wrap gap-1 mt-1">
                                  {Array.isArray(product.class_options) && product.class_options.length > 0 && <span className="px-1.5 py-0.5 rounded-full bg-cyan-50 text-cyan-800 text-[10px]">{product.class_options.length} class{product.class_options.length === 1 ? '' : 'es'}</span>}
                                  {Array.isArray(product.linked_product_ids) && product.linked_product_ids.length > 0 && <span className="px-1.5 py-0.5 rounded-full bg-blue-50 text-blue-700 text-[10px]">{product.linked_product_ids.length} linked</span>}
                                  {Array.isArray(product.full_description_blocks) && product.full_description_blocks.length > 0 && <span className="px-1.5 py-0.5 rounded-full bg-purple-50 text-purple-700 text-[10px]">Full description</span>}
                                  {product.show_reviews === false && <span className="px-1.5 py-0.5 rounded-full bg-gray-100 text-gray-600 text-[10px]">Reviews hidden</span>}
                                </div>
                              </div>
                            </div>
                          </td>
                          <td className="px-5 py-3 text-gray-600">{product.vendor_name || 'Unknown vendor'}</td>
                          <td className="px-5 py-3 text-gray-600">{product.category || '-'}</td>
                          <td className="px-5 py-3 font-semibold text-cyan-700">{formatCurrency(product.price)}</td>
                          <td className="px-5 py-3">
                            <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                              product.approval_status === 'approved' ? 'bg-green-100 text-green-700'
                                : product.approval_status === 'rejected' ? 'bg-red-100 text-red-700'
                                : 'bg-yellow-100 text-yellow-700'
                            }`}>
                              {product.approval_status === 'approved' ? 'Approved' : product.approval_status === 'rejected' ? 'Rejected' : 'Pending'}
                            </span>
                          </td>
                          <td className="px-5 py-3">
                            <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${product.available !== false ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-500'}`}>
                              {product.available !== false ? 'Visible' : 'Hidden'}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              );
            })()}
          </div>
        )}

        {/* Admins Tab -- super admin only, gated both by the button being
            hidden for non-super-admins AND by the backend requiring
            require_super_admin on every one of these endpoints. */}
        {activeTab === 'admins' && admin?.role === 'super_admin' && (
          <div className="space-y-6">
            <div className="bg-white rounded-lg shadow-sm overflow-hidden">
              <div className="p-6 border-b border-gray-200">
                <h2 className="text-xl font-semibold text-gray-900">Add Admin</h2>
                <p className="text-gray-600 text-sm mt-1">"Admin" has day-to-day access (vendors, products, orders). "Super Admin" additionally manages other admin accounts and platform-security settings.</p>
              </div>
              <form onSubmit={handleCreateAdmin} className="p-6 grid grid-cols-1 md:grid-cols-4 gap-4 items-end">
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Email</label>
                  <input type="email" required value={newAdminForm.email} onChange={(e) => setNewAdminForm(prev => ({ ...prev, email: e.target.value }))} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" />
                </div>
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Password</label>
                  <input type="password" required minLength={8} value={newAdminForm.password} onChange={(e) => setNewAdminForm(prev => ({ ...prev, password: e.target.value }))} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" placeholder="Min. 8 characters" />
                </div>
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Role</label>
                  <select value={newAdminForm.role} onChange={(e) => setNewAdminForm(prev => ({ ...prev, role: e.target.value }))} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm">
                    <option value="admin">Admin</option>
                    <option value="super_admin">Super Admin</option>
                  </select>
                </div>
                <button type="submit" disabled={creatingAdmin} className="px-4 py-2 bg-indigo-600 text-white rounded-lg font-semibold disabled:opacity-50">
                  {creatingAdmin ? 'Creating...' : 'Create Admin'}
                </button>
              </form>
            </div>

            <div className="bg-white rounded-lg shadow-sm overflow-hidden">
              <div className="p-6 border-b border-gray-200">
                <h2 className="text-xl font-semibold text-gray-900">All Admin Accounts</h2>
              </div>
              {loadingAdmins ? (
                <div className="p-10 text-center text-gray-500">Loading admins...</div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50 text-left text-gray-500">
                      <tr>
                        <th className="px-5 py-3 font-medium">Email</th>
                        <th className="px-5 py-3 font-medium">Role</th>
                        <th className="px-5 py-3 font-medium">Created</th>
                        <th className="px-5 py-3 font-medium">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {adminsList.map((a) => (
                        <tr key={a.admin_id} className="hover:bg-gray-50">
                          <td className="px-5 py-3 font-medium text-gray-900">
                            {a.email} {a.admin_id === admin.admin_id && <span className="text-xs text-gray-400">(you)</span>}
                          </td>
                          <td className="px-5 py-3">
                            <select
                              value={a.role || 'super_admin'}
                              onChange={(e) => handleUpdateAdminRole(a.admin_id, e.target.value)}
                              disabled={a.admin_id === admin.admin_id}
                              className="px-2 py-1 border border-gray-200 rounded-lg text-xs disabled:opacity-50"
                            >
                              <option value="admin">Admin</option>
                              <option value="super_admin">Super Admin</option>
                            </select>
                          </td>
                          <td className="px-5 py-3 text-gray-500">
                            {a.created_at ? new Date(a.created_at).toLocaleDateString() : '-'}
                          </td>
                          <td className="px-5 py-3">
                            <button
                              onClick={() => handleDeleteAdmin(a.admin_id, a.email)}
                              disabled={a.admin_id === admin.admin_id}
                              className="text-red-600 hover:text-red-800 disabled:opacity-30 disabled:cursor-not-allowed text-xs font-semibold"
                            >
                              Remove
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Review Moderation Tab */}
        {activeTab === 'reviews' && (
          <div className="bg-white rounded-lg shadow-sm overflow-hidden">
            <div className="p-6 border-b border-gray-200"><h2 className="text-xl font-semibold text-gray-900">Review Moderation</h2><p className="text-gray-600 text-sm mt-1">Approve customer ratings and reviews before they affect public scores.</p></div>
            {pendingReviews.length === 0 ? <div className="p-10 text-center text-gray-500">No reviews waiting for moderation.</div> : <div className="divide-y divide-gray-200">
              {pendingReviews.map(review => <div key={review.id} className="p-5 flex flex-col md:flex-row gap-4 md:items-center">
                <div className="flex-1"><div className="text-amber-500">{[1,2,3,4,5].map(n => <UiIcon key={n} name="star" size={14} className={`mr-1 ${n <= Number(review.rating) ? '' : 'opacity-20'}`} />)}</div><p className="font-semibold text-gray-900 mt-1">{review.customer_name || 'Customer'}</p><p className="text-sm text-gray-600">{review.comment || 'No written comment'}</p><p className="text-xs text-gray-500 mt-1">Product: <span className="font-semibold text-gray-700">{review.product_name || review.product_id}</span>{review.vendor_name ? ` · Vendor: ${review.vendor_name}` : ""}</p></div>
                <div className="flex gap-2"><button onClick={() => handleReviewModeration(review.id, 'approve')} className="px-4 py-2 bg-green-600 text-white rounded-lg">Approve</button><button onClick={() => handleReviewModeration(review.id, 'reject')} className="px-4 py-2 bg-red-600 text-white rounded-lg">Reject</button></div>
              </div>)}
            </div>}
          </div>
        )}

        {/* Agent Approvals Tab */}
        {activeTab === 'agent-approvals' && (
          <div className="bg-white rounded-lg shadow-sm overflow-hidden">
            <div className="p-6 border-b border-gray-200">
              <h2 className="text-xl font-semibold text-gray-900 flex items-center gap-2">
                <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                Pending Vendor Approvals
              </h2>
              <p className="text-gray-600 text-sm mt-1">Review and approve vendors registered by agents</p>
            </div>
            
            <div className="p-6 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">Agent Accounts Awaiting Approval</h3>
              <div className="mt-3 space-y-2">
                {agents.filter(a => a.approval_status === 'pending_admin_approval').length === 0 ? <p className="text-sm text-gray-500">No agents awaiting approval.</p> : agents.filter(a => a.approval_status === 'pending_admin_approval').map(agent => (
                  <div key={agent.id} className="flex flex-col md:flex-row md:items-center justify-between gap-3 p-3 bg-gray-50 rounded-lg">
                    <div><p className="font-medium text-gray-900">{agent.name}</p><p className="text-sm text-gray-500">{agent.email} · {agent.phone}</p></div>
                    <div className="flex gap-2"><button onClick={() => handleAgentApproval(agent.id, 'approve')} className="px-3 py-2 bg-green-600 text-white rounded-lg text-sm">Approve Agent</button><button onClick={() => handleAgentApproval(agent.id, 'reject')} className="px-3 py-2 bg-red-600 text-white rounded-lg text-sm">Reject Agent</button></div>
                  </div>
                ))}
              </div>
            </div>

            {pendingVendors.length === 0 ? (
              <div className="p-12 text-center">
                <div className="text-6xl mb-4"></div>
                <h3 className="text-xl font-semibold text-gray-800 mb-2">No Pending Approvals</h3>
                <p className="text-gray-600">All vendor registrations have been reviewed.</p>
              </div>
            ) : (
              <>
                <div className="flex gap-2 px-6 pt-4 border-b border-gray-200">
                  {[
                    { key: 'self', label: 'Self-Registered', count: pendingVendors.filter(v => !v.registered_by_agent).length },
                    { key: 'agent', label: 'Agent-Referred', count: pendingVendors.filter(v => v.registered_by_agent).length },
                  ].map(tab => (
                    <button
                      key={tab.key}
                      onClick={() => setPendingVendorTab(tab.key)}
                      className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px ${
                        pendingVendorTab === tab.key
                          ? 'border-imax-orange text-imax-orange'
                          : 'border-transparent text-gray-500 hover:text-gray-700'
                      }`}
                    >
                      {tab.label} ({tab.count})
                    </button>
                  ))}
                </div>
                {pendingVendors.filter(v => pendingVendorTab === 'agent' ? v.registered_by_agent : !v.registered_by_agent).length === 0 ? (
                  <div className="p-10 text-center text-gray-500 text-sm">
                    No {pendingVendorTab === 'agent' ? 'agent-referred' : 'self-registered'} vendors awaiting approval.
                  </div>
                ) : (
              <div className="divide-y divide-gray-200">
                {pendingVendors
                  .filter(v => pendingVendorTab === 'agent' ? v.registered_by_agent : !v.registered_by_agent)
                  .sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0))
                  .map((vendor) => (
                  <div key={vendor.id} className="p-6 hover:bg-gray-50">
                    <div className="flex flex-col md:flex-row md:items-start justify-between gap-4">
                      {/* Vendor Info */}
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <h3 className="text-lg font-semibold text-gray-900">{vendor.name}</h3>
                          <span className="px-2 py-1 text-xs bg-yellow-100 text-yellow-700 rounded-full">
                            Pending Approval
                          </span>
                        </div>
                        
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                          <div className="flex items-center gap-2 text-gray-600">
                            <span className="font-medium">Type:</span>
                            <span className="capitalize">{vendor.vendor_type}</span>
                          </div>
                          <div className="flex items-center gap-2 text-gray-600">
                            <span className="font-medium">Category:</span>
                            <span>{vendor.category}</span>
                          </div>
                          <div className="flex items-center gap-2 text-gray-600">
                            <span className="font-medium">Phone:</span>
                            <span>{vendor.phone}</span>
                          </div>
                          <div className="flex items-center gap-2 text-gray-600">
                            <span className="font-medium">Location:</span>
                            <span>{vendor.location}</span>
                          </div>
                          <div className="flex items-center gap-2 text-gray-600">
                            <span className="font-medium">Email:</span>
                            <span>{vendor.email}</span>
                          </div>
                          <div className="flex items-center gap-2 text-gray-600">
                            <span className="font-medium">Address:</span>
                            <span className="truncate">{vendor.address}</span>
                          </div>
                        </div>
                        
                        {/* Registration source ? self-registered vs agent-referred. This must
                            be conditional: agent_name/agent_commission only exist on
                            agent-referred vendors, so rendering this unconditionally
                            showed "Registered by Agent: undefined" on plain self-registrations. */}
                        {vendor.registered_by_agent ? (
                          <div className="mt-3 p-3 bg-green-50 rounded-lg">
                            <div className="flex items-center gap-2 text-green-700">
                              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                              </svg>
                              <span className="font-medium">Registered by Agent:</span>
                              <span>{vendor.agent_name}</span>
                              <span className="text-green-600">({vendor.agent_commission}% commission)</span>
                            </div>
                            <div className="text-xs text-green-600 mt-1">
                              Registered: {new Date(vendor.created_at).toLocaleString()}
                            </div>
                          </div>
                        ) : (
                          <div className="mt-3 p-3 bg-blue-50 rounded-lg">
                            <div className="flex items-center gap-2 text-blue-700">
                              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                              </svg>
                              <span className="font-medium">Self-Registered Vendor</span>
                            </div>
                            <div className="text-xs text-blue-600 mt-1">
                              Registered: {new Date(vendor.created_at).toLocaleString()}
                            </div>
                          </div>
                        )}
                        
                        {vendor.description && (
                          <p className="mt-2 text-sm text-gray-500">{vendor.description}</p>
                        )}
                      </div>
                      
                      {/* Action Buttons */}
                      <div className="flex flex-row md:flex-col gap-2">
                        <button
                          onClick={() => openApprovalModal(vendor)}
                          disabled={approvingVendor === vendor.id}
                          className="flex-1 md:flex-none px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                        >
                          {approvingVendor === vendor.id ? (
                            <UiIcon name="spinner" size={18} className="animate-spin" />
                          ) : (
                            <>
                              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                              </svg>
                              Approve
                            </>
                          )}
                        </button>
                        <button
                          onClick={() => handleVendorApproval(vendor.id, 'reject')}
                          disabled={approvingVendor === vendor.id}
                          className="flex-1 md:flex-none px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                        >
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                          </svg>
                          Reject
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
                )}
              </>
            )}
            
            {/* Approval Modal with Commission Settings */}
            {showApprovalModal && selectedVendorForApproval && (
              <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                <div className="bg-white rounded-2xl max-w-md w-full shadow-2xl">
                  <div className="p-6 border-b border-gray-200">
                    <div className="flex items-center justify-between">
                      <h3 className="text-xl font-bold text-gray-900">Approve Vendor</h3>
                      <button
                        onClick={() => {
                          setShowApprovalModal(false);
                          setSelectedVendorForApproval(null);
                        }}
                        className="text-gray-400 hover:text-gray-600"
                      >
                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </div>
                  </div>
                  
                  <div className="p-6">
                    {/* Vendor Info */}
                    <div className="bg-gray-50 rounded-lg p-4 mb-6">
                      <h4 className="font-semibold text-gray-800">{selectedVendorForApproval.name}</h4>
                      <p className="text-sm text-gray-500 capitalize">{selectedVendorForApproval.vendor_type} • {selectedVendorForApproval.category}</p>
                      <p className="text-sm text-gray-500 mt-1">
                        {selectedVendorForApproval.registered_by_agent
                          ? `Agent: ${selectedVendorForApproval.agent_name}`
                          : "Self-registered (no agent)"}
                      </p>
                    </div>
                    
                    {/* Commission settings only make sense when there's an
                        actual agent to receive them -- showing "Agent will
                        earn 5%" on a vendor with no agent was confusing and
                        wrong. */}
                    {selectedVendorForApproval.registered_by_agent && (
                      <>
                    {/* Commission Type */}
                    <div className="mb-4">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Commission Type
                      </label>
                      <div className="grid grid-cols-2 gap-3">
                        <button
                          type="button"
                          onClick={() => setApprovalCommission({
                            ...approvalCommission,
                            commission_type: 'percentage',
                            commission_value: 5
                          })}
                          className={`p-3 rounded-lg border-2 text-left transition-all ${
                            approvalCommission.commission_type === 'percentage'
                              ? 'border-green-500 bg-green-50'
                              : 'border-gray-200 hover:border-gray-300'
                          }`}
                        >
                          <div className="font-medium text-gray-800">Percentage</div>
                          <div className="text-xs text-gray-500">% of order total</div>
                          <div className="text-xs text-gray-400 mt-1">Best for products/services</div>
                        </button>
                        <button
                          type="button"
                          onClick={() => setApprovalCommission({
                            ...approvalCommission,
                            commission_type: 'fixed',
                            commission_value: 200
                          })}
                          className={`p-3 rounded-lg border-2 text-left transition-all ${
                            approvalCommission.commission_type === 'fixed'
                              ? 'border-green-500 bg-green-50'
                              : 'border-gray-200 hover:border-gray-300'
                          }`}
                        >
                          <div className="font-medium text-gray-800">Fixed Amount</div>
                          <div className="text-xs text-gray-500">₦ per order</div>
                          <div className="text-xs text-gray-400 mt-1">Best for restaurants</div>
                        </button>
                      </div>
                    </div>
                    
                    {/* Commission Value */}
                    <div className="mb-6">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        {approvalCommission.commission_type === 'percentage' 
                          ? 'Commission Percentage' 
                          : 'Commission Amount (?)'}
                      </label>
                      <div className="flex items-center gap-3">
                        <input
                          type="number"
                          min="0"
                          max={approvalCommission.commission_type === 'percentage' ? 50 : 10000}
                          step={approvalCommission.commission_type === 'percentage' ? 0.5 : 50}
                          value={approvalCommission.commission_value}
                          onChange={(e) => setApprovalCommission({
                            ...approvalCommission,
                            commission_value: parseFloat(e.target.value) || 0
                          })}
                          className="w-32 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent text-lg font-semibold"
                        />
                        <span className="text-xl text-gray-500">
                          {approvalCommission.commission_type === 'percentage' ? '%' : '?/order'}
                        </span>
                      </div>
                      <p className="text-sm text-gray-500 mt-2">
                        {approvalCommission.commission_type === 'percentage'
                          ? `Agent will earn ${approvalCommission.commission_value}% of each order total`
                          : `Agent will earn ?${approvalCommission.commission_value} for each order`}
                      </p>
                    </div>
                      </>
                    )}
                    {!selectedVendorForApproval.registered_by_agent && (
                      <div className="mb-6 p-3 bg-blue-50 rounded-lg text-sm text-blue-700">
                        This vendor registered directly, with no referring agent -- there's no commission to configure.
                      </div>
                    )}
                    
                    {/* Action Buttons */}
                    <div className="flex gap-3">
                      <button
                        onClick={() => {
                          setShowApprovalModal(false);
                          setSelectedVendorForApproval(null);
                        }}
                        className="flex-1 px-4 py-3 bg-gray-200 text-gray-700 rounded-lg font-semibold hover:bg-gray-300 transition-colors"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={() => handleVendorApproval(
                          selectedVendorForApproval.id, 
                          'approve', 
                          selectedVendorForApproval.registered_by_agent ? approvalCommission : null
                        )}
                        disabled={approvingVendor}
                        className="flex-1 px-4 py-3 bg-green-600 text-white rounded-lg font-semibold hover:bg-green-700 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                      >
                        {approvingVendor ? (
                          <UiIcon name="spinner" size={18} className="animate-spin" />
                        ) : (
                          <>
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                            </svg>
                            Approve Vendor
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}
            
            {/* Credentials Modal - Shows after vendor approval */}
            {showCredentialsModal && approvedCredentials && (
              <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                <div className="bg-white rounded-2xl max-w-md w-full shadow-2xl">
                  <div className="p-6 border-b border-gray-200 bg-green-50">
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 bg-green-500 rounded-full flex items-center justify-center">
                        <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                      </div>
                      <div>
                        <h3 className="text-xl font-bold text-gray-900">Vendor Approved!</h3>
                        <p className="text-sm text-gray-600">{approvedCredentials.vendorName}</p>
                      </div>
                    </div>
                  </div>
                  
                  <div className="p-6">
                    <p className="text-sm text-gray-600 mb-4">
                      Share these login credentials with the vendor. They can change the password after logging in.
                    </p>
                    
                    {/* Credentials Box */}
                    <div className="bg-gray-50 rounded-lg p-4 space-y-3 mb-4">
                      <div>
                        <label className="text-xs font-medium text-gray-500 uppercase">Login Email</label>
                        <div className="flex items-center gap-2 mt-1">
                          <input 
                            type="text" 
                            readOnly 
                            value={approvedCredentials.email}
                            className="flex-1 px-3 py-2 bg-white border border-gray-200 rounded-lg text-sm font-mono"
                          />
                          <button
                            onClick={() => {
                              navigator.clipboard.writeText(approvedCredentials.email);
                              toast.success('Email copied!');
                            }}
                            className="px-3 py-2 bg-gray-200 hover:bg-gray-300 rounded-lg text-sm"
                          >
                            
                          </button>
                        </div>
                      </div>
                      <div>
                        <label className="text-xs font-medium text-gray-500 uppercase">Temporary Password</label>
                        <div className="flex items-center gap-2 mt-1">
                          <input 
                            type="text" 
                            readOnly 
                            value={approvedCredentials.password}
                            className="flex-1 px-3 py-2 bg-white border border-gray-200 rounded-lg text-sm font-mono font-bold"
                          />
                          <button
                            onClick={() => {
                              navigator.clipboard.writeText(approvedCredentials.password);
                              toast.success('Password copied!');
                            }}
                            className="px-3 py-2 bg-gray-200 hover:bg-gray-300 rounded-lg text-sm"
                          >
                            
                          </button>
                        </div>
                      </div>
                    </div>
                    
                    {/* Commission Info -- only relevant when there's actually
                        an agent to earn it */}
                    {approvedCredentials.commissionType && (
                      <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 mb-4">
                        <p className="text-sm text-emerald-700">
                          <span className="font-medium">Agent Commission:</span>{' '}
                          {approvedCredentials.commissionType === 'fixed' 
                            ? `₦${approvedCredentials.commissionValue} per order`
                            : `${approvedCredentials.commissionValue}% of order total`}
                        </p>
                      </div>
                    )}
                    
                    {/* Copy All Button */}
                    <button
                      onClick={() => {
                        const text = `Login Email: ${approvedCredentials.email}\nTemporary Password: ${approvedCredentials.password}`;
                        navigator.clipboard.writeText(text);
                        toast.success('Credentials copied to clipboard!');
                      }}
                      className="w-full px-4 py-3 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700 transition-colors mb-3"
                    >
                       Copy All Credentials
                    </button>
                    
                    <button
                      onClick={() => {
                        setShowCredentialsModal(false);
                        setApprovedCredentials(null);
                      }}
                      className="w-full px-4 py-3 bg-gray-200 text-gray-700 rounded-lg font-semibold hover:bg-gray-300 transition-colors"
                    >
                      Close
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Agent Commission Settings Tab */}
        {activeTab === 'agent-settings' && (
          <div className="space-y-6">
            {/* Commission Settings Card */}
            <div className="bg-white rounded-lg shadow-sm overflow-hidden">
              <div className="p-6 border-b border-gray-200">
                <h2 className="text-xl font-semibold text-gray-900 flex items-center gap-2">
                  <svg className="w-6 h-6 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  Agent Commission Settings
                </h2>
                <p className="text-gray-600 text-sm mt-1">Set the default commission rate for agents. This replaces the previous vendor service fee system.</p>
              </div>
              
              <div className="p-6">
                <div className="max-w-md">
                  {/* Commission Value */}
                  {(agentSettings.commission_type || 'percentage') !== 'fixed' && (
                    <div className="mb-6">
                      <label className="block text-sm font-medium text-gray-700 mb-2">Default Commission Percentage</label>
                      <div className="flex items-center gap-4">
                        <input type="number" min="0" max="100" step="0.01" value={agentSettings.default_commission_percentage} onChange={(e) => setAgentSettings({...agentSettings, default_commission_percentage: parseFloat(e.target.value) || 0})} className="w-32 px-4 py-2 border border-gray-300 rounded-lg text-lg font-semibold" />
                        <span className="text-2xl text-gray-500">%</span>
                      </div>
                    </div>
                  )}
                  {(agentSettings.commission_type || 'percentage') !== 'percentage' && (
                    <div className="mb-6">
                      <label className="block text-sm font-medium text-gray-700 mb-2">Fixed Commission per Order</label>
                      <div className="flex items-center gap-4">
                        <span className="text-gray-500">₦</span>
                        <input type="number" min="0" step="1" value={agentSettings.commission_fixed_amount || 0} onChange={(e) => setAgentSettings({...agentSettings, commission_fixed_amount: parseFloat(e.target.value) || 0})} className="w-40 px-4 py-2 border border-gray-300 rounded-lg text-lg font-semibold" />
                      </div>
                    </div>
                  )}
                  
                  {/* Commission Type */}
                  <div className="mb-6">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Commission Type
                    </label>
                    <select
                      value={agentSettings.commission_type || 'percentage'}
                      onChange={(e) => setAgentSettings({
                        ...agentSettings,
                        commission_type: e.target.value
                      })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                    >
                      <option value="percentage">Percentage of Order Total</option>
                      <option value="fixed">Fixed Amount per Order</option>
                    </select>
                  </div>
                  
                  {/* Info Box */}
                  <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4 mb-6">
                    <div className="flex items-start gap-3">
                      <svg className="w-5 h-5 text-emerald-600 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <div className="text-sm text-emerald-700">
                        <p className="font-medium mb-1">How Agent Commission Works:</p>
                        <ul className="list-disc list-inside space-y-1">
                          <li>Agents register vendors on behalf of businesses</li>
                          <li>When a customer places an order with the vendor, the agent earns the configured commission from each successful order</li>
                          <li>This replaces the previous vendor-set service fee</li>
                          <li>Commission is calculated on the order subtotal</li>
                        </ul>
                      </div>
                    </div>
                  </div>
                  
                  {/* Save Button */}
                  <button
                    onClick={handleUpdateAgentSettings}
                    className="w-full px-6 py-3 bg-emerald-600 text-white font-semibold rounded-lg hover:bg-emerald-700 transition-colors flex items-center justify-center gap-2"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    Save Commission Settings
                  </button>
                </div>
              </div>
            </div>
            
            {/* Registered Agents List */}
            <div className="bg-white rounded-lg shadow-sm overflow-hidden">
              <div className="p-6 border-b border-gray-200">
                <h2 className="text-lg font-semibold text-gray-900">Registered Agents ({agents.length})</h2>
                <p className="text-gray-600 text-sm mt-1">All agents who can register vendors on your platform</p>
              </div>
              
              {agents.length === 0 ? (
                <div className="p-12 text-center">
                  <div className="text-6xl mb-4"></div>
                  <h3 className="text-xl font-semibold text-gray-800 mb-2">No Agents Yet</h3>
                  <p className="text-gray-600">When agents register, they will appear here.</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Agent</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Contact</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Vendors</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Commission</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {agents
                        .sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0))
                        .map((agent) => (
                        <tr key={agent.id} className="hover:bg-gray-50">
                          <td className="px-6 py-4">
                            <div className="font-medium text-gray-900">{agent.name}</div>
                            <div className="text-sm text-gray-500">ID: {agent.id.substring(0, 8)}...</div>
                          </td>
                          <td className="px-6 py-4">
                            <div className="text-sm text-gray-900">{agent.email}</div>
                            <div className="text-sm text-gray-500">{agent.phone}</div>
                          </td>
                          <td className="px-6 py-4">
                            <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded-full text-sm font-medium">
                              {agent.total_vendors_registered || 0} vendors
                            </span>
                          </td>
                          <td className="px-6 py-4">
                            <span className="text-emerald-600 font-medium">{agent.commission_type === 'fixed' ? `?${Number(agent.commission_fixed_amount || agent.commission_value || 0).toLocaleString()}` : agent.commission_type === 'percentage_plus_fixed' ? `${agent.commission_percentage || agent.commission_value || agentSettings.default_commission_percentage}% + ?${Number(agent.commission_fixed_amount || 0).toLocaleString()}` : `${agent.commission_percentage || agent.commission_value || agentSettings.default_commission_percentage}%`}</span>
                          </td>
                          <td className="px-6 py-4">
                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                              agent.is_active !== false 
                                ? 'bg-green-100 text-green-700' 
                                : 'bg-red-100 text-red-700'
                            }`}>
                              {agent.is_active !== false ? ' Active' : ' Inactive'}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Payment & Commission Settings Tab */}
        {activeTab === 'payment-settings' && (
          <div className="space-y-6">
            <div className="bg-white rounded-lg shadow-sm overflow-hidden">
              <div className="p-6 border-b border-gray-200">
                <h2 className="text-xl font-semibold text-gray-900">SchoolVerse Payment & Commission</h2>
                <p className="text-gray-600 text-sm mt-1">Set what SchoolVerse keeps from each successful marketplace order.</p>
              </div>
              <div className="p-6 max-w-2xl">
                <label className="block text-sm font-medium text-gray-700 mb-2">Commission model</label>
                <select value={paymentSettings.commission_type} onChange={(e) => setPaymentSettings({...paymentSettings, commission_type: e.target.value})} className="w-full px-4 py-3 border border-gray-300 rounded-lg mb-5">
                  <option value="percentage">Percentage</option>
                  <option value="fixed">Fixed amount per order</option>
                  <option value="percentage_plus_fixed">Percentage + fixed amount</option>
                </select>
                {(paymentSettings.commission_type !== 'fixed') && (
                  <div className="mb-5"><label className="block text-sm font-medium text-gray-700 mb-2">Percentage</label><div className="flex items-center gap-3"><input type="number" min="0" max="100" step="0.01" value={paymentSettings.commission_value} onChange={(e) => setPaymentSettings({...paymentSettings, commission_value: Math.max(0, Number(e.target.value) || 0)})} className="w-40 px-4 py-3 border border-gray-300 rounded-lg" /><span className="text-gray-500">%</span></div></div>
                )}
                {(paymentSettings.commission_type !== 'percentage') && (
                  <div className="mb-5"><label className="block text-sm font-medium text-gray-700 mb-2">Fixed amount per order</label><div className="flex items-center gap-3"><span className="text-gray-500">₦</span><input type="number" min="0" step="1" value={paymentSettings.fixed_amount} onChange={(e) => setPaymentSettings({...paymentSettings, fixed_amount: Math.max(0, Number(e.target.value) || 0)})} className="w-48 px-4 py-3 border border-gray-300 rounded-lg" /></div></div>
                )}
                <div className="bg-cyan-50 border border-cyan-200 rounded-lg p-4 mb-6"><p className="font-medium text-cyan-950">Example on ₦100,000</p><p className="text-sm text-cyan-900 mt-1">SchoolVerse commission: ₦{(((paymentSettings.commission_type === 'fixed' ? 0 : 100000 * Number(paymentSettings.commission_value || 0) / 100) + (paymentSettings.commission_type === 'percentage' ? 0 : Number(paymentSettings.fixed_amount || 0)))).toLocaleString()}</p></div>
                <button onClick={handleUpdatePaymentSettings} className="w-full px-6 py-3 bg-cyan-700 text-white font-semibold rounded-lg hover:bg-cyan-800 transition-colors">Save Payment Settings</button>
              </div>
            </div>

            {/* Pay on Delivery Settings */}
            <div className="bg-white rounded-lg shadow-sm overflow-hidden">
              <div className="p-6 border-b border-gray-200">
                <h2 className="text-xl font-semibold text-gray-900">Pay on Delivery</h2>
                <p className="text-gray-600 text-sm mt-1">Customers pay a required upfront amount online now, and the remaining product balance is collected at delivery.</p>
              </div>
              <div className="p-6 max-w-2xl">
                <label className="flex items-center gap-3 mb-5 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={podAdminSettings.pod_enabled}
                    onChange={(e) => setPodAdminSettings({ ...podAdminSettings, pod_enabled: e.target.checked })}
                    className="w-5 h-5"
                  />
                  <span className="font-medium text-gray-800">Pay on Delivery enabled</span>
                </label>

                <label className="block text-sm font-medium text-gray-700 mb-2">Minimum merchandise/order value</label>
                <div className="flex items-center gap-3 mb-5">
                  <span className="text-gray-500">₦</span>
                  <input
                    type="number"
                    min="0"
                    step="100"
                    value={podAdminSettings.pod_minimum_order_value}
                    onChange={(e) => setPodAdminSettings({ ...podAdminSettings, pod_minimum_order_value: Math.max(0, Number(e.target.value) || 0) })}
                    className="w-48 px-4 py-3 border border-gray-300 rounded-lg"
                  />
                </div>
                <p className="text-xs text-gray-500 mb-5">Orders below this merchandise value (delivery/fees excluded) will only offer Pay Now.</p>

                <label className="block text-sm font-medium text-gray-700 mb-2">Upfront payment percentage</label>
                <div className="flex items-center gap-3 mb-5">
                  <input
                    type="number"
                    min="1"
                    max="100"
                    step="1"
                    value={podAdminSettings.pod_upfront_percentage}
                    onChange={(e) => setPodAdminSettings({ ...podAdminSettings, pod_upfront_percentage: Math.max(1, Math.min(100, Number(e.target.value) || 1)) })}
                    className="w-40 px-4 py-3 border border-gray-300 rounded-lg"
                  />
                  <span className="text-gray-500">%</span>
                </div>

                <div className="bg-cyan-50 border border-cyan-200 rounded-lg p-4 mb-6">
                  <p className="font-medium text-cyan-950">Example on ₦50,000 merchandise</p>
                  <p className="text-sm text-cyan-900 mt-1">
                    Upfront now: ₦{Math.round(50000 * podAdminSettings.pod_upfront_percentage / 100).toLocaleString()} (+ delivery/fees) · Due on delivery: ₦{Math.round(50000 - (50000 * podAdminSettings.pod_upfront_percentage / 100)).toLocaleString()}
                  </p>
                </div>

                <button onClick={handleUpdatePodSettings} className="w-full px-6 py-3 bg-cyan-700 text-white font-semibold rounded-lg hover:bg-cyan-800 transition-colors">Save Pay on Delivery Settings</button>
              </div>
            </div>
            <div className="bg-white rounded-lg shadow-sm p-6"><h3 className="text-lg font-semibold text-gray-900 mb-2">Settlement flow</h3><p className="text-sm text-gray-600">New eligible Paystack transactions receive a dynamic split. The vendor and approved agent receive their calculated shares through their connected Paystack subaccounts; SchoolVerse retains its configured commission.</p></div>
          </div>
        )}

        {/* Settlement Ledger Tab */}
        {activeTab === 'settlements' && (
          <div className="space-y-6">
            <div className="flex items-center justify-between gap-3">
              <div><h2 className="text-2xl font-bold text-gray-900">Settlement Ledger</h2><p className="text-sm text-gray-500 mt-1">Audit every successful payment allocation before payouts are treated as payable.</p></div>
              <button onClick={() => fetchSettlements(settlementStatus)} className="px-4 py-2 bg-gray-900 text-white rounded-lg">Refresh</button>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
              <div className="bg-white rounded-lg border p-4"><p className="text-xs text-gray-500">Gross sales</p><p className="text-lg font-bold">₦{Number(settlementData.summary?.gross_sales || 0).toLocaleString()}</p></div>
              <div className="bg-white rounded-lg border p-4"><p className="text-xs text-gray-500">SchoolVerse</p><p className="text-lg font-bold text-cyan-700">₦{Number(settlementData.summary?.platform_commission || 0).toLocaleString()}</p></div>
              <div className="bg-white rounded-lg border p-4"><p className="text-xs text-gray-500">Agents</p><p className="text-lg font-bold text-green-600">₦{Number(settlementData.summary?.agent_commission || 0).toLocaleString()}</p></div>
              <div className="bg-white rounded-lg border p-4"><p className="text-xs text-gray-500">Vendors</p><p className="text-lg font-bold text-blue-600">₦{Number(settlementData.summary?.vendor_settlement || 0).toLocaleString()}</p></div>
              <div className="bg-white rounded-lg border p-4"><p className="text-xs text-gray-500">Voided</p><p className="text-lg font-bold text-red-600">{settlementData.summary?.voided || 0}</p></div>
            </div>
            <div className="bg-white rounded-lg border overflow-hidden">
              <div className="p-4 border-b flex items-center justify-between gap-3"><h3 className="font-semibold">Transactions</h3><select value={settlementStatus} onChange={(e) => { setSettlementStatus(e.target.value); fetchSettlements(e.target.value); }} className="border rounded-lg px-3 py-2"><option value="all">All</option><option value="paid">Paid</option><option value="pending_manual">Pending manual</option><option value="voided">Voided</option></select></div>
              {(!settlementData.transactions || settlementData.transactions.length === 0) ? <div className="p-8 text-center text-gray-500">No settlement records yet. A ledger row is created after a Paystack charge succeeds.</div> : (
                <div className="overflow-x-auto"><table className="w-full text-sm"><thead className="bg-gray-50"><tr><th className="px-4 py-3 text-left">Order</th><th className="px-4 py-3 text-left">Reference</th><th className="px-4 py-3 text-right">Gross</th><th className="px-4 py-3 text-right">SchoolVerse</th><th className="px-4 py-3 text-right">Agent</th><th className="px-4 py-3 text-right">Vendor</th><th className="px-4 py-3 text-left">Status</th></tr></thead><tbody className="divide-y">
                  {settlementData.transactions.map((row) => <tr key={row.ledger_id}><td className="px-4 py-3 font-medium">{row.order_number || row.order_id}</td><td className="px-4 py-3 text-gray-500">{row.payment_reference || '?'}</td><td className="px-4 py-3 text-right">₦{Number(row.order_total || 0).toLocaleString()}</td><td className="px-4 py-3 text-right">₦{Number(row.platform_commission_amount || 0).toLocaleString()}</td><td className="px-4 py-3 text-right">₦{Number(row.agent_commission_amount || 0).toLocaleString()}</td><td className="px-4 py-3 text-right">₦{Number(row.vendor_settlement_amount || 0).toLocaleString()}</td><td className="px-4 py-3"><span className={`px-2 py-1 rounded-full text-xs font-medium ${row.settlement_status === 'paid' ? 'bg-green-100 text-green-700' : row.settlement_status === 'voided' ? 'bg-red-100 text-red-700' : 'bg-yellow-100 text-yellow-700'}`}>{row.settlement_status}</span></td></tr>)}
                </tbody></table></div>
              )}
            </div>
          </div>
        )}

        {/* Import/Export Tab */}
        {activeTab === 'import-export' && (
          <div className="bg-white rounded-lg shadow-sm overflow-hidden">
            <div className="p-6 border-b border-gray-200">
              <h2 className="text-xl font-semibold text-gray-900">Data Import/Export</h2>
              <p className="text-gray-600 text-sm mt-1">Backup and restore your restaurants, products, banners, and referral codes</p>
            </div>
            
            <div className="p-6 space-y-6">
              {/* Export Section */}
              <div className="border border-gray-200 rounded-lg p-6">
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                      <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                      </svg>
                      Export Data
                    </h3>
                    <p className="text-gray-600 text-sm mt-1">Download all your data as a JSON file</p>
                  </div>
                </div>
                
                <div className="bg-gray-50 rounded-md p-4 mb-4">
                  <p className="text-sm text-gray-700 mb-2">This will export:</p>
                  <ul className="text-sm text-gray-600 space-y-1 ml-4">
                    <li>• All restaurants and their details</li>
                    <li>• All products and menu items</li>
                    <li>• All referral codes</li>
                    <li>• All banners</li>
                    <li>• Delivery areas and pricing</li>
                  </ul>
                </div>
                
                <button
                  onClick={async () => {
                    setLoading(true);
                    try {
                      const response = await fetch(`${backendUrl}/api/admin/export/data`, {
                        headers: {
                          'Authorization': `Bearer ${adminToken}`
                        }
                      });
                      
                      if (response.ok) {
                        const data = await response.json();
                        // Create a blob and download
                        const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
                        const url = window.URL.createObjectURL(blob);
                        const a = document.createElement('a');
                        a.href = url;
                        a.download = `imax-eat-export-${new Date().toISOString().split('T')[0]}.json`;
                        document.body.appendChild(a);
                        a.click();
                        document.body.removeChild(a);
                        window.URL.revokeObjectURL(url);
                        toast.success(`Successfully exported ${data.total_restaurants} restaurants, ${data.total_products} products, ${data.total_banners} banners, and ${data.total_referral_codes} referral codes!`);
                      } else {
                        const error = await response.json();
                        toast.error(`Error: ${error.detail}`);
                      }
                    } catch (error) {
                      console.error('Error exporting data:', error);
                      toast.error('Error exporting data');
                    }
                    setLoading(false);
                  }}
                  disabled={loading}
                  className="bg-green-600 text-white px-6 py-3 rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 flex items-center gap-2"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                  </svg>
                  {loading ? 'Exporting...' : 'Export All Data'}
                </button>
              </div>
              
              {/* Import Section */}
              <div className="border border-gray-200 rounded-lg p-6">
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                      <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M9 19l3 3m0 0l3-3m-3 3V10" />
                      </svg>
                      Import Data
                    </h3>
                    <p className="text-gray-600 text-sm mt-1">Upload a previously exported JSON file</p>
                  </div>
                </div>
                
                <div className="bg-yellow-50 border border-yellow-200 rounded-md p-4 mb-4">
                  <div className="flex gap-2">
                    <svg className="w-5 h-5 text-yellow-600 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                    </svg>
                    <div>
                      <p className="text-sm font-medium text-yellow-800">Important Notes:</p>
                      <ul className="text-sm text-yellow-700 mt-1 space-y-1">
                        <li>• This will add or update data (won't delete existing data)</li>
                        <li>• Only use files exported from this system</li>
                        <li>• Large files may take a few moments to process</li>
                      </ul>
                    </div>
                  </div>
                </div>
                
                <input
                  type="file"
                  accept=".json"
                  id="import-file"
                  className="hidden"
                  onChange={async (e) => {
                    const file = e.target.files[0];
                    if (!file) return;
                    
                    setLoading(true);
                    try {
                      const text = await file.text();
                      const data = JSON.parse(text);
                      
                      const response = await fetch(`${backendUrl}/api/admin/import/data`, {
                        method: 'POST',
                        headers: {
                          'Content-Type': 'application/json',
                          'Authorization': `Bearer ${adminToken}`
                        },
                        body: JSON.stringify(data)
                      });
                      
                      if (response.ok) {
                        const result = await response.json();
                        const summary = result.summary;
                        toast.success(`Import completed! Restaurants: ${summary.restaurants_imported}, Products: ${summary.products_imported}, Referral Codes: ${summary.referral_codes_imported}, Banners: ${summary.banners_imported}`);
                        
                        if (summary.errors && summary.errors.length > 0) {
                          toast.info(`Warnings: ${summary.errors.length} issue(s) found`);
                        }
                        
                        // Refresh data
                        onFetchRestaurants();
                        onFetchReferralCodes();
                        fetchBanners();
                      } else {
                        const error = await response.json();
                        toast.error(`Error: ${error.detail}`);
                      }
                    } catch (error) {
                      console.error('Error importing data:', error);
                      toast.error('Error importing data. Please make sure the file is a valid export file.');
                    }
                    setLoading(false);
                    e.target.value = ''; // Reset file input
                  }}
                />
                
                <button
                  onClick={() => document.getElementById('import-file').click()}
                  disabled={loading}
                  className="bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 flex items-center gap-2"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M9 19l3 3m0 0l3-3m-3 3V10" />
                  </svg>
                  {loading ? 'Importing...' : 'Choose File to Import'}
                </button>
              </div>
              
              {/* Instructions */}
              <div className="border border-blue-200 bg-blue-50 rounded-lg p-6">
                <h4 className="font-semibold text-blue-900 mb-2">How to use:</h4>
                <ol className="text-sm text-blue-800 space-y-2">
                  <li className="flex gap-2">
                    <span className="font-bold">1.</span>
                    <span>Export your data before deploying to production to create a backup</span>
                  </li>
                  <li className="flex gap-2">
                    <span className="font-bold">2.</span>
                    <span>After deployment, login to your production admin panel</span>
                  </li>
                  <li className="flex gap-2">
                    <span className="font-bold">3.</span>
                    <span>Go to Import/Export tab and import the file you exported</span>
                  </li>
                  <li className="flex gap-2">
                    <span className="font-bold">4.</span>
                    <span>All your restaurants, products, and settings will be restored!</span>
                  </li>
                </ol>
              </div>
            </div>
          </div>
        )}


        {/* Change Password Modal */}
        {showChangePassword && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Change Password</h2>
              
              {passwordError && (
                <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
                  {passwordError}
                </div>
              )}
              
              {passwordSuccess && (
                <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded mb-4">
                  {passwordSuccess}
                </div>
              )}
              
              <form onSubmit={handleChangePassword} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Current Password</label>
                  <input
                    type="password"
                    required
                    value={passwordData.old_password}
                    onChange={(e) => setPasswordData({...passwordData, old_password: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Enter current password"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">New Password</label>
                  <input
                    type="password"
                    required
                    value={passwordData.new_password}
                    onChange={(e) => setPasswordData({...passwordData, new_password: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Enter new password (min 6 characters)"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Confirm New Password</label>
                  <input
                    type="password"
                    required
                    value={passwordData.confirm_password}
                    onChange={(e) => setPasswordData({...passwordData, confirm_password: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Confirm new password"
                  />
                </div>
                
                <div className="flex gap-3 pt-4">
                  <button
                    type="submit"
                    disabled={loading}
                    className="flex-1 bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
                  >
                    {loading ? 'Changing...' : 'Change Password'}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setShowChangePassword(false);
                      setPasswordData({ old_password: '', new_password: '', confirm_password: '' });
                      setPasswordError('');
                      setPasswordSuccess('');
                    }}
                    className="flex-1 bg-gray-200 text-gray-700 px-6 py-2 rounded-lg hover:bg-gray-300 transition-colors"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

      </div>
      <PortalFooter role="admin" />
    </div>
  );
}

// ============ AGENT REGISTRATION PAGE ============
function AgentRegisterPage({ backendUrl, onSuccess, onSwitchToLogin }) {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    address: '',
    password: '',
    confirmPassword: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // Validation
    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (formData.password.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }

    setLoading(true);

    try {
      const response = await fetch(`${backendUrl}/api/agent/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          phone: formData.phone,
          address: formData.address,
          password: formData.password
        })
      });

      const data = await response.json();

      if (response.ok) {
        onSuccess(data);
      } else {
        setError(data.detail || 'Registration failed');
      }
    } catch (err) {
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 to-emerald-100 flex items-center justify-center py-12 px-4">
      <div className="max-w-md w-full">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="w-20 h-20 bg-gradient-to-br from-green-500 to-emerald-600 rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg">
            <svg className="w-10 h-10 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Become an Agent</h1>
          <p className="text-gray-600">Register to start earning commissions by listing businesses on SchoolVerse</p>
        </div>

        {/* Form Card */}
        <div className="bg-white rounded-2xl shadow-xl p-6">
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-4 flex items-center gap-2">
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Full Name */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Full Name</label>
              <input
                type="text"
                required
                placeholder="Enter your full name"
                value={formData.name}
                onChange={(e) => setFormData({...formData, name: e.target.value})}
                className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all"
              />
            </div>

            {/* Email */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Email Address</label>
              <input
                type="email"
                required
                placeholder="your@email.com"
                value={formData.email}
                onChange={(e) => setFormData({...formData, email: e.target.value})}
                className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all"
              />
            </div>

            {/* Phone */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Phone Number</label>
              <input
                type="tel"
                required
                placeholder="+234 800 000 0000"
                value={formData.phone}
                onChange={(e) => setFormData({...formData, phone: e.target.value})}
                className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all"
              />
            </div>

            {/* Address */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Address (optional)</label>
              <input
                type="text"
                placeholder="Your address"
                value={formData.address}
                onChange={(e) => setFormData({...formData, address: e.target.value})}
                className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all"
              />
            </div>

            {/* Password */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Password</label>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  required
                  placeholder="Create a password"
                  value={formData.password}
                  onChange={(e) => setFormData({...formData, password: e.target.value})}
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all pr-12"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                >
                  {showPassword ? (
                    <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M3 3l18 18"/><path d="M10.58 10.58a2 2 0 002.83 2.83"/><path d="M9.88 5.09A9.95 9.95 0 0112 5c4.48 0 8.27 2.94 9.54 7a10.07 10.07 0 01-4.2 5.36"/><path d="M6.1 6.1A10.05 10.05 0 002.46 12c1.27 4.06 5.06 7 9.54 7 1.07 0 2.1-.18 3.05-.51"/></svg>
                  ) : (
                    <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M2.46 12C3.73 7.94 7.52 5 12 5s8.27 2.94 9.54 7c-1.27 4.06-5.06 7-9.54 7s-8.27-2.94-9.54-7z"/><circle cx="12" cy="12" r="3"/></svg>
                  )}
                </button>
              </div>
            </div>

            {/* Confirm Password */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Confirm Password</label>
              <div className="relative">
                <input
                  type={showConfirmPassword ? 'text' : 'password'}
                  required
                  placeholder="Confirm your password"
                  value={formData.confirmPassword}
                  onChange={(e) => setFormData({...formData, confirmPassword: e.target.value})}
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all pr-12"
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                >
                  {showConfirmPassword ? 'â—‰' : 'â—Œ'}
                </button>
              </div>
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={loading}
              className="w-full bg-gradient-to-r from-green-500 to-emerald-600 text-white py-3 rounded-xl font-semibold hover:from-green-600 hover:to-emerald-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-lg"
            >
              {loading ? (
                <span className="flex items-center justify-center gap-2">
                  <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                  Creating Account...
                </span>
              ) : (
                ' Create Agent Account'
              )}
            </button>
          </form>

          {/* Login Link */}
          <div className="mt-6 text-center">
            <p className="text-gray-600">
              Already have an account?{' '}
              <button
                onClick={onSwitchToLogin}
                className="text-green-600 font-semibold hover:underline"
              >
                Sign In
              </button>
            </p>
          </div>

          {/* Back to Home */}
          <div className="mt-4 text-center">
            <a
              href="#/"
              onClick={(e) => {
                e.preventDefault();
                window.location.hash = '';
              }}
              className="text-gray-500 hover:text-gray-700 text-sm"
            >
              ? Back to Home
            </a>
          </div>
        </div>

        {/* Benefits */}
        <div className="mt-6 bg-white/60 backdrop-blur rounded-2xl p-4">
          <h3 className="font-semibold text-gray-800 mb-3 text-center">Why become an agent?</h3>
          <div className="space-y-2">
            <div className="flex items-center gap-2 text-sm text-gray-600">
              <span className="text-green-500"></span>
              <span>Earn commission on every order from businesses you register</span>
            </div>
            <div className="flex items-center gap-2 text-sm text-gray-600">
              <span className="text-green-500"></span>
              <span>No upfront investment required</span>
            </div>
            <div className="flex items-center gap-2 text-sm text-gray-600">
              <span className="text-green-500"></span>
              <span>Register unlimited businesses</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ============ VENDOR REGISTRATION SUCCESS ============
function VendorRegistrationSuccessPage({ result, onLogin, onHome }) {
  const vendor = result?.vendor || {};
  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4 py-12">
      <div className="max-w-lg w-full bg-white rounded-2xl shadow-xl p-8 text-center">
        <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-5" aria-hidden="true">
          <svg className="w-9 h-9 text-green-600" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M5 12l4 4L19 7"/></svg>
        </div>
        <h1 className="text-2xl font-bold text-gray-900">Business registration submitted</h1>
        <p className="mt-3 text-gray-600">Your business has been registered successfully and is awaiting SchoolVerse admin approval.</p>
        {vendor.name && <p className="mt-4 font-semibold text-gray-900">{vendor.name}</p>}
        {result?.vendor_id && <p className="mt-1 text-sm text-gray-500">Registration reference: {result.vendor_id}</p>}
        {result?.email && <p className="mt-1 text-sm text-gray-500">Registered email: {result.email}</p>}
        <div className="mt-6 text-left rounded-xl border border-gray-200 bg-gray-50 p-5">
          <h2 className="font-semibold text-gray-900">What happens next</h2>
          <ol className="mt-3 space-y-2 text-sm text-gray-600 list-decimal list-inside">
            <li>SchoolVerse reviews your business registration.</li>
            <li>SchoolVerse reviews and approves your business account.</li>
            <li>After approval, sign in with the email and password you used during registration.</li>
            <li>Use <strong>Complete registration</strong> in your business dashboard to add your legal and bank details.</li>
          </ol>
        </div>
        <div className="mt-6 grid gap-3">
          <button onClick={onLogin} className="w-full bg-imax-orange text-white py-3 rounded-xl font-semibold">Go to Business Login</button>
          <button onClick={onHome} className="w-full border border-gray-300 text-gray-700 py-3 rounded-xl font-semibold">Return to SchoolVerse</button>
        </div>
      </div>
    </div>
  );
}

function AgentRegistrationSuccessPage({ result, onLogin, onHome }) {
  const agent = result?.agent || {};
  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4 py-12">
      <div className="max-w-lg w-full bg-white rounded-2xl shadow-xl p-8 text-center">
        <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-5" aria-hidden="true">
          <svg className="w-9 h-9 text-green-600" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M5 12l4 4L19 7"/></svg>
        </div>
        <h1 className="text-2xl font-bold text-gray-900">Agent application submitted</h1>
        <p className="mt-3 text-gray-600">Your application was received and is awaiting SchoolVerse admin approval.</p>
        {agent.name && <p className="mt-4 font-semibold text-gray-900">{agent.name}</p>}
        {agent.email && <p className="mt-1 text-sm text-gray-500">Registered email: {agent.email}</p>}
        <div className="mt-6 text-left rounded-xl border border-gray-200 bg-gray-50 p-5">
          <h2 className="font-semibold text-gray-900">What happens next</h2>
          <ol className="mt-3 space-y-2 text-sm text-gray-600 list-decimal list-inside">
            <li>SchoolVerse reviews your agent application.</li>
            <li>You can sign in after your application is approved.</li>
            <li>Use Forgot Password on the Agent Login page if you ever need to reset your password.</li>
          </ol>
        </div>
        <div className="mt-6 grid gap-3">
          <button onClick={onLogin} className="w-full bg-imax-orange text-white py-3 rounded-xl font-semibold">Go to Agent Login</button>
          <button onClick={onHome} className="w-full border border-gray-300 text-gray-700 py-3 rounded-xl font-semibold">Return to SchoolVerse</button>
        </div>
      </div>
    </div>
  );
}

function AgentForgotPasswordPage({ backendUrl, onBackToLogin }) {
  const toast=useToast(); const [email,setEmail]=useState(''); const [loading,setLoading]=useState(false); const [submitted,setSubmitted]=useState(false);
  const submit=async(e)=>{e.preventDefault();setLoading(true);try{const r=await fetch(`${backendUrl}/api/agent/forgot-password`,{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({email})});const d=await r.json();if(!r.ok)throw new Error(d.detail||'Unable to request reset');setSubmitted(true);}catch(e){toast.error(e.message);}finally{setLoading(false);}};
  return <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4"><div className="max-w-md w-full bg-white rounded-2xl shadow-xl p-8"><h1 className="text-2xl font-bold text-gray-900 text-center">Agent password reset</h1>{submitted?<><p className="mt-4 text-center text-gray-600">If this email exists, a password reset link has been sent.</p><button onClick={onBackToLogin} className="mt-6 w-full bg-imax-orange text-white py-3 rounded-xl font-semibold">Back to Agent Login</button></>:<form onSubmit={submit} className="mt-6 space-y-4"><label className="block text-sm font-medium text-gray-700">Email address<input required type="email" value={email} onChange={e=>setEmail(e.target.value)} className="mt-1 w-full px-4 py-3 border rounded-xl"/></label><button disabled={loading} className="w-full bg-imax-orange text-white py-3 rounded-xl font-semibold">{loading?'Sending...':'Send Reset Link'}</button><button type="button" onClick={onBackToLogin} className="w-full text-gray-600 py-2">Back to Agent Login</button></form>}</div></div>;
}

function AgentResetPasswordPage({ backendUrl, token, onBackToLogin, onSuccess }) {
  const toast=useToast(); const [password,setPassword]=useState(''); const [confirm,setConfirm]=useState(''); const [valid,setValid]=useState(null); const [loading,setLoading]=useState(false);
  useEffect(()=>{if(!token){setValid(false);return;}fetch(`${backendUrl}/api/agent/check-reset-token?token=${encodeURIComponent(token)}`).then(r=>setValid(r.ok)).catch(()=>setValid(false));},[backendUrl,token]);
  const submit=async(e)=>{e.preventDefault();if(password!==confirm){toast.error('Passwords do not match');return;}if(password.length<8||!/[A-Za-z]/.test(password)||!/\d/.test(password)){toast.error('Password must be at least 8 characters and contain a letter and number');return;}setLoading(true);try{const r=await fetch(`${backendUrl}/api/agent/reset-password`,{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({token,new_password:password})});const d=await r.json();if(!r.ok)throw new Error(d.detail||'Reset failed');toast.success('Password reset successful.');onSuccess();}catch(e){toast.error(e.message);}finally{setLoading(false);}};
  if(valid===null)return <div className="min-h-screen flex items-center justify-center">Checking reset link...</div>;
  if(!valid)return <div className="min-h-screen flex items-center justify-center px-4"><div className="max-w-md w-full bg-white rounded-2xl shadow p-8 text-center"><h1 className="text-xl font-bold">Invalid or expired reset link</h1><button onClick={onBackToLogin} className="mt-6 text-imax-orange font-semibold">Back to Agent Login</button></div></div>;
  return <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4"><form onSubmit={submit} className="max-w-md w-full bg-white rounded-2xl shadow-xl p-8 space-y-4"><h1 className="text-2xl font-bold text-center">Create a new agent password</h1><input required type="password" placeholder="New password" value={password} onChange={e=>setPassword(e.target.value)} className="w-full px-4 py-3 border rounded-xl"/><input required type="password" placeholder="Confirm password" value={confirm} onChange={e=>setConfirm(e.target.value)} className="w-full px-4 py-3 border rounded-xl"/><button disabled={loading} className="w-full bg-imax-orange text-white py-3 rounded-xl font-semibold">{loading?'Saving...':'Reset Password'}</button></form></div>;
}

// ============ AGENT LOGIN PAGE ============
function AgentLoginPage({ backendUrl, onSuccess, onSwitchToRegister, onForgotPassword }) {
  const [formData, setFormData] = useState({ email: '', password: '' });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const response = await fetch(`${backendUrl}/api/agent/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });

      const data = await response.json();

      if (response.ok) {
        onSuccess(data);
      } else {
        setError(data.detail || 'Login failed');
      }
    } catch (err) {
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 to-emerald-100 flex items-center justify-center py-12 px-4">
      <div className="max-w-md w-full">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="w-20 h-20 bg-gradient-to-br from-green-500 to-emerald-600 rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg">
            <svg className="w-10 h-10 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
            </svg>
          </div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Agent Login</h1>
          <p className="text-gray-600">Sign in to your agent dashboard</p>
        </div>

        {/* Form Card */}
        <div className="bg-white rounded-2xl shadow-xl p-6">
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-4 flex items-center gap-2">
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Email */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Email Address</label>
              <input
                type="email"
                required
                placeholder="your@email.com"
                value={formData.email}
                onChange={(e) => setFormData({...formData, email: e.target.value})}
                className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all"
              />
            </div>

            {/* Password */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Password</label>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  required
                  placeholder="Enter your password"
                  value={formData.password}
                  onChange={(e) => setFormData({...formData, password: e.target.value})}
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all pr-12"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                >
                  {showPassword ? <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7S2 12 2 12Z"/><circle cx="12" cy="12" r="3"/></svg> : <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M3 3l18 18"/><path d="M10.6 5.1A10.8 10.8 0 0 1 12 5c7 0 10 7 10 7a18.5 18.5 0 0 1-3.1 4.3M6.5 6.5C3.6 8.4 2 12 2 12s3.5 7 10 7a9.8 9.8 0 0 0 4.1-.9"/><path d="M9.9 9.9a3 3 0 0 0 4.2 4.2"/></svg>}
                </button>
              </div>
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={loading}
              className="w-full bg-gradient-to-r from-green-500 to-emerald-600 text-white py-3 rounded-xl font-semibold hover:from-green-600 hover:to-emerald-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-lg"
            >
              {loading ? (
                <span className="flex items-center justify-center gap-2">
                  <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                  Signing In...
                </span>
              ) : (
                ' Sign In'
              )}
            </button>
          </form>

          <div className="mt-4 text-center">
            <button type="button" onClick={onForgotPassword} className="text-green-600 font-semibold hover:underline text-sm">Forgot Password?</button>
          </div>

          {/* Register Link */}
          <div className="mt-6 text-center">
            <p className="text-gray-600">
              Don't have an account?{' '}
              <button
                onClick={onSwitchToRegister}
                className="text-green-600 font-semibold hover:underline"
              >
                Register as Agent
              </button>
            </p>
          </div>

          {/* Back to Home */}
          <div className="mt-4 text-center">
            <a
              href="#/"
              onClick={(e) => {
                e.preventDefault();
                window.location.hash = '';
              }}
              className="text-gray-500 hover:text-gray-700 text-sm"
            >
              ? Back to Home
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}

// ============ AGENT DASHBOARD ============
function AgentDashboard({ agent, token, backendUrl, onLogout, vendorTypes, locations }) {
  const [activeTab, setActiveTab] = useState('overview');
  const [myVendors, setMyVendors] = useState([]);
  const [earnings, setEarnings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showRegisterModal, setShowRegisterModal] = useState(false);
  const [pendingWhatsApp, setPendingWhatsApp] = useState(null);

  // Fetch agent data
  useEffect(() => {
    fetchAgentData();
  }, []);

  const fetchAgentData = async () => {
    setLoading(true);
    try {
      // Fetch vendors
      const vendorsRes = await fetch(`${backendUrl}/api/agent/my-vendors`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (vendorsRes.ok) {
        const vendorsData = await vendorsRes.json();
        setMyVendors(vendorsData.vendors || []);
      }

      // Fetch earnings
      const earningsRes = await fetch(`${backendUrl}/api/agent/earnings`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (earningsRes.ok) {
        const earningsData = await earningsRes.json();
        setEarnings(earningsData);
      }
    } catch (err) {
      console.error('Error fetching agent data:', err);
    } finally {
      setLoading(false);
    }
  };

  // Get status badge
  const getStatusBadge = (status) => {
    switch (status) {
      case 'approved':
        return <span className="px-2 py-1 bg-green-100 text-green-700 rounded-full text-xs font-medium"> Approved</span>;
      case 'pending_admin_approval':
        return <span className="px-2 py-1 bg-yellow-100 text-yellow-700 rounded-full text-xs font-medium">Pending Admin</span>;
      case 'pending_vendor_approval':
        return <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded-full text-xs font-medium"> Awaiting Vendor</span>;
      case 'rejected':
        return <span className="px-2 py-1 bg-red-100 text-red-700 rounded-full text-xs font-medium"> Rejected</span>;
      default:
        return <span className="px-2 py-1 bg-gray-100 text-gray-700 rounded-full text-xs font-medium">{status}</span>;
    }
  };

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Header */}
      <header className="bg-gradient-to-r from-green-600 to-emerald-600 text-white shadow-lg">
        <div className="max-w-6xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                </svg>
              </div>
              <div>
                <h1 className="font-bold text-lg">Agent Dashboard</h1>
                <p className="text-sm text-white/80">{agent?.name || 'Agent'}</p>
              </div>
            </div>
            <button
              onClick={onLogout}
              className="px-4 py-2 bg-white/20 hover:bg-white/30 rounded-lg transition-colors text-sm font-medium"
            >
              Logout
            </button>
          </div>
        </div>
      </header>

      {/* Stats Cards */}
      <div className="max-w-6xl mx-auto px-4 -mt-6">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-white rounded-xl shadow-md p-4">
            <div className="text-3xl mb-1"></div>
            <div className="text-2xl font-bold text-gray-800">{earnings?.total_vendors || 0}</div>
            <div className="text-sm text-gray-500">Total Vendors</div>
          </div>
          <div className="bg-white rounded-xl shadow-md p-4">
            <div className="text-3xl mb-1"></div>
            <div className="text-2xl font-bold text-green-600">{earnings?.approved_vendors || 0}</div>
            <div className="text-sm text-gray-500">Approved</div>
          </div>
          <div className="bg-white rounded-xl shadow-md p-4">
            <UiIcon name="spinner" size={30} className="animate-spin mb-1" />
            <div className="text-2xl font-bold text-yellow-600">{earnings?.pending_vendors || 0}</div>
            <div className="text-sm text-gray-500">Pending</div>
          </div>
          <div className="bg-white rounded-xl shadow-md p-4">
            <div className="text-3xl mb-1"></div>
            <div className="text-2xl font-bold text-emerald-600">₦{(earnings?.total_earnings || 0).toLocaleString()}</div>
            <div className="text-sm text-gray-500">Total Earnings</div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-6xl mx-auto px-4 py-6">
        {/* Tabs */}
        <div className="flex gap-2 mb-6 overflow-x-auto pb-2">
          <button
            onClick={() => setActiveTab('overview')}
            className={`px-4 py-2 rounded-lg font-medium whitespace-nowrap transition-colors ${
              activeTab === 'overview' ? 'bg-green-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-100'
            }`}
          >
             Overview
          </button>
          <button
            onClick={() => setActiveTab('vendors')}
            className={`px-4 py-2 rounded-lg font-medium whitespace-nowrap transition-colors ${
              activeTab === 'vendors' ? 'bg-green-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-100'
            }`}
          >
             My Vendors
          </button>
          <button
            onClick={() => setActiveTab('earnings')}
            className={`px-4 py-2 rounded-lg font-medium whitespace-nowrap transition-colors ${
              activeTab === 'earnings' ? 'bg-green-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-100'
            }`}
          >
             Earnings
          </button>
        </div>

        {/* Register Vendor Button */}
        <button
          onClick={() => setShowRegisterModal(true)}
          className="w-full md:w-auto mb-6 px-6 py-3 bg-gradient-to-r from-green-500 to-emerald-600 text-white rounded-xl font-semibold hover:from-green-600 hover:to-emerald-700 transition-all shadow-lg flex items-center justify-center gap-2"
        >
          <span className="text-xl"></span>
          Register New Business
        </button>

        {loading ? (
          <div className="bg-white rounded-xl shadow-md p-8 text-center">
            <div className="animate-spin w-8 h-8 border-4 border-green-500 border-t-transparent rounded-full mx-auto mb-4"></div>
            <p className="text-gray-500">Loading...</p>
          </div>
        ) : (
          <>
            {/* Overview Tab */}
            {activeTab === 'overview' && (
              <div className="space-y-6">
                {/* Quick Stats */}
                <div className="bg-white rounded-xl shadow-md p-6">
                  <h2 className="text-lg font-bold text-gray-800 mb-4">Welcome, {agent?.name}! </h2>
                  <p className="text-gray-600 mb-4">
                    Your current commission rate is <strong className="text-green-600">{earnings?.commission_percentage || 5}%</strong> on all orders from vendors you register.
                  </p>
                  <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                    <p className="text-green-700 text-sm">
                       <strong>Tip:</strong> The more businesses you register, the more you earn! Start by clicking "Register New Business" above.
                    </p>
                  </div>
                </div>

                {/* Recent Activity */}
                {myVendors.length > 0 && (
                  <div className="bg-white rounded-xl shadow-md p-6">
                    <h2 className="text-lg font-bold text-gray-800 mb-4">Recent Registrations</h2>
                    <div className="space-y-3">
                      {myVendors.slice(0, 5).map((vendor) => (
                        <div key={vendor.vendor_id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                          <div>
                            <p className="font-medium text-gray-800">{vendor.vendor_name}</p>
                            <p className="text-sm text-gray-500">{vendor.vendor_details?.vendor_type || 'Business'}</p>
                          </div>
                          {getStatusBadge(vendor.status)}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {myVendors.length === 0 && (
                  <div className="bg-white rounded-xl shadow-md p-8 text-center">
                    <div className="text-6xl mb-4"></div>
                    <h3 className="text-xl font-bold text-gray-800 mb-2">No vendors registered yet</h3>
                    <p className="text-gray-600 mb-4">Start earning by registering your first business!</p>
                    <button
                      onClick={() => setShowRegisterModal(true)}
                      className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                    >
                      Register First Business
                    </button>
                <button
                  onClick={() => setActiveTab('payment')}
                  className={`px-4 py-2 rounded-lg font-medium ${activeTab === 'payment' ? 'bg-green-600 text-white' : 'text-gray-600 hover:bg-gray-100'}`}
                >Payment & Settlement</button>
                  </div>
                )}
              </div>
            )}

            {/* Vendors Tab */}
            {activeTab === 'vendors' && (
              <div className="bg-white rounded-xl shadow-md overflow-hidden">
                {myVendors.length === 0 ? (
                  <div className="p-8 text-center">
                    <div className="text-6xl mb-4"></div>
                    <h3 className="text-xl font-bold text-gray-800 mb-2">No vendors yet</h3>
                    <p className="text-gray-600">Vendors you register will appear here.</p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-4 py-3 text-left text-sm font-semibold text-gray-600">Business</th>
                          <th className="px-4 py-3 text-left text-sm font-semibold text-gray-600">Type</th>
                          <th className="px-4 py-3 text-left text-sm font-semibold text-gray-600">Commission</th>
                          <th className="px-4 py-3 text-left text-sm font-semibold text-gray-600">Status</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {myVendors.map((vendor) => (
                          <tr key={vendor.vendor_id} className="hover:bg-gray-50">
                            <td className="px-4 py-3">
                              <p className="font-medium text-gray-800">{vendor.vendor_name}</p>
                              <p className="text-sm text-gray-500">{vendor.vendor_phone}</p>
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-600">
                              {vendor.vendor_details?.vendor_type || 'N/A'}
                            </td>
                            <td className="px-4 py-3 text-sm text-green-600 font-medium">
                              {vendor.commission_percentage}%
                            </td>
                            <td className="px-4 py-3">
                              {getStatusBadge(vendor.status)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}

            {/* Earnings Tab */}
            {activeTab === 'earnings' && (
              <div className="space-y-6">
                <div className="bg-white rounded-xl shadow-md p-6">
                  <h2 className="text-lg font-bold text-gray-800 mb-4">Earnings Summary</h2>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="bg-gradient-to-br from-green-500 to-emerald-600 rounded-xl p-6 text-white">
                      <p className="text-white/80 text-sm">Total Earnings</p>
                      <p className="text-3xl font-bold">₦{(earnings?.total_earnings || 0).toLocaleString()}</p>
                    </div>
                    <div className="bg-gray-50 rounded-xl p-6">
                      <p className="text-gray-500 text-sm">Commission Rate</p>
                      <p className="text-3xl font-bold text-gray-800">{earnings?.commission_percentage || 5}%</p>
                    </div>
                  </div>
                </div>

                {earnings?.vendor_earnings && earnings.vendor_earnings.length > 0 && (
                  <div className="bg-white rounded-xl shadow-md p-6">
                    <h2 className="text-lg font-bold text-gray-800 mb-4">Earnings by Vendor</h2>
                    <div className="space-y-3">
                      {earnings.vendor_earnings.map((v, i) => (
                        <div key={i} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                          <div>
                            <p className="font-medium text-gray-800">{v.vendor_name}</p>
                            <p className="text-sm text-gray-500">Total Sales: ₦{(v.total_sales || 0).toLocaleString()}</p>
                          </div>
                          <div className="text-right">
                            <p className="font-bold text-green-600">₦{(v.commission_earned || 0).toLocaleString()}</p>
                            <p className="text-xs text-gray-500">{v.commission_percentage}% commission</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {(!earnings?.vendor_earnings || earnings.vendor_earnings.length === 0) && (
                  <div className="bg-white rounded-xl shadow-md p-8 text-center">
                    <div className="text-6xl mb-4"></div>
                    <h3 className="text-xl font-bold text-gray-800 mb-2">No earnings yet</h3>
                    <p className="text-gray-600">You'll start earning when your registered vendors receive orders.</p>
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </div>

      {activeTab === 'payment' && (
        <Suspense fallback={<div className="py-3 text-sm text-gray-500">Loading payment settings…</div>}>
          <PaymentAccountSetup backendUrl={backendUrl} token={token} ownerType="agent" />
        </Suspense>
      )}

      {/* Register Vendor Modal */}
      {showRegisterModal && (
        <AgentVendorRegisterModal
          backendUrl={backendUrl}
          token={token}
          vendorTypes={vendorTypes}
          locations={locations}
          onClose={() => setShowRegisterModal(false)}
          onSuccess={(data) => {
            setPendingWhatsApp(data);
            setShowRegisterModal(false);
            fetchAgentData();
          }}
        />
      )}

      {/* WhatsApp Confirmation Modal */}
      {pendingWhatsApp && (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-md w-full p-6 animate-slide-in">
            <div className="text-center mb-6">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-3xl"></span>
              </div>
              <h2 className="text-xl font-bold text-gray-800">Send Approval Request</h2>
              <p className="text-gray-600 mt-2">
                Now send the approval request to <strong>{pendingWhatsApp.vendor_phone}</strong> via WhatsApp
              </p>
            </div>

            <div className="bg-gray-50 rounded-lg p-4 mb-6">
              <p className="text-sm text-gray-600 whitespace-pre-wrap">{pendingWhatsApp.whatsapp_message}</p>
            </div>

            <div className="space-y-3">
              <a
                href={pendingWhatsApp.whatsapp_link}
                target="_blank"
                rel="noopener noreferrer"
                className="block w-full bg-green-500 text-white py-3 rounded-xl font-semibold text-center hover:bg-green-600 transition-colors"
              >
                 Open WhatsApp
              </a>
              <button
                onClick={() => setPendingWhatsApp(null)}
                className="w-full bg-gray-200 text-gray-700 py-3 rounded-xl font-semibold hover:bg-gray-300 transition-colors"
              >
                Done
              </button>
            </div>
          </div>
        </div>
      )}
      <PortalFooter role="agent" />
    </div>
  );
}

// ============ AGENT VENDOR REGISTER MODAL ============
function AgentVendorRegisterModal({ backendUrl, token, vendorTypes, locations, onClose, onSuccess }) {
  const [formData, setFormData] = useState({
    business_name: '',
    business_phone: '',
    business_email: '',
    description: '',
    category: '',
    vendor_type: '',
    location: '',
    address: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const response = await fetch(`${backendUrl}/api/agent/register-vendor`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(formData)
      });

      const data = await response.json();

      if (response.ok) {
        onSuccess(data);
      } else {
        setError(data.detail || 'Registration failed');
      }
    } catch (err) {
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4 overflow-y-auto">
      <div className="bg-white rounded-2xl max-w-lg w-full my-8 animate-slide-in">
        {/* Header */}
        <div className="bg-gradient-to-r from-green-500 to-emerald-600 p-4 rounded-t-2xl">
          <div className="flex items-center justify-between text-white">
            <h2 className="text-lg font-bold">Register New Business</h2>
            <button onClick={onClose} className="p-1 hover:bg-white/20 rounded-full transition-colors">
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {/* Form */}
        <div className="p-6 max-h-[70vh] overflow-y-auto">
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-4">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Business Name */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Business Name *</label>
              <input
                type="text"
                required
                placeholder="e.g., John's Restaurant"
                value={formData.business_name}
                onChange={(e) => setFormData({...formData, business_name: e.target.value})}
                className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-transparent"
              />
            </div>

            {/* Business Phone */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Business Phone (WhatsApp) *</label>
              <input
                type="tel"
                required
                placeholder="+234 800 000 0000"
                value={formData.business_phone}
                onChange={(e) => setFormData({...formData, business_phone: e.target.value})}
                className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-transparent"
              />
              <p className="text-xs text-gray-500 mt-1">Approval request will be sent to this number</p>
            </div>

            {/* Business Email */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Business Email</label>
              <input
                type="email"
                placeholder="business@example.com"
                value={formData.business_email}
                onChange={(e) => setFormData({...formData, business_email: e.target.value})}
                className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-transparent"
              />
            </div>

            {/* Business Type */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Business Type *</label>
              <select
                required
                value={formData.vendor_type}
                onChange={(e) => setFormData({...formData, vendor_type: e.target.value})}
                className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-transparent"
              >
                <option value="">Select type...</option>
                {(vendorTypes || []).map((type) => (
                  <option key={type.type} value={type.type}>
                    {type.label}
                  </option>
                ))}
              </select>
            </div>

            {/* Category */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Category *</label>
              <input
                type="text"
                required
                placeholder="e.g., African Cuisine, Electronics"
                value={formData.category}
                onChange={(e) => setFormData({...formData, category: e.target.value})}
                className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-transparent"
              />
            </div>

            {/* Location */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Location *</label>
              <select
                required
                value={formData.location}
                onChange={(e) => setFormData({...formData, location: e.target.value})}
                className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-transparent"
              >
                <option value="">Select location...</option>
                {(locations || []).map((loc) => (
                  <option key={loc.id || loc.name} value={loc.name}>
                    {loc.name}
                  </option>
                ))}
              </select>
            </div>

            {/* Address */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Full Address</label>
              <textarea
                rows={2}
                placeholder="Business address..."
                value={formData.address}
                onChange={(e) => setFormData({...formData, address: e.target.value})}
                className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-transparent resize-none"
              />
            </div>

            {/* Description */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
              <textarea
                rows={3}
                placeholder="Brief description of the business..."
                value={formData.description}
                onChange={(e) => setFormData({...formData, description: e.target.value})}
                className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-transparent resize-none"
              />
            </div>

            {/* Submit */}
            <div className="flex gap-3 pt-4">
              <button
                type="button"
                onClick={onClose}
                className="flex-1 px-4 py-3 bg-gray-200 text-gray-700 rounded-xl font-semibold hover:bg-gray-300 transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading}
                className="flex-1 px-4 py-3 bg-gradient-to-r from-green-500 to-emerald-600 text-white rounded-xl font-semibold hover:from-green-600 hover:to-emerald-700 transition-all disabled:opacity-50"
              >
                {loading ? 'Registering...' : 'Register Business'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

// ============ VENDOR APPROVAL PAGE ============
function VendorApprovalPage({ backendUrl, onComplete }) {
  const [status, setStatus] = useState('loading'); // loading, found, approved, error
  const [vendorInfo, setVendorInfo] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  // Extract token from URL
  const token = window.location.hash.split('/vendor-approval/')[1] || '';

  useEffect(() => {
    if (token) {
      fetchVendorInfo();
    } else {
      setStatus('error');
      setError('Invalid approval link');
    }
  }, [token]);

  const fetchVendorInfo = async () => {
    try {
      const response = await fetch(`${backendUrl}/api/vendor-approval/${token}`);
      const data = await response.json();

      if (response.ok) {
        setVendorInfo(data);
        if (data.status === 'pending_vendor_approval') {
          setStatus('found');
        } else if (data.status === 'pending_admin_approval' || data.status === 'approved') {
          setStatus('already_approved');
        } else {
          setStatus('error');
          setError('This registration has already been processed');
        }
      } else {
        setStatus('error');
        setError(data.detail || 'Invalid or expired link');
      }
    } catch (err) {
      setStatus('error');
      setError('Unable to verify link. Please try again.');
    }
  };

  const handleApprove = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${backendUrl}/api/vendor-approval/${token}`, {
        method: 'POST'
      });
      const data = await response.json();

      if (response.ok) {
        setStatus('approved');
      } else {
        setError(data.detail || 'Approval failed');
      }
    } catch (err) {
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center p-4">
      <div className="max-w-md w-full">
        {/* Loading State */}
        {status === 'loading' && (
          <div className="bg-white rounded-2xl shadow-xl p-8 text-center">
            <div className="animate-spin w-12 h-12 border-4 border-blue-500 border-t-transparent rounded-full mx-auto mb-4"></div>
            <p className="text-gray-600">Verifying approval link...</p>
          </div>
        )}

        {/* Found - Show Approval UI */}
        {status === 'found' && vendorInfo && (
          <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
            <div className="bg-gradient-to-r from-blue-600 to-indigo-600 p-6 text-white text-center">
              <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <h1 className="text-2xl font-bold">Business Registration</h1>
              <p className="text-white/80 mt-2">Approve your registration on SchoolVerse</p>
            </div>

            <div className="p-6">
              {error && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-4">
                  {error}
                </div>
              )}

              <div className="space-y-4 mb-6">
                <div className="bg-gray-50 rounded-lg p-4">
                  <p className="text-sm text-gray-500">Business Name</p>
                  <p className="font-semibold text-gray-800">{vendorInfo.vendor_name}</p>
                </div>
                <div className="bg-gray-50 rounded-lg p-4">
                  <p className="text-sm text-gray-500">Business Type</p>
                  <p className="font-semibold text-gray-800">{vendorInfo.business_type}</p>
                </div>
                <div className="bg-gray-50 rounded-lg p-4">
                  <p className="text-sm text-gray-500">Category</p>
                  <p className="font-semibold text-gray-800">{vendorInfo.category}</p>
                </div>
                <div className="bg-gray-50 rounded-lg p-4">
                  <p className="text-sm text-gray-500">Registered by Agent</p>
                  <p className="font-semibold text-gray-800">{vendorInfo.agent_name}</p>
                </div>
              </div>

              <p className="text-sm text-gray-600 mb-6 text-center">
                By approving, you confirm that this registration is authorized and you agree to list your business on SchoolVerse marketplace.
              </p>

              <button
                onClick={handleApprove}
                disabled={loading}
                className="w-full bg-gradient-to-r from-green-500 to-emerald-600 text-white py-3 rounded-xl font-semibold hover:from-green-600 hover:to-emerald-700 transition-all disabled:opacity-50"
              >
                {loading ? 'Approving...' : ' Approve Registration'}
              </button>

              <p className="text-xs text-gray-500 text-center mt-4">
                If you did not authorize this registration, please ignore this page.
              </p>
            </div>
          </div>
        )}

        {/* Already Approved */}
        {status === 'already_approved' && vendorInfo && (
          <div className="bg-white rounded-2xl shadow-xl p-8 text-center">
            <div className="w-20 h-20 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <span className="text-4xl"></span>
            </div>
            <h2 className="text-2xl font-bold text-gray-800 mb-2">Already Approved</h2>
            <p className="text-gray-600 mb-6">
              "{vendorInfo.vendor_name}" has already been approved and is pending admin review.
            </p>
            <button
              onClick={onComplete}
              className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              Go to Homepage
            </button>
          </div>
        )}

        {/* Approved Successfully */}
        {status === 'approved' && (
          <div className="bg-white rounded-2xl shadow-xl p-8 text-center">
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <span className="text-4xl"></span>
            </div>
            <h2 className="text-2xl font-bold text-gray-800 mb-2">Registration Approved!</h2>
            <p className="text-gray-600 mb-6">
              Thank you! Your business registration is now pending admin review. You'll receive your login credentials once approved.
            </p>
            <button
              onClick={onComplete}
              className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
            >
              Go to Homepage
            </button>
          </div>
        )}

        {/* Error State */}
        {status === 'error' && (
          <div className="bg-white rounded-2xl shadow-xl p-8 text-center">
            <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <span className="text-4xl"></span>
            </div>
            <h2 className="text-2xl font-bold text-gray-800 mb-2">Invalid Link</h2>
            <p className="text-gray-600 mb-6">{error}</p>
            <button
              onClick={onComplete}
              className="px-6 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
            >
              Go to Homepage
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

// Wrap App with ToastProvider + WishlistProvider
function AppWithToast() {
  return (
    <ToastProvider>
      <WishlistProvider>
        <App />
        <AIAssistant />
      </WishlistProvider>
    </ToastProvider>
  );
}

export default AppWithToast;



























